import { useEffect, useRef, useState, useCallback, useImperativeHandle, forwardRef } from 'react';
import { BACKEND_URL } from '@/API_CONFIG';
import { ShoppingCart } from 'lucide-react';
import LevelBadge from './LevelBadge';
// REMOVED: ChevronUp, ChevronDown, ChevronLeft, ChevronRight - D-pad no longer exists

// Get WebSocket URL from backend URL
const getWsUrl = () => {
  const url = new URL(BACKEND_URL);
  const protocol = url.protocol === 'https:' ? 'wss:' : 'ws:';
  return `${protocol}//${url.host}`;
};
const WS_URL = getWsUrl();

const InteractiveMap = forwardRef(({ user, nearbyPlayers, onPlayerClick, onOpenStore }, ref) => {
  const canvasRef = useRef(null);
  const containerRef = useRef(null);
  
  // WebSocket for real-time battle sync
  const battleWsRef = useRef(null);
  const [useWebSocket, setUseWebSocket] = useState(true); // Try WebSocket first
  const [canvasDimensions, setCanvasDimensions] = useState({ width: 800, height: 600 });
  // REMOVED: playerPos - walking character no longer exists
  // REMOVED: targetPos - walking character no longer exists
  // REMOVED: direction, isMoving - walking character no longer exists
  // REMOVED: animFrame - walking character no longer exists
  // REMOVED: dpadPressed - D-pad no longer exists
  
  // Beyblade 1 (Right side - original blue)
  const [top1Active, setTop1Active] = useState(false);
  const [top1Pos, setTop1Pos] = useState({ x: 400, y: 300 });
  const [top1Rotation, setTop1Rotation] = useState(0);
  const [top1Speed, setTop1Speed] = useState(0);
  const [top1Velocity, setTop1Velocity] = useState({ x: 0, y: 0 });
  const [isControlling1, setIsControlling1] = useState(false);
  const controlTimer1Ref = useRef(null);
  const touchpadActive1Ref = useRef(false);
  
  // Beyblade 2 (Left side - new red/orange)
  const [top2Active, setTop2Active] = useState(false);
  const [top2Pos, setTop2Pos] = useState({ x: 400, y: 300 });
  const [top2TargetPos, setTop2TargetPos] = useState({ x: 400, y: 300 }); // For smooth interpolation
  const [top2Rotation, setTop2Rotation] = useState(0);
  const [top2Speed, setTop2Speed] = useState(0);
  const [top2Velocity, setTop2Velocity] = useState({ x: 0, y: 0 });
  const [isControlling2, setIsControlling2] = useState(false);
  const controlTimer2Ref = useRef(null);
  const touchpadActive2Ref = useRef(false);
  
  // Dual-spin beyblade states (for Gravity Perseus)
  const [dualSpinDirection1, setDualSpinDirection1] = useState('right'); // Current spin direction for P1
  const [dualSpinDirection2, setDualSpinDirection2] = useState('right'); // Current spin direction for P2
  
  // Cooldown states
  const [cooldown1, setCooldown1] = useState(false);
  const [cooldown2, setCooldown2] = useState(false);
  const cooldownTimer1Ref = useRef(null);
  const cooldownTimer2Ref = useRef(null);
  
  // Speed boost states
  const [speedBoost1, setSpeedBoost1] = useState(false);
  const [speedBoost2, setSpeedBoost2] = useState(false);
  const [speedMultiplier1, setSpeedMultiplier1] = useState(1);
  const [speedMultiplier2, setSpeedMultiplier2] = useState(1);
  const boostTimer1Ref = useRef(null);
  const boostTimer2Ref = useRef(null);
  
  // Clash lock state - when both beyblades are manually controlled and collide
  const [clashLocked, setClashLocked] = useState(false);
  const [clashLockPos, setClashLockPos] = useState({ x: 0, y: 0 }); // Center point of clash
  const [clashLockP1Pos, setClashLockP1Pos] = useState({ x: 0, y: 0 }); // P1's position when clash started
  const [clashLockP2Pos, setClashLockP2Pos] = useState({ x: 0, y: 0 }); // P2's position when clash started
  const clashLockTimerRef = useRef(null);
  const [speedBoostCooldown1, setSpeedBoostCooldown1] = useState(false);
  const [speedBoostCooldown2, setSpeedBoostCooldown2] = useState(false);
  const speedBoostCooldownTimer1Ref = useRef(null);
  const speedBoostCooldownTimer2Ref = useRef(null);
  
  // Ghost mode states (intangible/no collision)
  const [ghostMode1, setGhostMode1] = useState(false);
  const [ghostMode2, setGhostMode2] = useState(false);
  const ghostTimer1Ref = useRef(null);
  const ghostTimer2Ref = useRef(null);
  const [ghostCooldown1, setGhostCooldown1] = useState(false);
  const [ghostCooldown2, setGhostCooldown2] = useState(false);
  const ghostCooldownTimer1Ref = useRef(null);
  const ghostCooldownTimer2Ref = useRef(null);
  
  // Deflection/Counter states
  const [deflection1, setDeflection1] = useState(false);
  const [deflection2, setDeflection2] = useState(false);
  const deflectionTimer1Ref = useRef(null);
  const deflectionTimer2Ref = useRef(null);
  const [deflectionCooldown1, setDeflectionCooldown1] = useState(false);
  const [deflectionCooldown2, setDeflectionCooldown2] = useState(false);
  const deflectionCooldownTimer1Ref = useRef(null);
  const deflectionCooldownTimer2Ref = useRef(null);
  
  // Shadow Clone states (for Shinobi Shadow - ID 36)
  const [shadowClones1, setShadowClones1] = useState([]); // Array of clone positions for P1
  const [shadowClones2, setShadowClones2] = useState([]); // Array of clone positions for P2
  const [shadowClonesActive1, setShadowClonesActive1] = useState(false);
  const [shadowClonesActive2, setShadowClonesActive2] = useState(false);
  const shadowCloneTimer1Ref = useRef(null);
  const shadowCloneTimer2Ref = useRef(null);
  const [shadowCloneUsed1, setShadowCloneUsed1] = useState(false); // Track if ability was used this round
  const [shadowCloneUsed2, setShadowCloneUsed2] = useState(false);
  
  // Rewind Time states (for Clock Mirage - ID 49)
  const [positionHistory1, setPositionHistory1] = useState([]); // Stores last 2 seconds of P1 positions
  const [positionHistory2, setPositionHistory2] = useState([]); // Stores last 2 seconds of P2 positions
  const [rewindUsed1, setRewindUsed1] = useState(false); // Track if ability was used this round (P1)
  const [rewindUsed2, setRewindUsed2] = useState(false); // Track if ability was used this round (P2)
  const [rewindFreezeActive, setRewindFreezeActive] = useState(false); // Both beyblades frozen after rewind
  const rewindFreezeTimerRef = useRef(null);
  
  // Final Drive states (for Pegasus Beyblades - IDs 5, 21, 44, 45, 53, Dran Sword - ID 2, Dran Brave - ID 37, Dran Brave B - ID 59, Dran Dagger - ID 60, and all Left Spin Beyblades - IDs 4, 18, 42, 47, 54)
  const FINAL_DRIVE_IDS = [5, 21, 44, 45, 53, 2, 37, 59, 60, 4, 18, 42, 47, 54, 69, 73, 74, 78, 79, 80, 81]; // All beyblades that have Final Drive (includes Ultimate Valkyrie, Guilty Longinus, Glory Valkyrie, Drake Brave, Luster Dragoon)
  const hasFinalDrive = (id) => FINAL_DRIVE_IDS.includes(id);
  const [finalDriveActive1, setFinalDriveActive1] = useState(false); // P1 Final Drive active
  const [finalDriveActive2, setFinalDriveActive2] = useState(false); // P2 Final Drive active
  const [finalDriveUsed1, setFinalDriveUsed1] = useState(false); // Track if ability was used this round (P1)
  const [finalDriveUsed2, setFinalDriveUsed2] = useState(false); // Track if ability was used this round (P2)
  const finalDriveTimer1Ref = useRef(null);
  const finalDriveTimer2Ref = useRef(null);
  
  // Restore Health states (for Wizard Rod - ID 24, Wizard Arc - ID 38, Wizard Arrow - ID 8, Wizard Rod G - ID 57, Wizard Rod C - ID 65)
  const WIZARD_IDS = [24, 38, 8, 57, 65]; // All Wizard beyblades that have Restore Health (includes Wizard Rod C)
  const isWizard = (id) => WIZARD_IDS.includes(id);
  const [restoreHealthUsed1, setRestoreHealthUsed1] = useState(false); // Track if ability was used this round (P1)
  const [restoreHealthUsed2, setRestoreHealthUsed2] = useState(false); // Track if ability was used this round (P2)
  
  // Iron Wall states (for Knight Mail - ID 31, Knight Mail B - ID 16, Knight Shield - ID 10, Scorpio Spear - ID 32)
  const KNIGHT_IDS = [31, 16, 10, 1, 61, 62, 32]; // All beyblades that have Iron Wall (includes Cobalt Drake, Cobalt Drake C, Cobalt Drake R, Scorpio Spear)
  const isKnight = (id) => KNIGHT_IDS.includes(id);
  const [ironWallActive1, setIronWallActive1] = useState(false); // P1 Iron Wall active
  const [ironWallActive2, setIronWallActive2] = useState(false); // P2 Iron Wall active
  const [ironWallUsed1, setIronWallUsed1] = useState(false); // Track if ability was used this round (P1)
  const [ironWallUsed2, setIronWallUsed2] = useState(false); // Track if ability was used this round (P2)
  const ironWallTimer1Ref = useRef(null);
  const ironWallTimer2Ref = useRef(null);
  
  // Burning Strike states (for Hells Beyblades - IDs 9, 23, 40)
  const HELLS_IDS = [9, 23, 40, 67, 50, 70, 71, 72]; // All Hells beyblades that have Burning Strike (includes Phoenix Flare, Phoenix Rudder, Fox Brush variants)
  const isHells = (id) => HELLS_IDS.includes(id);
  const [burningStrikeActive1, setBurningStrikeActive1] = useState(false); // P1 Burning Strike active
  const [burningStrikeActive2, setBurningStrikeActive2] = useState(false); // P2 Burning Strike active
  const [burningStrikeUsed1, setBurningStrikeUsed1] = useState(false); // Track if ability was used this round (P1)
  const [burningStrikeUsed2, setBurningStrikeUsed2] = useState(false); // Track if ability was used this round (P2)
  const burningStrikeTimer1Ref = useRef(null);
  const burningStrikeTimer2Ref = useRef(null);
  const [fireRotation1, setFireRotation1] = useState(0); // Rotation angle for fire emojis P1
  const [fireRotation2, setFireRotation2] = useState(0); // Rotation angle for fire emojis P2
  const burningStrikeDamageRef1 = useRef(false); // Prevent multiple damage hits
  const burningStrikeDamageRef2 = useRef(false);
  
  // Stadium selection states
  const [selectedStadium, setSelectedStadium] = useState(() => {
    // Initialize from localStorage to prevent flash of wrong stadium
    const cached = localStorage.getItem('stadiumPreferences');
    if (cached) {
      try {
        const prefs = JSON.parse(cached);
        return prefs.selected_stadium || 'bx';
      } catch { return 'bx'; }
    }
    return 'bx';
  }); // 'bx', 'bb10', or 'bxInfinity' - default BX-Stadium
  const [showStadiumPicker, setShowStadiumPicker] = useState(false);
  
  // ==================== VS AI MODE ====================
  const [aiMode, setAiMode] = useState(false); // Is AI mode active?
  const [aiDifficulty, setAiDifficulty] = useState('normal'); // 'easy', 'normal', 'hard'
  const [aiLaunched, setAiLaunched] = useState(false); // Has AI launched their beyblade?
  // ====================================================
  
  // BX-Infinity - Coordinates finalized (no longer editable)
  
  // Xtreme Dash trigger points - 2 blue (right spin) and 2 red (left spin)
  // These define the infinity loop path endpoints - FIXED VALUES
  const xtremeDashPoints = {
    // Right Spin (Blue) Triggers - top-right to bottom-left diagonal
    blueTop: { x: 473, y: 64 },
    blueBottom: { x: 327, y: 517 },
    // Left Spin (Red) Triggers - top-left to bottom-right diagonal
    redTop: { x: 327, y: 64 },
    redBottom: { x: 473, y: 517 },
  };
  
  // Knockout Boundary vertices - FIXED VALUES (anchor points for drawing lines)
  // Canvas is 800x600, center at (400, 300)
  const boundaryVertices = [
    { x: 400, y: -10 },  // 0
    { x: 540, y: -8 },   // 1
    { x: 622, y: 97 },   // 2
    { x: 659, y: 128 },  // 3
    { x: 700, y: 163 },  // 4
    { x: 716, y: 205 },  // 5
    { x: 722, y: 255 },  // 6
    { x: 722, y: 305 },  // 7
    { x: 716, y: 361 },  // 8
    { x: 686, y: 415 },  // 9
    { x: 583, y: 507 },  // 10
    { x: 521, y: 595 },  // 11
    { x: 400, y: 599 },  // 12
    { x: 279, y: 595 },  // 13
    { x: 196, y: 483 },  // 14
    { x: 111, y: 406 },  // 15
    { x: 87, y: 355 },   // 16
    { x: 84, y: 305 },   // 17
    { x: 84, y: 255 },   // 18
    { x: 87, y: 205 },   // 19
    { x: 106, y: 160 },  // 20
    { x: 138, y: 131 },  // 21
    { x: 205, y: 73 },   // 22
    { x: 263, y: -8 },   // 23
  ];
  
  // Lines drawn between vertices - each line has start, end, and type (FIXED VALUES)
  const drawnLines = [
    { start: 13, end: 11, type: 'xtreme' },   // Line 0 - Bottom Xtreme zone
    { start: 23, end: 1, type: 'xtreme' },    // Line 1 - Top Xtreme zone
    { start: 11, end: 9, type: 'knockout' },  // Line 2
    { start: 13, end: 15, type: 'knockout' }, // Line 3
    { start: 23, end: 20, type: 'knockout' }, // Line 4
    { start: 1, end: 4, type: 'knockout' },   // Line 5
    { start: 9, end: 6, type: 'wall' },       // Line 6
    { start: 6, end: 4, type: 'wall' },       // Line 7
    { start: 16, end: 15, type: 'wall' },     // Line 8
    { start: 16, end: 20, type: 'wall' },     // Line 9
  ];

  // === KNOCKOUT ZONE CONFIG FOR BX-STADIUM ===
  // Permanent/hardcoded zone positions for all users
  const zoneConfig = {
    leftZone: { startAngle: 0.965, endAngle: 0.425, outerRadius: 30 },
    centerZone: { startAngle: 1.9775, endAngle: 1.1775, outerRadius: 30 },
    rightZone: { startAngle: 2.755, endAngle: 2.185, outerRadius: 28 }
  };
  
  // Xtreme Dash freeze states - beyblade stops for 1 second after dashing
  const [xtremeDashFreeze1, setXtremeDashFreeze1] = useState(false);
  const [xtremeDashFreeze2, setXtremeDashFreeze2] = useState(false);
  const xtremeDashFreeze1Ref = useRef(false);
  const xtremeDashFreeze2Ref = useRef(false);
  
  // BX-Infinity Xtreme Dash line animation states
  const [xtremeDashLine1Active, setXtremeDashLine1Active] = useState(false); // P1 doing line dash
  const [xtremeDashLine2Active, setXtremeDashLine2Active] = useState(false); // P2 doing line dash
  const [xtremeDashLine1Color, setXtremeDashLine1Color] = useState(null); // 'blue' or 'red'
  const [xtremeDashLine2Color, setXtremeDashLine2Color] = useState(null); // 'blue' or 'red'
  const [xtremeDashLine1GoingDown, setXtremeDashLine1GoingDown] = useState(true); // direction along line
  const [xtremeDashLine2GoingDown, setXtremeDashLine2GoingDown] = useState(true);
  const xtremeDashLine1Ref = useRef(false);
  const xtremeDashLine2Ref = useRef(false);
  const xtremeDashLine1ColorRef = useRef(null);
  const xtremeDashLine2ColorRef = useRef(null);
  const xtremeDashLine1GoingDownRef = useRef(true);
  const xtremeDashLine2GoingDownRef = useRef(true);
  
  // Smooth transition states for returning to auto-rotation after Xtreme Dash
  const [xtremeDashTransition1, setXtremeDashTransition1] = useState(null); // { startPos, targetPos, startTime, duration }
  const [xtremeDashTransition2, setXtremeDashTransition2] = useState(null);
  const xtremeDashTransition1Ref = useRef(null);
  const xtremeDashTransition2Ref = useRef(null);
  
  // Stadium options
  const STADIUMS = {
    bx: {
      name: 'BX-Stadium',
      url: 'https://customer-assets.emergentagent.com/job_beyblade-battles-1/artifacts/ylc52l4o_IMG_1380.jpeg',
      alternativeImages: [
        'https://customer-assets.emergentagent.com/job_beyblade-battles-1/artifacts/ylc52l4o_IMG_1380.jpeg',
        'https://customer-assets.emergentagent.com/job_spin-battle-3/artifacts/31btaiw5_IMG_1757.jpeg',
        'https://customer-assets.emergentagent.com/job_bey-stadium-picker/artifacts/zb4zgq71_IMG_1765.jpeg',
        'https://customer-assets.emergentagent.com/job_bey-stadium-picker/artifacts/qg0sn1qb_IMG_1766.jpeg',
        'https://customer-assets.emergentagent.com/job_bey-stadium-picker/artifacts/4c2mpink_IMG_1822.jpeg',
        'https://customer-assets.emergentagent.com/job_bey-stadium-picker/artifacts/jq44hreb_IMG_1842.jpeg',
        'https://customer-assets.emergentagent.com/job_bey-stadium-picker/artifacts/30fbpvom_IMG_1841.jpeg',
        'https://customer-assets.emergentagent.com/job_bey-stadium-picker/artifacts/hjrgwxts_IMG_1839.jpeg',
        'https://customer-assets.emergentagent.com/job_bey-stadium-picker/artifacts/elkmakzg_IMG_1840.jpeg',
        'https://customer-assets.emergentagent.com/job_bey-stadium-picker/artifacts/2hy64t0b_IMG_1937.jpeg',
        'https://customer-assets.emergentagent.com/job_bey-stadium-picker/artifacts/rzd9gh5b_IMG_1934.jpeg',
        'https://customer-assets.emergentagent.com/job_bey-stadium-picker/artifacts/5gz88rug_IMG_1920.jpeg',
        'https://customer-assets.emergentagent.com/job_bey-stadium-picker/artifacts/zz1t3wdc_IMG_1923.jpeg'
      ]
    },
    bb10: {
      name: 'BB-10 Stadium',
      url: 'https://customer-assets.emergentagent.com/job_spin-battle-3/artifacts/3lsvsac5_IMG_1747.png',
      alternativeImages: [
        'https://customer-assets.emergentagent.com/job_spin-battle-3/artifacts/1o1tq437_IMG_0906.png',
        'https://customer-assets.emergentagent.com/job_spin-battle-3/artifacts/3lsvsac5_IMG_1747.png',
        'https://customer-assets.emergentagent.com/job_spin-battle-3/artifacts/cj0zu56w_IMG_1745.png',
        'https://customer-assets.emergentagent.com/job_spin-battle-3/artifacts/nipogo11_IMG_1846.png',
        'https://customer-assets.emergentagent.com/job_spin-battle-3/artifacts/qia8kqmj_IMG_1848.png',
        'https://customer-assets.emergentagent.com/job_bey-stadium-picker/artifacts/jdua9wpf_IMG_1970.png',
        'https://customer-assets.emergentagent.com/job_bey-stadium-picker/artifacts/w7ncd86h_IMG_1963.png'
      ]
    },
    bxInfinity: {
      name: 'BX-Infinity',
      url: 'https://customer-assets.emergentagent.com/job_44cdc5c5-20ef-4ebb-82f0-5ae8b3c7d36b/artifacts/2g9dj4r2_IMG_1486.png',
      usesBB10Physics: true // Uses BB-10 stadium physics (circular boundary)
    }
  };
  
  // State for selected alternative stadium images
  const [bxStadiumImageIndex, setBxStadiumImageIndex] = useState(() => {
    const cached = localStorage.getItem('stadiumPreferences');
    if (cached) {
      try {
        const prefs = JSON.parse(cached);
        return prefs.bx_stadium_image_index || 0;
      } catch { return 0; }
    }
    return 0;
  });
  const [bb10StadiumImageIndex, setBb10StadiumImageIndex] = useState(() => {
    const cached = localStorage.getItem('stadiumPreferences');
    if (cached) {
      try {
        const prefs = JSON.parse(cached);
        return prefs.bb10_stadium_image_index || 0;
      } catch { return 0; }
    }
    return 0;
  });
  const [showStadiumImagePicker, setShowStadiumImagePicker] = useState(null); // Which stadium's image picker to show
  const stadiumLongPressTimer = useRef(null);
  
  // Helper function to get the current stadium image URL (considering alternatives)
  const getStadiumImageUrl = (stadiumKey) => {
    const stadium = STADIUMS[stadiumKey];
    if (stadiumKey === 'bx' && stadium.alternativeImages) {
      return stadium.alternativeImages[bxStadiumImageIndex] || stadium.url;
    }
    if (stadiumKey === 'bb10' && stadium.alternativeImages) {
      return stadium.alternativeImages[bb10StadiumImageIndex] || stadium.url;
    }
    return stadium.url;
  };
  
  // ==================== VS AI FUNCTIONS ====================
  
  // AI launches its beyblade after Player 1 launches
  const aiLaunchBeyblade = () => {
    if (!aiMode || aiLaunched || top2Active) return;
    
    if (top1KnockedOut || top2KnockedOut || roundWinner || isDraw) return;
    setAiLaunched(true);
    setTop2Active(true);
    setTop2Pos({ x: STADIUM_CENTER_X - BEYBLADE_RADIUS, y: STADIUM_CENTER_Y });
    setTop2Speed(100);
    setTop2Rotation(0);
    setHealth2(getMaxHealth(selectedBeyblade2?.id));
  };
  
  // Reset AI state when battle resets
  const resetAiState = () => {
    setAiLaunched(false);
  };
  
  // ============================================================
  
  // BX-Stadium Rail (Xtreme Line) states
  const [ridingRail1, setRidingRail1] = useState(false); // P1 beyblade riding the rail
  const [ridingRail2, setRidingRail2] = useState(false); // P2 beyblade riding the rail
  const [railAngle1, setRailAngle1] = useState(0); // Current angle on rail for P1
  const [railAngle2, setRailAngle2] = useState(0); // Current angle on rail for P2
  const RAIL_SPEED_MULTIPLIER = 2.5; // Speed boost when riding rail
  const RAIL_ENGAGE_DISTANCE = 15; // Distance from wall to engage rail
  
  // Knockout states
  const [top1KnockedOut, setTop1KnockedOut] = useState(false);
  const [top2KnockedOut, setTop2KnockedOut] = useState(false);
  const [knockbackVelocity1, setKnockbackVelocity1] = useState({ x: 0, y: 0 });
  const [knockbackVelocity2, setKnockbackVelocity2] = useState({ x: 0, y: 0 });
  
  // Wall collision cooldown
  const wallCooldown1Ref = useRef(false);
  const wallCooldown2Ref = useRef(false);
  
  // Beyblade stats map - defines unique stats for each beyblade
  const BEYBLADE_STATS = {
    1: { stamina: 50, health: 10, basicDamage: 1, boostDamage: 2, speed: 100, hiddenAbility: 'None', specialMove: 'Iron Wall - Invulnerable to damage and knockback for 4 seconds', spinDirection: 'Right' }, // Cobalt Drake - 50 sec stamina, speed 100, 10 HP, has Iron Wall
    2: { stamina: 50, health: 10, basicDamage: 1, boostDamage: 2, speed: 100, hiddenAbility: 'None', specialMove: 'Final Drive - Halves stamina, increases speed to 200%, basic damage to 3, boost damage to 4 for 4 seconds', spinDirection: 'Right' }, // Dran Sword - 50 sec stamina, speed 100, has Final Drive
    3: { stamina: 50, health: 10, basicDamage: 1, boostDamage: 2, speed: 100, hiddenAbility: 'None', specialMove: 'None', spinDirection: 'Right' }, // Phoenix Wing - 50 sec stamina, speed 100
    68: { stamina: 50, health: 10, basicDamage: 1, boostDamage: 2, speed: 100, hiddenAbility: 'None', specialMove: 'None', spinDirection: 'Right' }, // Phoenix Wing M - Legendary, same as Phoenix Wing
    66: { stamina: 50, health: 10, basicDamage: 1, boostDamage: 2, speed: 100, hiddenAbility: 'None', specialMove: 'None', spinDirection: 'Right' }, // Phoenix Wing B - Uncommon, same as Phoenix Wing
    5: { stamina: 50, health: 10, basicDamage: 1, boostDamage: 2, speed: 100, hiddenAbility: 'None', specialMove: 'Final Drive - Halves stamina, increases speed to 200%, basic damage to 3, boost damage to 4 for 4 seconds', spinDirection: 'Right' }, // Aero Pegasus - has Final Drive
    6: { stamina: 40, health: 11, basicDamage: 1, boostDamage: 2, speed: 100, hiddenAbility: 'None', specialMove: 'None', spinDirection: 'Right' }, // Valkyrie Volt - 40 sec stamina, 11 HP
    8: { stamina: 105, health: 10, basicDamage: 1, boostDamage: 2, speed: 50, hiddenAbility: 'None', specialMove: 'Restore Health - Fully recovers health points once per battle', spinDirection: 'Right' }, // Wizard Arrow - 1 min 45 sec stamina, has Restore Health
    9: { stamina: 100, health: 10, basicDamage: 1, boostDamage: 2, speed: 75, hiddenAbility: 'None', specialMove: 'Burning Strike - Fire rotates around beyblade for 2 seconds, deals 2 damage on contact, immune to damage/knockback', spinDirection: 'Right' }, // Hells Scythe - Speed 75, has Burning Strike
    67: { stamina: 100, health: 10, basicDamage: 1, boostDamage: 2, speed: 75, hiddenAbility: 'None', specialMove: 'Burning Strike - Fire rotates around beyblade for 2 seconds, deals 2 damage on contact, immune to damage/knockback', spinDirection: 'Right' }, // Phoenix Flare - Rare, same as Hells Scythe, has Burning Strike
    50: { stamina: 100, health: 10, basicDamage: 1, boostDamage: 2, speed: 75, hiddenAbility: 'None', specialMove: 'Burning Strike - Fire rotates around beyblade for 2 seconds, deals 2 damage on contact, immune to damage/knockback', spinDirection: 'Right' }, // Phoenix Rudder - same as Phoenix Flare/Hells Scythe, has Burning Strike
    10: { stamina: 90, health: 15, basicDamage: 1, boostDamage: 2, speed: 50, hiddenAbility: 'None', specialMove: 'Iron Wall - Immune to knockback and damage for 4 seconds', spinDirection: 'Right' }, // Knight Shield - 15 HP, has Iron Wall
    15: { stamina: 50, health: 10, basicDamage: 1, boostDamage: 2, speed: 100, hiddenAbility: 'None', specialMove: 'None', spinDirection: 'Right' }, // Phoenix Wing CC - 50 sec stamina, speed 100
    22: { stamina: 30, health: 10, basicDamage: 4, boostDamage: 8, speed: 70, hiddenAbility: 'Power Knockback - Knocks back opponents at twice the speed', specialMove: 'None', spinDirection: 'Right' }, // Dran Buster - High damage, low stamina, 2x knockback
    64: { stamina: 30, health: 10, basicDamage: 4, boostDamage: 8, speed: 70, hiddenAbility: 'Power Knockback - Knocks back opponents at twice the speed', specialMove: 'None', spinDirection: 'Right' }, // Dran Buster C - Legendary, same as Dran Buster, 2x knockback
    23: { stamina: 100, health: 10, basicDamage: 1, boostDamage: 2, speed: 75, hiddenAbility: 'None', specialMove: 'Burning Strike - Fire rotates around beyblade for 2 seconds, deals 2 damage on contact, immune to damage/knockback', spinDirection: 'Right' }, // Hells Hammer - has Burning Strike
    24: { stamina: 120, health: 10, basicDamage: 1, boostDamage: 2, speed: 50, hiddenAbility: 'None', specialMove: 'Restore Health - Fully recovers health points once per battle', spinDirection: 'Right' }, // Wizard Rod - High stamina, has Restore Health
    57: { stamina: 120, health: 10, basicDamage: 1, boostDamage: 2, speed: 50, hiddenAbility: 'None', specialMove: 'Restore Health - Fully recovers health points once per battle', spinDirection: 'Right' }, // Wizard Rod G - Same as Wizard Rod, has Restore Health
    65: { stamina: 120, health: 10, basicDamage: 1, boostDamage: 2, speed: 50, hiddenAbility: 'None', specialMove: 'Restore Health - Fully recovers health points once per battle', spinDirection: 'Right' }, // Wizard Rod C - Uncommon, same as Wizard Rod, has Restore Health
    58: { stamina: 50, health: 10, basicDamage: 1, boostDamage: 2, speed: 100, hiddenAbility: 'None', specialMove: 'None', spinDirection: 'Right' }, // Phoenix Feather - Legendary, same as Phoenix Wing
    37: { stamina: 50, health: 10, basicDamage: 1, boostDamage: 2, speed: 100, hiddenAbility: 'None', specialMove: 'Final Drive - Halves stamina, increases speed to 200%, basic damage to 2, boost damage to 4 for 4 seconds', spinDirection: 'Right' }, // Dran Brave - same as Dran Sword, has Final Drive
    59: { stamina: 50, health: 10, basicDamage: 1, boostDamage: 2, speed: 100, hiddenAbility: 'None', specialMove: 'Final Drive - Halves stamina, increases speed to 200%, basic damage to 2, boost damage to 4 for 4 seconds', spinDirection: 'Right' }, // Dran Brave B - same as Dran Sword, has Final Drive
    60: { stamina: 50, health: 10, basicDamage: 1, boostDamage: 2, speed: 100, hiddenAbility: 'None', specialMove: 'Final Drive - Halves stamina, increases speed to 200%, basic damage to 2, boost damage to 4 for 4 seconds', spinDirection: 'Right' }, // Dran Dagger - same as Dran Sword, has Final Drive
    61: { stamina: 50, health: 10, basicDamage: 1, boostDamage: 2, speed: 100, hiddenAbility: 'None', specialMove: 'Iron Wall - Invulnerable to damage and knockback for 4 seconds', spinDirection: 'Right' }, // Cobalt Drake C - same as Cobalt Drake, has Iron Wall
    62: { stamina: 50, health: 10, basicDamage: 1, boostDamage: 2, speed: 100, hiddenAbility: 'None', specialMove: 'Iron Wall - Invulnerable to damage and knockback for 4 seconds', spinDirection: 'Right' }, // Cobalt Drake R - same as Cobalt Drake, has Iron Wall
    38: { stamina: 120, health: 8, basicDamage: 1, boostDamage: 2, speed: 50, hiddenAbility: 'None', specialMove: 'Restore Health - Fully recovers health points once per battle', spinDirection: 'Right' }, // Wizard Arc - has Restore Health
    31: { stamina: 60, health: 12, basicDamage: 1, boostDamage: 2, speed: 50, hiddenAbility: 'None', specialMove: 'Iron Wall - Immune to knockback and damage for 4 seconds', spinDirection: 'Right' }, // Knight Mail - defensive, has Iron Wall
    16: { stamina: 60, health: 12, basicDamage: 1, boostDamage: 2, speed: 50, hiddenAbility: 'None', specialMove: 'Iron Wall - Immune to knockback and damage for 4 seconds', spinDirection: 'Right' }, // Knight Mail B - has Iron Wall
    36: { stamina: 90, health: 10, basicDamage: 1, boostDamage: 2, speed: 50, hiddenAbility: 'None', specialMove: 'Shadow Clones - Spawns 5 clones and teleports to a random clone position', spinDirection: 'Right' }, // Shinobi Shadow
    45: { stamina: 50, health: 10, basicDamage: 1, boostDamage: 2, speed: 100, hiddenAbility: 'None', specialMove: 'Final Drive - Halves stamina, increases speed to 200%, basic damage to 3, boost damage to 4 for 4 seconds', spinDirection: 'Right' }, // Storm Pegasus - has Final Drive
    48: { stamina: 50, health: 10, basicDamage: 1, boostDamage: 2, speed: 100, hiddenAbility: 'None', specialMove: 'None', spinDirection: 'Right' }, // Victory Valkyrie - same as Phoenix Wing
    49: { stamina: 180, health: 7, basicDamage: 1, boostDamage: 2, speed: 50, hiddenAbility: 'None', specialMove: 'Rewind Time - Teleports all beyblades to positions from 2 seconds ago', spinDirection: 'Right' }, // Clock Mirage - Very high stamina
    53: { stamina: 50, health: 10, basicDamage: 1, boostDamage: 2, speed: 100, hiddenAbility: 'None', specialMove: 'Final Drive - Halves stamina, increases speed to 200%, basic damage to 3, boost damage to 4 for 4 seconds', spinDirection: 'Right' }, // Galaxy Pegasus - has Final Drive
    55: { stamina: 90, health: 10, basicDamage: 1, boostDamage: 2, speed: 50, hiddenAbility: 'None', specialMove: 'Dual Spin - Can switch between left and right spin during battle', spinDirection: 'Dual (Left/Right)' }, // Gravity Perseus
      56: { stamina: 90, health: 10, basicDamage: 1, boostDamage: 2, speed: 50, hiddenAbility: 'None', specialMove: 'Dual Spin - Can switch between left and right spin during battle', spinDirection: 'Dual (Left/Right)' }, // Vari Ares (same as Gravity Perseus)
    54: { stamina: 50, health: 10, basicDamage: 1, boostDamage: 2, speed: 100, hiddenAbility: 'Spin Steal - Regains health & stamina when damaging right-spinning opponents', specialMove: 'Final Drive - Halves stamina, increases speed to 200%, basic damage to 2, boost damage to 4 for 4 seconds', spinDirection: 'Left' }, // Meteo L Drago
    42: { stamina: 90, health: 10, basicDamage: 1, boostDamage: 2, speed: 100, hiddenAbility: 'Spin Steal - Regains health & stamina when damaging right-spinning opponents', specialMove: 'Final Drive - Halves stamina, increases speed to 200%, basic damage to 2, boost damage to 4 for 4 seconds', spinDirection: 'Left' }, // Meteor Dragoon
    4: { stamina: 90, health: 10, basicDamage: 1, boostDamage: 2, speed: 100, hiddenAbility: 'None', specialMove: 'Final Drive - Halves stamina, increases speed to 200%, basic damage to 2, boost damage to 4 for 4 seconds', spinDirection: 'Left' }, // Cobalt Dragoon
    18: { stamina: 90, health: 10, basicDamage: 1, boostDamage: 2, speed: 100, hiddenAbility: 'None', specialMove: 'Final Drive - Halves stamina, increases speed to 200%, basic damage to 2, boost damage to 4 for 4 seconds', spinDirection: 'Left' }, // Cobalt Dragoon B
    47: { stamina: 50, health: 10, basicDamage: 1, boostDamage: 2, speed: 100, hiddenAbility: 'None', specialMove: 'Final Drive - Halves stamina, increases speed to 200%, basic damage to 2, boost damage to 4 for 4 seconds', spinDirection: 'Left' }, // LL Drago
    52: { stamina: 40, health: 10, basicDamage: 3, boostDamage: 6, speed: 60, hiddenAbility: 'Power Knockback - Knocks back opponents at twice the speed', specialMove: 'None', spinDirection: 'Right' }, // Xeno Xcaliber - 40 sec stamina, high damage, 2x knockback
    30: { stamina: 50, health: 10, basicDamage: 1, boostDamage: 2, speed: 90, hiddenAbility: 'Power Knockback - Knocks back opponents at twice the speed', specialMove: 'None', spinDirection: 'Right' }, // Impact Drake - 50 sec stamina, 2x knockback, speed 90
    32: { stamina: 90, health: 10, basicDamage: 1, boostDamage: 2, speed: 50, hiddenAbility: 'Power Knockback - Knocks back opponents at twice the speed', specialMove: 'Iron Wall - Immune to knockback and damage for 4 seconds', spinDirection: 'Right' }, // Scorpio Spear - has Iron Wall + Power Knockback
    21: { stamina: 50, health: 10, basicDamage: 1, boostDamage: 2, speed: 100, hiddenAbility: 'None', specialMove: 'Final Drive - Halves stamina, increases speed to 200%, basic damage to 3, boost damage to 4 for 4 seconds', spinDirection: 'Right' }, // Pegasus Blast - has Final Drive ability
    69: { stamina: 50, health: 10, basicDamage: 1, boostDamage: 2, speed: 100, hiddenAbility: 'None', specialMove: 'Final Drive - Halves stamina, increases speed to 200%, basic damage to 3, boost damage to 4 for 4 seconds', spinDirection: 'Right' }, // Pegasus Blast R - Legendary, same as Pegasus Blast, has Final Drive
    44: { stamina: 50, health: 10, basicDamage: 1, boostDamage: 2, speed: 100, hiddenAbility: 'None', specialMove: 'Final Drive - Halves stamina, increases speed to 200%, basic damage to 3, boost damage to 4 for 4 seconds', spinDirection: 'Right' }, // Pegasus Brush - has Final Drive
    40: { stamina: 100, health: 10, basicDamage: 1, boostDamage: 2, speed: 75, hiddenAbility: 'None', specialMove: 'Burning Strike - Fire rotates around beyblade for 2 seconds, deals 2 damage on contact, immune to damage/knockback', spinDirection: 'Right' }, // Hells Reaper - has Burning Strike
    70: { stamina: 100, health: 10, basicDamage: 1, boostDamage: 2, speed: 75, hiddenAbility: 'None', specialMove: 'Burning Strike - Fire rotates around beyblade for 2 seconds, deals 2 damage on contact, immune to damage/knockback', spinDirection: 'Right' }, // Fox Brush - Rare, same as Hells Scythe
    71: { stamina: 100, health: 10, basicDamage: 1, boostDamage: 2, speed: 75, hiddenAbility: 'None', specialMove: 'Burning Strike - Fire rotates around beyblade for 2 seconds, deals 2 damage on contact, immune to damage/knockback', spinDirection: 'Right' }, // Fox Brush G - Rare, same as Hells Scythe
    72: { stamina: 100, health: 10, basicDamage: 1, boostDamage: 2, speed: 75, hiddenAbility: 'None', specialMove: 'Burning Strike - Fire rotates around beyblade for 2 seconds, deals 2 damage on contact, immune to damage/knockback', spinDirection: 'Right' }, // Fox Brush B - Rare, same as Hells Scythe
    73: { stamina: 50, health: 10, basicDamage: 1, boostDamage: 2, speed: 100, hiddenAbility: 'None', specialMove: 'Final Drive - Halves stamina, increases speed to 200%, basic damage to 3, boost damage to 4 for 4 seconds', spinDirection: 'Right' }, // Ultimate Valkyrie - Common, same as Dran Sword
    74: { stamina: 50, health: 10, basicDamage: 1, boostDamage: 2, speed: 100, hiddenAbility: 'None', specialMove: 'Final Drive - Halves stamina, increases speed to 200%, basic damage to 2, boost damage to 4 for 4 seconds', spinDirection: 'Left' }, // Guilty Longinus - Common, same as LL Drago (Left Spin)
    75: { stamina: 30, health: 10, basicDamage: 4, boostDamage: 8, speed: 70, hiddenAbility: 'Power Knockback - Knocks back opponents at twice the speed', specialMove: 'None', spinDirection: 'Right' }, // Dran Buster C1 - Legendary, same as Dran Buster
    76: { stamina: 30, health: 10, basicDamage: 4, boostDamage: 8, speed: 70, hiddenAbility: 'Power Knockback - Knocks back opponents at twice the speed', specialMove: 'None', spinDirection: 'Right' }, // Dran Buster C2 - Legendary, same as Dran Buster
    77: { stamina: 30, health: 10, basicDamage: 4, boostDamage: 8, speed: 70, hiddenAbility: 'Power Knockback - Knocks back opponents at twice the speed', specialMove: 'None', spinDirection: 'Right' }, // Dran Buster C3 - Legendary, same as Dran Buster
    78: { stamina: 50, health: 10, basicDamage: 1, boostDamage: 2, speed: 100, hiddenAbility: 'None', specialMove: 'Final Drive - Halves stamina, increases speed to 200%, basic damage to 3, boost damage to 4 for 4 seconds', spinDirection: 'Right' }, // Glory Valkyrie - Mythic, same as Aero Pegasus, has Final Drive
    79: { stamina: 50, health: 10, basicDamage: 1, boostDamage: 2, speed: 100, hiddenAbility: 'None', specialMove: 'Final Drive - Halves stamina, increases speed to 200%, basic damage to 3, boost damage to 4 for 4 seconds', spinDirection: 'Right' }, // Glory Valkyrie (Legendary) - same as Aero Pegasus, has Final Drive
    80: { stamina: 50, health: 10, basicDamage: 1, boostDamage: 2, speed: 100, hiddenAbility: 'None', specialMove: 'Final Drive - Halves stamina, increases speed to 200%, basic damage to 3, boost damage to 4 for 4 seconds', spinDirection: 'Right' }, // Drake Brave - Legendary, same as Aero Pegasus, has Final Drive
    81: { stamina: 50, health: 10, basicDamage: 1, boostDamage: 2, speed: 100, hiddenAbility: 'None', specialMove: 'Final Drive - Halves stamina, increases speed to 200%, basic damage to 2, boost damage to 4 for 4 seconds', spinDirection: 'Left' } // Luster Dragoon - Rare, same as LL Drago, Left Spin, has Final Drive
  };
  const DEFAULT_STATS = { stamina: 90, health: 10, basicDamage: 1, boostDamage: 2, speed: 50, hiddenAbility: 'None', specialMove: 'None', spinDirection: 'Right' };
  
  // Helper function to get max health for a beyblade based on stats
  const getMaxHealth = (beybladeId) => {
    const stats = BEYBLADE_STATS[beybladeId] || DEFAULT_STATS;
    return stats.health;
  };
  
  // Health states (10 hits = Burst Finish)
  const [health1, setHealth1] = useState(10);
  const [health2, setHealth2] = useState(10);
  const [roundWinner, setRoundWinner] = useState(null); // 1 or 2 when someone wins
  const [winType, setWinType] = useState(null); // 'knockout' | 'spinFinish' | 'burstFinish'
  const [isDraw, setIsDraw] = useState(false); // True when both players lose simultaneously
  const [drawType, setDrawType] = useState(null); // 'knockout' | 'spinFinish' | 'burstFinish'
  const scoreAwardedRef = useRef(false); // Prevents duplicate score awards in same round
  
  // Win type scoring: Spin Finish = 1pt, Burst Finish = 2pts, Knock Out = 3pts, Pocket (BX) = 2pts
  const WIN_POINTS = {
    spinFinish: 1,
    burstFinish: 2,
    knockout: 3,
    pocket: 2  // BX-Stadium pocket knockout
  };
  
  // Track win type for pocket vs regular knockout
  const [isPocketKO, setIsPocketKO] = useState(false);
  const [pocketKOType, setPocketKOType] = useState(null); // 'xtremeZone' or 'overZone'
  
  // Stamina states (default 90 seconds = 1 minute 30 seconds, varies by beyblade)
  const DEFAULT_STAMINA = 90; // seconds
  const [stamina1, setStamina1] = useState(DEFAULT_STAMINA);
  const [stamina2, setStamina2] = useState(DEFAULT_STAMINA);
  const [maxStamina1, setMaxStamina1] = useState(DEFAULT_STAMINA); // Track max stamina for P1's beyblade
  const [maxStamina2, setMaxStamina2] = useState(DEFAULT_STAMINA); // Track max stamina for P2's beyblade
  const [stoppedSpinning1, setStoppedSpinning1] = useState(false); // True when P1's beyblade ran out of stamina
  const [stoppedSpinning2, setStoppedSpinning2] = useState(false); // True when P2's beyblade ran out of stamina
  const staminaIntervalRef = useRef(null);
  
  // Helper function to get max stamina for a beyblade
  const getMaxStamina = (beybladeId) => {
    const stats = BEYBLADE_STATS[beybladeId] || DEFAULT_STATS;
    return stats.stamina;
  };
  
  // Clash effect state
  const [clashEffects, setClashEffects] = useState([]);
  const [clashSparks, setClashSparks] = useState([]);
  
  // Beyblade selection state
  const [showBeybladeSelect1, setShowBeybladeSelect1] = useState(false);
  const [showBeybladeSelect2, setShowBeybladeSelect2] = useState(false);
  const [selectedBeyblade1, setSelectedBeyblade1] = useState(null); // Will be set after BEYBLADES is defined
  const [selectedBeyblade2, setSelectedBeyblade2] = useState(null); // Will be set after BEYBLADES is defined
  
  // Modal state for customizing player rosters
  const [showRosterModal1, setShowRosterModal1] = useState(false);
  const [showRosterModal2, setShowRosterModal2] = useState(false);
  
  // Online multiplayer states
  const [isOnlineMode, setIsOnlineMode] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [matchFound, setMatchFound] = useState(false);
  const [opponentUsername, setOpponentUsername] = useState(null);
  const [opponentId, setOpponentId] = useState(null);
  const [opponentRosterIds, setOpponentRosterIds] = useState([]);
  const [opponentRoster, setOpponentRoster] = useState([]);
  const [myPlayerNumber, setMyPlayerNumber] = useState(1);
  const [showMatchmaking, setShowMatchmaking] = useState(false);
  const [matchCode, setMatchCode] = useState('');
  const [waitingForOpponent, setWaitingForOpponent] = useState(false);
  const [myScore, setMyScore] = useState(0);
  const [opponentScore, setOpponentScore] = useState(0);
  
  // Online rematch states
  const [waitingForRematch, setWaitingForRematch] = useState(false);
  const [opponentWantsRematch, setOpponentWantsRematch] = useState(false);
  const [currentRound, setCurrentRound] = useState(1);
  
  // Each player's custom roster (6 Beyblades they've selected from ALL_BEYBLADES)
  const [player1Roster, setPlayer1Roster] = useState([]);
  const [player2Roster, setPlayer2Roster] = useState([]);
  
  // Guest Player feature - allows verified users to load another verified user's roster as P2
  const [guestPlayer, setGuestPlayer] = useState(null); // { id, username, level, roster }
  const [showGuestPlayerModal, setShowGuestPlayerModal] = useState(false);
  
  const [verifiedUsers, setVerifiedUsers] = useState([]);
  const [loadingVerifiedUsers, setLoadingVerifiedUsers] = useState(false);
  const [guestSearchQuery, setGuestSearchQuery] = useState('');
  const [searchingGuest, setSearchingGuest] = useState(false);
  
  // Stats popup state for long-press on roster beyblades
  const [statsPopup, setStatsPopup] = useState(null);
  const longPressTimerRef = useRef(null);
  const longPressDuration = 500; // 500ms for long press

  // Expose openGuestPlayerModal function to parent components via ref
  useImperativeHandle(ref, () => ({
    openGuestPlayerModal: () => {
      if (!isOnlineMode && !top2Active && !top1Active) {
        setShowGuestPlayerModal(true);
      }
    }
  }), [isOnlineMode, top2Active, top1Active]);

  // Get beyblade rarity
  const getBeybladeRarity = (id) => {
    if ([1, 5, 6, 57, 78].includes(id)) return 'mythic'; // Glory Valkyrie (78) is Mythic
    if ([14, 15, 16, 17, 18, 58, 62, 63, 64, 68, 69, 75, 76, 77, 79, 80].includes(id)) return 'legendary'; // Glory Valkyrie (79) and Drake Brave (80) are Legendary
    if ([7, 19, 20, 21, 29, 30, 31, 32, 41, 42, 43, 44, 49, 56, 59, 61, 67, 70, 71, 72, 81].includes(id)) return 'rare'; // Added Luster Dragoon (81)
    if ([60, 65, 66, 3, 4, 25, 26, 33, 34, 35, 36, 50, 51].includes(id)) return 'uncommon'; // Added Phoenix Wing B (66)
    return 'common';
  };

  // Get rarity colors for borders, backgrounds and popup
  const getRarityColors = (rarity) => {
    switch (rarity) {
      case 'mythic':
        return {
          border: 'border-purple-500/70',
          borderSelected: 'border-purple-400',
          bg: 'from-purple-900/90 via-purple-800/80 to-purple-900/90',
          cardBg: 'bg-purple-900/40',
          cardBgHover: 'hover:bg-purple-800/50',
          cardBgSelected: 'bg-purple-800/60',
          accent: 'text-purple-400',
          checkBg: 'bg-purple-500'
        };
      case 'legendary':
        return {
          border: 'border-yellow-500/70',
          borderSelected: 'border-yellow-400',
          bg: 'from-yellow-900/90 via-orange-800/80 to-yellow-900/90',
          cardBg: 'bg-gradient-to-br from-yellow-600/40 to-orange-600/40',
          cardBgHover: 'hover:from-yellow-600/50 hover:to-orange-600/50',
          cardBgSelected: 'bg-gradient-to-br from-yellow-600/50 to-orange-600/50',
          accent: 'text-yellow-400',
          checkBg: 'bg-yellow-500'
        };
      case 'rare':
        return {
          border: 'border-blue-500/70',
          borderSelected: 'border-blue-400',
          bg: 'from-blue-900/90 via-blue-800/80 to-blue-900/90',
          cardBg: 'bg-gradient-to-br from-blue-800/60 to-blue-900/60',
          cardBgHover: 'hover:from-blue-800/70 hover:to-blue-900/70',
          cardBgSelected: 'bg-gradient-to-br from-blue-800/70 to-blue-900/70',
          accent: 'text-blue-400',
          checkBg: 'bg-blue-500'
        };
      case 'uncommon':
        return {
          border: 'border-green-500/70',
          borderSelected: 'border-green-400',
          bg: 'from-green-900/90 via-emerald-800/80 to-green-900/90',
          cardBg: 'bg-gradient-to-br from-green-700/50 to-emerald-800/50',
          cardBgHover: 'hover:from-green-700/60 hover:to-emerald-800/60',
          cardBgSelected: 'bg-gradient-to-br from-green-700/60 to-emerald-800/60',
          accent: 'text-green-400',
          checkBg: 'bg-green-500'
        };
      default: // common
        return {
          border: 'border-cyan-500/50',
          borderSelected: 'border-cyan-400',
          bg: 'from-slate-900/90 via-slate-800/80 to-slate-900/90',
          cardBg: 'bg-slate-700/50',
          cardBgHover: 'hover:bg-cyan-900/30',
          cardBgSelected: 'bg-cyan-900/50',
          accent: 'text-cyan-400',
          checkBg: 'bg-cyan-500'
        };
    }
  };

  // Get beyblade stats
  // Basic Damage: damage inflicted when hitting opponent without speed boost
  // Boost Damage: damage inflicted when speed boost is active
  // Speed: percentage affecting auto-rotation speed (50 = default, doubled when speed boost active)
  // Stamina: time in seconds before beyblade stops spinning (90 = 1 min 30 sec)
  const getBeybladeStats = (id) => {
    return BEYBLADE_STATS[id] || DEFAULT_STATS;
  };

  // Long press handlers for stats popup
  const handleRosterLongPressStart = useCallback((beyblade) => {
    longPressTimerRef.current = setTimeout(() => {
      const stats = getBeybladeStats(beyblade.id);
      const rarity = getBeybladeRarity(beyblade.id);
      const colors = getRarityColors(rarity);
      setStatsPopup({ ...beyblade, stats, rarity, colors });
    }, longPressDuration);
  }, []);

  const handleRosterLongPressEnd = useCallback(() => {
    if (longPressTimerRef.current) {
      clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
    }
  }, []);
  
  // Available Beyblades (filtered by purchased)
  const [availableBeyblades, setAvailableBeyblades] = useState([]);
  
  // Convert opponent roster IDs to full Beyblade objects whenever IDs change
  useEffect(() => {
    
    if (opponentRosterIds.length > 0) {
      // Use ALL_BEYBLADES (complete catalog) instead of availableBeyblades (user's purchased)
      const beyblades = opponentRosterIds
        .map(id => ALL_BEYBLADES.find(b => b.id === id))
        .filter(Boolean);
      setOpponentRoster(beyblades);
    }
  }, [opponentRosterIds]);
  
  // Fetch opponent roster when opponent ID is set
  useEffect(() => {
    
    const fetchOpponentRoster = async () => {
      if (opponentId && matchFound) {
        try {
          const token = localStorage.getItem('token');
          
          const response = await fetch(`${BACKEND_URL}/api/battle/opponent-roster/${opponentId}`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          
          
          if (response.ok) {
            const data = await response.json();
            setOpponentRosterIds(data.roster || []);
          } else {
            const errorText = await response.text();
            console.error('[Fetch Opponent Roster] Failed:', response.status, errorText);
          }
        } catch (error) {
          console.error('[Fetch Opponent Roster] Error:', error);
        }
      }
    };
    
    fetchOpponentRoster();
  }, [opponentId, matchFound]);
  
  // Function to search for users by name (for Player 2 selection)
  const searchVerifiedUsers = async (query) => {
    if (!query || query.length < 2) {
      setVerifiedUsers([]);
      return;
    }
    
    setSearchingGuest(true);
    try {
      const token = localStorage.getItem('token');
      // Use the same endpoint as points transfer search
      const response = await fetch(`${BACKEND_URL}/api/users/search?q=${encodeURIComponent(query)}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const users = await response.json();
        // Filter out current user from results
        const filteredUsers = users.filter(u => u.id !== user?.id);
        setVerifiedUsers(filteredUsers);
      } else {
        console.error('[Guest Player] Search failed');
        setVerifiedUsers([]);
      }
    } catch (error) {
      console.error('[Guest Player] Search error:', error);
      setVerifiedUsers([]);
    } finally {
      setSearchingGuest(false);
    }
  };
  
  // Function to load a guest player's roster
  const loadGuestPlayerRoster = async (selectedUser) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${BACKEND_URL}/api/users/${selectedUser.id}/roster`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        const rosterIds = data.roster || [];
        
        // Convert roster IDs to beyblade objects from LOCAL ALL_BEYBLADES data
        const rosterObjects = rosterIds.map(id => ALL_BEYBLADES.find(b => b.id === id)).filter(b => b !== undefined);
        
        // Set guest player info (include all display data from selectedUser)
        setGuestPlayer({
          id: selectedUser.id,
          username: data.username,
          level: data.level || selectedUser.level,
          roster: rosterObjects,
          profile_picture: selectedUser.profile_picture,
          circle_color: selectedUser.circle_color,
          banner_image: selectedUser.banner_image,
          battle_points: selectedUser.battle_points,
          has_made_first_purchase: selectedUser.has_made_first_purchase
        });
        
        // Update P2 roster with guest's P1 roster (using local beyblade data)
        setPlayer2Roster(rosterObjects);
        
        // Select first beyblade if available
        if (rosterObjects.length > 0) {
          setSelectedBeyblade2(rosterObjects[0]);
        }
        
        setShowGuestPlayerModal(false);
        setGuestSearchQuery('');
        setVerifiedUsers([]);
      } else {
        console.error('[Guest Player] Failed to load roster');
        alert('Failed to load this user\'s roster. They may not have any Beyblades selected.');
      }
    } catch (error) {
      console.error('[Guest Player] Error loading roster:', error);
      alert('Error loading roster. Please try again.');
    }
  };
  
  // Function to clear guest player and restore original P2 roster
  const clearGuestPlayer = () => {
    setGuestPlayer(null);
    setGuestSearchQuery('');
    setVerifiedUsers([]);
    // Restore original P2 roster from user data
    const roster2Ids = user?.beyblade_roster_p2 && user.beyblade_roster_p2.length > 0 
      ? user.beyblade_roster_p2 
      : [1, 2, 3, 4, 5, 6];
    const roster2Objects = roster2Ids.map(id => ALL_BEYBLADES.find(b => b.id === id)).filter(b => b !== undefined);
    setPlayer2Roster(roster2Objects);
    if (roster2Objects.length > 0) {
      setSelectedBeyblade2(roster2Objects[0]);
    }
  };
  
  // Open guest player modal
  const openGuestPlayerModal = () => {
    setShowGuestPlayerModal(true);
    setGuestSearchQuery('');
    setVerifiedUsers([]);
  };
  
  // Scoreboard state (resets on logout/refresh)
  const [player1Score, setPlayer1Score] = useState(0);
  const [player2Score, setPlayer2Score] = useState(0);
  
  // Countdown state
  const [countdownText, setCountdownText] = useState('START');
  const [countdownActive, setCountdownActive] = useState(false);
  
  // Countdown function
  const startCountdown = () => {
    if (countdownActive) return;
    setCountdownActive(true);
    
    setCountdownText('3!');
    setTimeout(() => setCountdownText('2!'), 1000);
    setTimeout(() => setCountdownText('1!'), 2000);
    setTimeout(() => setCountdownText('Go Shoot!'), 3000);
    setTimeout(() => {
      setCountdownText('START');
      setCountdownActive(false);
    }, 4500);
  };
  
  // Score adjustment popup state
  const [scoreAdjustPlayer, setScoreAdjustPlayer] = useState(null); // 1, 2, or null
  
  const [circleAngle, setCircleAngle] = useState(0);
  const animationRef = useRef(null);
  // REMOVED: dpadIntervalRef - D-pad no longer exists
  const top1ImageRef = useRef(null);
  const top2ImageRef = useRef(null);
  // REMOVED: characterCanvasRef - walking character no longer exists
  
  // Use refs for hit cooldown to ensure immediate updates
  const hitCooldownRef1 = useRef(false);
  const hitCooldownRef2 = useRef(false);
  
  // Refs to track all timeouts for proper cleanup (memory leak fix)
  const hitCooldownTimeout1Ref = useRef(null);
  const hitCooldownTimeout2Ref = useRef(null);
  
  // Loading state for critical assets
  const [assetsLoaded, setAssetsLoaded] = useState(false);
  const stadiumImageRef = useRef(null);
  
  // Track previous canvas dimensions for scaling positions on resize
  const prevCanvasDimensionsRef = useRef({ width: 800, height: 600 });
  
  // FIXED WALL POSITIONS - Permanently configured for BB-10 Stadium (circular)
  const BB10_WALLS = [
    {
      "start": {
        "x": 315.27093596059115,
        "y": 45.02446982055465
      },
      "end": {
        "x": 549.7536945812808,
        "y": 70.47308319738988
      },
      "label": "Wall 1"
    },
    {
      "start": {
        "x": 137.93103448275863,
        "y": 234.91027732463294
      },
      "end": {
        "x": 212.80788177339903,
        "y": 483.52365415986947
      },
      "label": "Wall 2"
    },
    {
      "start": {
        "x": 671.9211822660099,
        "y": 301.46818923327896
      },
      "end": {
        "x": 512.3152709359606,
        "y": 526.5905383360522
      },
      "label": "Wall 3"
    }
  ];
  
  // BX-Stadium Rail Shape - Open oval track with V-shaped exit at top
  // Based on actual stadium image analysis:
  // - The rail is NOT a closed loop - it has a V-shaped opening at the top
  // - Beyblades can exit through the V-opening when they reach it
  // - The shape follows the green line in the stadium image
  // Center should match the actual stadium center in the image
  const BX_CENTER_X = 400;  // Centered horizontally (was 432, causing asymmetry)
  const BX_CENTER_Y = 270;
  
  // Rail parameters matching the green line in the stadium
  const BX_RAIL_WIDTH = 260;   // Horizontal radius
  const BX_RAIL_HEIGHT = 230;  // Vertical radius (oval shape)
  
  // V-Opening parameters (at the top of the stadium)
  // The opening is where beyblades EXIT the rail
  const BX_V_OPENING_START_ANGLE = -Math.PI / 2 - 0.35; // ~70 degrees from top
  const BX_V_OPENING_END_ANGLE = -Math.PI / 2 + 0.35;   // ~110 degrees from top
  const BX_V_APEX_Y = BX_CENTER_Y - BX_RAIL_HEIGHT + 30; // The tip of the V (where rail ends)
  
  // Check if an angle is in the V-opening zone (where there's NO rail)
  const isInVOpening = (angle) => {
    // Normalize angle to -PI to PI
    let normalizedAngle = angle;
    while (normalizedAngle > Math.PI) normalizedAngle -= 2 * Math.PI;
    while (normalizedAngle < -Math.PI) normalizedAngle += 2 * Math.PI;
    
    // V-opening is at the top (around -PI/2)
    return normalizedAngle > BX_V_OPENING_START_ANGLE && normalizedAngle < BX_V_OPENING_END_ANGLE;
  };
  
  // Function to get point on the BX rail at a given angle
  // Returns null if the angle is in the V-opening (no rail there)
  const getBXRailPoint = (angle) => {
    // If in V-opening, there's no rail - return null
    if (isInVOpening(angle)) {
      return null;
    }
    
    // Regular oval point following the green line
    return {
      x: BX_CENTER_X + Math.cos(angle) * BX_RAIL_WIDTH,
      y: BX_CENTER_Y + Math.sin(angle) * BX_RAIL_HEIGHT
    };
  };
  
  // Function to find the closest point on the BX rail from a given position
  // Returns null if nearest point would be in V-opening
  const getClosestBXRailPoint = (x, y) => {
    const dx = x - BX_CENTER_X;
    const dy = y - BX_CENTER_Y;
    const angle = Math.atan2(dy, dx);
    
    // If in V-opening zone, there's no rail to snap to
    if (isInVOpening(angle)) {
      return null;
    }
    
    return getBXRailPoint(angle);
  };
  
  // Function to get distance from a point to the BX rail
  // Returns Infinity if in V-opening (no rail there)
  const getDistanceToBXRail = (x, y) => {
    const railPoint = getClosestBXRailPoint(x, y);
    if (!railPoint) return Infinity; // In V-opening, no rail
    return Math.sqrt(Math.pow(x - railPoint.x, 2) + Math.pow(y - railPoint.y, 2));
  };
  
  // Check if beyblade is near the V-opening exit
  const isNearVOpening = (x, y) => {
    const dx = x - BX_CENTER_X;
    const dy = y - BX_CENTER_Y;
    const angle = Math.atan2(dy, dx);
    return isInVOpening(angle);
  };
  
  // For backward compatibility - approximate radius (used for knockout checks)
  const BX_RING_RADIUS = Math.max(BX_RAIL_WIDTH, BX_RAIL_HEIGHT);
  const BX_WALL_SEGMENTS = 32; // More segments for smoother oval approximation
  
  const generateBXWalls = () => {
    const walls = [];
    for (let i = 0; i < BX_WALL_SEGMENTS; i++) {
      const angle1 = (i / BX_WALL_SEGMENTS) * 2 * Math.PI - Math.PI; // Start from -PI
      const angle2 = ((i + 1) / BX_WALL_SEGMENTS) * 2 * Math.PI - Math.PI;
      
      // Skip wall segments in the V-opening (no walls there - it's an exit)
      if (isInVOpening(angle1) || isInVOpening(angle2)) {
        continue;
      }
      
      const point1 = getBXRailPoint(angle1);
      const point2 = getBXRailPoint(angle2);
      
      if (point1 && point2) {
        walls.push({
          start: point1,
          end: point2,
          label: `BX Rail Segment ${i + 1}`
        });
      }
    }
    return walls;
  };
  
  const BX_WALLS = generateBXWalls();
  
  // BX-Infinity wall lines (generated from vertices)
  const BX_INFINITY_VERTICES = [
    { x: 400, y: -10 },  // 0
    { x: 540, y: -8 },   // 1
    { x: 622, y: 97 },   // 2
    { x: 659, y: 128 },  // 3
    { x: 694, y: 180 },  // 4 - Top of right wall (vertical)
    { x: 694, y: 250 },  // 5 - Right wall middle-upper (vertical)
    { x: 694, y: 298 },  // 6 - Right wall center (vertical)
    { x: 694, y: 350 },  // 7 - Right wall middle-lower (vertical)
    { x: 694, y: 380 },  // 8 - Right wall lower (vertical)
    { x: 694, y: 415 },  // 9 - Bottom of right wall (vertical)
    { x: 583, y: 507 },  // 10
    { x: 521, y: 595 },  // 11
    { x: 400, y: 599 },  // 12
    { x: 279, y: 595 },  // 13
    { x: 196, y: 483 },  // 14
    { x: 106, y: 415 },  // 15 - Bottom of left wall (vertical)
    { x: 106, y: 298 },  // 16 - Left wall center (vertical)
    { x: 106, y: 350 },  // 17 - Left wall middle-lower (vertical)
    { x: 106, y: 250 },  // 18 - Left wall middle-upper (vertical)
    { x: 106, y: 220 },  // 19 - Left wall upper (vertical)
    { x: 106, y: 180 },  // 20 - Top of left wall (vertical)
    { x: 138, y: 131 },  // 21
    { x: 205, y: 73 },   // 22
    { x: 263, y: -8 },   // 23
  ];
  
  const BX_INFINITY_LINES = [
    { start: 13, end: 11, type: 'xtreme' },   // Line 0 - Bottom Xtreme zone
    { start: 23, end: 1, type: 'xtreme' },    // Line 1 - Top Xtreme zone
    { start: 11, end: 9, type: 'knockout' },  // Line 2 - Bottom right Over zone
    { start: 13, end: 15, type: 'knockout' }, // Line 3 - Bottom left Over zone
    { start: 23, end: 20, type: 'knockout' }, // Line 4 - Top left Over zone
    { start: 1, end: 4, type: 'knockout' },   // Line 5 - Top right Over zone
    { start: 9, end: 4, type: 'wall' },       // Line 6 - Right wall (single vertical line)
    { start: 15, end: 20, type: 'wall' },     // Line 7 - Left wall (single vertical line)
  ];
  
  // Convert BX-Infinity lines to wall format (only wall type lines)
  const BX_INFINITY_WALLS = BX_INFINITY_LINES
    .filter(line => line.type === 'wall')
    .map(line => ({
      start: BX_INFINITY_VERTICES[line.start],
      end: BX_INFINITY_VERTICES[line.end]
    }));
  
  // Get the active walls based on selected stadium
  const FIXED_WALLS = selectedStadium === 'bx' ? BX_WALLS : selectedStadium === 'bxInfinity' ? BX_INFINITY_WALLS : BB10_WALLS;

  // Canvas dimensions
  // Dynamic canvas dimensions based on container size
  const CANVAS_WIDTH = canvasDimensions.width;
  const CANVAS_HEIGHT = canvasDimensions.height;
  // REMOVED: MOVE_SPEED - walking character no longer exists
  // REMOVED: DPAD_SPEED - D-pad no longer exists
  const TOP_SPEED = 8;
  
  // Image URLs
  const STADIUM_IMAGE_URL = getStadiumImageUrl(selectedStadium) || STADIUMS.bb10.url;
  
  // Beyblade options
  // ALL available Beyblades that players can choose from
  const ALL_BEYBLADES = [
    {
      id: 1,
      name: 'Cobalt Drake',
      url: 'https://customer-assets.emergentagent.com/job_blader-progress/artifacts/zw2i6ecd_IMG_0908.png',
      spinDirection: 'right', // Spins counter-clockwise
      rotationSpeed: 40
    },
    {
      id: 2,
      name: 'Dran Sword',
      url: 'https://customer-assets.emergentagent.com/job_beypark/artifacts/b6xmsvw0_IMG_0911.png',
      spinDirection: 'right',
      rotationSpeed: 40
    },
    {
      id: 3,
      name: 'Phoenix Wing',
      url: 'https://customer-assets.emergentagent.com/job_battle-tops-3/artifacts/9gd0mo3x_IMG_0918.png',
      spinDirection: 'right',
      rotationSpeed: 40
    },
    {
      id: 4,
      name: 'Cobalt Dragoon',
      url: 'https://customer-assets.emergentagent.com/job_battle-tops-3/artifacts/eqx6fcua_IMG_0920.png',
      spinDirection: 'left', // Spins clockwise - REVERSED!
      rotationSpeed: 40
    },
    {
      id: 5,
      name: 'Aero Pegasus',
      url: 'https://customer-assets.emergentagent.com/job_57de14b1-dd79-40f8-9905-e428eae842bc/artifacts/ae815y75_IMG_0922.png',
      spinDirection: 'right', // Standard physics like first Beyblade
      rotationSpeed: 40
    },
    {
      id: 6,
      name: 'Valkyrie Volt',
      url: 'https://customer-assets.emergentagent.com/job_57de14b1-dd79-40f8-9905-e428eae842bc/artifacts/2rl351jz_IMG_0926.png',
      spinDirection: 'right', // Standard physics like first Beyblade
      rotationSpeed: 40
    },
    {
      id: 7,
      name: 'Shark Scale',
      url: 'https://customer-assets.emergentagent.com/job_57de14b1-dd79-40f8-9905-e428eae842bc/artifacts/018lhj90_IMG_0932.png',
      spinDirection: 'right', // Standard physics like first Beyblade
      rotationSpeed: 40
    },
    {
      id: 8,
      name: 'Wizard Arrow',
      url: 'https://customer-assets.emergentagent.com/job_beyduel-game/artifacts/lh4lkr8d_IMG_0951.png',
      spinDirection: 'right', // Standard physics like first Beyblade
      rotationSpeed: 40
    },
    {
      id: 9,
      name: 'Hells Scythe',
      url: 'https://customer-assets.emergentagent.com/job_beyduel-game/artifacts/xv1i2whp_IMG_0953.png',
      spinDirection: 'right', // Same physics as Wizard Arrow
      rotationSpeed: 40
    },
    {
      id: 10,
      name: 'Knight Shield',
      url: 'https://customer-assets.emergentagent.com/job_beyduel-game/artifacts/3b1i2oge_IMG_0956.png',
      spinDirection: 'right', // Same physics as Hells Scythe
      rotationSpeed: 40
    },
    {
      id: 11,
      name: 'Knight Lance',
      url: 'https://customer-assets.emergentagent.com/job_beyduel-game/artifacts/tai6ttk9_IMG_0959.png',
      spinDirection: 'right', // Same physics as Knight Shield
      rotationSpeed: 40
    },
    {
      id: 12,
      name: 'Unicorn Sting',
      url: 'https://customer-assets.emergentagent.com/job_beyduel-game/artifacts/4ch9bvsm_IMG_0961.png',
      spinDirection: 'right', // Same physics as Knight Lance
      rotationSpeed: 40
    },
    {
      id: 13,
      name: 'Shark Edge',
      url: 'https://customer-assets.emergentagent.com/job_beyduel-game/artifacts/vhc53hi5_IMG_0963.png',
      spinDirection: 'right', // Same physics as Unicorn Sting
      rotationSpeed: 40
    },
    {
      id: 14,
      name: 'Wyvern Hover MC',
      url: 'https://customer-assets.emergentagent.com/job_battle-arena-577/artifacts/aeeivbh5_IMG_0975.png',
      spinDirection: 'right', // Same physics as Dran Sword
      rotationSpeed: 40
    },
    {
      id: 15,
      name: 'Phoenix Wing CC',
      url: 'https://customer-assets.emergentagent.com/job_battle-arena-577/artifacts/i2zfqebx_IMG_0983.png',
      spinDirection: 'right', // Same physics as Dran Sword
      rotationSpeed: 40
    },
    {
      id: 16,
      name: 'Knight Mail B',
      url: 'https://customer-assets.emergentagent.com/job_battle-arena-577/artifacts/setl3t4g_IMG_0982.png',
      spinDirection: 'right', // Same physics as Dran Sword
      rotationSpeed: 40
    },
    {
      id: 17,
      name: 'Leon Fang',
      url: 'https://customer-assets.emergentagent.com/job_battle-arena-577/artifacts/b056b1rw_IMG_0993.png',
      spinDirection: 'right', // Same physics as Dran Sword
      rotationSpeed: 40
    },
    {
      id: 18,
      name: 'Cobalt Dragoon B',
      url: 'https://customer-assets.emergentagent.com/job_battle-arena-577/artifacts/1bscobtv_IMG_0995.png',
      spinDirection: 'left', // Same physics as Cobalt Dragoon - LEFT SPIN
      rotationSpeed: 40
    },
    {
      id: 19,
      name: 'Sol Eclipse',
      url: 'https://customer-assets.emergentagent.com/job_battle-arena-577/artifacts/dddzndhc_IMG_0998.png',
      spinDirection: 'right', // Same physics as Dran Sword
      rotationSpeed: 40
    },
    {
      id: 20,
      name: 'Emperor Might',
      url: 'https://customer-assets.emergentagent.com/job_battle-arena-577/artifacts/zo0593i3_IMG_0999.png',
      spinDirection: 'right', // Same physics as Dran Sword
      rotationSpeed: 40
    },
    {
      id: 21,
      name: 'Pegasus Blast',
      url: 'https://customer-assets.emergentagent.com/job_battle-arena-577/artifacts/nzo7v3hh_IMG_1002.png',
      spinDirection: 'right', // Same physics as Dran Sword
      rotationSpeed: 40
    },
    {
      id: 22,
      name: 'Dran Buster',
      url: 'https://customer-assets.emergentagent.com/job_beyblade-battles/artifacts/j3vumbrl_IMG_1006.png',
      spinDirection: 'right', // Same physics as Dran Sword
      rotationSpeed: 40
    },
    {
      id: 23,
      name: 'Hells Hammer',
      url: 'https://customer-assets.emergentagent.com/job_beyblade-battles/artifacts/jupug5bm_IMG_1008.png',
      spinDirection: 'right', // Same physics as Dran Sword
      rotationSpeed: 40
    },
    {
      id: 24,
      name: 'Wizard Rod',
      url: 'https://customer-assets.emergentagent.com/job_beyblade-battles/artifacts/vjqujewp_IMG_1010.png',
      spinDirection: 'right', // Same physics as Dran Sword
      rotationSpeed: 40
    },
    {
      id: 25,
      name: 'Wale Wave',
      url: 'https://customer-assets.emergentagent.com/job_beyblade-battles/artifacts/93whrda1_IMG_1004.png',
      spinDirection: 'right', // Same physics as Dran Sword
      rotationSpeed: 40
    },
    {
      id: 26,
      name: 'Shelter Drake',
      url: 'https://customer-assets.emergentagent.com/job_beyblade-battles/artifacts/fdvt1mcs_IMG_1014.png',
      spinDirection: 'right', // Same physics as Dran Sword
      rotationSpeed: 40
    },
    {
      id: 27,
      name: 'Leon Claw',
      url: 'https://customer-assets.emergentagent.com/job_beyblade-battles/artifacts/l5wqu0eq_IMG_1019.png',
      spinDirection: 'right', // Same physics as Dran Sword
      rotationSpeed: 40
    },
    {
      id: 28,
      name: 'Sphinx Cowl',
      url: 'https://customer-assets.emergentagent.com/job_beyblade-battles/artifacts/3ys9urgf_IMG_1016.png',
      spinDirection: 'right', // Same physics as Dran Sword
      rotationSpeed: 40
    },
    {
      id: 29,
      name: 'Silver Wolf',
      url: 'https://customer-assets.emergentagent.com/job_beyblade-battles/artifacts/cq1rrejw_IMG_1056.png',
      spinDirection: 'right', // Same physics as Dran Sword
      rotationSpeed: 40
    },
    {
      id: 30,
      name: 'Impact Drake',
      url: 'https://customer-assets.emergentagent.com/job_beyblade-battles/artifacts/uyzyvs0f_IMG_1061.png',
      spinDirection: 'right', // Same physics as Dran Sword
      rotationSpeed: 40
    },
    {
      id: 31,
      name: 'Knight Mail',
      url: 'https://customer-assets.emergentagent.com/job_beyblade-battles/artifacts/gbc3cbr4_IMG_1062.png',
      spinDirection: 'right', // Same physics as Dran Sword
      rotationSpeed: 40
    },
    {
      id: 32,
      name: 'Scorpio Spear',
      url: 'https://customer-assets.emergentagent.com/job_beyblade-battles/artifacts/p8r05bmr_IMG_1066.png',
      spinDirection: 'right', // Same physics as Dran Sword
      rotationSpeed: 40
    },
    {
      id: 33,
      name: 'Leon Crest',
      url: 'https://customer-assets.emergentagent.com/job_beyblade-battles/artifacts/pdnh7yfj_IMG_1058.png',
      spinDirection: 'right', // Same physics as Dran Sword
      rotationSpeed: 40
    },
    {
      id: 34,
      name: 'Golem Rock',
      url: 'https://customer-assets.emergentagent.com/job_beyblade-battles/artifacts/1i79j7t9_IMG_1070.png',
      spinDirection: 'right', // Same physics as Dran Sword
      rotationSpeed: 40
    },
    {
      id: 35,
      name: 'Samurai Saber',
      url: 'https://customer-assets.emergentagent.com/job_beyblade-battles/artifacts/gf3r8pwx_IMG_1064.png',
      spinDirection: 'right', // Same physics as Dran Sword
      rotationSpeed: 40
    },
    {
      id: 36,
      name: 'Shinobi Shadow',
      url: 'https://customer-assets.emergentagent.com/job_beyblade-battles/artifacts/hqvirsc4_IMG_1071.png',
      spinDirection: 'right', // Same physics as Dran Sword
      rotationSpeed: 40
    },
    {
      id: 37,
      name: 'Dran Brave',
      url: 'https://customer-assets.emergentagent.com/job_beyblade-battles/artifacts/z29ujjed_IMG_1081.png',
      spinDirection: 'right', // Same physics as Dran Sword
      rotationSpeed: 40
    },
    {
      id: 38,
      name: 'Wizard Arc',
      url: 'https://customer-assets.emergentagent.com/job_beyblade-battles/artifacts/llk6d0je_IMG_1083.png',
      spinDirection: 'right', // Same physics as Dran Sword
      rotationSpeed: 40
    },
    {
      id: 39,
      name: 'Perseus Dark',
      url: 'https://customer-assets.emergentagent.com/job_beyblade-battles/artifacts/75v5o0yr_IMG_1082.png',
      spinDirection: 'right', // Same physics as Dran Sword
      rotationSpeed: 40
    },
    {
      id: 40,
      name: 'Hells Reaper',
      url: 'https://customer-assets.emergentagent.com/job_beyblade-battles/artifacts/up29j0hg_IMG_1085.png',
      spinDirection: 'right', // Same physics as Dran Sword
      rotationSpeed: 40
    },
    {
      id: 41,
      name: 'Wyvern Hover',
      url: 'https://customer-assets.emergentagent.com/job_beyblades-battle/artifacts/2ip00mdr_IMG_1077.png',
      spinDirection: 'right', // Same physics as Dran Sword
      rotationSpeed: 40
    },
    {
      id: 42,
      name: 'Meteor Dragoon',
      url: 'https://customer-assets.emergentagent.com/job_beyblades-battle/artifacts/0fgghl73_IMG_1012.png',
      spinDirection: 'left', // Spins clockwise - REVERSED like Cobalt Dragoon
      rotationSpeed: 40
    },
    {
      id: 43,
      name: 'Mummy Curse',
      url: 'https://customer-assets.emergentagent.com/job_beyblades-battle/artifacts/kxtfgb7q_IMG_1115.png',
      spinDirection: 'right', // Same physics as Dran Sword
      rotationSpeed: 40
    },
    {
      id: 44,
      name: 'Pegasus Brush',
      url: 'https://customer-assets.emergentagent.com/job_beyblades-battle/artifacts/e1w2jid2_IMG_1117.png',
      spinDirection: 'right', // Same physics as Dran Sword
      rotationSpeed: 40
    },
    {
      id: 45,
      name: 'Storm Pegasus',
      url: 'https://customer-assets.emergentagent.com/job_beyblade-arena-4/artifacts/zbtxlzwg_IMG_1200.png',
      spinDirection: 'right', // Same physics as Dran Sword
      rotationSpeed: 40
    },
    {
      id: 46,
      name: 'Rock Leon',
      url: 'https://customer-assets.emergentagent.com/job_beyblade-arena-4/artifacts/rtz6hrx9_IMG_1201.png',
      spinDirection: 'right', // Same physics as Dran Sword
      rotationSpeed: 40
    },
    {
      id: 47,
      name: 'LL Drago',
      url: 'https://customer-assets.emergentagent.com/job_beyblade-arena-4/artifacts/y327vnu9_IMG_1202.png',
      spinDirection: 'left', // Left spin like Cobalt Dragoon
      rotationSpeed: 40
    },
    {
      id: 48,
      name: 'Victory Valkyrie',
      url: 'https://customer-assets.emergentagent.com/job_beyblade-arena-4/artifacts/0up5sihr_IMG_1203.png',
      spinDirection: 'right', // Same physics as Dran Sword
      rotationSpeed: 40
    },
    {
      id: 49,
      name: 'Clock Mirage',
      url: 'https://customer-assets.emergentagent.com/job_beyblade-arena-4/artifacts/7vaa4m9i_IMG_1087.png',
      spinDirection: 'right', // Same physics as Dran Sword
      rotationSpeed: 40
    },
    {
      id: 50,
      name: 'Phoenix Rudder',
      url: 'https://customer-assets.emergentagent.com/job_beyblade-arena-4/artifacts/8jzkk3oo_IMG_1075.png',
      spinDirection: 'right', // Same physics as Dran Sword
      rotationSpeed: 40
    },
    {
      id: 51,
      name: 'Ghost Circle',
      url: 'https://customer-assets.emergentagent.com/job_beyblade-arena-4/artifacts/3yuc0fhg_IMG_1072.png',
      spinDirection: 'right', // Same physics as Dran Sword
      rotationSpeed: 40
    },
    {
      id: 52,
      name: 'Xeno Xcaliber',
      url: 'https://customer-assets.emergentagent.com/job_beyblade-arena-4/artifacts/541bmhls_IMG_1204.png',
      spinDirection: 'right', // Same physics as Dran Sword
      rotationSpeed: 40
    },
    {
      id: 53,
      name: 'Galaxy Pegasus',
      url: 'https://customer-assets.emergentagent.com/job_beyblade-arena-4/artifacts/tfp4tn0i_IMG_1238.png',
      spinDirection: 'right', // Same physics as Dran Sword
      rotationSpeed: 40
    },
    {
      id: 54,
      name: 'Meteo L Drago',
      url: 'https://customer-assets.emergentagent.com/job_beyblade-arena-4/artifacts/kp5i34f1_IMG_1239.png',
      spinDirection: 'left', // Left spin like Cobalt Dragoon
      rotationSpeed: 40
    },
    {
      id: 55,
      name: 'Gravity Perseus',
      url: 'https://customer-assets.emergentagent.com/job_beyblade-arena-4/artifacts/vncu9cse_IMG_1244.png',
      spinDirection: 'dual', // Special dual-spin beyblade - can switch between left and right
      rotationSpeed: 40
    },
    {
      id: 56,
      name: 'Vari Ares',
      url: 'https://customer-assets.emergentagent.com/job_battle-arena-626/artifacts/84u59dh1_IMG_1254.png',
      spinDirection: 'dual', // Special dual-spin beyblade - can switch between left and right (same as Gravity Perseus)
      rotationSpeed: 40
    },
    {
      id: 57,
      name: 'Wizard Rod G',
      url: 'https://customer-assets.emergentagent.com/job_8cf175b2-6a18-4f7b-949a-e3aab798aba9/artifacts/yfj0jsjj_IMG_1643.png',
      spinDirection: 'right',
      rotationSpeed: 40
    },
    {
      id: 58,
      name: 'Phoenix Feather',
      url: 'https://customer-assets.emergentagent.com/job_8cf175b2-6a18-4f7b-949a-e3aab798aba9/artifacts/rrmnl4t8_IMG_1645.png',
      spinDirection: 'right',
      rotationSpeed: 40
    },
    {
      id: 59,
      name: 'Dran Brave B',
      url: 'https://customer-assets.emergentagent.com/job_8cf175b2-6a18-4f7b-949a-e3aab798aba9/artifacts/iinrjbke_IMG_1652.png',
      spinDirection: 'right',
      rotationSpeed: 40
    },
    {
      id: 60,
      name: 'Dran Dagger',
      url: 'https://customer-assets.emergentagent.com/job_8cf175b2-6a18-4f7b-949a-e3aab798aba9/artifacts/wvp4odkd_IMG_1656.png',
      spinDirection: 'right',
      rotationSpeed: 40
    },
    {
      id: 61,
      name: 'Cobalt Drake C',
      url: 'https://customer-assets.emergentagent.com/job_bey-battlezone/artifacts/6eav4612_IMG_1650.png',
      spinDirection: 'right',
      rotationSpeed: 40
    },
    {
      id: 62,
      name: 'Cobalt Drake R',
      url: 'https://customer-assets.emergentagent.com/job_bey-battlezone/artifacts/oqx5w08s_IMG_1651.png',
      spinDirection: 'right',
      rotationSpeed: 40
    },
    {
      id: 63,
      name: 'Samurai Saber C',
      url: 'https://customer-assets.emergentagent.com/job_bey-battlezone/artifacts/ht26xzfl_IMG_1670.png',
      spinDirection: 'right',
      rotationSpeed: 40
    },
    {
      id: 64,
      name: 'Dran Buster C',
      url: 'https://customer-assets.emergentagent.com/job_bey-battlezone/artifacts/xwtosigy_IMG_1674.png',
      spinDirection: 'right',
      rotationSpeed: 40
    },
    {
      id: 65,
      name: 'Wizard Rod C',
      url: 'https://customer-assets.emergentagent.com/job_bey-battlezone/artifacts/v1p00bju_IMG_1680.png',
      spinDirection: 'right',
      rotationSpeed: 40
    },
    {
      id: 66,
      name: 'Phoenix Wing B',
      url: 'https://customer-assets.emergentagent.com/job_bey-battlezone/artifacts/qllo84cv_IMG_1682.png',
      spinDirection: 'right',
      rotationSpeed: 40
    },
    {
      id: 67,
      name: 'Phoenix Flare',
      url: 'https://customer-assets.emergentagent.com/job_bey-battlezone/artifacts/sb0yvasr_IMG_1668.png',
      spinDirection: 'right',
      rotationSpeed: 40
    },
    {
      id: 68,
      name: 'Phoenix Wing M',
      url: 'https://customer-assets.emergentagent.com/job_bey-battlezone/artifacts/u4qospky_IMG_1683.png',
      spinDirection: 'right',
      rotationSpeed: 40
    },
    {
      id: 69,
      name: 'Pegasus Blast R',
      url: 'https://customer-assets.emergentagent.com/job_bey-battlezone/artifacts/ehmmk8ey_IMG_1685.png',
      spinDirection: 'right',
      rotationSpeed: 40
    },
    {
      id: 70,
      name: 'Fox Brush',
      url: 'https://customer-assets.emergentagent.com/job_spin-battle-3/artifacts/arcjnokl_IMG_1705.png',
      spinDirection: 'right',
      rotationSpeed: 40
    },
    {
      id: 71,
      name: 'Fox Brush G',
      url: 'https://customer-assets.emergentagent.com/job_spin-battle-3/artifacts/hnsc5oqv_IMG_1706.png',
      spinDirection: 'right',
      rotationSpeed: 40
    },
    {
      id: 72,
      name: 'Fox Brush B',
      url: 'https://customer-assets.emergentagent.com/job_spin-battle-3/artifacts/1wwswe1a_IMG_1707.png',
      spinDirection: 'right',
      rotationSpeed: 40
    },
    {
      id: 73,
      name: 'Ultimate Valkyrie',
      url: 'https://customer-assets.emergentagent.com/job_spin-battle-3/artifacts/aa5rsuge_IMG_1732.png',
      spinDirection: 'right',
      rotationSpeed: 40
    },
    {
      id: 74,
      name: 'Guilty Longinus',
      url: 'https://customer-assets.emergentagent.com/job_spin-battle-3/artifacts/yi2wexk5_IMG_1731.png',
      spinDirection: 'left',
      rotationSpeed: 40
    },
    {
      id: 75,
      name: 'Dran Buster C1',
      url: 'https://customer-assets.emergentagent.com/job_spin-battle-3/artifacts/l40px0ao_IMG_1714.png',
      spinDirection: 'right',
      rotationSpeed: 40
    },
    {
      id: 76,
      name: 'Dran Buster C2',
      url: 'https://customer-assets.emergentagent.com/job_spin-battle-3/artifacts/o7nexuz8_IMG_1712.png',
      spinDirection: 'right',
      rotationSpeed: 40
    },
    {
      id: 77,
      name: 'Dran Buster C3',
      url: 'https://customer-assets.emergentagent.com/job_spin-battle-3/artifacts/44x7o4l9_IMG_1713.png',
      spinDirection: 'right',
      rotationSpeed: 40
    },
    {
      id: 78,
      name: 'Glory Valkyrie',
      url: 'https://customer-assets-gfyr7b9c.emergentagent.net/job_80d2796e-4c98-4810-b14d-894ef7171682/artifacts/6ad1cdeb1c66c362_IMG_6835.png',
      spinDirection: 'right',
      rotationSpeed: 40
    },
    {
      id: 79,
      name: 'Glory Valkyrie',
      url: 'https://customer-assets-gfyr7b9c.emergentagent.net/job_80d2796e-4c98-4810-b14d-894ef7171682/artifacts/c9711bd34d55590b_IMG_4322.png',
      spinDirection: 'right',
      rotationSpeed: 40
    },
    {
      id: 80,
      name: 'Drake Brave',
      url: 'https://customer-assets-gfyr7b9c.emergentagent.net/job_80d2796e-4c98-4810-b14d-894ef7171682/artifacts/b927dc7095c4e505_IMG_6841.png',
      spinDirection: 'right',
      rotationSpeed: 40
    },
    {
      id: 81,
      name: 'Luster Dragoon',
      url: 'https://customer-assets-gfyr7b9c.emergentagent.net/job_80d2796e-4c98-4810-b14d-894ef7171682/artifacts/6a9e4d7e8d9d10c6_IMG_6843.png',
      spinDirection: 'left',
      rotationSpeed: 40
    }
  ];
  
  // Stadium wall positions - matching the 3 yellow highlighted sections
  // These are now responsive and scale with canvas dimensions
  const BASE_CANVAS_WIDTH = 800; // Original design width
  const BASE_CANVAS_HEIGHT = 600; // Original design height
  const SCALE_FACTOR = Math.min(CANVAS_WIDTH / BASE_CANVAS_WIDTH, CANVAS_HEIGHT / BASE_CANVAS_HEIGHT);
  
  const STADIUM_CENTER_X = CANVAS_WIDTH / 2;
  // BX-Infinity uses true center to match the stadium image, other stadiums use offset
  const STADIUM_CENTER_Y = selectedStadium === 'bxInfinity' ? CANVAS_HEIGHT / 2 : CANVAS_HEIGHT / 2 - 15;
  const STADIUM_RADIUS = 155 * SCALE_FACTOR; // Outer wall radius (scaled)
  const BEYBLADE_RADIUS = 145 * SCALE_FACTOR; // Radius where Beyblades move (scaled)
  
  // BX-Stadium specific settings (octagonal with pockets)
  const BX_STADIUM_RADIUS = 160 * SCALE_FACTOR; // Slightly larger for octagonal shape
  const BX_POCKET_RADIUS = 25 * SCALE_FACTOR; // Size of pocket holes
  // Pocket positions at corners of octagon (8 corners, but only 4 have pockets in BX-10)
  const BX_POCKET_ANGLES = [
    Math.PI * 0.25,   // Top-right corner (45°)
    Math.PI * 0.75,   // Top-left corner (135°)
    Math.PI * 1.25,   // Bottom-left corner (225°)
    Math.PI * 1.75,   // Bottom-right corner (315°)
  ];
  const BX_POCKET_DISTANCE = 140 * SCALE_FACTOR; // Distance from center to pocket centers
  
  // 2 orbital rings for auto-spin: center and outer
  const ORBITAL_RINGS = [
    0,                          // Ring 0: Center (stationary spin)
    145 * SCALE_FACTOR,         // Ring 1: Outer ring (original BEYBLADE_RADIUS)
  ];
  
  // Function to find nearest orbital ring
  const findNearestRing = (distanceFromCenter) => {
    let nearestRing = ORBITAL_RINGS[0];
    let minDiff = Math.abs(distanceFromCenter - ORBITAL_RINGS[0]);
    
    for (let i = 1; i < ORBITAL_RINGS.length; i++) {
      const diff = Math.abs(distanceFromCenter - ORBITAL_RINGS[i]);
      if (diff < minDiff) {
        minDiff = diff;
        nearestRing = ORBITAL_RINGS[i];
      }
    }
    return nearestRing;
  };
  
  const WALL_ANGLES = [
    0.35 * Math.PI,           // Top-right wall (~63°)
    0.95 * Math.PI,           // Bottom-left wall (~171°)
    1.65 * Math.PI            // Bottom-right wall (~297°)
  ];
  const WALL_ARC_SIZE = Math.PI / 4; // ~45° arc for each wall (wider sections)

  // Save roster to backend for a specific player
  const saveRoster = async (player, roster) => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        console.error('No auth token found');
        return false;
      }

      const rosterIds = roster.map(b => b.id);

      const response = await fetch(`${BACKEND_URL}/api/beyblade/roster`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ 
          player: player,
          roster: rosterIds 
        })
      });

      if (response.ok) {
        const data = await response.json();
        return true;
      } else {
        console.error('Failed to save roster:', await response.text());
        return false;
      }
    } catch (error) {
      console.error('Error saving roster:', error);
      return false;
    }
  };

  // REMOVED: CHARACTER_COLORS - walking character no longer exists

  // Handle responsive canvas sizing  
  useEffect(() => {
    const updateCanvasSize = () => {
      if (containerRef.current) {
        const container = containerRef.current;
        const containerWidth = container.clientWidth;
        const containerHeight = container.clientHeight;
        
        // Maintain 4:3 aspect ratio
        let width = containerWidth;
        let height = containerWidth * 0.75;  // 4:3 ratio
        
        if (height > containerHeight) {
          height = containerHeight;
          width = height * 1.333; // 4:3 ratio
        }
        
        setCanvasDimensions({ width, height });
      }
    };
    
    updateCanvasSize();
    window.addEventListener('resize', updateCanvasSize);
    
    return () => window.removeEventListener('resize', updateCanvasSize);
  }, []);

  // Load user's rosters and filter available Beyblades
  useEffect(() => {
    if (user) {
      // Filter ALL_BEYBLADES to only show purchased ones, maintaining the order from purchased_beyblades
      const purchasedIds = user.purchased_beyblades || [2, 9, 8, 10]; // Default free Beyblades
      
      // Map purchased IDs to Beyblade objects in the same order
      const filtered = purchasedIds
        .map(id => ALL_BEYBLADES.find(b => b.id === id))
        .filter(b => b !== undefined);
      
      setAvailableBeyblades(filtered);
      
      // Load rosters from user data (default to free Beyblades if empty)
      const roster1Ids = user.beyblade_roster_p1 && user.beyblade_roster_p1.length > 0 
        ? user.beyblade_roster_p1 
        : [2, 9, 8, 10];
      const roster2Ids = user.beyblade_roster_p2 && user.beyblade_roster_p2.length > 0 
        ? user.beyblade_roster_p2 
        : [2, 9, 8, 10];
      
      // Convert IDs to Beyblade objects
      const roster1Objects = roster1Ids.map(id => ALL_BEYBLADES.find(b => b.id === id)).filter(b => b !== undefined);
      const roster2Objects = roster2Ids.map(id => ALL_BEYBLADES.find(b => b.id === id)).filter(b => b !== undefined);
      
      setPlayer1Roster(roster1Objects);
      setPlayer2Roster(roster2Objects);
      
      // Set default selected Beyblades to first Beyblade in each player's roster
      if (roster1Objects.length > 0 && !selectedBeyblade1) {
        setSelectedBeyblade1(roster1Objects[0]);
      }
      if (roster2Objects.length > 0 && !selectedBeyblade2) {
        setSelectedBeyblade2(roster2Objects[0]);
      }
    }
  }, [user]);

  // Auto-select first Beyblade when roster changes
  useEffect(() => {
    // If selected Beyblade is not in current roster, select first one
    if (player1Roster.length > 0) {
      const isInRoster = player1Roster.some(b => b.id === selectedBeyblade1?.id);
      if (!isInRoster) {
        setSelectedBeyblade1(player1Roster[0]);
      }
    }
  }, [player1Roster]);

  useEffect(() => {
    // If selected Beyblade is not in current roster, select first one
    // Only apply in LOCAL mode - online mode uses opponentRoster
    if (!isOnlineMode && player2Roster.length > 0) {
      const isInRoster = player2Roster.some(b => b.id === selectedBeyblade2?.id);
      if (!isInRoster) {
        setSelectedBeyblade2(player2Roster[0]);
      }
    }
  }, [player2Roster, isOnlineMode]);

  // Update health bars and stamina immediately when beyblade selection changes (before battle starts)
  useEffect(() => {
    if (selectedBeyblade1 && !top1Active) {
      setHealth1(getMaxHealth(selectedBeyblade1.id));
      const maxStam = getMaxStamina(selectedBeyblade1.id);
      setMaxStamina1(maxStam);
      setStamina1(maxStam);
    }
  }, [selectedBeyblade1]);

  useEffect(() => {
    if (selectedBeyblade2 && !top2Active) {
      setHealth2(getMaxHealth(selectedBeyblade2.id));
      const maxStam = getMaxStamina(selectedBeyblade2.id);
      setMaxStamina2(maxStam);
      setStamina2(maxStam);
    }
  }, [selectedBeyblade2]);
  
  // Auto-select opponent's first Beyblade in ONLINE mode
  useEffect(() => {
    if (isOnlineMode && matchFound && opponentRoster.length > 0) {
      setSelectedBeyblade2(opponentRoster[0]);
    }
  }, [isOnlineMode, matchFound, opponentRoster]);

  // Scale Beyblade positions proportionally when canvas dimensions change (e.g., device rotation)
  useEffect(() => {
    const prevWidth = prevCanvasDimensionsRef.current.width;
    const prevHeight = prevCanvasDimensionsRef.current.height;
    const newWidth = canvasDimensions.width;
    const newHeight = canvasDimensions.height;
    
    // Only scale if dimensions actually changed (not initial render)
    if (prevWidth !== newWidth || prevHeight !== newHeight) {
      const scaleX = newWidth / prevWidth;
      const scaleY = newHeight / prevHeight;
      
      // Scale Beyblade 1 position if active
      if (top1Active) {
        setTop1Pos(prev => ({
          x: prev.x * scaleX,
          y: prev.y * scaleY
        }));
        setTop1Velocity(prev => ({
          x: prev.x * scaleX,
          y: prev.y * scaleY
        }));
        setKnockbackVelocity1(prev => ({
          x: prev.x * scaleX,
          y: prev.y * scaleY
        }));
      }
      
      // Scale Beyblade 2 position if active
      if (top2Active) {
        setTop2Pos(prev => ({
          x: prev.x * scaleX,
          y: prev.y * scaleY
        }));
        setTop2Velocity(prev => ({
          x: prev.x * scaleX,
          y: prev.y * scaleY
        }));
        setKnockbackVelocity2(prev => ({
          x: prev.x * scaleX,
          y: prev.y * scaleY
        }));
      }
      
      // Update the previous dimensions ref
      prevCanvasDimensionsRef.current = { width: newWidth, height: newHeight };
    }
  }, [canvasDimensions, top1Active, top2Active]);

  // Load saved rosters from backend for both players
  useEffect(() => {
    const loadRosters = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) return;

        const response = await fetch(`${BACKEND_URL}/api/beyblade/roster`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        if (response.ok) {
          const data = await response.json();
          if (data.success) {
            // Load Player 1 roster
            if (data.roster_p1) {
              const p1Beyblades = data.roster_p1
                .map(id => ALL_BEYBLADES.find(b => b.id === id))
                .filter(b => b !== undefined);
              
              if (p1Beyblades.length > 0) {
                setPlayer1Roster(p1Beyblades);
              } else {
                setPlayer1Roster(ALL_BEYBLADES.slice(0, 6));
              }
            }

            // Load Player 2 roster
            if (data.roster_p2) {
              const p2Beyblades = data.roster_p2
                .map(id => ALL_BEYBLADES.find(b => b.id === id))
                .filter(b => b !== undefined);
              
              if (p2Beyblades.length > 0) {
                setPlayer2Roster(p2Beyblades);
              } else {
                setPlayer2Roster(ALL_BEYBLADES.slice(0, 6));
              }
            }
          }
        }
      } catch (error) {
        console.error('Failed to load rosters:', error);
        // Fallback to default rosters
        setPlayer1Roster(ALL_BEYBLADES.slice(0, 6));
        setPlayer2Roster(ALL_BEYBLADES.slice(0, 6));
      }
    };

    loadRosters();
  }, []);

  // Load stadium preferences from backend
  useEffect(() => {
    const loadStadiumPreferences = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) return;

        const response = await fetch(`${BACKEND_URL}/api/stadium/preferences`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        if (response.ok) {
          const data = await response.json();
          if (data.success) {
            setSelectedStadium(data.selected_stadium || 'bx');
            setBxStadiumImageIndex(data.bx_stadium_image_index || 0);
            setBb10StadiumImageIndex(data.bb10_stadium_image_index || 0);
            // Cache stadium preferences for instant loading on next visit
            localStorage.setItem('stadiumPreferences', JSON.stringify({
              selected_stadium: data.selected_stadium || 'bx',
              bx_stadium_image_index: data.bx_stadium_image_index || 0,
              bb10_stadium_image_index: data.bb10_stadium_image_index || 0
            }));
          }
        }
      } catch (error) {
        console.error('Failed to load stadium preferences:', error);
      }
    };

    loadStadiumPreferences();
  }, []);

  // Save stadium preferences to backend
  const saveStadiumPreferences = async (stadium, bxIndex, bb10Index) => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;

      // Update localStorage cache immediately for instant loading
      localStorage.setItem('stadiumPreferences', JSON.stringify({
        selected_stadium: stadium,
        bx_stadium_image_index: bxIndex,
        bb10_stadium_image_index: bb10Index
      }));

      await fetch(`${BACKEND_URL}/api/stadium/preferences`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          selected_stadium: stadium,
          bx_stadium_image_index: bxIndex,
          bb10_stadium_image_index: bb10Index
        })
      });
    } catch (error) {
      console.error('Failed to save stadium preferences:', error);
    }
  };

  // Pre-load all critical images before rendering game
  useEffect(() => {
    let loadedCount = 0;
    const totalImages = 1 + ALL_BEYBLADES.length; // Stadium + all beyblades
    
    const checkAllLoaded = () => {
      loadedCount++;
      if (loadedCount === totalImages) {
        setAssetsLoaded(true);
      }
    };
    
    // Preload ALL stadium images (both BB-10 and BX-10)
    Object.values(STADIUMS).forEach((stadium) => {
      const img = new Image();
      img.src = stadium.url;
      img.onload = () => {
      };
    });
    
    // Load current stadium image (critical - must load first)
    const stadiumImg = new Image();
    stadiumImg.src = STADIUM_IMAGE_URL;
    stadiumImg.onload = () => {
      stadiumImageRef.current = stadiumImg;
      checkAllLoaded();
    };
    stadiumImg.onerror = () => {
      console.error('Failed to load stadium image');
      checkAllLoaded(); // Continue anyway
    };
    
    // Load all beyblade images
    ALL_BEYBLADES.forEach((beyblade, index) => {
      const img = new Image();
      img.src = beyblade.url;
      img.onload = () => {
        if (index === 0) top1ImageRef.current = img;
        if (index === 1) top2ImageRef.current = img;
        checkAllLoaded();
      };
      img.onerror = () => {
        console.error(`Failed to load Beyblade ${beyblade.name} image`);
        checkAllLoaded();
      };
    });
  }, []);

  // Update stadium image when selection changes
  useEffect(() => {
    const stadiumImg = new Image();
    stadiumImg.src = STADIUM_IMAGE_URL;
    stadiumImg.onload = () => {
      stadiumImageRef.current = stadiumImg;
    };
  }, [selectedStadium, STADIUM_IMAGE_URL]);

  // Walls are now fixed - no editing needed

  // Line segment intersection check
  const lineSegmentIntersection = (p1, p2, p3, p4) => {
    const x1 = p1.x, y1 = p1.y;
    const x2 = p2.x, y2 = p2.y;
    const x3 = p3.x, y3 = p3.y;
    const x4 = p4.x, y4 = p4.y;

    const denom = (y4 - y3) * (x2 - x1) - (x4 - x3) * (y2 - y1);
    if (denom === 0) return false;

    const ua = ((x4 - x3) * (y1 - y3) - (y4 - y3) * (x1 - x3)) / denom;
    const ub = ((x2 - x1) * (y1 - y3) - (y2 - y1) * (x1 - x3)) / denom;

    return ua >= 0 && ua <= 1 && ub >= 0 && ub <= 1;
  };

  // Check if Beyblade is out of stadium bounds (outer circle boundary)
  // Returns: { outOfBounds: boolean, isPocket: boolean, pocketType: string, pocketPoints: number }
  const isOutOfBounds = (pos) => {
    if (selectedStadium === 'bx') {
      // BX-10 Xtreme Stadium knockout zones - uses configurable zoneConfig
      const dx = pos.x - BX_CENTER_X;
      const dy = pos.y - BX_CENTER_Y;
      const normalizedDist = (dx * dx) / (BX_RAIL_WIDTH * BX_RAIL_WIDTH) + 
                             (dy * dy) / (BX_RAIL_HEIGHT * BX_RAIL_HEIGHT);
      
      // Calculate angle from center (0 = right, PI/2 = bottom, PI = left)
      const angle = Math.atan2(dy, dx);
      
      // Only check for knockout if beyblade is outside the play area (beyond green rail)
      // AND in the bottom half of the stadium
      if (normalizedDist > 1.0 && dy > 0) {
        
        // Left Over Zone pocket - uses zoneConfig angles
        if (angle > zoneConfig.leftZone.endAngle && angle <= zoneConfig.leftZone.startAngle) {
          return { outOfBounds: true, isPocket: true, pocketType: 'overZone', pocketPoints: 2 };
        }
        
        // Center Xtreme Zone pocket - uses zoneConfig angles
        if (angle > zoneConfig.centerZone.endAngle && angle <= zoneConfig.centerZone.startAngle) {
          return { outOfBounds: true, isPocket: true, pocketType: 'xtremeZone', pocketPoints: 3 };
        }
        
        // Right Over Zone pocket - uses zoneConfig angles
        if (angle >= zoneConfig.rightZone.endAngle && angle <= zoneConfig.rightZone.startAngle) {
          return { outOfBounds: true, isPocket: true, pocketType: 'overZone', pocketPoints: 2 };
        }
      }
      
      // Not in any pocket - beyblade stays in play
      return { outOfBounds: false, isPocket: false, pocketType: null, pocketPoints: 0 };
    } else if (selectedStadium === 'bxInfinity') {
      // BX-Infinity Stadium: Custom lines for walls and knockout zones
      const vertices = [
        { x: 400, y: -10 },  // 0
        { x: 521, y: -8 },   // 1 - Adjusted to match bottom Xtreme zone width
        { x: 622, y: 97 },   // 2
        { x: 659, y: 128 },  // 3
        { x: 694, y: 180 },  // 4 - Top of right wall (vertical)
        { x: 694, y: 250 },  // 5 - Right wall middle-upper (vertical)
        { x: 694, y: 298 },  // 6 - Right wall center (vertical)
        { x: 694, y: 350 },  // 7 - Right wall middle-lower (vertical)
        { x: 694, y: 380 },  // 8 - Right wall lower (vertical)
        { x: 694, y: 415 },  // 9 - Bottom of right wall (vertical)
        { x: 583, y: 507 },  // 10
        { x: 521, y: 595 },  // 11
        { x: 400, y: 599 },  // 12
        { x: 279, y: 595 },  // 13
        { x: 196, y: 483 },  // 14
        { x: 106, y: 415 },  // 15 - Bottom of left wall (vertical)
        { x: 106, y: 298 },  // 16 - Left wall center (vertical)
        { x: 106, y: 350 },  // 17 - Left wall middle-lower (vertical)
        { x: 106, y: 250 },  // 18 - Left wall middle-upper (vertical)
        { x: 106, y: 220 },  // 19 - Left wall upper (vertical)
        { x: 106, y: 180 },  // 20 - Top of left wall (vertical)
        { x: 138, y: 131 },  // 21
        { x: 205, y: 73 },   // 22
        { x: 279, y: -8 },   // 23 - Adjusted to match bottom Xtreme zone width
      ];
      
      // Lines configuration
      const lines = [
        { start: 13, end: 11, type: 'xtreme' },   // Line 0 - Bottom Xtreme zone
        { start: 23, end: 1, type: 'xtreme' },    // Line 1 - Top Xtreme zone
        { start: 11, end: 9, type: 'knockout' },  // Line 2 - Bottom right Over zone
        { start: 13, end: 15, type: 'knockout' }, // Line 3 - Bottom left Over zone
        { start: 23, end: 20, type: 'knockout' }, // Line 4 - Top left Over zone
        { start: 1, end: 4, type: 'knockout' },   // Line 5 - Top right Over zone
        { start: 9, end: 4, type: 'wall' },       // Line 6 - Right wall (single vertical line)
        { start: 15, end: 20, type: 'wall' },     // Line 7 - Left wall (single vertical line)
      ];
      
      // Check distance from point to line segment
      const distToLineSegment = (px, py, x1, y1, x2, y2) => {
        const A = px - x1;
        const B = py - y1;
        const C = x2 - x1;
        const D = y2 - y1;
        
        const dot = A * C + B * D;
        const lenSq = C * C + D * D;
        let param = -1;
        
        if (lenSq !== 0) param = dot / lenSq;
        
        let xx, yy;
        
        if (param < 0) {
          xx = x1;
          yy = y1;
        } else if (param > 1) {
          xx = x2;
          yy = y2;
        } else {
          xx = x1 + param * C;
          yy = y1 + param * D;
        }
        
        const dx = px - xx;
        const dy = py - yy;
        return Math.sqrt(dx * dx + dy * dy);
      };
      
      const beybladeRadius = 25; // Approximate beyblade radius for collision
      
      // Check each line for collision
      for (const line of lines) {
        const p1 = vertices[line.start];
        const p2 = vertices[line.end];
        const dist = distToLineSegment(pos.x, pos.y, p1.x, p1.y, p2.x, p2.y);
        
        if (dist < beybladeRadius) {
          if (line.type === 'knockout') {
            // Crossed a knockout zone line - 2 points (like Over Zone in BX stadium)
            return { outOfBounds: true, isPocket: true, pocketType: 'overZone', pocketPoints: 2 };
          }
          if (line.type === 'xtreme') {
            // Crossed an Xtreme zone line - 3 points (like Xtreme Zone in BX stadium)
            return { outOfBounds: true, isPocket: true, pocketType: 'xtremeZone', pocketPoints: 3 };
          }
          // Wall collision is handled separately in physics, not here
        }
      }
      
      // Also check if completely outside the outer boundary polygon
      const isInsidePolygon = (px, py, polygon) => {
        let inside = false;
        for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
          const xi = polygon[i].x, yi = polygon[i].y;
          const xj = polygon[j].x, yj = polygon[j].y;
          
          if (((yi > py) !== (yj > py)) && (px < (xj - xi) * (py - yi) / (yj - yi) + xi)) {
            inside = !inside;
          }
        }
        return inside;
      };
      
      const isInside = isInsidePolygon(pos.x, pos.y, vertices);
      if (!isInside) {
        return { outOfBounds: true, isPocket: false, pocketType: null, pocketPoints: 0 };
      }
      
      return { outOfBounds: false, isPocket: false, pocketType: null, pocketPoints: 0 };
    } else {
      // BB-10 Stadium: Circular boundary
      const centerX = CANVAS_WIDTH / 2;
      const centerY = CANVAS_HEIGHT / 2;
      const dx = pos.x - centerX;
      const dy = pos.y - centerY;
      const distanceFromCenter = Math.sqrt(dx * dx + dy * dy);
      const stadiumOuterRadius = 280 * SCALE_FACTOR; // Outer edge of the full stadium (larger, scaled)
      
      // Knocked out if beyond outer stadium radius
      return { outOfBounds: distanceFromCenter > stadiumOuterRadius, isPocket: false, pocketType: null, pocketPoints: 0 };
    }
  };
  
  // Legacy function for backward compatibility
  const isOutOfBoundsSimple = (pos) => {
    return isOutOfBounds(pos).outOfBounds;
  };

  // Check and handle Beyblade collision - returns adjusted position or null if knockout
  const handleBeybladeCollision = (newPos, otherPos, isThisControlled, isOtherControlled, thisKnockedOut, otherKnockedOut) => {
    if (thisKnockedOut || otherKnockedOut) return newPos;
    
    // Skip collision detection if either beyblade isn't active (not launched yet)
    if (!top1Active || !top2Active) return newPos;
    
    // Skip collision detection if either player is in ghost mode
    if (ghostMode1 || ghostMode2) return newPos;

    const dx = newPos.x - otherPos.x;
    const dy = newPos.y - otherPos.y;
    const distance = Math.sqrt(dx * dx + dy * dy);
    
    // Base collision distance, expanded when Burning Strike is active
    const baseCollisionDistance = 60 * SCALE_FACTOR; // Hitbox matches 60px visual Beyblade size
    const burningStrikeDistance = BEYBLADE_RADIUS * 0.9; // Matches fire emoji position
    
    // Use expanded distance if either player has Burning Strike active
    const collisionDistance = (burningStrikeActive1 || burningStrikeActive2) 
      ? burningStrikeDistance 
      : baseCollisionDistance;

    if (distance < collisionDistance && distance > 0) {
      // Collision detected! Push them apart
      const angle = Math.atan2(dy, dx);
      const overlap = collisionDistance - distance;
      const separationX = Math.cos(angle) * (overlap / 2);
      const separationY = Math.sin(angle) * (overlap / 2);
      
      // Move this Beyblade away from other
      const adjustedPos = {
        x: newPos.x + separationX,
        y: newPos.y + separationY
      };
      
      return adjustedPos;
    }
    
    return newPos;
  };

  // Apply knockback when controlled Beyblade hits uncontrolled one
  const applyKnockbackOnHit = (pos1, pos2, controlling1, controlling2) => {
    if (top1KnockedOut || top2KnockedOut) return;
    
    // Skip collision if either beyblade isn't active (not launched yet)
    if (!top1Active || !top2Active) return;
    
    // Skip collision if either player is in ghost mode
    if (ghostMode1 || ghostMode2) return;
    
    // Skip collision if both beyblades have stopped spinning (both ran out of stamina)
    if (stoppedSpinning1 && stoppedSpinning2) return;
    
    // During clash lock, NO damage unless speed boost is activated
    // Speed boost can break the clash lock stalemate
    if (clashLocked && !speedBoost1 && !speedBoost2) {
      return; // No collision processing during clash lock without speed boost
    }
    
    // Helper function: Check if beyblade has Spin Steal ability (Meteo L Drago ID 54, Meteor Dragoon ID 42)
    const hasSpinSteal = (beybladeId) => beybladeId === 54 || beybladeId === 42;
    
    // Helper function: Get opponent's spin direction
    const getSpinDirection = (beyblade, dualSpinDirection) => {
      if (!beyblade) return 'right';
      if (beyblade.spinDirection === 'dual') {
        return dualSpinDirection;
      }
      return beyblade.spinDirection || 'right';
    };
    
    // Helper function: Get damage values based on attacker's stats
    const getAttackerDamage = (attackerBeyblade, hasSpeedBoost, isFinalDriveActive) => {
      const stats = getBeybladeStats(attackerBeyblade?.id);
      // Final Drive overrides damage values for Pegasus beyblades
      if (isFinalDriveActive && hasFinalDrive(attackerBeyblade?.id)) {
        return hasSpeedBoost ? 4 : 2; // Final Drive: basic=2, boost=4
      }
      return hasSpeedBoost ? stats.boostDamage : stats.basicDamage;
    };
    
    // Helper function: Apply spin steal - heal attacker AND restore stamina when damaging right-spinning opponent
    const applySpinSteal = (attackerId, attackerBeyblade, defenderBeyblade, defenderDualSpin, damageAmount, setAttackerHealth, setAttackerStamina, maxStamina) => {
      if (!hasSpinSteal(attackerId)) return;
      
      const defenderSpin = getSpinDirection(defenderBeyblade, defenderDualSpin);
      if (defenderSpin === 'right') {
        // Spin steal activated! Heal AND restore stamina based on damage dealt
        const healAmount = damageAmount;
        const staminaRestoreAmount = damageAmount; // 1 second per damage point
        const maxHP = getMaxHealth(attackerId);
        setAttackerHealth(prev => Math.min(maxHP, prev + healAmount)); // Cap at max HP
        setAttackerStamina(prev => Math.min(maxStamina, prev + staminaRestoreAmount)); // Cap at max stamina
      }
    };
    
    const isOnlineBattle = isOnlineMode && matchFound;
    
    const dx = pos1.x - pos2.x;
    const dy = pos1.y - pos2.y;
    const distance = Math.sqrt(dx * dx + dy * dy);
    
    // Base collision radius, expanded when Burning Strike is active
    const baseCollisionRadius = 60 * SCALE_FACTOR; // Hitbox matches 60px visual Beyblade size
    const burningStrikeRadius = BEYBLADE_RADIUS * 0.9; // Matches fire emoji position
    
    // Use expanded radius if either player has Burning Strike active
    const p1Radius = burningStrikeActive1 ? burningStrikeRadius : baseCollisionRadius;
    const p2Radius = burningStrikeActive2 ? burningStrikeRadius : baseCollisionRadius;
    const collisionRadius = Math.max(p1Radius, p2Radius);
    
    if (distance < collisionRadius && distance > 0) {
      const angle = Math.atan2(dy, dx);
      const knockbackForce = 41 * SCALE_FACTOR; // Increased by 1/4
      
      // Dran Buster (ID 22), Xeno Xcaliber (ID 52), Dran Buster C (ID 64), and Dran Buster C1/C2/C3 (IDs 75, 76, 77) have Power Knockback - 2x knockback speed
      const hasPowerKnockback1 = selectedBeyblade1?.id === 22 || selectedBeyblade1?.id === 52 || selectedBeyblade1?.id === 64 || selectedBeyblade1?.id === 30 || selectedBeyblade1?.id === 32 || selectedBeyblade1?.id === 75 || selectedBeyblade1?.id === 76 || selectedBeyblade1?.id === 77;
      const hasPowerKnockback2 = selectedBeyblade2?.id === 22 || selectedBeyblade2?.id === 52 || selectedBeyblade2?.id === 64 || selectedBeyblade2?.id === 30 || selectedBeyblade2?.id === 32 || selectedBeyblade2?.id === 75 || selectedBeyblade2?.id === 76 || selectedBeyblade2?.id === 77;
      const powerKnockbackMultiplier = 2.0; // Twice the knockback speed
      
      // COLLISION RULES (same for local and online):
      // 1. If NEITHER is being controlled AND neither has speed boost -> no damage, just visual clash
      // 2. Speed boost is powerful - it deals damage even to manually controlled beyblades
      // 3. Manual control only deals damage to idle beyblades (not to controlled or speed boosted)
      // 4. Deflection is the ONLY way to block damage
      // 5. Speed boost = 2 damage always, Manual control = 1 damage
      // 6. During clash lock - only speed boost can deal damage
      
      const clashX = (pos1.x + pos2.x) / 2;
      const clashY = (pos1.y + pos2.y) / 2;
      
      // BURNING STRIKE DAMAGE - applies regardless of control state
      // P1 has Burning Strike active - P2 takes gradual fire damage (blocked by Iron Wall or Deflect)
      if (burningStrikeActive1 && !hitCooldownRef2.current && !ironWallActive2 && !deflection2) {
        const fireDamage = 2; // Fire damage per hit
        addClashEffect(clashX, clashY);
        if (!stoppedSpinning2) {
          setKnockbackVelocity2({ x: -Math.cos(angle) * knockbackForce * 0.5, y: -Math.sin(angle) * knockbackForce * 0.5 });
        }
        if (!isOnlineBattle) {
          setHealth2(prev => Math.max(0, prev - fireDamage));
        }
        hitCooldownRef2.current = true;
        setTimeout(() => { hitCooldownRef2.current = false; }, 200); // Faster cooldown for continuous burn
      }
      
      // P2 has Burning Strike active - P1 takes gradual fire damage (blocked by Iron Wall or Deflect)
      if (burningStrikeActive2 && !hitCooldownRef1.current && !ironWallActive1 && !deflection1) {
        const fireDamage = 2; // Fire damage per hit
        addClashEffect(clashX, clashY);
        if (!stoppedSpinning1) {
          setKnockbackVelocity1({ x: Math.cos(angle) * knockbackForce * 0.5, y: Math.sin(angle) * knockbackForce * 0.5 });
        }
        if (!isOnlineBattle) {
          setHealth1(prev => Math.max(0, prev - fireDamage));
        }
        hitCooldownRef1.current = true;
        setTimeout(() => { hitCooldownRef1.current = false; }, 200); // Faster cooldown for continuous burn
      }
      
      // If Burning Strike is the only thing active, skip normal collision rules
      if ((burningStrikeActive1 || burningStrikeActive2) && !controlling1 && !controlling2 && !speedBoost1 && !speedBoost2 && !finalDriveActive1 && !finalDriveActive2) {
        return;
      }
      
      // Case: Neither is being controlled AND neither has speed boost or Final Drive - just visual clash
      if (!controlling1 && !controlling2 && !speedBoost1 && !speedBoost2 && !finalDriveActive1 && !finalDriveActive2) {
        if (!hitCooldownRef1.current) {
          // P1 knockback - only if not stopped spinning and not Iron Wall (immovable)
          if (!stoppedSpinning1 && !ironWallActive1) {
            setKnockbackVelocity1({
              x: Math.cos(angle) * knockbackForce * 0.2,
              y: Math.sin(angle) * knockbackForce * 0.2
            });
          }
          // P2 knockback - in online mode, opponent handles their own physics
          // Only if not stopped spinning and not Iron Wall (immovable)
          if (!isOnlineBattle && !stoppedSpinning2 && !ironWallActive2) {
            setKnockbackVelocity2({
              x: -Math.cos(angle) * knockbackForce * 0.2,
              y: -Math.sin(angle) * knockbackForce * 0.2
            });
          }
          hitCooldownRef1.current = true;
          setTimeout(() => { hitCooldownRef1.current = false; }, 300);
          addClashEffect(clashX, clashY);
        }
        return;
      }
      
      // SPEED BOOST ATTACKS - Speed boost can hit ANYONE (even manually controlled)
      // But speed boost WITHOUT manual control does NOT protect from manual control attacks
      
      // P1 has speed boost - P2 takes damage (unless deflecting or Iron Wall)
      if (speedBoost1 && !hitCooldownRef2.current) {
        addClashEffect(clashX, clashY);
        
        // Speed boost knockback multiplier (same as final drive)
        const speedBoostKnockback = 1.5;
        
        // P2 has Iron Wall - completely blocks knockback and damage
        if (ironWallActive2) {
          hitCooldownRef2.current = true;
          setTimeout(() => { hitCooldownRef2.current = false; }, 500);
        } else if (deflection2) {
          // P2 has deflection - blocks damage but takes half knockback
          // In online mode, knockback is visual only (opponent handles their own physics)
          // But we show it for immediate feedback - only if not stopped spinning
          if (!stoppedSpinning2) {
            // Power Knockback + Speed Boost = 2.5x, otherwise just speedBoostKnockback (1.5x)
            const kbMultiplier = hasPowerKnockback1 ? 2.5 : speedBoostKnockback;
            setKnockbackVelocity2({ x: -Math.cos(angle) * knockbackForce * 0.5 * kbMultiplier, y: -Math.sin(angle) * knockbackForce * 0.5 * kbMultiplier });
          }
          hitCooldownRef2.current = true;
          setTimeout(() => { hitCooldownRef2.current = false; }, 500);
        } else {
          // P2 takes damage - Use P1's boost damage stat
          const boostDamage = getAttackerDamage(selectedBeyblade1, true, finalDriveActive1);
          // Only apply knockback if not stopped spinning
          if (!stoppedSpinning2 && !ironWallActive2 && !burningStrikeActive2) {
            // Power Knockback + Speed Boost = 2.5x, otherwise just speedBoostKnockback (1.5x)
            const kbMultiplier = hasPowerKnockback1 ? 2.5 : speedBoostKnockback;
            setKnockbackVelocity2({ x: -Math.cos(angle) * knockbackForce * kbMultiplier, y: -Math.sin(angle) * knockbackForce * kbMultiplier });
          }
          // In online mode, health sync will override, but update locally for visual feedback
          if (!isOnlineBattle && !ironWallActive2 && !burningStrikeActive2) {
            setHealth2(prev => Math.max(0, prev - boostDamage));
            // Reduce stamina by 1 second for each point of damage taken
            setStamina2(prev => Math.max(0, prev - boostDamage));
            // Spin Steal: P1 heals AND restores stamina if attacking right-spinning opponent
            applySpinSteal(selectedBeyblade1?.id, selectedBeyblade1, selectedBeyblade2, dualSpinDirection2, boostDamage, setHealth1, setStamina1, maxStamina1);
          }
          hitCooldownRef2.current = true;
          setTimeout(() => { hitCooldownRef2.current = false; }, 500);
        }
        // Continue to check if P2 can counter-attack (don't return)
      }
      
      // P2 has speed boost - P1 takes damage (unless deflecting or Iron Wall)
      if (speedBoost2 && !hitCooldownRef1.current) {
        addClashEffect(clashX, clashY);
        
        // Speed boost knockback multiplier (same as final drive)
        const speedBoostKnockback = 1.5;
        
        // P1 has Iron Wall - completely blocks knockback and damage
        if (ironWallActive1) {
          hitCooldownRef1.current = true;
          setTimeout(() => { hitCooldownRef1.current = false; }, 500);
        } else if (deflection1) {
          // P1 has deflection - blocks damage but takes half knockback
          // Only apply knockback if not stopped spinning
          if (!stoppedSpinning1) {
            // Power Knockback + Speed Boost = 2.5x, otherwise just speedBoostKnockback (1.5x)
            const kbMultiplier = hasPowerKnockback2 ? 2.5 : speedBoostKnockback;
            setKnockbackVelocity1({ x: Math.cos(angle) * knockbackForce * 0.5 * kbMultiplier, y: Math.sin(angle) * knockbackForce * 0.5 * kbMultiplier });
          }
          hitCooldownRef1.current = true;
          setTimeout(() => { hitCooldownRef1.current = false; }, 500);
          // Don't return yet - check if P1 can counter-attack
        } else {
          // P1 takes damage - Use P2's boost damage stat
          const boostDamage = getAttackerDamage(selectedBeyblade2, true, finalDriveActive2);
          // Only apply knockback if not stopped spinning and not Iron Wall or Burning Strike active
          if (!stoppedSpinning1 && !ironWallActive1 && !burningStrikeActive1) {
            // Power Knockback + Speed Boost = 2.5x, otherwise just speedBoostKnockback (1.5x)
            const kbMultiplier = hasPowerKnockback2 ? 2.5 : speedBoostKnockback;
            setKnockbackVelocity1({ x: Math.cos(angle) * knockbackForce * kbMultiplier, y: Math.sin(angle) * knockbackForce * kbMultiplier });
          }
          if (!ironWallActive1 && !burningStrikeActive1) {
            setHealth1(prev => Math.max(0, prev - boostDamage));
            // Reduce stamina by 1 second for each point of damage taken
            setStamina1(prev => Math.max(0, prev - boostDamage));
            // Spin Steal: P2 heals AND restores stamina if attacking right-spinning opponent
            applySpinSteal(selectedBeyblade2?.id, selectedBeyblade2, selectedBeyblade1, dualSpinDirection1, boostDamage, setHealth2, setStamina2, maxStamina2);
          }
          hitCooldownRef1.current = true;
          setTimeout(() => { hitCooldownRef1.current = false; }, 500);
        }
        // Continue to check if P1 can counter-attack via manual control (don't return)
      }
      
      // If both had speed boost and both attacked, we're done
      if (speedBoost1 && speedBoost2) return;
      
      // FINAL DRIVE ATTACKS - Final Drive can hit ANYONE (similar to speed boost)
      // Enhanced knockback multiplier for Final Drive
      const finalDriveKnockback = 1.5;
      
      // P1 has Final Drive - P2 takes damage (unless Iron Wall, deflecting, or has immunity)
      if (finalDriveActive1 && !speedBoost1 && !hitCooldownRef2.current) {
        addClashEffect(clashX, clashY);
        
        // P2 has Iron Wall - completely blocks knockback and damage
        if (ironWallActive2) {
          hitCooldownRef2.current = true;
          setTimeout(() => { hitCooldownRef2.current = false; }, 500);
        } else if (deflection2) {
          // P2 has deflection - blocks damage but takes half knockback
          if (!stoppedSpinning2) {
            const kbMultiplier = hasPowerKnockback1 ? powerKnockbackMultiplier : 1.0;
            setKnockbackVelocity2({ x: -Math.cos(angle) * knockbackForce * 0.5 * finalDriveKnockback * kbMultiplier, y: -Math.sin(angle) * knockbackForce * 0.5 * finalDriveKnockback * kbMultiplier });
          }
          hitCooldownRef2.current = true;
          setTimeout(() => { hitCooldownRef2.current = false; }, 500);
        } else if (!burningStrikeActive2) {
          // P2 takes damage - Final Drive alone = 2 damage
          const damage = getAttackerDamage(selectedBeyblade1, false, finalDriveActive1);
          if (!stoppedSpinning2) {
            const kbMultiplier = hasPowerKnockback1 ? powerKnockbackMultiplier : 1.0;
            setKnockbackVelocity2({ x: -Math.cos(angle) * knockbackForce * finalDriveKnockback * kbMultiplier, y: -Math.sin(angle) * knockbackForce * finalDriveKnockback * kbMultiplier });
          }
          if (!isOnlineBattle) {
            setHealth2(prev => Math.max(0, prev - damage));
            setStamina2(prev => Math.max(0, prev - damage));
            applySpinSteal(selectedBeyblade1?.id, selectedBeyblade1, selectedBeyblade2, dualSpinDirection2, damage, setHealth1, setStamina1, maxStamina1);
          }
          hitCooldownRef2.current = true;
          setTimeout(() => { hitCooldownRef2.current = false; }, 500);
        }
      }
      
      // P2 has Final Drive - P1 takes damage (unless Iron Wall, deflecting, or has immunity)
      if (finalDriveActive2 && !speedBoost2 && !hitCooldownRef1.current) {
        addClashEffect(clashX, clashY);
        
        // P1 has Iron Wall - completely blocks knockback and damage
        if (ironWallActive1) {
          hitCooldownRef1.current = true;
          setTimeout(() => { hitCooldownRef1.current = false; }, 500);
        } else if (deflection1) {
          // P1 has deflection - blocks damage but takes half knockback
          if (!stoppedSpinning1) {
            const kbMultiplier = hasPowerKnockback2 ? powerKnockbackMultiplier : 1.0;
            setKnockbackVelocity1({ x: Math.cos(angle) * knockbackForce * 0.5 * finalDriveKnockback * kbMultiplier, y: Math.sin(angle) * knockbackForce * 0.5 * finalDriveKnockback * kbMultiplier });
          }
          hitCooldownRef1.current = true;
          setTimeout(() => { hitCooldownRef1.current = false; }, 500);
        } else if (!burningStrikeActive1) {
          // P1 takes damage - Final Drive alone = 2 damage
          const damage = getAttackerDamage(selectedBeyblade2, false, finalDriveActive2);
          if (!stoppedSpinning1) {
            const kbMultiplier = hasPowerKnockback2 ? powerKnockbackMultiplier : 1.0;
            setKnockbackVelocity1({ x: Math.cos(angle) * knockbackForce * finalDriveKnockback * kbMultiplier, y: Math.sin(angle) * knockbackForce * finalDriveKnockback * kbMultiplier });
          }
          if (!isOnlineBattle) {
            setHealth1(prev => Math.max(0, prev - damage));
            setStamina1(prev => Math.max(0, prev - damage));
            applySpinSteal(selectedBeyblade2?.id, selectedBeyblade2, selectedBeyblade1, dualSpinDirection1, damage, setHealth2, setStamina2, maxStamina2);
          }
          hitCooldownRef1.current = true;
          setTimeout(() => { hitCooldownRef1.current = false; }, 500);
        }
      }
      
      // If both had Final Drive and both attacked, we're done
      if (finalDriveActive1 && finalDriveActive2) return;
      
      // MANUAL CONTROL ATTACKS
      // Manual control can hit any beyblade that is NOT manually controlled
      // (even if they have speed boost - speed boost only helps offense, not defense)
      
      // Knockback multiplier - Final Drive gets enhanced knockback, Dran Buster gets 2x knockback
      const p1KnockbackMultiplier = (finalDriveActive1 ? 1.5 : 1.0) * (hasPowerKnockback1 ? powerKnockbackMultiplier : 1.0);
      const p2KnockbackMultiplier = (finalDriveActive2 ? 1.5 : 1.0) * (hasPowerKnockback2 ? powerKnockbackMultiplier : 1.0);
      
      // P1 controlled, P2 NOT controlled - P2 takes damage (even with speed boost)
      if (controlling1 && !controlling2 && !hitCooldownRef2.current) {
        addClashEffect(clashX, clashY);
        
        // Iron Wall completely blocks knockback
        if (ironWallActive2) {
          hitCooldownRef2.current = true;
          setTimeout(() => { hitCooldownRef2.current = false; }, 500);
          return;
        }
        
        if (deflection2) {
          // Visual knockback for feedback - only if not stopped spinning
          if (!stoppedSpinning2) {
            setKnockbackVelocity2({ x: -Math.cos(angle) * knockbackForce * 0.5 * p1KnockbackMultiplier, y: -Math.sin(angle) * knockbackForce * 0.5 * p1KnockbackMultiplier });
          }
          hitCooldownRef2.current = true;
          setTimeout(() => { hitCooldownRef2.current = false; }, 500);
          return;
        }
        
        // Use P1's basic damage stat (manual control without speed boost)
        const basicDamage = getAttackerDamage(selectedBeyblade1, false, finalDriveActive1);
        // Visual knockback and local damage (online mode syncs health from opponent) - only if not stopped spinning and not Iron Wall or Burning Strike
        if (!stoppedSpinning2 && !burningStrikeActive2) {
          setKnockbackVelocity2({ x: -Math.cos(angle) * knockbackForce * p1KnockbackMultiplier, y: -Math.sin(angle) * knockbackForce * p1KnockbackMultiplier });
        }
        if (!isOnlineBattle && !ironWallActive2 && !burningStrikeActive2) {
          setHealth2(prev => Math.max(0, prev - basicDamage));
          // Reduce stamina by 1 second for each point of damage taken
          setStamina2(prev => Math.max(0, prev - basicDamage));
          // Spin Steal: P1 heals AND restores stamina if attacking right-spinning opponent
          applySpinSteal(selectedBeyblade1?.id, selectedBeyblade1, selectedBeyblade2, dualSpinDirection2, basicDamage, setHealth1, setStamina1, maxStamina1);
        }
        hitCooldownRef2.current = true;
        setTimeout(() => { hitCooldownRef2.current = false; }, 500);
        return;
      }
      
      // P2 controlled, P1 NOT controlled - P1 takes damage (even with speed boost)
      if (controlling2 && !controlling1 && !hitCooldownRef1.current) {
        addClashEffect(clashX, clashY);
        
        // Iron Wall completely blocks knockback
        if (ironWallActive1) {
          hitCooldownRef1.current = true;
          setTimeout(() => { hitCooldownRef1.current = false; }, 500);
          return;
        }
        
        if (deflection1) {
          // Only apply knockback if not stopped spinning
          if (!stoppedSpinning1) {
            setKnockbackVelocity1({ x: Math.cos(angle) * knockbackForce * 0.5 * p2KnockbackMultiplier, y: Math.sin(angle) * knockbackForce * 0.5 * p2KnockbackMultiplier });
          }
          hitCooldownRef1.current = true;
          setTimeout(() => { hitCooldownRef1.current = false; }, 500);
          return;
        }
        
        // Use P2's basic damage stat (manual control without speed boost)
        const basicDamage = getAttackerDamage(selectedBeyblade2, false, finalDriveActive2);
        // Only apply knockback if not stopped spinning and not Burning Strike
        if (!stoppedSpinning1 && !burningStrikeActive1) {
          setKnockbackVelocity1({ x: Math.cos(angle) * knockbackForce * p2KnockbackMultiplier, y: Math.sin(angle) * knockbackForce * p2KnockbackMultiplier });
        }
        if (!burningStrikeActive1) {
          setHealth1(prev => Math.max(0, prev - basicDamage));
          // Reduce stamina by 1 second for each point of damage taken
          setStamina1(prev => Math.max(0, prev - basicDamage));
          // Spin Steal: P2 heals AND restores stamina if attacking right-spinning opponent
          applySpinSteal(selectedBeyblade2?.id, selectedBeyblade2, selectedBeyblade1, dualSpinDirection1, basicDamage, setHealth2, setStamina2, maxStamina2);
        }
        hitCooldownRef1.current = true;
        setTimeout(() => { hitCooldownRef1.current = false; }, 500);
        return;
      }
      
      // Both controlled, NEITHER has ANY power-up active - CLASH LOCK for 2 seconds!
      const p1HasPowerUp = speedBoost1 || deflection1 || ghostMode1 || burningStrikeActive1 || finalDriveActive1;
      const p2HasPowerUp = speedBoost2 || deflection2 || ghostMode2 || burningStrikeActive2 || finalDriveActive2;
      
      if (controlling1 && controlling2 && !p1HasPowerUp && !p2HasPowerUp && !clashLocked) {
        addClashEffect(clashX, clashY);
        
        // Trigger clash lock - beyblades stick together for 2 seconds
        // Save their actual positions at the moment of clash
        setClashLocked(true);
        setClashLockPos({ x: clashX, y: clashY });
        setClashLockP1Pos({ x: pos1.x, y: pos1.y });
        setClashLockP2Pos({ x: pos2.x, y: pos2.y });
        
        // Instantly deactivate both trackpads when entering clash
        setCooldown1(true);
        setCooldown2(true);
        
        // Clear any existing timer
        if (clashLockTimerRef.current) {
          clearTimeout(clashLockTimerRef.current);
        }
        
        // Release after 2 seconds - check power-ups at release time using refs
        const releaseAngle = angle; // Capture angle at clash start
        clashLockTimerRef.current = setTimeout(() => {
          setClashLocked(false);
          
          // Instantly reactivate both trackpads with timer reset when clash ends
          setCooldown1(false);
          setCooldown2(false);
          
          // Check power-up states at release time using refs (to get current values)
          const p1HasDeflection = deflection1Ref.current;
          const p2HasDeflection = deflection2Ref.current;
          const p1HasSpeedBoost = speedBoost1Ref.current;
          const p2HasSpeedBoost = speedBoost2Ref.current;
          const p1HasFinalDrive = finalDriveActive1Ref.current;
          const p2HasFinalDrive = finalDriveActive2Ref.current;
          const p1HasIronWall = ironWallActive1Ref.current;
          const p2HasIronWall = ironWallActive2Ref.current;
          
          // Combine speed boost and final drive (they work the same in clash lock)
          const p1HasAttackPower = p1HasSpeedBoost || p1HasFinalDrive;
          const p2HasAttackPower = p2HasSpeedBoost || p2HasFinalDrive;
          
          // Combine deflection and iron wall for defense (iron wall blocks Final Drive knockback)
          const p1HasDefense = p1HasDeflection || p1HasIronWall;
          const p2HasDefense = p2HasDeflection || p2HasIronWall;
          
          // Define forces directly
          const baseKnockback = 41 * SCALE_FACTOR;
          const attackKnockbackMultiplier = 1.5;       // Same as speed boost/final drive outside clash lock
          const fullForce = baseKnockback * 0.8 * attackKnockbackMultiplier;  // Full knockback with 1.5x multiplier
          const halfForce = fullForce * 0.5;           // Half knockback (deflect vs speed boost/final drive)
          const reducedForce = baseKnockback * 0.05;   // Minimal knockback
          const driftForce = baseKnockback * 0.15;     // Gentle drift apart when no power-ups
          
          // Calculate stacked damage for each player
          // Final Drive + Speed Boost = 4, Final Drive alone = 2, Speed Boost alone = 2
          const p1Damage = (p1HasFinalDrive && p1HasSpeedBoost) ? 4 : 2;
          const p2Damage = (p2HasFinalDrive && p2HasSpeedBoost) ? 4 : 2;
          
          // Check for Power Knockback ability
          const p1HasPowerKnockback = selectedBeyblade1?.id === 22 || selectedBeyblade1?.id === 52 || selectedBeyblade1?.id === 64 || selectedBeyblade1?.id === 30 || selectedBeyblade1?.id === 32;
          const p2HasPowerKnockback = selectedBeyblade2?.id === 22 || selectedBeyblade2?.id === 52 || selectedBeyblade2?.id === 64 || selectedBeyblade2?.id === 30 || selectedBeyblade2?.id === 32;
          
          // Power Knockback + Speed Boost = 2.5x knockback multiplier
          const powerKnockbackSpeedBoostMultiplier = 2.5;
          
          // Calculate actual knockback force based on Power Knockback + Speed Boost
          const p1FullForce = (p1HasPowerKnockback && p1HasSpeedBoost) ? (baseKnockback * 0.8 * powerKnockbackSpeedBoostMultiplier) : fullForce;
          const p2FullForce = (p2HasPowerKnockback && p2HasSpeedBoost) ? (baseKnockback * 0.8 * powerKnockbackSpeedBoostMultiplier) : fullForce;
          
          // CLASH LOCK RELEASE LOGIC:
          // - No power-ups = gentle drift apart
          // - Speed boost/Final Drive vs None = opponent thrown back + damaged (1.5x knockback, or 2.5x with Power Knockback)
          // - Speed boost + Final Drive stacked = 4 damage instead of 2
          // - Speed boost/Final Drive vs Deflect = both thrown back, attacker FULL (1.5x/2.5x), deflect HALF (no damage to deflect)
          // - Speed boost/Final Drive vs Iron Wall = attacker thrown back FULL (1.5x/2.5x), Iron Wall NO knockback (immovable)
          // - Speed boost/Final Drive vs Speed boost/Final Drive = both thrown back (1.5x/2.5x) + both damaged
          // - Deflect/Iron Wall vs None = defender minimal knockback, opponent gentle drift
          
          // P1 knockback and damage calculation - only if not stopped spinning
          if (!stoppedSpinning1Ref.current) {
            let p1Force = driftForce; // Default: gentle drift
            
            if (p2HasAttackPower && p1HasIronWall) {
              // P2 has attack power, P1 has Iron Wall - P1 is IMMOVABLE, no knockback, no damage
              p1Force = 0;
            } else if (p2HasAttackPower && p1HasDeflection) {
              // P2 has attack power, P1 has deflection - P1 gets HALF knockback, no damage
              p1Force = p2FullForce * 0.5;
            } else if (p2HasAttackPower && !p1HasDefense) {
              // P2 has attack power, P1 has no defense - P1 gets thrown back + damaged (stacked if both active)
              p1Force = p2FullForce;
              setHealth1(prev => Math.max(0, prev - p2Damage));
              setStamina1(prev => Math.max(0, prev - p2Damage));
            } else if (p1HasAttackPower && p2HasDefense) {
              // P1 has attack power, P2 has defense - P1 gets thrown back MORE
              p1Force = p1FullForce;
            } else if (p1HasAttackPower && !p2HasDefense) {
              // P1 has attack power, P2 has no defense - P1 gets minimal knockback (attacker)
              p1Force = reducedForce;
            } else if (p1HasDefense) {
              // P1 has defense only - minimal knockback
              p1Force = reducedForce;
            }
            
            if (p1Force > 0) {
              setKnockbackVelocity1({ 
                x: Math.cos(releaseAngle) * p1Force, 
                y: Math.sin(releaseAngle) * p1Force 
              });
            }
          }
          
          // P2 knockback and damage calculation - only if not stopped spinning
          if (!stoppedSpinning2Ref.current) {
            let p2Force = driftForce; // Default: gentle drift
            
            if (p1HasAttackPower && p2HasIronWall) {
              // P1 has attack power, P2 has Iron Wall - P2 is IMMOVABLE, no knockback, no damage
              p2Force = 0;
            } else if (p1HasAttackPower && p2HasDeflection) {
              // P1 has attack power, P2 has deflection - P2 gets HALF knockback, no damage
              p2Force = p1FullForce * 0.5;
            } else if (p1HasAttackPower && !p2HasDefense) {
              // P1 has attack power, P2 has no defense - P2 gets thrown back + damaged (stacked if both active)
              p2Force = p1FullForce;
              setHealth2(prev => Math.max(0, prev - p1Damage));
              setStamina2(prev => Math.max(0, prev - p1Damage));
            } else if (p2HasAttackPower && p1HasDefense) {
              // P2 has attack power, P1 has defense - P2 gets thrown back MORE
              p2Force = p2FullForce;
            } else if (p2HasAttackPower && !p1HasDefense) {
              // P2 has attack power, P1 has no defense - P2 gets minimal knockback (attacker)
              p2Force = reducedForce;
            } else if (p2HasDefense) {
              // P2 has defense only - minimal knockback
              p2Force = reducedForce;
            }
            
            if (p2Force > 0) {
              setKnockbackVelocity2({ 
                x: -Math.cos(releaseAngle) * p2Force, 
                y: -Math.sin(releaseAngle) * p2Force 
              });
            }
          }
        }, 1000); // 1 second clash duration
        
        return;
      }
    }
  };
  
  // Add clash visual effect
  const addClashEffect = (x, y) => {
    const newEffect = {
      id: Date.now() + Math.random(), // Ensure unique ID
      x,
      y,
      age: 0,
      maxAge: 300 // milliseconds
    };
    setClashEffects(prev => [...prev, newEffect]);
    // Cleanup handled automatically in the update loop
  };
  
  // Update clash effect ages and remove expired ones
  // FIX: Use ref to prevent interval recreation on every clash
  const clashIntervalRef = useRef(null);
  
  useEffect(() => {
    // Only create interval once, not on every clash
    if (!clashIntervalRef.current) {
      clashIntervalRef.current = setInterval(() => {
        setClashEffects(prev => {
          if (prev.length === 0) return prev; // No changes if empty
          
          return prev
            .map(effect => ({
              ...effect,
              age: effect.age + 16
            }))
            .filter(effect => effect.age < effect.maxAge); // Remove expired effects
        });
      }, 16);
    }
    
    return () => {
      if (clashIntervalRef.current) {
        clearInterval(clashIntervalRef.current);
        clashIntervalRef.current = null;
      }
    };
  }, []); // ✅ Only run once on mount

  // Check collision with the fixed wall lines
  // Calculate distance from point to line segment
  const pointToLineDistance = (px, py, x1, y1, x2, y2) => {
    const A = px - x1;
    const B = py - y1;
    const C = x2 - x1;
    const D = y2 - y1;
    
    const dot = A * C + B * D;
    const lenSq = C * C + D * D;
    let param = -1;
    
    if (lenSq !== 0) param = dot / lenSq;
    
    let xx, yy;
    if (param < 0) {
      xx = x1;
      yy = y1;
    } else if (param > 1) {
      xx = x2;
      yy = y2;
    } else {
      xx = x1 + param * C;
      yy = y1 + param * D;
    }
    
    const dx = px - xx;
    const dy = py - yy;
    return Math.sqrt(dx * dx + dy * dy);
  };

  // Find which wall was hit (line intersection OR too close to wall)
  const getCollidingWall = (newX, newY, prevX, prevY) => {
    const beybladeRadius = 20 * SCALE_FACTOR; // Half of 40px beyblade size
    
    for (const wall of FIXED_WALLS) {
      // Check line intersection (path crosses wall)
      if (lineSegmentIntersection(
        { x: prevX, y: prevY },
        { x: newX, y: newY },
        wall.start,
        wall.end
      )) {
        return wall;
      }
      
      // Also check proximity - if beyblade center is too close to wall line
      const distToWall = pointToLineDistance(newX, newY, wall.start.x, wall.start.y, wall.end.x, wall.end.y);
      if (distToWall < beybladeRadius) {
        return wall;
      }
    }
    return null;
  };

  const checkWallCollision = (newX, newY, prevX, prevY) => {
    return getCollidingWall(newX, newY, prevX, prevY) !== null;
  };

  // Get wall normal pointing toward stadium center
  const getWallNormalToCenter = (wall) => {
    const wallDx = wall.end.x - wall.start.x;
    const wallDy = wall.end.y - wall.start.y;
    const wallLength = Math.sqrt(wallDx * wallDx + wallDy * wallDy);
    
    // Perpendicular to wall
    let normalX = -wallDy / wallLength;
    let normalY = wallDx / wallLength;
    
    // Make sure normal points toward stadium center
    const stadiumCenterX = CANVAS_WIDTH / 2;
    const stadiumCenterY = CANVAS_HEIGHT / 2;
    const wallCenterX = (wall.start.x + wall.end.x) / 2;
    const wallCenterY = (wall.start.y + wall.end.y) / 2;
    const toStadiumX = stadiumCenterX - wallCenterX;
    const toStadiumY = stadiumCenterY - wallCenterY;
    
    if (normalX * toStadiumX + normalY * toStadiumY < 0) {
      normalX = -normalX;
      normalY = -normalY;
    }
    
    return { x: normalX, y: normalY };
  };

  // Apply wall collision like hitting a beyblade - triggers knockback for Beyblade 1
  // For BX-Stadium: Engage rail riding instead of bouncing
  const applyWallCollision1 = (newX, newY, prevX, prevY) => {
    // BX-Stadium: Closed stadium with boundary enforcement matching manual control
    if (selectedStadium === 'bx' && !stoppedSpinning1) {
      const dx = newX - BX_CENTER_X;
      const dy = newY - BX_CENTER_Y;
      const currentAngle = Math.atan2(dy, dx);
      
      // Calculate normalized distance and angle for pocket zone detection
      const normalizedDist = (dx * dx) / (BX_RAIL_WIDTH * BX_RAIL_WIDTH) + 
                             (dy * dy) / (BX_RAIL_HEIGHT * BX_RAIL_HEIGHT);
      
      // Check if beyblade is in a knockout pocket zone FIRST (before any bounce logic)
      const inLeftZone = currentAngle > zoneConfig.leftZone.endAngle && currentAngle <= zoneConfig.leftZone.startAngle;
      const inCenterZone = currentAngle > zoneConfig.centerZone.endAngle && currentAngle <= zoneConfig.centerZone.startAngle;
      const inRightZone = currentAngle >= zoneConfig.rightZone.endAngle && currentAngle <= zoneConfig.rightZone.startAngle;
      const inAnyPocketZone = (inLeftZone || inCenterZone || inRightZone) && dy > 0;
      
      // If in a pocket zone, SKIP all bounce logic - let beyblade pass through
      if (inAnyPocketZone && normalizedDist > 0.75) {
        setRidingRail1(false);
        return { x: newX, y: newY }; // Allow movement into pocket - NO BOUNCE
      }
      
      // CLOSED STADIUM - Apply same elliptical boundary as manual control
      // This prevents beyblades from exiting through the top (V-opening area)
      // Only apply if NOT in a pocket zone
      
      // If beyblade is trying to go beyond 85% of elliptical boundary, bounce it back
      // This matches the manual control boundary exactly
      if (normalizedDist > 0.85 && !wallCooldown1Ref.current && !inAnyPocketZone) {
        // Calculate bounce direction (away from edge, toward center)
        const dist = Math.sqrt(dx * dx + dy * dy);
        const normalX = dist > 0 ? -dx / dist : 0;
        const normalY = dist > 0 ? -dy / dist : 1;
        
        const knockbackForce = 31 * SCALE_FACTOR; // Same as BB-10 stadium
        setKnockbackVelocity1({
          x: normalX * knockbackForce,
          y: normalY * knockbackForce
        });
        
        setTop1Velocity({ x: 0, y: 0 });
        wallCooldown1Ref.current = true;
        setTimeout(() => { wallCooldown1Ref.current = false; }, 200);
        
        // Scale position back to safe boundary (85% of ellipse)
        const scale = Math.sqrt(0.85 / normalizedDist);
        return {
          x: BX_CENTER_X + dx * scale,
          y: BX_CENTER_Y + dy * scale
        };
      }
      
      // Rail riding disabled - just set state to false
      setRidingRail1(false);
      
      // XTREME DASH CHECK - Check for top boundary collision BEFORE other collision handling
      // This triggers when hitting the top area of the stadium with speed boost available
      // IMPORTANT: This check runs even during manual control to allow triggering while on touchpad
      const dx_dash = newX - BX_CENTER_X;
      const dy_dash = newY - BX_CENTER_Y;
      const normalizedDist_dash = (dx_dash * dx_dash) / (BX_RAIL_WIDTH * BX_RAIL_WIDTH) + 
                             (dy_dash * dy_dash) / (BX_RAIL_HEIGHT * BX_RAIL_HEIGHT);
      
      // Trigger zone reduced by another half - only very top portion of the rail
      const triggerLineY = -(BX_RAIL_HEIGHT * 0.75 + 5); // 3/4 up from center
      
      // Trigger Xtreme Dash when:
      // 1. Near the boundary (normalizedDist > 0.75)
      // 2. In the TOP HALF of the trigger zone (dy < triggerLineY)
      // 3. Speed boost is available OR already active (just not on cooldown)
      // 4. Not already in freeze state
      // 5. On the correct side based on spin direction:
      //    - Right spin = right side only (dx > 0)
      //    - Left spin = left side only (dx < 0)
      if (normalizedDist_dash > 0.75 && dy_dash < triggerLineY && top1Active && !xtremeDashFreeze1Ref.current) {
        // Check spin direction (handle dual-spin beyblades)
        let spinDir = selectedBeyblade1?.spinDirection;
        if (spinDir === 'dual') {
          spinDir = dualSpinDirection1Ref.current;
        }
        
        // Check if on correct side of stadium for spin direction
        const onRightSide = dx_dash > 0;
        const onLeftSide = dx_dash < 0;
        const correctSide = (spinDir === 'right' && onRightSide) || (spinDir === 'left' && onLeftSide);
        
        // Can Xtreme Dash if: not on cooldown AND on correct side
        // Speed boost can be either available OR already active (manually pressed)
        const canXtremeDash = !speedBoostCooldown1Ref.current && correctSide;
        if (canXtremeDash) {
          
          // BREAK MANUAL CONTROL - Release touchpad control so the dash takes over
          setIsControlling1(false);
          
          // Reset trackpad timer (same as BX-Infinity)
          if (controlTimer1Ref.current) {
            clearTimeout(controlTimer1Ref.current);
            controlTimer1Ref.current = null;
          }
          
          // Only activate speed boost if not already active
          if (!speedBoost1Ref.current) {
            handleSpeedBoost1();
          }
          
          // Set freeze state to mark we're in a dash
          setXtremeDashFreeze1(true);
          
          // Target: Bottom of the safe play area (within safe boundary)
          const targetX = BX_CENTER_X;
          const targetY = BX_CENTER_Y + BX_RAIL_HEIGHT * 0.45; // 45% down - stops before bottom
          const dirX = targetX - newX;
          const dirY = targetY - newY;
          const dirLen = Math.sqrt(dirX * dirX + dirY * dirY);
          
          // Apply force based on beyblade's speed stat (same modifier as orbital speed with speed boost)
          const beybladeSpeedStat = getBeybladeStats(selectedBeyblade1?.id)?.speed || 50;
          const effectiveSpeed = (finalDriveActive1 && hasFinalDrive(selectedBeyblade1?.id)) ? 200 : beybladeSpeedStat;
          const speedStatModifier = 1 + (effectiveSpeed - 50) / 100;
          const baseForce = 47 * SCALE_FACTOR; // Base force calibrated for speed 50
          const xtremeDashForce = baseForce * 1.5 * speedStatModifier; // Apply speed boost multiplier and speed stat
          setKnockbackVelocity1({
            x: (dirX / dirLen) * xtremeDashForce,
            y: (dirY / dirLen) * xtremeDashForce
          });
          setTop1Velocity({ x: 0, y: 0 });
          
          // After 1 second, end dash state and stop knockback so auto-rotation resumes
          setTimeout(() => {
            setXtremeDashFreeze1(false);
            setKnockbackVelocity1({ x: 0, y: 0 }); // Stop knockback so auto-rotation takes over
          }, 1000);
          
          // Return current position (knockback will move it)
          return { x: newX, y: newY };
        }
      }
      
      // If player is manually controlling (D-pad), they use their own boundary checks
      if (isControlling1) {
        return { x: newX, y: newY };
      }
      
      // RAIL RIDING DISABLED FOR NOW
      // Get distance to the rail (returns Infinity if in V-opening)
      // const distToRail = getDistanceToBXRail(newX, newY);
      // const railEngageThreshold = 40; // Distance from rail to start engaging
      
      // Rail riding disabled - just set state to false
      setRidingRail1(false);
      
      /* RAIL RIDING CODE - DISABLED
      // Check if beyblade is close enough to the rail to engage
      if (distToRail <= railEngageThreshold && distToRail !== Infinity) {
        // Engage rail riding!
        setRidingRail1(true);
        
        // Get the closest point on the rail
        const railPoint = getClosestBXRailPoint(newX, newY);
        if (!railPoint) {
          // No rail at this position (V-opening), let beyblade move freely
          setRidingRail1(false);
          return { x: newX, y: newY };
        }
        
        // Determine rotation direction based on spin direction
        // Right spin = counter-clockwise, Left spin = clockwise
        const spinDirection = selectedBeyblade1?.spinDirection || 'Right';
        const rotationDir = spinDirection === 'Right' ? 1 : -1;
        
        // Calculate tangent direction along the oval rail at the rail point
        const railDx = railPoint.x - BX_CENTER_X;
        const railDy = railPoint.y - BX_CENTER_Y;
        
        // Get sin(θ) and cos(θ) from the rail point
        const cosTheta = railDx / BX_RAIL_WIDTH;
        const sinTheta = railDy / BX_RAIL_HEIGHT;
        
        // Tangent direction: dx/dθ = -a*sin(θ), dy/dθ = b*cos(θ)
        const tangentX_raw = -BX_RAIL_WIDTH * sinTheta;
        const tangentY_raw = BX_RAIL_HEIGHT * cosTheta;
        const tangentLength = Math.sqrt(tangentX_raw * tangentX_raw + tangentY_raw * tangentY_raw);
        
        // On the bottom-left quadrant (cosTheta < 0 and sinTheta > 0), 
        // flip direction to continue counter-clockwise like bottom-right
        const inBottomLeft = cosTheta < 0 && sinTheta > 0;
        const directionFix = inBottomLeft ? -1 : 1;
        
        // Apply tangential velocity along the rail
        const railSpeed = 6 * SCALE_FACTOR * RAIL_SPEED_MULTIPLIER;
        const tangentX = (tangentX_raw / tangentLength) * railSpeed * rotationDir * directionFix;
        const tangentY = (tangentY_raw / tangentLength) * railSpeed * rotationDir * directionFix;
        
        setKnockbackVelocity1({
          x: tangentX,
          y: tangentY
        });
        
        // Snap beyblade to the rail position
        const snapFactor = 0.97; // Keep on the rail
        return { 
          x: BX_CENTER_X + (railPoint.x - BX_CENTER_X) * snapFactor, 
          y: BX_CENTER_Y + (railPoint.y - BX_CENTER_Y) * snapFactor 
        };
      } else {
        // Not on rail
        setRidingRail1(false);
      }
      END RAIL RIDING CODE */
      
      // BX-Stadium: Wall bounce WITHOUT damage (CLOSED stadium - no knockouts)
      // Check if beyblade is hitting the oval boundary (the green rail)
      const railPoint = getClosestBXRailPoint(newX, newY);
      
      // If in V-opening area (railPoint is null), apply HARD boundary
      // Since we already checked at function start, this is a backup that also
      // handles edge cases where beyblade might slip through
      if (!railPoint) {
        // V-opening area - ALWAYS enforce the elliptical boundary here
        const dx = newX - BX_CENTER_X;
        const dy = newY - BX_CENTER_Y;
        const normalizedDist = (dx * dx) / (BX_RAIL_WIDTH * BX_RAIL_WIDTH) + 
                               (dy * dy) / (BX_RAIL_HEIGHT * BX_RAIL_HEIGHT);
        
        // ALWAYS scale back to safe boundary if past 0.85
        if (normalizedDist > 0.85) {
          const scale = Math.sqrt(0.85 / normalizedDist);
          return {
            x: BX_CENTER_X + dx * scale,
            y: BX_CENTER_Y + dy * scale
          };
        }
        
        return { x: newX, y: newY };
      }
      
      if (railPoint) {
        const railDist = Math.sqrt(
          Math.pow(railPoint.x - BX_CENTER_X, 2) + Math.pow(railPoint.y - BX_CENTER_Y, 2)
        );
        const beyDist = Math.sqrt(
          Math.pow(newX - BX_CENTER_X, 2) + Math.pow(newY - BX_CENTER_Y, 2)
        );
        
        // Check if beyblade is entering a knockout pocket zone FIRST
        // Uses the same angle-based detection as isOutOfBounds with zoneConfig
        const dx = newX - BX_CENTER_X;
        const dy = newY - BX_CENTER_Y;
        const normalizedDist = (dx * dx) / (BX_RAIL_WIDTH * BX_RAIL_WIDTH) + 
                               (dy * dy) / (BX_RAIL_HEIGHT * BX_RAIL_HEIGHT);
        const angle = Math.atan2(dy, dx);
        
        // Check if angle falls within any of the knockout zones (using zoneConfig)
        // Check this for ANY position in the bottom half
        const inLeftZone = angle > zoneConfig.leftZone.endAngle && angle <= zoneConfig.leftZone.startAngle;
        const inCenterZone = angle > zoneConfig.centerZone.endAngle && angle <= zoneConfig.centerZone.startAngle;
        const inRightZone = angle >= zoneConfig.rightZone.endAngle && angle <= zoneConfig.rightZone.startAngle;
        const inAnyPocketZone = (inLeftZone || inCenterZone || inRightZone) && dy > 0;
        
        // If in any pocket zone and near/past boundary, allow beyblade to pass through (NO BOUNCE)
        if (inAnyPocketZone && normalizedDist > 0.75) {
          return { x: newX, y: newY }; // Allow movement into pocket - no bounce!
        }
        
        // If beyblade has reached or passed the rail boundary AND NOT in pocket zone, apply bounce
        if (beyDist >= railDist - 5 && !wallCooldown1Ref.current && !inAnyPocketZone) {
          // Calculate normal pointing toward center (for bounce direction)
          const dist = Math.sqrt(dx * dx + dy * dy);
          const normalX = -dx / dist; // Point toward center
          const normalY = -dy / dist;
          
          // Apply knockback bounce (no damage) - same force as BB-10 stadium
          const knockbackForce = 31 * SCALE_FACTOR;
          setKnockbackVelocity1({
            x: normalX * knockbackForce,
            y: normalY * knockbackForce
          });
          
          // Clear regular velocity
          setTop1Velocity({ x: 0, y: 0 });
          
          // Set cooldown to prevent multiple bounces
          wallCooldown1Ref.current = true;
          setTimeout(() => { wallCooldown1Ref.current = false; }, 200);
          
          // Push beyblade back inside (just inside the rail)
          const safeFactor = 0.95; // Changed from 0.90 to 0.95 to stay closer to rail
          return {
            x: BX_CENTER_X + (railPoint.x - BX_CENTER_X) * safeFactor,
            y: BX_CENTER_Y + (railPoint.y - BX_CENTER_Y) * safeFactor
          };
        }
      }
      
      return { x: newX, y: newY };
    }
    
    // BX-Infinity Stadium: Wall bounce without damage, knockout zones handled in isOutOfBounds
    if (selectedStadium === 'bxInfinity') {
      // Check for Xtreme Dash trigger point collision (only if not already dashing and not on cooldown)
      if (!xtremeDashLine1Ref.current && !speedBoostCooldown1Ref.current) {
        const TRIGGER_RADIUS = 35 * SCALE_FACTOR; // Halved collision radius to match smaller visual circles
        
        // Scale factor to convert trigger points from 800x600 base to actual canvas size
        const scaleX = CANVAS_WIDTH / BASE_CANVAS_WIDTH;
        const scaleY = CANVAS_HEIGHT / BASE_CANVAS_HEIGHT;
        
        // Check all 4 trigger points
        const triggerPoints = ['blueTop', 'blueBottom', 'redTop', 'redBottom'];
        for (const triggerKey of triggerPoints) {
          const trigger = xtremeDashPoints[triggerKey];
          // Scale trigger coordinates to match beyblade coordinate system
          const scaledTriggerX = trigger.x * scaleX;
          const scaledTriggerY = trigger.y * scaleY;
          const dx = newX - scaledTriggerX;
          const dy = newY - scaledTriggerY;
          const dist = Math.sqrt(dx * dx + dy * dy);
          
          if (dist < TRIGGER_RADIUS) {
            // Beyblade touched a trigger point - attempt to activate Xtreme Dash
            // This will activate speed boost and start the looping dash
            handleXtremeDashLine1(triggerKey);
            break;
          }
        }
      }
      
      const wall = getCollidingWall(newX, newY, prevX, prevY);
      
      if (wall && !wallCooldown1Ref.current && !stoppedSpinning1) {
        // Get normal pointing INTO the stadium
        const normal = getWallNormalToCenter(wall);
        
        // Apply knockback - no damage for BX-Infinity
        const knockbackForce = 31 * SCALE_FACTOR;
        setKnockbackVelocity1({
          x: normal.x * knockbackForce,
          y: normal.y * knockbackForce
        });
        
        // Clear regular velocity
        setTop1Velocity({ x: 0, y: 0 });
        
        // Set cooldown to prevent multiple hits
        wallCooldown1Ref.current = true;
        setTimeout(() => { wallCooldown1Ref.current = false; }, 200);
        
        // Push beyblade away from wall toward stadium center
        const pushDistance = 25 * SCALE_FACTOR;
        return { 
          x: prevX + normal.x * pushDistance, 
          y: prevY + normal.y * pushDistance 
        };
      }
      return { x: newX, y: newY };
    }
    
    // BB-10 Stadium: Normal wall bounce with damage
    const wall = getCollidingWall(newX, newY, prevX, prevY);
    
    if (wall && !wallCooldown1Ref.current) {
      // Get normal pointing INTO the stadium
      const normal = getWallNormalToCenter(wall);
      
      // Apply knockback - reduced by 1/4 from beyblade collision, deflection reduces by another half
      // Only apply if not stopped spinning (immovable when stamina depleted)
      if (!stoppedSpinning1) {
        const baseKnockbackForce = 31 * SCALE_FACTOR; // 41 * 3/4 ≈ 31
        const knockbackForce = deflection1 ? baseKnockbackForce * 0.5 : baseKnockbackForce;
        
        setKnockbackVelocity1({
          x: normal.x * knockbackForce,
          y: normal.y * knockbackForce
        });
      }
      
      // Deal 1 damage unless deflection or ghost mode is active
      if (!deflection1 && !ghostMode1) {
        setHealth1(prev => Math.max(0, prev - 1));
        // Reduce stamina by 1 second for each point of damage taken
        setStamina1(prev => Math.max(0, prev - 1));
      }
      
      // Clear regular velocity
      setTop1Velocity({ x: 0, y: 0 });
      
      // Set cooldown to prevent multiple hits
      wallCooldown1Ref.current = true;
      setTimeout(() => { wallCooldown1Ref.current = false; }, 300);
      
      // Push beyblade away from wall toward stadium center (only if not stopped spinning)
      if (!stoppedSpinning1) {
        const pushDistance = 25 * SCALE_FACTOR;
        return { 
          x: prevX + normal.x * pushDistance, 
          y: prevY + normal.y * pushDistance 
        };
      }
      return { x: prevX, y: prevY }; // Stay in place if stopped spinning
    }
    return { x: newX, y: newY };
  };

  // Apply wall collision like hitting a beyblade - triggers knockback for Beyblade 2
  // For BX-Stadium: Closed stadium with boundary enforcement matching manual control
  const applyWallCollision2 = (newX, newY, prevX, prevY) => {
    // BX-Stadium: Closed stadium with boundary enforcement matching manual control
    if (selectedStadium === 'bx' && !stoppedSpinning2) {
      const dx = newX - BX_CENTER_X;
      const dy = newY - BX_CENTER_Y;
      const currentAngle = Math.atan2(dy, dx);
      
      // Calculate normalized distance and angle for pocket zone detection
      const normalizedDist = (dx * dx) / (BX_RAIL_WIDTH * BX_RAIL_WIDTH) + 
                             (dy * dy) / (BX_RAIL_HEIGHT * BX_RAIL_HEIGHT);
      
      // Check if beyblade is in a knockout pocket zone FIRST (before any bounce logic)
      const inLeftZone = currentAngle > zoneConfig.leftZone.endAngle && currentAngle <= zoneConfig.leftZone.startAngle;
      const inCenterZone = currentAngle > zoneConfig.centerZone.endAngle && currentAngle <= zoneConfig.centerZone.startAngle;
      const inRightZone = currentAngle >= zoneConfig.rightZone.endAngle && currentAngle <= zoneConfig.rightZone.startAngle;
      const inAnyPocketZone = (inLeftZone || inCenterZone || inRightZone) && dy > 0;
      
      // If in a pocket zone, SKIP all bounce logic - let beyblade pass through
      if (inAnyPocketZone && normalizedDist > 0.75) {
        setRidingRail2(false);
        return { x: newX, y: newY }; // Allow movement into pocket - NO BOUNCE
      }
      
      // CLOSED STADIUM - Apply same elliptical boundary as manual control
      // This prevents beyblades from exiting through the top (V-opening area)
      // Only apply if NOT in a pocket zone
      
      // If beyblade is trying to go beyond 85% of elliptical boundary, bounce it back
      // This matches the manual control boundary exactly
      if (normalizedDist > 0.85 && !wallCooldown2Ref.current && !inAnyPocketZone) {
        // Calculate bounce direction (away from edge, toward center)
        const dist = Math.sqrt(dx * dx + dy * dy);
        const normalX = dist > 0 ? -dx / dist : 0;
        const normalY = dist > 0 ? -dy / dist : 1;
        
        const knockbackForce = 31 * SCALE_FACTOR; // Same as BB-10 stadium
        setKnockbackVelocity2({
          x: normalX * knockbackForce,
          y: normalY * knockbackForce
        });
        
        setTop2Velocity({ x: 0, y: 0 });
        wallCooldown2Ref.current = true;
        setTimeout(() => { wallCooldown2Ref.current = false; }, 200);
        
        // Scale position back to safe boundary (85% of ellipse)
        const scale = Math.sqrt(0.85 / normalizedDist);
        return {
          x: BX_CENTER_X + dx * scale,
          y: BX_CENTER_Y + dy * scale
        };
      }
      
      // Rail riding disabled - just set state to false
      setRidingRail2(false);
      
      // XTREME DASH CHECK - Check for top boundary collision BEFORE other collision handling
      // This triggers when hitting the top area of the stadium with speed boost available
      // IMPORTANT: This check runs even during manual control to allow triggering while on touchpad
      const dx_dash = newX - BX_CENTER_X;
      const dy_dash = newY - BX_CENTER_Y;
      const normalizedDist_dash = (dx_dash * dx_dash) / (BX_RAIL_WIDTH * BX_RAIL_WIDTH) + 
                             (dy_dash * dy_dash) / (BX_RAIL_HEIGHT * BX_RAIL_HEIGHT);
      
      // Trigger zone reduced by another half - only very top portion of the rail
      const triggerLineY = -(BX_RAIL_HEIGHT * 0.75 + 5); // 3/4 up from center
      
      // Trigger Xtreme Dash when:
      // 1. Near the boundary (normalizedDist > 0.75)
      // 2. In the TOP HALF of the trigger zone (dy < triggerLineY)
      // 3. Speed boost is available OR already active (just not on cooldown)
      // 4. Not already in freeze state
      // 5. On the correct side based on spin direction:
      //    - Right spin = right side only (dx > 0)
      //    - Left spin = left side only (dx < 0)
      if (normalizedDist_dash > 0.75 && dy_dash < triggerLineY && top2Active && !xtremeDashFreeze2Ref.current) {
        // Check spin direction (handle dual-spin beyblades)
        let spinDir = selectedBeyblade2?.spinDirection;
        if (spinDir === 'dual') {
          spinDir = dualSpinDirection2Ref.current;
        }
        
        // Check if on correct side of stadium for spin direction
        const onRightSide = dx_dash > 0;
        const onLeftSide = dx_dash < 0;
        const correctSide = (spinDir === 'right' && onRightSide) || (spinDir === 'left' && onLeftSide);
        
        // Can Xtreme Dash if: not on cooldown AND on correct side
        // Speed boost can be either available OR already active (manually pressed)
        const canXtremeDash = !speedBoostCooldown2Ref.current && correctSide;
        if (canXtremeDash) {
          
          // BREAK MANUAL CONTROL - Release touchpad control so the dash takes over
          setIsControlling2(false);
          
          // Reset trackpad timer (same as BX-Infinity)
          if (controlTimer2Ref.current) {
            clearTimeout(controlTimer2Ref.current);
            controlTimer2Ref.current = null;
          }
          
          // Only activate speed boost if not already active
          if (!speedBoost2Ref.current) {
            handleSpeedBoost2();
          }
          
          // Set freeze state to mark we're in a dash
          setXtremeDashFreeze2(true);
          
          // Target: Bottom of the safe play area (within safe boundary)
          const targetX = BX_CENTER_X;
          const targetY = BX_CENTER_Y + BX_RAIL_HEIGHT * 0.45; // 45% down - stops before bottom
          const dirX = targetX - newX;
          const dirY = targetY - newY;
          const dirLen = Math.sqrt(dirX * dirX + dirY * dirY);
          
          // Apply force based on beyblade's speed stat (same modifier as orbital speed with speed boost)
          const beybladeSpeedStat = getBeybladeStats(selectedBeyblade2?.id)?.speed || 50;
          const effectiveSpeed = (finalDriveActive2 && hasFinalDrive(selectedBeyblade2?.id)) ? 200 : beybladeSpeedStat;
          const speedStatModifier = 1 + (effectiveSpeed - 50) / 100;
          const baseForce = 47 * SCALE_FACTOR; // Base force calibrated for speed 50
          const xtremeDashForce = baseForce * 1.5 * speedStatModifier; // Apply speed boost multiplier and speed stat
          setKnockbackVelocity2({
            x: (dirX / dirLen) * xtremeDashForce,
            y: (dirY / dirLen) * xtremeDashForce
          });
          setTop2Velocity({ x: 0, y: 0 });
          
          // After 1 second, end dash state and stop knockback so auto-rotation resumes
          setTimeout(() => {
            setXtremeDashFreeze2(false);
            setKnockbackVelocity2({ x: 0, y: 0 }); // Stop knockback so auto-rotation takes over
          }, 1000);
          
          // Return current position (knockback will move it)
          return { x: newX, y: newY };
        }
      }
      
      // If player is manually controlling (D-pad), they use their own boundary checks
      if (isControlling2) {
        return { x: newX, y: newY };
      }
      
      /* RAIL RIDING CODE - DISABLED
      // Check if beyblade is close enough to the rail to engage
      if (distToRail <= railEngageThreshold && distToRail !== Infinity) {
        // Engage rail riding!
        setRidingRail2(true);
        
        // Get the closest point on the rail
        const railPoint = getClosestBXRailPoint(newX, newY);
        if (!railPoint) {
          // No rail at this position (V-opening), let beyblade move freely
          setRidingRail2(false);
          return { x: newX, y: newY };
        }
        
        // Determine rotation direction based on spin direction
        // Right spin = counter-clockwise, Left spin = clockwise
        const spinDirection = selectedBeyblade2?.spinDirection || 'Right';
        const rotationDir = spinDirection === 'Right' ? 1 : -1;
        
        // Calculate tangent direction along the oval rail at the rail point
        const railDx = railPoint.x - BX_CENTER_X;
        const railDy = railPoint.y - BX_CENTER_Y;
        
        // Get sin(θ) and cos(θ) from the rail point
        const cosTheta = railDx / BX_RAIL_WIDTH;
        const sinTheta = railDy / BX_RAIL_HEIGHT;
        
        // Tangent direction: dx/dθ = -a*sin(θ), dy/dθ = b*cos(θ)
        const tangentX_raw = -BX_RAIL_WIDTH * sinTheta;
        const tangentY_raw = BX_RAIL_HEIGHT * cosTheta;
        const tangentLength = Math.sqrt(tangentX_raw * tangentX_raw + tangentY_raw * tangentY_raw);
        
        // On the bottom-left quadrant (cosTheta < 0 and sinTheta > 0), 
        // flip direction to continue counter-clockwise like bottom-right
        const inBottomLeft = cosTheta < 0 && sinTheta > 0;
        const directionFix = inBottomLeft ? -1 : 1;
        
        // Apply tangential velocity along the rail
        const railSpeed = 6 * SCALE_FACTOR * RAIL_SPEED_MULTIPLIER;
        const tangentX = (tangentX_raw / tangentLength) * railSpeed * rotationDir * directionFix;
        const tangentY = (tangentY_raw / tangentLength) * railSpeed * rotationDir * directionFix;
        
        setKnockbackVelocity2({
          x: tangentX,
          y: tangentY
        });
        
        // Snap beyblade to the rail position
        const snapFactor = 0.97; // Keep on the rail
        return { 
          x: BX_CENTER_X + (railPoint.x - BX_CENTER_X) * snapFactor, 
          y: BX_CENTER_Y + (railPoint.y - BX_CENTER_Y) * snapFactor 
        };
      } else {
        // Not on rail
        setRidingRail2(false);
      }
      END RAIL RIDING CODE */
      
      // BX-Stadium: Wall bounce WITHOUT damage (CLOSED stadium - no knockouts)
      // Check if beyblade is hitting the oval boundary (the green rail)
      const railPoint = getClosestBXRailPoint(newX, newY);
      
      // If in V-opening area (railPoint is null), apply HARD boundary
      // Since we already checked at function start, this is a backup that also
      // handles edge cases where beyblade might slip through
      if (!railPoint) {
        // V-opening area - ALWAYS enforce the elliptical boundary here
        const dx = newX - BX_CENTER_X;
        const dy = newY - BX_CENTER_Y;
        const normalizedDist = (dx * dx) / (BX_RAIL_WIDTH * BX_RAIL_WIDTH) + 
                               (dy * dy) / (BX_RAIL_HEIGHT * BX_RAIL_HEIGHT);
        
        // ALWAYS scale back to safe boundary if past 0.85
        if (normalizedDist > 0.85) {
          const scale = Math.sqrt(0.85 / normalizedDist);
          return {
            x: BX_CENTER_X + dx * scale,
            y: BX_CENTER_Y + dy * scale
          };
        }
        
        return { x: newX, y: newY };
      }
      
      if (railPoint) {
        const railDist = Math.sqrt(
          Math.pow(railPoint.x - BX_CENTER_X, 2) + Math.pow(railPoint.y - BX_CENTER_Y, 2)
        );
        const beyDist = Math.sqrt(
          Math.pow(newX - BX_CENTER_X, 2) + Math.pow(newY - BX_CENTER_Y, 2)
        );
        
        // Check if beyblade is entering a knockout pocket zone
        // Uses the same angle-based detection as isOutOfBounds with zoneConfig
        const dx = newX - BX_CENTER_X;
        const dy = newY - BX_CENTER_Y;
        const normalizedDist = (dx * dx) / (BX_RAIL_WIDTH * BX_RAIL_WIDTH) + 
                               (dy * dy) / (BX_RAIL_HEIGHT * BX_RAIL_HEIGHT);
        const angle = Math.atan2(dy, dx);
        
        // If beyblade is near or beyond the ellipse boundary AND in bottom half, check for pocket entry
        if (normalizedDist > 0.85 && dy > 0) {
          // Check if angle falls within any of the knockout zones (using zoneConfig)
          const inLeftZone = angle > zoneConfig.leftZone.endAngle && angle <= zoneConfig.leftZone.startAngle;
          const inCenterZone = angle > zoneConfig.centerZone.endAngle && angle <= zoneConfig.centerZone.startAngle;
          const inRightZone = angle >= zoneConfig.rightZone.endAngle && angle <= zoneConfig.rightZone.startAngle;
          
          // If in any pocket zone, allow beyblade to pass through (knockout)
          if (inLeftZone || inCenterZone || inRightZone) {
            return { x: newX, y: newY }; // Allow movement into pocket
          }
        }
        
        // If beyblade has reached or passed the rail boundary, apply bounce
        // Changed from (railDist - 15) to (railDist - 5) so bounce happens AT the rail
        if (beyDist >= railDist - 5 && !wallCooldown2Ref.current) {
          // Calculate normal pointing toward center (for bounce direction)
          const dx = newX - BX_CENTER_X;
          const dy = newY - BX_CENTER_Y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          const normalX = -dx / dist; // Point toward center
          const normalY = -dy / dist;
          
          // Apply knockback bounce (no damage) - same force as BB-10 stadium
          const knockbackForce = 31 * SCALE_FACTOR;
          setKnockbackVelocity2({
            x: normalX * knockbackForce,
            y: normalY * knockbackForce
          });
          
          // Clear regular velocity
          setTop2Velocity({ x: 0, y: 0 });
          
          // Set cooldown to prevent multiple bounces
          wallCooldown2Ref.current = true;
          setTimeout(() => { wallCooldown2Ref.current = false; }, 200);
          
          // Push beyblade back inside (just inside the rail)
          const safeFactor = 0.95; // Changed from 0.90 to 0.95 to stay closer to rail
          return {
            x: BX_CENTER_X + (railPoint.x - BX_CENTER_X) * safeFactor,
            y: BX_CENTER_Y + (railPoint.y - BX_CENTER_Y) * safeFactor
          };
        }
      }
      
      return { x: newX, y: newY };
    }
    
    // BX-Infinity Stadium: Wall bounce without damage, knockout zones handled in isOutOfBounds
    if (selectedStadium === 'bxInfinity') {
      // Check for Xtreme Dash trigger point collision (only if not already dashing and not on cooldown)
      if (!xtremeDashLine2Ref.current && !speedBoostCooldown2Ref.current) {
        const TRIGGER_RADIUS = 35 * SCALE_FACTOR; // Halved collision radius to match smaller visual circles
        
        // Scale factor to convert trigger points from 800x600 base to actual canvas size
        const scaleX = CANVAS_WIDTH / BASE_CANVAS_WIDTH;
        const scaleY = CANVAS_HEIGHT / BASE_CANVAS_HEIGHT;
        
        // Check all 4 trigger points
        const triggerPoints = ['blueTop', 'blueBottom', 'redTop', 'redBottom'];
        for (const triggerKey of triggerPoints) {
          const trigger = xtremeDashPoints[triggerKey];
          // Scale trigger coordinates to match beyblade coordinate system
          const scaledTriggerX = trigger.x * scaleX;
          const scaledTriggerY = trigger.y * scaleY;
          const dx = newX - scaledTriggerX;
          const dy = newY - scaledTriggerY;
          const dist = Math.sqrt(dx * dx + dy * dy);
          
          if (dist < TRIGGER_RADIUS) {
            // Beyblade touched a trigger point - attempt to activate Xtreme Dash
            // This will activate speed boost and start the looping dash
            handleXtremeDashLine2(triggerKey);
            break;
          }
        }
      }
      
      const wall = getCollidingWall(newX, newY, prevX, prevY);
      
      if (wall && !wallCooldown2Ref.current && !stoppedSpinning2) {
        // Get normal pointing INTO the stadium
        const normal = getWallNormalToCenter(wall);
        
        // Apply knockback - no damage for BX-Infinity
        const knockbackForce = 31 * SCALE_FACTOR;
        setKnockbackVelocity2({
          x: normal.x * knockbackForce,
          y: normal.y * knockbackForce
        });
        
        // Clear regular velocity
        setTop2Velocity({ x: 0, y: 0 });
        
        // Set cooldown to prevent multiple hits
        wallCooldown2Ref.current = true;
        setTimeout(() => { wallCooldown2Ref.current = false; }, 200);
        
        // Push beyblade away from wall toward stadium center
        const pushDistance = 25 * SCALE_FACTOR;
        return { 
          x: prevX + normal.x * pushDistance, 
          y: prevY + normal.y * pushDistance 
        };
      }
      return { x: newX, y: newY };
    }
    
    // BB-10 Stadium: Normal wall bounce with damage
    const wall = getCollidingWall(newX, newY, prevX, prevY);
    
    if (wall && !wallCooldown2Ref.current) {
      // Get normal pointing INTO the stadium
      const normal = getWallNormalToCenter(wall);
      
      // Apply knockback - reduced by 1/4 from beyblade collision, deflection reduces by another half
      // Only apply if not stopped spinning (immovable when stamina depleted)
      if (!stoppedSpinning2) {
        const baseKnockbackForce = 31 * SCALE_FACTOR; // 41 * 3/4 ≈ 31
        const knockbackForce = deflection2 ? baseKnockbackForce * 0.5 : baseKnockbackForce;
        
        setKnockbackVelocity2({
          x: normal.x * knockbackForce,
          y: normal.y * knockbackForce
        });
      }
      
      // Deal 1 damage unless deflection or ghost mode is active
      if (!deflection2 && !ghostMode2) {
        setHealth2(prev => Math.max(0, prev - 1));
        // Reduce stamina by 1 second for each point of damage taken
        setStamina2(prev => Math.max(0, prev - 1));
      }
      
      // Clear regular velocity
      setTop2Velocity({ x: 0, y: 0 });
      
      // Set cooldown to prevent multiple hits
      wallCooldown2Ref.current = true;
      setTimeout(() => { wallCooldown2Ref.current = false; }, 300);
      
      // Push beyblade away from wall toward stadium center (only if not stopped spinning)
      if (!stoppedSpinning2) {
        const pushDistance = 25 * SCALE_FACTOR;
        return { 
          x: prevX + normal.x * pushDistance, 
          y: prevY + normal.y * pushDistance 
        };
      }
      return { x: prevX, y: prevY }; // Stay in place if stopped spinning
    }
    return { x: newX, y: newY };
  };

  // Generic wall collision for backward compatibility
  const applyWallCollision = applyWallCollision1;
  // CIRCULAR GRID - fills canvas with circular pattern around stadium
  const drawGrid = (ctx) => {
    const centerX = CANVAS_WIDTH / 2;
    const centerY = CANVAS_HEIGHT / 2;
    // Use the smaller dimension to create a circle that fits the canvas
    const maxRadius = Math.min(CANVAS_WIDTH, CANVAS_HEIGHT) / 2;
    const stadiumRadius = 160; // Stadium is 320px wide, so radius is 160px
    
    ctx.strokeStyle = '#2a3f5f';
    ctx.lineWidth = 1.5;

    // Draw concentric circles - rings at 40px intervals, but skip the stadium area
    for (let radius = 40; radius <= maxRadius; radius += 40) {
      // Skip circles that are within or too close to the stadium
      if (radius < stadiumRadius) {
        continue;
      }
      ctx.beginPath();
      ctx.arc(centerX, centerY, radius, 0, Math.PI * 2);
      ctx.stroke();
    }

    // Draw radial lines - 16 spokes, but only outside the stadium
    const numSpokes = 16;
    for (let i = 0; i < numSpokes; i++) {
      const angle = (i / numSpokes) * Math.PI * 2;
      ctx.beginPath();
      // Start from stadium edge instead of center
      ctx.moveTo(
        centerX + stadiumRadius * Math.cos(angle),
        centerY + stadiumRadius * Math.sin(angle)
      );
      // Draw to canvas edge
      ctx.lineTo(
        centerX + maxRadius * Math.cos(angle),
        centerY + maxRadius * Math.sin(angle)
      );
      ctx.stroke();
    }
  };

  // REMOVED: drawCharacter - walking character no longer exists

  const drawNearbyPlayers = (ctx) => {
    if (!nearbyPlayers || nearbyPlayers.length === 0) return;

    nearbyPlayers.forEach((player, index) => {
      // Position players around the map based on their distance
      // For demo: distribute them in a circle around center
      const angle = (index / nearbyPlayers.length) * Math.PI * 2;
      const distance = 150 + (player.distance || 0) * 10;
      const px = CANVAS_WIDTH / 2 + Math.cos(angle) * distance;
      const py = CANVAS_HEIGHT / 2 + Math.sin(angle) * distance;

      // Draw player marker
      ctx.fillStyle = '#888';
      ctx.beginPath();
      ctx.arc(px, py, 12, 0, Math.PI * 2);
      ctx.fill();

      // Player initial
      ctx.fillStyle = '#fff';
      ctx.font = 'bold 12px Arial';
      ctx.textAlign = 'center';
      ctx.fillText(player.username.charAt(0).toUpperCase(), px, py + 4);

      // Username below
      ctx.fillStyle = '#ccc';
      ctx.font = '10px Arial';
      ctx.fillText(player.username, px, py + 20);

      // Distance label
      ctx.fillStyle = '#4488FF';
      ctx.fillText(`${player.distance.toFixed(1)} mi`, px, py + 30);
    });
  };

  // REMOVED: moveTowardsTarget - walking character no longer exists
  // Beyblade 1 spinning animation - 60fps for smooth rotation
  // Use ref to track current spin direction so interval always has latest value
  const dualSpinDirection1Ref = useRef(dualSpinDirection1);
  useEffect(() => {
    dualSpinDirection1Ref.current = dualSpinDirection1;
  }, [dualSpinDirection1]);
  
  // Refs for stopped spinning states
  const stoppedSpinning1Ref = useRef(stoppedSpinning1);
  const stoppedSpinning2Ref = useRef(stoppedSpinning2);
  useEffect(() => { stoppedSpinning1Ref.current = stoppedSpinning1; }, [stoppedSpinning1]);
  useEffect(() => { stoppedSpinning2Ref.current = stoppedSpinning2; }, [stoppedSpinning2]);

  useEffect(() => {
    if (!top1Active || !selectedBeyblade1) return;

    // 520 RPM = 8.67 rotations/sec = 50° per frame at 60fps
    const spinInterval = setInterval(() => {
      // Stop rotation if beyblade ran out of stamina
      if (stoppedSpinning1Ref.current) return;
      
      // For dual-spin beyblades, read from ref to get current direction
      let baseRotation;
      if (selectedBeyblade1.spinDirection === 'dual') {
        baseRotation = dualSpinDirection1Ref.current === 'left' ? -50 : 50;
      } else {
        baseRotation = selectedBeyblade1.spinDirection === 'left' ? -50 : 50;
      }
      setTop1Rotation((prev) => (prev + baseRotation + 360) % 360);
      setTop1Speed((prev) => Math.max(0, prev - 0.17));
    }, 16); // 60fps for smooth spinning

    return () => clearInterval(spinInterval);
  }, [top1Active, selectedBeyblade1]);

  // Beyblade 2 spinning animation - 60fps for smooth rotation
  // Use ref to track current spin direction so interval always has latest value
  const dualSpinDirection2Ref = useRef(dualSpinDirection2);
  useEffect(() => {
    dualSpinDirection2Ref.current = dualSpinDirection2;
  }, [dualSpinDirection2]);

  useEffect(() => {
    if (!top2Active || !selectedBeyblade2) return;

    // 520 RPM = 8.67 rotations/sec = 50° per frame at 60fps
    const spinInterval = setInterval(() => {
      // Stop rotation if beyblade ran out of stamina
      if (stoppedSpinning2Ref.current) return;
      
      // For dual-spin beyblades, read from ref to get current direction
      let baseRotation;
      if (selectedBeyblade2.spinDirection === 'dual') {
        baseRotation = dualSpinDirection2Ref.current === 'left' ? -50 : 50;
      } else {
        baseRotation = selectedBeyblade2.spinDirection === 'left' ? -50 : 50;
      }
      setTop2Rotation((prev) => (prev + baseRotation + 360) % 360);
      setTop2Speed((prev) => Math.max(0, prev - 0.17));
    }, 16); // 60fps for smooth spinning

    return () => clearInterval(spinInterval);
  }, [top2Active, selectedBeyblade2]);

  // Fire rotation animation for Burning Strike
  useEffect(() => {
    if (!burningStrikeActive1 && !burningStrikeActive2) return;
    
    const fireInterval = setInterval(() => {
      if (burningStrikeActive1) {
        setFireRotation1(prev => (prev + 15) % 360); // Fast rotation
      }
      if (burningStrikeActive2) {
        setFireRotation2(prev => (prev + 15) % 360);
      }
    }, 16); // 60fps

    return () => clearInterval(fireInterval);
  }, [burningStrikeActive1, burningStrikeActive2]);

  // Burning Strike - damage is now handled through expanded collision radius in applyKnockbackOnHit
  // The fire rotation animation remains active for visual effect

  // Shadow clone orbital movement - clones move around the stadium like regular beyblades
  useEffect(() => {
    if (!shadowClonesActive1 && !shadowClonesActive2) return;
    
    const speed = 0.067; // Same speed as regular beyblades
    
    const moveInterval = setInterval(() => {
      // Move P1 clones
      if (shadowClonesActive1 && shadowClones1.length > 0) {
        setShadowClones1(prevClones => prevClones.map(clone => {
          const dx = clone.x - STADIUM_CENTER_X;
          const dy = clone.y - STADIUM_CENTER_Y;
          const currentRadius = Math.sqrt(dx * dx + dy * dy);
          const targetRadius = findNearestRing(currentRadius);
          
          // If at center, just wobble slightly
          if (targetRadius === 0) {
            return {
              ...clone,
              x: STADIUM_CENTER_X + (Math.random() - 0.5) * 2,
              y: STADIUM_CENTER_Y + (Math.random() - 0.5) * 2,
            };
          }
          
          // Orbit around the stadium (right spin direction)
          const newAngle = clone.angle - speed; // Right spin = counter-clockwise
          return {
            ...clone,
            angle: newAngle,
            x: STADIUM_CENTER_X + targetRadius * Math.cos(newAngle),
            y: STADIUM_CENTER_Y + targetRadius * Math.sin(newAngle),
          };
        }));
      }
      
      // Move P2 clones
      if (shadowClonesActive2 && shadowClones2.length > 0) {
        setShadowClones2(prevClones => prevClones.map(clone => {
          const dx = clone.x - STADIUM_CENTER_X;
          const dy = clone.y - STADIUM_CENTER_Y;
          const currentRadius = Math.sqrt(dx * dx + dy * dy);
          const targetRadius = findNearestRing(currentRadius);
          
          // If at center, just wobble slightly
          if (targetRadius === 0) {
            return {
              ...clone,
              x: STADIUM_CENTER_X + (Math.random() - 0.5) * 2,
              y: STADIUM_CENTER_Y + (Math.random() - 0.5) * 2,
            };
          }
          
          // Orbit around the stadium (right spin direction)
          const newAngle = clone.angle - speed; // Right spin = counter-clockwise
          return {
            ...clone,
            angle: newAngle,
            x: STADIUM_CENTER_X + targetRadius * Math.cos(newAngle),
            y: STADIUM_CENTER_Y + targetRadius * Math.sin(newAngle),
          };
        }));
      }
    }, 16); // 60fps
    
    return () => clearInterval(moveInterval);
  }, [shadowClonesActive1, shadowClonesActive2, shadowClones1.length, shadowClones2.length]);

  // Check collision between Beyblades and apply knockback
  const checkBeybladeCollision = () => {
    if (top1KnockedOut || top2KnockedOut) return;
    
    const dx = top1Pos.x - top2Pos.x;
    const dy = top1Pos.y - top2Pos.y;
    const distance = Math.sqrt(dx * dx + dy * dy);
    
    // Hitbox increased by 1/4 (40 * 1.25 = 50)
    const hitboxSize = 50 * SCALE_FACTOR;
    if (distance < hitboxSize && distance > 0) {
      // Apply knockback based on who is controlling
      applyKnockbackOnHit(top1Pos, top2Pos, isControlling1, isControlling2);
    }
  };

  // Collision detection and knockback physics
  useEffect(() => {
    // Allow knockback physics to run even if only one beyblade is active
    // (needed for rail riding when alone)
    if (!top1Active && !top2Active) return;

    // Smooth knockback physics at 60fps for ball-rolling effect
    const FRICTION = 0.97; // Smooth deceleration (adjusted for 60fps)
    const MIN_VELOCITY = 0.05; // Lower threshold for smoother stop
    
    const collisionInterval = setInterval(() => {
      // Check collision between Beyblades (only if both are active)
      if (top1Active && top2Active) {
        checkBeybladeCollision();
      }

      // Apply knockback to Beyblade 1 - smooth rolling physics
      // Skip if beyblade has stopped spinning (ran out of stamina) - it becomes immovable
      if (top1Active && (knockbackVelocity1.x !== 0 || knockbackVelocity1.y !== 0) && !stoppedSpinning1Ref.current) {
        setTop1Pos(prev => {
          // Scale velocity for 60fps (divide by ~2 since we doubled the framerate)
          const newX = prev.x + knockbackVelocity1.x * 0.5;
          const newY = prev.y + knockbackVelocity1.y * 0.5;
          
          // During BX-Infinity Xtreme Dash Line: Just follow the line, no wall collision
          if (xtremeDashLine1Ref.current) {
            return { x: newX, y: newY };
          }
          
          // During Xtreme Dash: Enforce play area boundary AND prevent touching bottom
          if (xtremeDashFreeze1Ref.current) {
            const dx = newX - BX_CENTER_X;
            const dy = newY - BX_CENTER_Y;
            
            // Limit how far down it can go (dy can't exceed 50% of rail height)
            const maxDownY = BX_RAIL_HEIGHT * 0.5;
            
            // If beyblade hits the bottom barrier, STOP and go into auto-rotation
            if (dy >= maxDownY) {
              // Stop at the barrier position
              const stoppedY = BX_CENTER_Y + maxDownY;
              // Clear knockback velocity immediately
              setKnockbackVelocity1({ x: 0, y: 0 });
              // End the dash state
              setXtremeDashFreeze1(false);
              return { x: newX, y: stoppedY };
            }
            
            // Also enforce side boundaries (65% ellipse)
            const normalizedDist = (dx * dx) / (BX_RAIL_WIDTH * BX_RAIL_WIDTH) + 
                                   (dy * dy) / (BX_RAIL_HEIGHT * BX_RAIL_HEIGHT);
            const maxDashBoundary = 0.65;
            
            if (normalizedDist > maxDashBoundary) {
              const scale = Math.sqrt(maxDashBoundary / normalizedDist);
              return {
                x: BX_CENTER_X + dx * scale,
                y: BX_CENTER_Y + dy * scale
              };
            }
            return { x: newX, y: newY };
          }
          
          // Check wall collision FIRST - walls should stop the beyblade before it goes out of bounds
          const afterWall = applyWallCollision1(newX, newY, prev.x, prev.y);
          
          // Check if knocked out of bounds (only if wall didn't stop it)
          const boundsCheck = isOutOfBounds(afterWall);
          
          // Immediate knockout for both pocket and non-pocket
          if (boundsCheck.outOfBounds && !scoreAwardedRef.current) {
            scoreAwardedRef.current = true; // Prevent duplicate scoring
            setTop1KnockedOut(true);
            setTop1Active(false);
            setRoundWinner(2);
            setWinType('knockout');
            if (boundsCheck.isPocket) {
              setIsPocketKO(true);
              setPocketKOType(boundsCheck.pocketType);
              setPlayer2Score(prev => prev + boundsCheck.pocketPoints);
            } else {
              setIsPocketKO(false);
              setPocketKOType(null);
              setPlayer2Score(prev => prev + WIN_POINTS.knockout);
            }
            return prev;
          }
          
          return afterWall;
        });
        
        // Smooth friction - gradual deceleration like a rolling ball
        // BUT: Skip friction when riding the rail in BX-Stadium (rail maintains constant speed)
        if (!ridingRail1) {
          setKnockbackVelocity1(prev => {
            const newVelX = prev.x * FRICTION;
            const newVelY = prev.y * FRICTION;
            
            // Stop when velocity is negligible
            if (Math.abs(newVelX) < MIN_VELOCITY && Math.abs(newVelY) < MIN_VELOCITY) {
              return { x: 0, y: 0 };
            }
            return { x: newVelX, y: newVelY };
          });
        }
      }

      // Apply knockback to Beyblade 2 (local mode only)
      // Skip if beyblade has stopped spinning (ran out of stamina) - it becomes immovable
      const shouldApplyPhysicsToP2 = !isOnlineMode || !matchFound;
      if (top2Active && shouldApplyPhysicsToP2 && (knockbackVelocity2.x !== 0 || knockbackVelocity2.y !== 0) && !stoppedSpinning2Ref.current) {
        setTop2Pos(prev => {
          const newX = prev.x + knockbackVelocity2.x * 0.5;
          const newY = prev.y + knockbackVelocity2.y * 0.5;
          
          // During BX-Infinity Xtreme Dash Line: Just follow the line, no wall collision
          if (xtremeDashLine2Ref.current) {
            return { x: newX, y: newY };
          }
          
          // During Xtreme Dash: Enforce play area boundary AND prevent touching bottom
          if (xtremeDashFreeze2Ref.current) {
            const dx = newX - BX_CENTER_X;
            const dy = newY - BX_CENTER_Y;
            
            // Limit how far down it can go (dy can't exceed 50% of rail height)
            const maxDownY = BX_RAIL_HEIGHT * 0.5;
            
            // If beyblade hits the bottom barrier, STOP and go into auto-rotation
            if (dy >= maxDownY) {
              // Stop at the barrier position
              const stoppedY = BX_CENTER_Y + maxDownY;
              // Clear knockback velocity immediately
              setKnockbackVelocity2({ x: 0, y: 0 });
              // End the dash state
              setXtremeDashFreeze2(false);
              return { x: newX, y: stoppedY };
            }
            
            // Also enforce side boundaries (65% ellipse)
            const normalizedDist = (dx * dx) / (BX_RAIL_WIDTH * BX_RAIL_WIDTH) + 
                                   (dy * dy) / (BX_RAIL_HEIGHT * BX_RAIL_HEIGHT);
            const maxDashBoundary = 0.65;
            
            if (normalizedDist > maxDashBoundary) {
              const scale = Math.sqrt(maxDashBoundary / normalizedDist);
              return {
                x: BX_CENTER_X + dx * scale,
                y: BX_CENTER_Y + dy * scale
              };
            }
            return { x: newX, y: newY };
          }
          
          // Check wall collision FIRST - walls should stop the beyblade before it goes out of bounds
          const afterWall = applyWallCollision2(newX, newY, prev.x, prev.y);
          
          // Check if knocked out of bounds (only if wall didn't stop it)
          const boundsCheck = isOutOfBounds(afterWall);
          if (boundsCheck.outOfBounds && !scoreAwardedRef.current) {
            scoreAwardedRef.current = true; // Prevent duplicate scoring
            setTop2KnockedOut(true);
            setTop2Active(false);
            setRoundWinner(1);
            setWinType('knockout');
            if (boundsCheck.isPocket) {
              setIsPocketKO(true);
              setPocketKOType(boundsCheck.pocketType); // Track if Xtreme or Over finish
              setPlayer1Score(prev => prev + boundsCheck.pocketPoints); // Use actual pocket points (2 or 3)
            } else {
              setIsPocketKO(false);
              setPocketKOType(null);
              setPlayer1Score(prev => prev + WIN_POINTS.knockout); // Knock Out: 3 points
            }
            return prev;
          }
          
          return afterWall;
        });
        
        // Smooth friction - skip when riding the rail in BX-Stadium
        if (!ridingRail2) {
          setKnockbackVelocity2(prev => {
            const newVelX = prev.x * FRICTION;
            const newVelY = prev.y * FRICTION;
            
            if (Math.abs(newVelX) < MIN_VELOCITY && Math.abs(newVelY) < MIN_VELOCITY) {
              return { x: 0, y: 0 };
            }
            return { x: newVelX, y: newVelY };
          });
        }
      }
    }, 16); // 60fps for smooth rolling physics

    return () => clearInterval(collisionInterval);
  }, [top1Active, top2Active, top1Pos, top2Pos, isControlling1, isControlling2, knockbackVelocity1, knockbackVelocity2, isOnlineMode, matchFound, myPlayerNumber, ridingRail1, ridingRail2]);

  // Check for Burst Finish when health reaches 0 - also check for DRAW
  useEffect(() => {
    // Skip if already have a winner, draw, or score already awarded
    if (roundWinner || isDraw || scoreAwardedRef.current) return;
    
    // Check for DRAW first - both health at 0 simultaneously
    if (health1 <= 0 && health2 <= 0 && !top1KnockedOut && !top2KnockedOut) {
      scoreAwardedRef.current = true; // Prevent any other win condition
      setIsDraw(true);
      setDrawType('burstFinish');
      setTop1Active(false);
      setTop2Active(false);
      setTop1KnockedOut(true);
      setTop2KnockedOut(true);
      // No points awarded for draw
      return;
    }
    
    // Single player health depletion - Burst Finish (2 points)
    // Don't trigger if already knocked out (that means it was a knockout, not burst)
    if (health1 <= 0 && !top1KnockedOut) {
      scoreAwardedRef.current = true; // Prevent duplicate scoring
      setRoundWinner(2); // Player 2 wins
      setWinType('burstFinish');
      setPlayer2Score(prev => prev + WIN_POINTS.burstFinish); // Burst Finish: 2 points
      setTop1Active(false);
      setTop1KnockedOut(true);
    } else if (health2 <= 0 && !top2KnockedOut) {
      scoreAwardedRef.current = true; // Prevent duplicate scoring
      setRoundWinner(1); // Player 1 wins
      setWinType('burstFinish');
      setPlayer1Score(prev => prev + WIN_POINTS.burstFinish); // Burst Finish: 2 points
      setTop2Active(false);
      setTop2KnockedOut(true);
    }
  }, [health1, health2, roundWinner, top1KnockedOut, top2KnockedOut, isDraw]);

  // Stamina timer - starts when both beyblades are launched
  useEffect(() => {
    // Start stamina drain when both beyblades are active
    if (top1Active && top2Active && !roundWinner && !isDraw && !top1KnockedOut && !top2KnockedOut) {
      // Clear any existing interval
      if (staminaIntervalRef.current) {
        clearInterval(staminaIntervalRef.current);
      }
      
      // Start draining stamina every 100ms (for smooth animation)
      staminaIntervalRef.current = setInterval(() => {
        setStamina1(prev => {
          const newStamina = Math.max(0, prev - 0.1);
          return Math.round(newStamina * 10) / 10; // Round to 1 decimal
        });
        setStamina2(prev => {
          const newStamina = Math.max(0, prev - 0.1);
          return Math.round(newStamina * 10) / 10; // Round to 1 decimal
        });
      }, 100);
    } else {
      // Stop the timer if battle is over or beyblades not active
      if (staminaIntervalRef.current) {
        clearInterval(staminaIntervalRef.current);
        staminaIntervalRef.current = null;
      }
    }
    
    return () => {
      if (staminaIntervalRef.current) {
        clearInterval(staminaIntervalRef.current);
      }
    };
  }, [top1Active, top2Active, roundWinner, isDraw, top1KnockedOut, top2KnockedOut]);

  // Check for stamina depletion - beyblade stops spinning but stays in place
  useEffect(() => {
    // P1 runs out of stamina - stop spinning (but don't determine winner yet)
    if (stamina1 <= 0 && !stoppedSpinning1 && top1Active && !top1KnockedOut) {
      setStoppedSpinning1(true);
    }
    
    // P2 runs out of stamina - stop spinning (but don't determine winner yet)
    if (stamina2 <= 0 && !stoppedSpinning2 && top2Active && !top2KnockedOut) {
      setStoppedSpinning2(true);
    }
    
    // Skip winner determination if already have a winner, draw, or score already awarded
    if (roundWinner || isDraw || scoreAwardedRef.current) return;
    
    // Both stopped spinning at same time = Draw (Spin Finish)
    // Only if BOTH still have health > 0 (otherwise it's a burst finish)
    if (stoppedSpinning1 && stoppedSpinning2 && !top1KnockedOut && !top2KnockedOut && top1Active && top2Active && health1 > 0 && health2 > 0) {
      scoreAwardedRef.current = true; // Prevent any other win condition
      setIsDraw(true);
      setDrawType('spinFinish');
      return;
    }
    
    // P1 stopped, P2 still spinning - P2 wins by Spin Finish (1 point)
    // Only if P1 still has health > 0 (otherwise it should be burst finish, not spin finish)
    if (stoppedSpinning1 && !stoppedSpinning2 && stamina2 > 0 && !top1KnockedOut && top1Active && health1 > 0) {
      scoreAwardedRef.current = true; // Prevent duplicate scoring
      setRoundWinner(2);
      setWinType('spinFinish');
      setPlayer2Score(prev => prev + WIN_POINTS.spinFinish); // Spin Finish: 1 point
    }
    
    // P2 stopped, P1 still spinning - P1 wins by Spin Finish (1 point)
    // Only if P2 still has health > 0 (otherwise it should be burst finish, not spin finish)
    if (stoppedSpinning2 && !stoppedSpinning1 && stamina1 > 0 && !top2KnockedOut && top2Active && health2 > 0) {
      scoreAwardedRef.current = true; // Prevent duplicate scoring
      setRoundWinner(1);
      setWinType('spinFinish');
      setPlayer1Score(prev => prev + WIN_POINTS.spinFinish); // Spin Finish: 1 point
    }
  }, [stamina1, stamina2, stoppedSpinning1, stoppedSpinning2, roundWinner, isDraw, top1KnockedOut, top2KnockedOut, top1Active, top2Active, health1, health2]);

  // Clear shadow clones when Shinobi Shadow is defeated (knockout or burst finish)
  useEffect(() => {
    // P1 defeated - clear P1's shadow clones
    if (top1KnockedOut && shadowClonesActive1) {
      setShadowClones1([]);
      setShadowClonesActive1(false);
      if (shadowCloneTimer1Ref.current) {
        clearTimeout(shadowCloneTimer1Ref.current);
        shadowCloneTimer1Ref.current = null;
      }
    }
  }, [top1KnockedOut, shadowClonesActive1]);

  useEffect(() => {
    // P2 defeated - clear P2's shadow clones
    if (top2KnockedOut && shadowClonesActive2) {
      setShadowClones2([]);
      setShadowClonesActive2(false);
      if (shadowCloneTimer2Ref.current) {
        clearTimeout(shadowCloneTimer2Ref.current);
        shadowCloneTimer2Ref.current = null;
      }
    }
  }, [top2KnockedOut, shadowClonesActive2]);

  // CLASH LOCK: Hold both beyblades in place when locked together
  useEffect(() => {
    if (!clashLocked) return;
    
    const lockInterval = setInterval(() => {
      // Hold P1 at its position when clash started
      setTop1Pos(clashLockP1Pos);
      
      // Hold P2 at its position when clash started (LOCAL MODE ONLY)
      if (!isOnlineMode || !matchFound) {
        setTop2Pos(clashLockP2Pos);
      }
    }, 16); // 60fps to override any other movement
    
    return () => clearInterval(lockInterval);
  }, [clashLocked, clashLockP1Pos, clashLockP2Pos, isOnlineMode, matchFound]);

  // Generate orange sparks during clash lock
  useEffect(() => {
    if (!clashLocked) {
      setClashSparks([]); // Clear sparks when clash ends
      return;
    }
    
    // Calculate center point between the two beyblades
    const centerX = (clashLockP1Pos.x + clashLockP2Pos.x) / 2;
    const centerY = (clashLockP1Pos.y + clashLockP2Pos.y) / 2;
    
    const sparkInterval = setInterval(() => {
      // Generate 2-4 sparks per frame
      const newSparks = [];
      const sparkCount = 2 + Math.floor(Math.random() * 3);
      
      for (let i = 0; i < sparkCount; i++) {
        const angle = Math.random() * Math.PI * 2;
        const speed = 2 + Math.random() * 4;
        const spread = 10; // Random offset from center
        
        newSparks.push({
          id: Date.now() + Math.random(),
          x: centerX + (Math.random() - 0.5) * spread,
          y: centerY + (Math.random() - 0.5) * spread,
          vx: Math.cos(angle) * speed,
          vy: Math.sin(angle) * speed,
          life: 1, // 1 = full life, 0 = dead
          size: 3 + Math.random() * 4,
        });
      }
      
      setClashSparks(prev => [...prev, ...newSparks]);
    }, 50); // Generate sparks every 50ms
    
    // Update spark positions and fade them out
    const updateInterval = setInterval(() => {
      setClashSparks(prev => 
        prev
          .map(spark => ({
            ...spark,
            x: spark.x + spark.vx,
            y: spark.y + spark.vy,
            vy: spark.vy + 0.2, // Gravity
            life: spark.life - 0.05,
          }))
          .filter(spark => spark.life > 0)
      );
    }, 16);
    
    return () => {
      clearInterval(sparkInterval);
      clearInterval(updateInterval);
    };
  }, [clashLocked, clashLockP1Pos, clashLockP2Pos]);

  // BX-Infinity Xtreme Dash Line Movement - Player 1
  // This useEffect handles movement along the CURVED infinity path between trigger points
  useEffect(() => {
    if (!xtremeDashLine1Active || !xtremeDashLine1Color) return;
    
    // Scale factor to convert trigger points from 800x600 base to actual canvas size
    const scaleX = CANVAS_WIDTH / BASE_CANVAS_WIDTH;
    const scaleY = CANVAS_HEIGHT / BASE_CANVAS_HEIGHT;
    
    // Get the two endpoints for this color (scaled to current canvas size)
    const topPoint = xtremeDashLine1Color === 'blue' 
      ? { x: xtremeDashPoints.blueTop.x * scaleX, y: xtremeDashPoints.blueTop.y * scaleY }
      : { x: xtremeDashPoints.redTop.x * scaleX, y: xtremeDashPoints.redTop.y * scaleY };
    const bottomPoint = xtremeDashLine1Color === 'blue'
      ? { x: xtremeDashPoints.blueBottom.x * scaleX, y: xtremeDashPoints.blueBottom.y * scaleY }
      : { x: xtremeDashPoints.redBottom.x * scaleX, y: xtremeDashPoints.redBottom.y * scaleY };
    
    // Control points for the bezier curves (matching the SVG path)
    // Blue curves right first, Red curves left first
    // Reduced curve offset (80 -> 53 -> 40 -> 30 -> 22)
    const controlPointRight = { x: (CANVAS_WIDTH / 2 + 22) * scaleX, y: (CANVAS_HEIGHT / 2) * scaleY };
    const controlPointLeft = { x: (CANVAS_WIDTH / 2 - 22) * scaleX, y: (CANVAS_HEIGHT / 2) * scaleY };
    
    // Track position along the curve (0 to 1 for each half)
    let t = 0;
    // Use beyblade's speed stat to determine dash speed (same formula as orbital speed with speed boost)
    const beybladeSpeedStat1 = getBeybladeStats(selectedBeyblade1?.id)?.speed || 50;
    const effectiveSpeed1 = (finalDriveActive1 && hasFinalDrive(selectedBeyblade1?.id)) ? 200 : beybladeSpeedStat1;
    const speedStatModifier1 = 1 + (effectiveSpeed1 - 50) / 100;
    const CURVE_SPEED = 0.067 * 1.5 * speedStatModifier1; // Base speed * speed boost multiplier * beyblade speed modifier
    
    // Quadratic bezier curve function: B(t) = (1-t)²P0 + 2(1-t)tP1 + t²P2
    const quadraticBezier = (p0, p1, p2, t) => {
      const oneMinusT = 1 - t;
      return {
        x: oneMinusT * oneMinusT * p0.x + 2 * oneMinusT * t * p1.x + t * t * p2.x,
        y: oneMinusT * oneMinusT * p0.y + 2 * oneMinusT * t * p1.y + t * t * p2.y
      };
    };
    
    // Calculate center and outer radius once at the start of this effect
    // BX-Infinity uses true center (CANVAS_HEIGHT / 2)
    const centerX = CANVAS_WIDTH / 2;
    const centerY = CANVAS_HEIGHT / 2; // BX-Infinity uses true center
    const scaleFactor = Math.min(CANVAS_WIDTH / 800, CANVAS_HEIGHT / 600);
    const outerRadius = 145 * scaleFactor; // Outer orbital ring radius
    
    const dashInterval = setInterval(() => {
      // Check if we should stop (speed boost ended or dash deactivated)
      if (!speedBoost1Ref.current || !xtremeDashLine1Ref.current) {
        setXtremeDashLine1Active(false);
        setXtremeDashLine1Color(null);
        
        // Start smooth transition to outer ring instead of instant snap
        setTop1Pos(prev => {
          // Calculate angle from center to current position
          const dx = prev.x - centerX;
          const dy = prev.y - centerY;
          const angle = Math.atan2(dy, dx);
          
          // Closest point on outer ring is at the same angle from center
          const targetX = centerX + Math.cos(angle) * outerRadius;
          const targetY = centerY + Math.sin(angle) * outerRadius;
          
          // Set up smooth transition (300ms duration)
          const transitionData = {
            startPos: { x: prev.x, y: prev.y },
            targetPos: { x: targetX, y: targetY },
            startTime: Date.now(),
            duration: 300 // milliseconds
          };
          setXtremeDashTransition1(transitionData);
          xtremeDashTransition1Ref.current = transitionData;
          
          
          return prev; // Don't move yet, transition useEffect will handle it
        });
        return;
      }
      
      // Increment t
      t += CURVE_SPEED;
      
      // If we've completed one curve segment, switch to the other
      if (t >= 1) {
        t = 0;
        // Update ref immediately to prevent flicker, then sync state
        xtremeDashLine1GoingDownRef.current = !xtremeDashLine1GoingDownRef.current;
        setXtremeDashLine1GoingDown(xtremeDashLine1GoingDownRef.current);
      }
      
      const goingDown = xtremeDashLine1GoingDownRef.current;
      
      // Calculate position along the bezier curve
      // Use OPPOSITE curvature first - if visual goes right, beyblade curves left first
      let newPos;
      if (goingDown) {
        // Going from top to bottom - use the OPPOSITE curve (left for blue, right for red)
        const controlPoint = xtremeDashLine1Color === 'blue' ? controlPointLeft : controlPointRight;
        newPos = quadraticBezier(topPoint, controlPoint, bottomPoint, t);
      } else {
        // Going from bottom to top - use the other curve
        const controlPoint = xtremeDashLine1Color === 'blue' ? controlPointRight : controlPointLeft;
        newPos = quadraticBezier(bottomPoint, controlPoint, topPoint, t);
      }
      
      setTop1Pos(newPos);
    }, 16); // 60fps
    
    return () => clearInterval(dashInterval);
  }, [xtremeDashLine1Active, xtremeDashLine1Color, CANVAS_WIDTH, CANVAS_HEIGHT]);
  
  // BX-Infinity Xtreme Dash Line Movement - Player 2
  // BX-Infinity Xtreme Dash Line Movement - Player 2
  // This useEffect handles movement along the CURVED infinity path between trigger points
  useEffect(() => {
    if (!xtremeDashLine2Active || !xtremeDashLine2Color) return;
    
    // Scale factor to convert trigger points from 800x600 base to actual canvas size
    const scaleX = CANVAS_WIDTH / BASE_CANVAS_WIDTH;
    const scaleY = CANVAS_HEIGHT / BASE_CANVAS_HEIGHT;
    
    // Get the two endpoints for this color (scaled to current canvas size)
    const topPoint = xtremeDashLine2Color === 'blue' 
      ? { x: xtremeDashPoints.blueTop.x * scaleX, y: xtremeDashPoints.blueTop.y * scaleY }
      : { x: xtremeDashPoints.redTop.x * scaleX, y: xtremeDashPoints.redTop.y * scaleY };
    const bottomPoint = xtremeDashLine2Color === 'blue'
      ? { x: xtremeDashPoints.blueBottom.x * scaleX, y: xtremeDashPoints.blueBottom.y * scaleY }
      : { x: xtremeDashPoints.redBottom.x * scaleX, y: xtremeDashPoints.redBottom.y * scaleY };
    
    // Control points for the bezier curves
    // Reduced curve offset (80 -> 53 -> 40 -> 30 -> 22)
    const controlPointRight = { x: (CANVAS_WIDTH / 2 + 22) * scaleX, y: (CANVAS_HEIGHT / 2) * scaleY };
    const controlPointLeft = { x: (CANVAS_WIDTH / 2 - 22) * scaleX, y: (CANVAS_HEIGHT / 2) * scaleY };
    
    // Track position along the curve (0 to 1 for each half)
    let t = 0;
    // Use beyblade's speed stat to determine dash speed (same formula as orbital speed with speed boost)
    const beybladeSpeedStat2 = getBeybladeStats(selectedBeyblade2?.id)?.speed || 50;
    const effectiveSpeed2 = (finalDriveActive2 && hasFinalDrive(selectedBeyblade2?.id)) ? 200 : beybladeSpeedStat2;
    const speedStatModifier2 = 1 + (effectiveSpeed2 - 50) / 100;
    const CURVE_SPEED = 0.067 * 1.5 * speedStatModifier2; // Base speed * speed boost multiplier * beyblade speed modifier
    
    // Quadratic bezier curve function: B(t) = (1-t)²P0 + 2(1-t)tP1 + t²P2
    const quadraticBezier = (p0, p1, p2, t) => {
      const oneMinusT = 1 - t;
      return {
        x: oneMinusT * oneMinusT * p0.x + 2 * oneMinusT * t * p1.x + t * t * p2.x,
        y: oneMinusT * oneMinusT * p0.y + 2 * oneMinusT * t * p1.y + t * t * p2.y
      };
    };
    
    // Calculate center and outer radius once at the start of this effect
    // BX-Infinity uses true center (CANVAS_HEIGHT / 2)
    const centerX = CANVAS_WIDTH / 2;
    const centerY = CANVAS_HEIGHT / 2; // BX-Infinity uses true center
    const scaleFactor = Math.min(CANVAS_WIDTH / 800, CANVAS_HEIGHT / 600);
    const outerRadius = 145 * scaleFactor; // Outer orbital ring radius
    
    const dashInterval = setInterval(() => {
      // Check if we should stop (speed boost ended or dash deactivated)
      if (!speedBoost2Ref.current || !xtremeDashLine2Ref.current) {
        setXtremeDashLine2Active(false);
        setXtremeDashLine2Color(null);
        
        // Start smooth transition to outer ring instead of instant snap
        setTop2Pos(prev => {
          // Calculate angle from center to current position
          const dx = prev.x - centerX;
          const dy = prev.y - centerY;
          const angle = Math.atan2(dy, dx);
          
          // Closest point on outer ring is at the same angle from center
          const targetX = centerX + Math.cos(angle) * outerRadius;
          const targetY = centerY + Math.sin(angle) * outerRadius;
          
          // Set up smooth transition (300ms duration)
          const transitionData = {
            startPos: { x: prev.x, y: prev.y },
            targetPos: { x: targetX, y: targetY },
            startTime: Date.now(),
            duration: 300 // milliseconds
          };
          setXtremeDashTransition2(transitionData);
          xtremeDashTransition2Ref.current = transitionData;
          
          
          return prev; // Don't move yet, transition useEffect will handle it
        });
        return;
      }
      
      // Increment t
      t += CURVE_SPEED;
      
      // If we've completed one curve segment, switch to the other
      if (t >= 1) {
        t = 0;
        // Update ref immediately to prevent flicker, then sync state
        xtremeDashLine2GoingDownRef.current = !xtremeDashLine2GoingDownRef.current;
        setXtremeDashLine2GoingDown(xtremeDashLine2GoingDownRef.current);
      }
      
      const goingDown = xtremeDashLine2GoingDownRef.current;
      
      // Calculate position along the bezier curve
      // Use OPPOSITE curvature first - if visual goes right, beyblade curves left first
      let newPos;
      if (goingDown) {
        // Going from top to bottom - use the OPPOSITE curve (left for blue, right for red)
        const controlPoint = xtremeDashLine2Color === 'blue' ? controlPointLeft : controlPointRight;
        newPos = quadraticBezier(topPoint, controlPoint, bottomPoint, t);
      } else {
        // Going from bottom to top - use the other curve
        const controlPoint = xtremeDashLine2Color === 'blue' ? controlPointRight : controlPointLeft;
        newPos = quadraticBezier(bottomPoint, controlPoint, topPoint, t);
      }
      
      setTop2Pos(newPos);
    }, 16); // 60fps
    
    return () => clearInterval(dashInterval);
  }, [xtremeDashLine2Active, xtremeDashLine2Color, CANVAS_WIDTH, CANVAS_HEIGHT]);

  // BX-Infinity: Collision detection between two beyblades both doing Xtreme Dash
  // When they collide, both are knocked back and their dashes are cancelled
  useEffect(() => {
    // Only check if both beyblades are actively dashing
    if (!xtremeDashLine1Active || !xtremeDashLine2Active) return;
    
    const collisionCheckInterval = setInterval(() => {
      // Check if both are still dashing (use refs for current values)
      if (!xtremeDashLine1Ref.current || !xtremeDashLine2Ref.current) return;
      
      // Get current positions
      const pos1 = top1Pos;
      const pos2 = top2Pos;
      
      // Calculate distance between beyblades
      const dx = pos1.x - pos2.x;
      const dy = pos1.y - pos2.y;
      const distance = Math.sqrt(dx * dx + dy * dy);
      
      // Collision hitbox (same as regular collision)
      const scaleFactor = Math.min(CANVAS_WIDTH / 800, CANVAS_HEIGHT / 600);
      const hitboxSize = 50 * scaleFactor;
      
      if (distance < hitboxSize && distance > 0) {
        
        // Calculate knockback direction (normalized)
        const nx = dx / distance;
        const ny = dy / distance;
        
        // Strong knockback force for dramatic effect
        const knockbackForce = 25;
        
        // Cancel both Xtreme Dashes
        setXtremeDashLine1Active(false);
        setXtremeDashLine1Color(null);
        setXtremeDashLine2Active(false);
        setXtremeDashLine2Color(null);
        
        // Apply knockback to both beyblades (opposite directions)
        setKnockbackVelocity1({ x: nx * knockbackForce, y: ny * knockbackForce });
        setKnockbackVelocity2({ x: -nx * knockbackForce, y: -ny * knockbackForce });
        
        // Add clash effect at collision point
        const clashX = (pos1.x + pos2.x) / 2;
        const clashY = (pos1.y + pos2.y) / 2;
        setClashEffects(prev => [...prev, { x: clashX, y: clashY, id: Date.now() }]);
        
      }
    }, 16); // 60fps
    
    return () => clearInterval(collisionCheckInterval);
  }, [xtremeDashLine1Active, xtremeDashLine2Active, top1Pos, top2Pos, CANVAS_WIDTH, CANVAS_HEIGHT]);

  // Smooth transition animation for P1 returning to outer ring after Xtreme Dash
  useEffect(() => {
    if (!xtremeDashTransition1) return;
    
    const { startPos, startTime, duration } = xtremeDashTransition1;
    
    // Calculate center point for BX-Infinity
    const centerX = CANVAS_WIDTH / 2;
    const centerY = CANVAS_HEIGHT / 2;
    const scaleFactor = Math.min(CANVAS_WIDTH / 800, CANVAS_HEIGHT / 600);
    const outerRadius = 145 * scaleFactor;
    
    // Calculate starting polar coordinates (angle and radius from center)
    const startDx = startPos.x - centerX;
    const startDy = startPos.y - centerY;
    const startAngle = Math.atan2(startDy, startDx);
    const startRadius = Math.sqrt(startDx * startDx + startDy * startDy);
    
    // Get spin direction for rotation during transition
    let spinDirection = selectedBeyblade1?.spinDirection || 'right';
    if (spinDirection === 'dual') {
      spinDirection = dualSpinDirection1Ref.current || 'right';
    }
    const rotationDirection = spinDirection === 'left' ? 1 : -1;
    
    // Easing function for smooth deceleration (ease-out quad)
    const easeOutQuad = (t) => t * (2 - t);
    
    // Get orbital speed for seamless handoff
    const beybladeSpeedStat = getBeybladeStats(selectedBeyblade1?.id)?.speed || 50;
    // Final Drive overrides speed to 200% for Pegasus beyblades
    const effectiveSpeed = (finalDriveActive1 && hasFinalDrive(selectedBeyblade1?.id)) ? 200 : beybladeSpeedStat;
    const speedStatModifier = 1 + (effectiveSpeed - 50) / 100;
    const orbitalSpeed = 0.067 * speedMultiplier1 * speedStatModifier;
    
    let currentAngle = startAngle;
    let hasReachedOrbit = false;
    
    const transitionInterval = setInterval(() => {
      const elapsed = Date.now() - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const easedProgress = easeOutQuad(progress);
      
      // Spiral outward: interpolate radius from start to outer ring
      const currentRadius = startRadius + (outerRadius - startRadius) * easedProgress;
      
      if (progress < 1) {
        // Still spiraling - rotate about 90 degrees over the duration
        const rotationAmount = (Math.PI / 2) * easedProgress * rotationDirection;
        currentAngle = startAngle + rotationAmount;
      } else {
        // Reached orbit - continue at orbital speed for seamless handoff
        if (!hasReachedOrbit) {
          hasReachedOrbit = true;
        }
        // Continue rotating at orbital speed
        currentAngle += orbitalSpeed * rotationDirection;
        
        // Clear transition after a few frames of orbital motion
        if (elapsed > duration + 50) {
          setXtremeDashTransition1(null);
          xtremeDashTransition1Ref.current = null;
          return;
        }
      }
      
      // Calculate new position using polar coordinates
      const newX = centerX + Math.cos(currentAngle) * currentRadius;
      const newY = centerY + Math.sin(currentAngle) * currentRadius;
      
      setTop1Pos({ x: newX, y: newY });
    }, 16); // 60fps
    
    return () => clearInterval(transitionInterval);
  }, [xtremeDashTransition1, CANVAS_WIDTH, CANVAS_HEIGHT, selectedBeyblade1?.spinDirection]);

  // Smooth transition animation for P2 returning to outer ring after Xtreme Dash
  useEffect(() => {
    if (!xtremeDashTransition2) return;
    
    const { startPos, startTime, duration } = xtremeDashTransition2;
    
    // Calculate center point for BX-Infinity
    const centerX = CANVAS_WIDTH / 2;
    const centerY = CANVAS_HEIGHT / 2;
    const scaleFactor = Math.min(CANVAS_WIDTH / 800, CANVAS_HEIGHT / 600);
    const outerRadius = 145 * scaleFactor;
    
    // Calculate starting polar coordinates (angle and radius from center)
    const startDx = startPos.x - centerX;
    const startDy = startPos.y - centerY;
    const startAngle = Math.atan2(startDy, startDx);
    const startRadius = Math.sqrt(startDx * startDx + startDy * startDy);
    
    // Get spin direction for rotation during transition
    let spinDirection = selectedBeyblade2?.spinDirection || 'right';
    if (spinDirection === 'dual') {
      spinDirection = dualSpinDirection2Ref.current || 'right';
    }
    const rotationDirection = spinDirection === 'left' ? 1 : -1;
    
    // Easing function for smooth deceleration (ease-out quad)
    const easeOutQuad = (t) => t * (2 - t);
    
    // Get orbital speed for seamless handoff
    const beybladeSpeedStat = getBeybladeStats(selectedBeyblade2?.id)?.speed || 50;
    // Final Drive overrides speed to 200% for Pegasus beyblades
    const effectiveSpeed = (finalDriveActive2 && hasFinalDrive(selectedBeyblade2?.id)) ? 200 : beybladeSpeedStat;
    const speedStatModifier = 1 + (effectiveSpeed - 50) / 100;
    const orbitalSpeed = 0.067 * speedMultiplier2 * speedStatModifier;
    
    let currentAngle = startAngle;
    let hasReachedOrbit = false;
    
    const transitionInterval = setInterval(() => {
      const elapsed = Date.now() - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const easedProgress = easeOutQuad(progress);
      
      // Spiral outward: interpolate radius from start to outer ring
      const currentRadius = startRadius + (outerRadius - startRadius) * easedProgress;
      
      if (progress < 1) {
        // Still spiraling - rotate about 90 degrees over the duration
        const rotationAmount = (Math.PI / 2) * easedProgress * rotationDirection;
        currentAngle = startAngle + rotationAmount;
      } else {
        // Reached orbit - continue at orbital speed for seamless handoff
        if (!hasReachedOrbit) {
          hasReachedOrbit = true;
        }
        // Continue rotating at orbital speed
        currentAngle += orbitalSpeed * rotationDirection;
        
        // Clear transition after a few frames of orbital motion
        if (elapsed > duration + 50) {
          setXtremeDashTransition2(null);
          xtremeDashTransition2Ref.current = null;
          return;
        }
      }
      
      // Calculate new position using polar coordinates
      const newX = centerX + Math.cos(currentAngle) * currentRadius;
      const newY = centerY + Math.sin(currentAngle) * currentRadius;
      
      setTop2Pos({ x: newX, y: newY });
    }, 16); // 60fps
    
    return () => clearInterval(transitionInterval);
  }, [xtremeDashTransition2, CANVAS_WIDTH, CANVAS_HEIGHT, selectedBeyblade2?.spinDirection]);

  // Automatic circular motion for Beyblade 1 (MY beyblade)
  useEffect(() => {
    if (!top1Active || isControlling1 || (knockbackVelocity1.x !== 0 || knockbackVelocity1.y !== 0)) {
      return; // Don't auto-move if knocked back
    }

    // In online mode, P1 is ALWAYS my beyblade - physics should run for ALL players
    // (myPlayerNumber is just the server room slot, not which beyblade to control)

    const moveInterval = setInterval(() => {
      if (isControlling1) return;
      
      // Skip auto-move if clash locked (handled by dedicated useEffect)
      if (clashLocked) return;
      
      // Skip auto-move if rewind freeze is active (use ref for current value in interval)
      if (rewindFreezeActiveRef.current) return;
      
      // Skip auto-move if beyblade stopped spinning (ran out of stamina)
      if (stoppedSpinning1) return;
      
      // Skip auto-move if Xtreme Dash line is active (BX-Infinity)
      if (xtremeDashLine1Ref.current) return;
      
      // Skip auto-move if transitioning back from Xtreme Dash
      if (xtremeDashTransition1Ref.current) return;

      const centerX = STADIUM_CENTER_X;
      const centerY = STADIUM_CENTER_Y;
      // Get beyblade's speed stat (default 50), calculate speed modifier
      // Speed 50 = 100% (1.0), Speed 60 = 110% (1.1), etc.
      const beybladeSpeedStat = getBeybladeStats(selectedBeyblade1?.id)?.speed || 50;
      // Final Drive overrides speed to 200% for Pegasus beyblades
      const effectiveSpeed = (finalDriveActive1 && hasFinalDrive(selectedBeyblade1?.id)) ? 200 : beybladeSpeedStat;
      const speedStatModifier = 1 + (effectiveSpeed - 50) / 100;
      const speed = 0.067 * speedMultiplier1 * speedStatModifier;

      setTop1Pos((prev) => {
        const dx = prev.x - centerX;
        const dy = prev.y - centerY;
        const currentAngle = Math.atan2(dy, dx);
        const distanceFromCenter = Math.sqrt(dx * dx + dy * dy);
        
        // Find the nearest orbital ring
        // For BX-Infinity stadium: use outer ring unless beyblade is already in center (placed there via manual control)
        let targetRadius;
        if (selectedStadium === 'bxInfinity') {
          // If beyblade is close to center (placed there manually), let it stay
          if (distanceFromCenter < 50 * SCALE_FACTOR) {
            targetRadius = 0; // Stay in center
          } else {
            targetRadius = ORBITAL_RINGS[1]; // Use outer ring
          }
        } else {
          targetRadius = findNearestRing(distanceFromCenter);
        }
        const distanceToRing = Math.abs(distanceFromCenter - targetRadius);

        let newX, newY;

        // If at center ring (radius 0), stay in center with slight wobble
        if (targetRadius === 0 && distanceToRing < 10) {
          // Stay near center with small random movement
          newX = centerX + (Math.random() - 0.5) * 2;
          newY = centerY + (Math.random() - 0.5) * 2;
          setTop1Velocity({ x: 0, y: 0 });
        } else if (distanceToRing > 5) {
          // Move towards the nearest ring
          const nearestX = targetRadius === 0 ? centerX : centerX + targetRadius * Math.cos(currentAngle);
          const nearestY = targetRadius === 0 ? centerY : centerY + targetRadius * Math.sin(currentAngle);
          const toRingX = nearestX - prev.x;
          const toRingY = nearestY - prev.y;
          const blendFactor = 0.15;
          const newVelX = top1Velocity.x * (1 - blendFactor) + toRingX * blendFactor;
          const newVelY = top1Velocity.y * (1 - blendFactor) + toRingY * blendFactor;
          setTop1Velocity({ x: newVelX, y: newVelY });
          newX = prev.x + newVelX;
          newY = prev.y + newVelY;
        } else {
          // Orbit on the ring
          // Direction based on selected beyblade (for dual-spin, use current direction from ref)
          let orbitDirection;
          if (selectedBeyblade1?.spinDirection === 'dual') {
            orbitDirection = dualSpinDirection1Ref.current === 'left' ? 'left' : 'right';
          } else {
            orbitDirection = selectedBeyblade1?.spinDirection;
          }
          const directionMultiplier = orbitDirection === 'left' ? 1 : -1; // left = clockwise (+), right = counter-clockwise (-)
          const newAngle = currentAngle + (speed * directionMultiplier);
          newX = centerX + targetRadius * Math.cos(newAngle);
          newY = centerY + targetRadius * Math.sin(newAngle);
          const tangentVelX = targetRadius * speed * Math.sin(newAngle) * -directionMultiplier;
          const tangentVelY = -targetRadius * speed * Math.cos(newAngle) * -directionMultiplier;
          setTop1Velocity({ x: tangentVelX, y: tangentVelY });
        }

        // Check wall collision
        const afterWallCollision = applyWallCollision1(newX, newY, prev.x, prev.y);
        
        // Check Beyblade collision
        const finalPos = handleBeybladeCollision(afterWallCollision, top2Pos, isControlling1, isControlling2, top1KnockedOut, top2KnockedOut);
        
        // Apply knockback if attacking
        applyKnockbackOnHit(finalPos, top2Pos, isControlling1, isControlling2);
        
        return finalPos;
      });
    }, 16); // 60fps for smooth movement

    return () => clearInterval(moveInterval);
  }, [top1Active, isControlling1, top1Velocity, knockbackVelocity1, selectedBeyblade1, canvasDimensions, BEYBLADE_RADIUS, isOnlineMode, matchFound, myPlayerNumber]);

  // Ref for target position to avoid effect restart on every update
  const top2TargetPosRef = useRef(top2TargetPos);
  useEffect(() => { top2TargetPosRef.current = top2TargetPos; }, [top2TargetPos]);
  
  // Real-time position sync for opponent's beyblade in online mode
  useEffect(() => {
    if (!isOnlineMode || !matchFound || !top2Active) return;
    
    // Very high lerp speed for near-instant tracking (0.85 = 85% per frame)
    // At 60fps, this catches up to target in ~3 frames (~50ms)
    const LERP_SPEED = 0.85;
    
    const interpolateInterval = setInterval(() => {
      setTop2Pos(prev => {
        const target = top2TargetPosRef.current;
        const dx = target.x - prev.x;
        const dy = target.y - prev.y;
        const distance = Math.sqrt(dx * dx + dy * dy);
        
        // If close enough, snap to exact position (no jitter)
        if (distance < 5) return target;
        
        // Fast interpolation for real-time feel
        return {
          x: prev.x + dx * LERP_SPEED,
          y: prev.y + dy * LERP_SPEED
        };
      });
    }, 16); // 60fps interpolation
    
    return () => clearInterval(interpolateInterval);
  }, [isOnlineMode, matchFound, top2Active]);
  
  // Automatic circular motion for Beyblade 2 (LOCAL MODE ONLY)
  useEffect(() => {
    if (!top2Active || isControlling2 || (knockbackVelocity2.x !== 0 || knockbackVelocity2.y !== 0)) {
      return; // Don't auto-move if knocked back
    }

    // Skip local physics for Player 2 in online mode - P2 is ALWAYS the opponent (synced from server)
    if (isOnlineMode && matchFound) {
      return;
    }

    const moveInterval = setInterval(() => {
      if (isControlling2) return;
      
      // Skip auto-move if clash locked (handled by dedicated useEffect)
      if (clashLocked) return;
      
      // Skip auto-move if rewind freeze is active (use ref for current value in interval)
      if (rewindFreezeActiveRef.current) return;
      
      // Skip auto-move if beyblade stopped spinning (ran out of stamina)
      if (stoppedSpinning2) return;
      
      // Skip auto-move if Xtreme Dash line is active (BX-Infinity)
      if (xtremeDashLine2Ref.current) return;
      
      // Skip auto-move if transitioning back from Xtreme Dash
      if (xtremeDashTransition2Ref.current) return;

      const centerX = STADIUM_CENTER_X;
      const centerY = STADIUM_CENTER_Y;
      // Get beyblade's speed stat (default 50), calculate speed modifier
      // Speed 50 = 100% (1.0), Speed 60 = 110% (1.1), etc.
      const beybladeSpeedStat = getBeybladeStats(selectedBeyblade2?.id)?.speed || 50;
      // Final Drive overrides speed to 200% for Pegasus beyblades
      const effectiveSpeed = (finalDriveActive2 && hasFinalDrive(selectedBeyblade2?.id)) ? 200 : beybladeSpeedStat;
      const speedStatModifier = 1 + (effectiveSpeed - 50) / 100;
      const speed = 0.067 * speedMultiplier2 * speedStatModifier;

      setTop2Pos((prev) => {
        const dx = prev.x - centerX;
        const dy = prev.y - centerY;
        const currentAngle = Math.atan2(dy, dx);
        const distanceFromCenter = Math.sqrt(dx * dx + dy * dy);
        
        // Find the nearest orbital ring
        // For BX-Infinity stadium: use outer ring unless beyblade is already in center (placed there via manual control)
        let targetRadius;
        if (selectedStadium === 'bxInfinity') {
          // If beyblade is close to center (placed there manually), let it stay
          if (distanceFromCenter < 50 * SCALE_FACTOR) {
            targetRadius = 0; // Stay in center
          } else {
            targetRadius = ORBITAL_RINGS[1]; // Use outer ring
          }
        } else {
          targetRadius = findNearestRing(distanceFromCenter);
        }
        const distanceToRing = Math.abs(distanceFromCenter - targetRadius);

        let newX, newY;

        // If at center ring (radius 0), stay in center with slight wobble
        if (targetRadius === 0 && distanceToRing < 10) {
          // Stay near center with small random movement
          newX = centerX + (Math.random() - 0.5) * 2;
          newY = centerY + (Math.random() - 0.5) * 2;
          setTop2Velocity({ x: 0, y: 0 });
        } else if (distanceToRing > 5) {
          // Move towards the nearest ring
          const nearestX = targetRadius === 0 ? centerX : centerX + targetRadius * Math.cos(currentAngle);
          const nearestY = targetRadius === 0 ? centerY : centerY + targetRadius * Math.sin(currentAngle);
          const toRingX = nearestX - prev.x;
          const toRingY = nearestY - prev.y;
          const blendFactor = 0.15;
          const newVelX = top2Velocity.x * (1 - blendFactor) + toRingX * blendFactor;
          const newVelY = top2Velocity.y * (1 - blendFactor) + toRingY * blendFactor;
          setTop2Velocity({ x: newVelX, y: newVelY });
          newX = prev.x + newVelX;
          newY = prev.y + newVelY;
        } else {
          // Orbit on the ring
          // Direction based on selected beyblade (for dual-spin, use current direction from ref)
          let orbitDirection;
          if (selectedBeyblade2?.spinDirection === 'dual') {
            orbitDirection = dualSpinDirection2Ref.current === 'left' ? 'left' : 'right';
          } else {
            orbitDirection = selectedBeyblade2?.spinDirection;
          }
          const directionMultiplier = orbitDirection === 'left' ? 1 : -1; // left = clockwise (+), right = counter-clockwise (-)
          const newAngle = currentAngle + (speed * directionMultiplier);
          newX = centerX + targetRadius * Math.cos(newAngle);
          newY = centerY + targetRadius * Math.sin(newAngle);
          const tangentVelX = targetRadius * speed * Math.sin(newAngle) * -directionMultiplier;
          const tangentVelY = -targetRadius * speed * Math.cos(newAngle) * -directionMultiplier;
          setTop2Velocity({ x: tangentVelX, y: tangentVelY });
        }

        // Check wall collision
        const afterWallCollision = applyWallCollision2(newX, newY, prev.x, prev.y);
        
        // Check Beyblade collision
        const finalPos = handleBeybladeCollision(afterWallCollision, top1Pos, isControlling2, isControlling1, top2KnockedOut, top1KnockedOut);
        
        // Apply knockback if attacking - P2 attacking P1
        applyKnockbackOnHit(top1Pos, finalPos, isControlling1, isControlling2);
        
        return finalPos;
      });
    }, 16); // 60fps for smooth movement

    return () => clearInterval(moveInterval);
  }, [top2Active, isControlling2, top2Velocity, knockbackVelocity2, selectedBeyblade2, canvasDimensions, BEYBLADE_RADIUS, isOnlineMode, matchFound, myPlayerNumber]);

  // REMOVED: Character canvas drawing - walking character no longer exists

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d', { alpha: true, desynchronized: true });
    
    // Performance optimization: limit frame rate for smoother experience
    let lastFrameTime = 0;
    const targetFPS = 60;
    const frameDelay = 1000 / targetFPS;
    
    // Animation loop
    const animate = (currentTime) => {
      // Throttle to target FPS
      if (currentTime - lastFrameTime < frameDelay) {
        animationRef.current = requestAnimationFrame(animate);
        return;
      }
      lastFrameTime = currentTime;
      
      // Clear canvas with transparency
      ctx.clearRect(0, 0, CANVAS_WIDTH, CANVAS_HEIGHT);

      // Draw grid (transparent, won't cover stadium)
      drawGrid(ctx);

      // Draw nearby players
      drawNearbyPlayers(ctx);

      animationRef.current = requestAnimationFrame(animate);
    };

    animate(0);

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [nearbyPlayers]);

  // REMOVED: Animation frame cycling - walking character no longer exists

  // REMOVED: D-pad movement handler - D-pad and walking character no longer exist
  // Beyblades are now controlled only via touchpads

  // REMOVED: Keyboard controls for D-pad - D-pad no longer exists

  // HTTP Polling for online multiplayer (WebSocket not supported on platform)
  const matchmakingPollInterval = useRef(null);
  const gameStatePollInterval = useRef(null);
  const startHttpPollingRef = useRef(null); // Ref to call HTTP polling from WebSocket error handler
  
  // Track if we've already handled a match to prevent duplicate processing
  const matchHandledRef = useRef(false);
  
  const startMatchmakingPoll = () => {
    // Reset match handled flag when starting new search
    matchHandledRef.current = false;
    
    // Poll every 500ms for faster matching - also serves as heartbeat to keep user in queue
    matchmakingPollInterval.current = setInterval(async () => {
      // Skip if we've already handled a match
      if (matchHandledRef.current) {
        clearInterval(matchmakingPollInterval.current);
        matchmakingPollInterval.current = null;
        return;
      }
      
      try {
        const token = localStorage.getItem('token');
        const response = await fetch(`${BACKEND_URL}/api/battle/matchmaking/status`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        const data = await response.json();
        
        if (data.matched && !matchHandledRef.current) {
          // Mark as handled FIRST to prevent duplicate processing
          matchHandledRef.current = true;
          
          // Clear interval immediately
          clearInterval(matchmakingPollInterval.current);
          matchmakingPollInterval.current = null;
          
          
          // Prevent self-matching
          if (data.opponent_username === user?.username) {
            console.error('[Matchmaking Poll] Self-match detected! Cancelling...');
            alert('Cannot match with yourself! Please try again.');
            setIsSearching(false);
            setWaitingForOpponent(false);
            matchHandledRef.current = false; // Allow retry
            return;
          }
          
          setMatchFound(true);
          setOpponentUsername(data.opponent_username);
          setOpponentRosterIds(data.opponent_roster || []);
          setMyPlayerNumber(data.your_player_number);
          setShowMatchmaking(false);
          setIsSearching(false);
          setWaitingForOpponent(false);
          setMyScore(0);
          setOpponentScore(0);
          
          // Start game state polling IMMEDIATELY
          startGameStatePoll();
          
          // Show notification ONCE
          alert(`Match found! You're playing against ${data.opponent_username}!`);
        }
      } catch (error) {
        console.error('[Matchmaking Poll] Error:', error);
      }
    }, 500); // Poll every 500ms for faster matching
  };
  
  // Process opponent state update (shared between WebSocket and HTTP polling)
  const processOpponentState = useCallback((data) => {
    if (!data || typeof data !== 'object') return;
    
    // Skip if room not active (user not in a battle)
    if (data.active === false) return;
    
    // Update scores
    if (data.your_score !== undefined) setMyScore(data.your_score);
    if (data.opponent_score !== undefined) setOpponentScore(data.opponent_score);
    
    // Handle rematch status
    if (data.opponent_wants_rematch !== undefined) {
      setOpponentWantsRematch(data.opponent_wants_rematch);
    }
    
    // Handle round reset
    if (data.round_reset_pending) {
      setCurrentRound(data.round_number);
      setWaitingForRematch(false);
      setOpponentWantsRematch(false);
      setTop1KnockedOut(false);
      setTop2KnockedOut(false);
      setTop1Active(false);
      setTop2Active(false);
      setKnockbackVelocity1({ x: 0, y: 0 });
      setKnockbackVelocity2({ x: 0, y: 0 });
      setHealth1(getMaxHealth(selectedBeyblade1?.id));
      setHealth2(getMaxHealth(selectedBeyblade2?.id));
      setStamina1(getMaxStamina(selectedBeyblade1?.id));
      setStamina2(getMaxStamina(selectedBeyblade2?.id));
      setMaxStamina1(getMaxStamina(selectedBeyblade1?.id));
      setMaxStamina2(getMaxStamina(selectedBeyblade2?.id));
      setStoppedSpinning1(false);
      setStoppedSpinning2(false);
      setRoundWinner(null);
      setWinType(null);
      setIsPocketKO(false);
      setPocketKOType(null);
      setIsDraw(false);
      setDrawType(null);
      scoreAwardedRef.current = false; // Reset score award flag for new round
      setDeflection1(false);
      setDeflection2(false);
      setGhostMode1(false);
      setGhostMode2(false);
      setSpeedBoost1(false);
      setSpeedBoost2(false);
      setSpeedMultiplier1(1);
      setSpeedMultiplier2(1);
      setDeflectionCooldown1(false);
      setDeflectionCooldown2(false);
      setGhostCooldown1(false);
      setGhostCooldown2(false);
      // Reset clash lock
      setClashLocked(false);
      if (clashLockTimerRef.current) clearTimeout(clashLockTimerRef.current);
      setSpeedBoostCooldown1(false);
      setSpeedBoostCooldown2(false);
    }
    
    // Process opponent's beyblade state
    const oppState = data.opponent_state;
    if (oppState && Object.keys(oppState).length > 0) {
      // Set opponent's beyblade as active if they've launched
      if (oppState.active !== undefined) {
        // If opponent just launched (active becoming true), set position directly for immediate visibility
        if (oppState.active && !top2Active) {
          if (oppState.pos) {
            setTop2Pos(oppState.pos);
            setTop2TargetPos(oppState.pos);
          }
          if (oppState.speed) setTop2Speed(oppState.speed);
        }
        setTop2Active(oppState.active);
      }
      if (oppState.pos) {
        // Only set target position - let interpolation handle smooth movement
        setTop2TargetPos(oppState.pos);
      }
      if (oppState.rotation !== undefined) setTop2Rotation(oppState.rotation);
      if (oppState.speed !== undefined) setTop2Speed(oppState.speed);
      if (oppState.health !== undefined) setHealth2(oppState.health);
      if (oppState.knockbackVelocity) setKnockbackVelocity2(oppState.knockbackVelocity);
      if (oppState.isControlling !== undefined) setIsControlling2(oppState.isControlling);
      
      // Sync opponent's power-up states (so we can SEE their effects)
      if (oppState.deflection !== undefined) setDeflection2(oppState.deflection);
      if (oppState.ghostMode !== undefined) setGhostMode2(oppState.ghostMode);
      if (oppState.speedBoost !== undefined) {
        setSpeedBoost2(oppState.speedBoost);
        setSpeedMultiplier2(oppState.speedBoost ? 1.5 : 1);
      }
      if (oppState.deflectionCooldown !== undefined) setDeflectionCooldown2(oppState.deflectionCooldown);
      if (oppState.ghostCooldown !== undefined) setGhostCooldown2(oppState.ghostCooldown);
      if (oppState.speedBoostCooldown !== undefined) setSpeedBoostCooldown2(oppState.speedBoostCooldown);
      if (oppState.trackpadCooldown !== undefined) setCooldown2(oppState.trackpadCooldown);
      
      // Sync clash lock state from opponent
      if (oppState.clashLocked !== undefined && oppState.clashLocked && !clashLocked) {
        setClashLocked(true);
        if (oppState.clashLockPos) setClashLockPos(oppState.clashLockPos);
        // Note: In online mode, opponent's P1 is our P2 and vice versa
        if (oppState.clashLockP1Pos) setClashLockP2Pos(oppState.clashLockP1Pos); // Their P1 = our P2
        if (oppState.clashLockP2Pos) setClashLockP1Pos(oppState.clashLockP2Pos); // Their P2 = our P1
        // Auto-release after 1 second (in case our timer didn't trigger)
        if (clashLockTimerRef.current) clearTimeout(clashLockTimerRef.current);
        clashLockTimerRef.current = setTimeout(() => {
          setClashLocked(false);
        }, 1000); // 1 second clash duration
      }
      
      // Handle knockout
      if (oppState.knockedOut && !top2KnockedOut) {
        setTop2KnockedOut(true);
        setTop2Active(false);
        if (oppState.winType !== undefined) setWinType(oppState.winType);
        if (oppState.roundWinner === 2) setRoundWinner(1);
      }
    }
  }, [top2KnockedOut]);
  
  // Ref for clash lock state
  const clashLockedRef = useRef(clashLocked);
  const clashLockPosRef = useRef(clashLockPos);
  const clashLockP1PosRef = useRef(clashLockP1Pos);
  const clashLockP2PosRef = useRef(clashLockP2Pos);
  useEffect(() => { clashLockedRef.current = clashLocked; }, [clashLocked]);
  useEffect(() => { clashLockPosRef.current = clashLockPos; }, [clashLockPos]);
  useEffect(() => { clashLockP1PosRef.current = clashLockP1Pos; }, [clashLockP1Pos]);
  useEffect(() => { clashLockP2PosRef.current = clashLockP2Pos; }, [clashLockP2Pos]);
  
  // Get current game state for syncing (includes power-ups for opponent to see)
  const getMyState = useCallback(() => ({
    active: top1ActiveRef.current,
    pos: top1PosRef.current,
    rotation: top1RotationRef.current,
    speed: top1SpeedRef.current,
    health: health1Ref.current,
    knockbackVelocity: knockbackVelocity1Ref.current,
    knockedOut: top1KnockedOutRef.current,
    winType: winTypeRef.current,
    roundWinner: roundWinnerRef.current,
    isControlling: isControlling1Ref.current,
    // Power-up states for opponent to see visually
    deflection: deflection1Ref.current,
    ghostMode: ghostMode1Ref.current,
    speedBoost: speedBoost1Ref.current,
    deflectionCooldown: deflectionCooldown1Ref.current,
    ghostCooldown: ghostCooldown1Ref.current,
    speedBoostCooldown: speedBoostCooldown1Ref.current,
    trackpadCooldown: cooldown1Ref.current,
    // Clash lock state
    clashLocked: clashLockedRef.current,
    clashLockPos: clashLockPosRef.current,
    clashLockP1Pos: clashLockP1PosRef.current,
    clashLockP2Pos: clashLockP2PosRef.current
  }), []);
  
  // Send state immediately via WebSocket (for critical events like launching)
  const sendImmediateState = useCallback((stateOverrides = {}) => {
    const ws = battleWsRef.current;
    if (ws && ws.readyState === WebSocket.OPEN) {
      const state = { ...getMyState(), ...stateOverrides };
      ws.send(JSON.stringify({ type: 'sync', state }));
    }
  }, [getMyState]);
  
  // WebSocket-based game sync (much faster than HTTP polling)
  const startWebSocketSync = useCallback(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      console.error('[Battle WS] No token, falling back to HTTP polling');
      setUseWebSocket(false);
      return false;
    }
    
    const ws = new WebSocket(`${WS_URL}/ws/battle/${token}`);
    
    ws.onopen = () => {
      battleWsRef.current = ws;
      
      // Send initial state IMMEDIATELY on connect
      ws.send(JSON.stringify({
        type: 'sync',
        state: getMyState()
      }));
      
      // Start sending state updates at 60fps for real-time sync
      const syncInterval = setInterval(() => {
        if (ws.readyState === WebSocket.OPEN) {
          ws.send(JSON.stringify({
            type: 'sync',
            state: getMyState()
          }));
        }
      }, 16); // ~60 updates per second for minimal latency
      
      // Store interval for cleanup
      ws._syncInterval = syncInterval;
    };
    
    ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        
        if (data.type === 'sync_response') {
          processOpponentState(data);
        } else if (data.type === 'opponent_update') {
          // Direct push from opponent - fastest path!
          const oppState = data.opponent_state;
          if (oppState) {
            // Handle opponent launch - set position immediately for visibility
            if (oppState.active && !top2ActiveRef.current) {
              if (oppState.pos) {
                setTop2Pos(oppState.pos);
                setTop2TargetPos(oppState.pos);
              }
              if (oppState.speed) setTop2Speed(oppState.speed);
            }
            if (oppState.active !== undefined) {
              setTop2Active(oppState.active);
            }
            if (oppState.pos) {
              // Set target for interpolation
              setTop2TargetPos(oppState.pos);
            }
            if (oppState.rotation !== undefined) setTop2Rotation(oppState.rotation);
            if (oppState.speed !== undefined) setTop2Speed(oppState.speed);
            if (oppState.health !== undefined) setHealth2(oppState.health);
            if (oppState.knockbackVelocity) setKnockbackVelocity2(oppState.knockbackVelocity);
            if (oppState.isControlling !== undefined) setIsControlling2(oppState.isControlling);
            // Sync power-ups for visual display
            if (oppState.deflection !== undefined) setDeflection2(oppState.deflection);
            if (oppState.ghostMode !== undefined) setGhostMode2(oppState.ghostMode);
            if (oppState.speedBoost !== undefined) {
              setSpeedBoost2(oppState.speedBoost);
              setSpeedMultiplier2(oppState.speedBoost ? 1.5 : 1);
            }
            if (oppState.deflectionCooldown !== undefined) setDeflectionCooldown2(oppState.deflectionCooldown);
            if (oppState.ghostCooldown !== undefined) setGhostCooldown2(oppState.ghostCooldown);
            if (oppState.speedBoostCooldown !== undefined) setSpeedBoostCooldown2(oppState.speedBoostCooldown);
            if (oppState.trackpadCooldown !== undefined) setCooldown2(oppState.trackpadCooldown);
          }
        } else if (data.type === 'rematch_status_update') {
          // Opponent wants rematch - update UI immediately
          if (data.opponent_wants_rematch) {
            setOpponentWantsRematch(true);
          }
          if (data.both_ready || data.round_reset_pending) {
            // Both ready - trigger round reset
            setCurrentRound(data.round_number || 1);
            setWaitingForRematch(false);
            setOpponentWantsRematch(false);
            setTop1KnockedOut(false);
            setTop2KnockedOut(false);
            setTop1Active(false);
            setTop2Active(false);
            setKnockbackVelocity1({ x: 0, y: 0 });
            setKnockbackVelocity2({ x: 0, y: 0 });
            setHealth1(getMaxHealth(selectedBeyblade1?.id));
            setHealth2(getMaxHealth(selectedBeyblade2?.id));
            setStamina1(getMaxStamina(selectedBeyblade1?.id));
            setStamina2(getMaxStamina(selectedBeyblade2?.id));
            setMaxStamina1(getMaxStamina(selectedBeyblade1?.id));
            setMaxStamina2(getMaxStamina(selectedBeyblade2?.id));
            setStoppedSpinning1(false);
            setStoppedSpinning2(false);
            setRoundWinner(null);
            setWinType(null);
            setIsDraw(false);
            setDrawType(null);
            scoreAwardedRef.current = false; // Reset score award flag for new round
          }
        }
      } catch (e) {
        console.error('[Battle WS] Parse error:', e);
      }
    };
    
    ws.onerror = (error) => {
      console.error('[Battle WS] ❌ Error:', error);
      setUseWebSocket(false);
      // Start HTTP polling as fallback using ref
      if (startHttpPollingRef.current) {
        startHttpPollingRef.current();
      }
    };
    
    ws.onclose = () => {
      if (ws._syncInterval) clearInterval(ws._syncInterval);
      battleWsRef.current = null;
      // If we were actively in a match, fall back to HTTP polling
      if (matchFoundRef.current && !gameStatePollInterval.current) {
        if (startHttpPollingRef.current) {
          startHttpPollingRef.current();
        }
      }
    };
    
    return true;
  }, [getMyState, processOpponentState]);
  
  // HTTP polling fallback (slower but more reliable)
  const startHttpPolling = useCallback(() => {
    if (gameStatePollInterval.current) return;
    
    
    const intervalId = setInterval(async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) return;
        
        const response = await fetch(`${BACKEND_URL}/api/battle/game/sync`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(getMyState())
        });
        
        if (response.ok) {
          const data = await response.json();
          processOpponentState(data);
        }
      } catch (error) {
        console.error('[HTTP Sync] Error:', error);
      }
    }, 16); // ~60fps for HTTP polling - smoother tracking
    
    gameStatePollInterval.current = intervalId;
  }, [getMyState, processOpponentState]);
  
  // Update ref whenever callback changes
  useEffect(() => {
    startHttpPollingRef.current = startHttpPolling;
  }, [startHttpPolling]);
  
  // Main function to start game sync - ALWAYS start HTTP polling for reliability
  const startGameStatePoll = useCallback(() => {
    
    // ALWAYS start HTTP polling first - it's reliable
    startHttpPolling();
    
    // ALSO try WebSocket for faster updates (it will enhance HTTP, not replace it)
    if (useWebSocket) {
      startWebSocketSync();
    }
  }, [useWebSocket, startWebSocketSync, startHttpPolling]);
  
  const stopAllPolling = useCallback(() => {
    if (matchmakingPollInterval.current) {
      clearInterval(matchmakingPollInterval.current);
      matchmakingPollInterval.current = null;
    }
    if (gameStatePollInterval.current) {
      clearInterval(gameStatePollInterval.current);
      gameStatePollInterval.current = null;
    }
    // Close WebSocket connection
    if (battleWsRef.current) {
      if (battleWsRef.current._syncInterval) {
        clearInterval(battleWsRef.current._syncInterval);
      }
      battleWsRef.current.close();
      battleWsRef.current = null;
    }
  }, []);
  
  // Game state and score syncing is now handled by startGameStatePoll() polling function
  
  // Refs to track current state for cleanup (avoids stale closure issues)
  const isOnlineModeRef = useRef(isOnlineMode);
  const matchFoundRef = useRef(matchFound);
  
  // Refs for game state syncing (to avoid stale closures in setInterval)
  const top1ActiveRef = useRef(top1Active);
  const top2ActiveRef = useRef(top2Active);
  const top1PosRef = useRef(top1Pos);
  const top2PosRef = useRef(top2Pos);
  const top1RotationRef = useRef(top1Rotation);
  const top1SpeedRef = useRef(top1Speed);
  const health1Ref = useRef(health1);
  const health2Ref = useRef(health2);
  const selectedBeyblade1Ref = useRef(selectedBeyblade1);
  const top1VelocityRef = useRef(top1Velocity);
  const knockbackVelocity1Ref = useRef(knockbackVelocity1);
  const isControlling1Ref = useRef(isControlling1);
  
  // Track component mount time to prevent cleanup during React StrictMode double-render
  const componentMountTimeRef = useRef(Date.now());
  const MIN_MOUNT_DURATION_MS = 5000; // Only cleanup if mounted for >5 seconds (increased from 2s)
  
  // Keep refs in sync with state
  useEffect(() => {
    isOnlineModeRef.current = isOnlineMode;
  }, [isOnlineMode]);
  
  useEffect(() => {
    matchFoundRef.current = matchFound;
  }, [matchFound]);
  
  // Keep game state refs in sync
  useEffect(() => { top1ActiveRef.current = top1Active; }, [top1Active]);
  useEffect(() => { top1PosRef.current = top1Pos; }, [top1Pos]);
  useEffect(() => { top2PosRef.current = top2Pos; }, [top2Pos]);
  useEffect(() => { top1RotationRef.current = top1Rotation; }, [top1Rotation]);
  useEffect(() => { top1SpeedRef.current = top1Speed; }, [top1Speed]);
  useEffect(() => { health1Ref.current = health1; }, [health1]);
  useEffect(() => { health2Ref.current = health2; }, [health2]);
  useEffect(() => { selectedBeyblade1Ref.current = selectedBeyblade1; }, [selectedBeyblade1]);
  useEffect(() => { top1VelocityRef.current = top1Velocity; }, [top1Velocity]);
  useEffect(() => { knockbackVelocity1Ref.current = knockbackVelocity1; }, [knockbackVelocity1]);
  useEffect(() => { isControlling1Ref.current = isControlling1; }, [isControlling1]);
  useEffect(() => { top2ActiveRef.current = top2Active; }, [top2Active]);
  
  // ==================== AI MODE: Auto-launch when Player 1 launches ====================
  useEffect(() => {
    if (aiMode && top1Active && !top2Active && !aiLaunched) {
      // Delay AI launch slightly for realism (0.5-1.5 seconds after player)
      const delay = 125 + Math.random() * 250;
      const timer = setTimeout(() => {
        aiLaunchBeyblade();
      }, delay);
      return () => clearTimeout(timer);
    }
  }, [aiMode, top1Active, top2Active, aiLaunched]);
  
  // Reset AI state when battle resets
  useEffect(() => {
    if (!top1Active && !top2Active && aiLaunched) {
      setAiLaunched(false);
    }
  }, [top1Active, top2Active, aiLaunched]);
  // ====================================================================================
  
  // Ref for rewindFreezeActive so auto-spin intervals can read current value
  const rewindFreezeActiveRef = useRef(rewindFreezeActive);
  useEffect(() => { rewindFreezeActiveRef.current = rewindFreezeActive; }, [rewindFreezeActive]);
  
  // Track position, health, and special ability states for Rewind Time ability (Clock Mirage - ID 49)
  // Records every 100ms, keeps last 2 seconds (20 entries)
  useEffect(() => {
    const intervalId = setInterval(() => {
      // Only track when beyblades are active (battle in progress)
      if (top1ActiveRef.current) {
        setPositionHistory1(prev => {
          const newHistory = [...prev, { 
            x: top1PosRef.current.x, 
            y: top1PosRef.current.y,
            health: health1Ref.current,
            // Track P1's special ability states (for when P2 uses rewind)
            deflectionCooldown: deflectionCooldown1Ref.current,
            ghostCooldown: ghostCooldown1Ref.current,
            speedBoostCooldown: speedBoostCooldown1Ref.current,
            shadowCloneUsed: shadowCloneUsed1Ref.current,
            // Track active effects
            deflection: deflection1Ref.current,
            ghostMode: ghostMode1Ref.current,
            speedBoost: speedBoost1Ref.current,
            shadowClonesActive: shadowClonesActive1Ref.current
          }];
          // Keep only last 20 entries (2 seconds at 100ms intervals)
          if (newHistory.length > 20) {
            return newHistory.slice(-20);
          }
          return newHistory;
        });
      }
      if (top2ActiveRef.current) {
        setPositionHistory2(prev => {
          const newHistory = [...prev, { 
            x: top2PosRef.current.x, 
            y: top2PosRef.current.y,
            health: health2Ref.current,
            // Track P2's special ability states (for when P1 uses rewind)
            deflectionCooldown: deflectionCooldown2Ref.current,
            ghostCooldown: ghostCooldown2Ref.current,
            speedBoostCooldown: speedBoostCooldown2Ref.current,
            shadowCloneUsed: shadowCloneUsed2Ref.current,
            // Track active effects
            deflection: deflection2Ref.current,
            ghostMode: ghostMode2Ref.current,
            speedBoost: speedBoost2Ref.current,
            shadowClonesActive: shadowClonesActive2Ref.current
          }];
          // Keep only last 20 entries (2 seconds at 100ms intervals)
          if (newHistory.length > 20) {
            return newHistory.slice(-20);
          }
          return newHistory;
        });
      }
    }, 100); // Record every 100ms
    
    return () => clearInterval(intervalId);
  }, []); // Empty dependency array - runs once, uses refs for current values
  
  // Refs for knockout sync (online mode)
  const top1KnockedOutRef = useRef(top1KnockedOut);
  const roundWinnerRef = useRef(roundWinner);
  const winTypeRef = useRef(winType);
  useEffect(() => { top1KnockedOutRef.current = top1KnockedOut; }, [top1KnockedOut]);
  useEffect(() => { roundWinnerRef.current = roundWinner; }, [roundWinner]);
  useEffect(() => { winTypeRef.current = winType; }, [winType]);
  
  // Refs for power-up sync (online mode) - so opponent can see your power-ups
  const deflection1Ref = useRef(deflection1);
  const ghostMode1Ref = useRef(ghostMode1);
  const speedBoost1Ref = useRef(speedBoost1);
  const finalDriveActive1Ref = useRef(finalDriveActive1);
  const finalDriveActive2Ref = useRef(finalDriveActive2);
  const ironWallActive1Ref = useRef(ironWallActive1);
  const ironWallActive2Ref = useRef(ironWallActive2);
  const deflectionCooldown1Ref = useRef(deflectionCooldown1);
  const ghostCooldown1Ref = useRef(ghostCooldown1);
  const speedBoostCooldown1Ref = useRef(speedBoostCooldown1);
  const cooldown1Ref = useRef(cooldown1); // Track pad cooldown
  useEffect(() => { deflection1Ref.current = deflection1; }, [deflection1]);
  useEffect(() => { ghostMode1Ref.current = ghostMode1; }, [ghostMode1]);
  useEffect(() => { speedBoost1Ref.current = speedBoost1; }, [speedBoost1]);
  useEffect(() => { finalDriveActive1Ref.current = finalDriveActive1; }, [finalDriveActive1]);
  useEffect(() => { finalDriveActive2Ref.current = finalDriveActive2; }, [finalDriveActive2]);
  useEffect(() => { ironWallActive1Ref.current = ironWallActive1; }, [ironWallActive1]);
  useEffect(() => { ironWallActive2Ref.current = ironWallActive2; }, [ironWallActive2]);
  useEffect(() => { deflectionCooldown1Ref.current = deflectionCooldown1; }, [deflectionCooldown1]);
  useEffect(() => { ghostCooldown1Ref.current = ghostCooldown1; }, [ghostCooldown1]);
  useEffect(() => { speedBoostCooldown1Ref.current = speedBoostCooldown1; }, [speedBoostCooldown1]);
  useEffect(() => { cooldown1Ref.current = cooldown1; }, [cooldown1]);
  useEffect(() => { xtremeDashFreeze1Ref.current = xtremeDashFreeze1; }, [xtremeDashFreeze1]);
  useEffect(() => { xtremeDashFreeze2Ref.current = xtremeDashFreeze2; }, [xtremeDashFreeze2]);
  useEffect(() => { xtremeDashLine1Ref.current = xtremeDashLine1Active; }, [xtremeDashLine1Active]);
  useEffect(() => { xtremeDashLine2Ref.current = xtremeDashLine2Active; }, [xtremeDashLine2Active]);
  useEffect(() => { xtremeDashLine1ColorRef.current = xtremeDashLine1Color; }, [xtremeDashLine1Color]);
  useEffect(() => { xtremeDashLine2ColorRef.current = xtremeDashLine2Color; }, [xtremeDashLine2Color]);
  useEffect(() => { xtremeDashLine1GoingDownRef.current = xtremeDashLine1GoingDown; }, [xtremeDashLine1GoingDown]);
  useEffect(() => { xtremeDashLine2GoingDownRef.current = xtremeDashLine2GoingDown; }, [xtremeDashLine2GoingDown]);
  
  // Refs for P2 power-up states (for clash lock release logic)
  const deflection2Ref = useRef(deflection2);
  const speedBoost2Ref = useRef(speedBoost2);
  const ghostMode2Ref = useRef(ghostMode2);
  const deflectionCooldown2Ref = useRef(deflectionCooldown2);
  const ghostCooldown2Ref = useRef(ghostCooldown2);
  const speedBoostCooldown2Ref = useRef(speedBoostCooldown2);
  const shadowCloneUsed1Ref = useRef(shadowCloneUsed1);
  const shadowCloneUsed2Ref = useRef(shadowCloneUsed2);
  const shadowClonesActive1Ref = useRef(shadowClonesActive1);
  const shadowClonesActive2Ref = useRef(shadowClonesActive2);
  useEffect(() => { deflection2Ref.current = deflection2; }, [deflection2]);
  useEffect(() => { speedBoost2Ref.current = speedBoost2; }, [speedBoost2]);
  useEffect(() => { ghostMode2Ref.current = ghostMode2; }, [ghostMode2]);
  useEffect(() => { deflectionCooldown2Ref.current = deflectionCooldown2; }, [deflectionCooldown2]);
  useEffect(() => { ghostCooldown2Ref.current = ghostCooldown2; }, [ghostCooldown2]);
  useEffect(() => { speedBoostCooldown2Ref.current = speedBoostCooldown2; }, [speedBoostCooldown2]);
  useEffect(() => { shadowCloneUsed1Ref.current = shadowCloneUsed1; }, [shadowCloneUsed1]);
  useEffect(() => { shadowCloneUsed2Ref.current = shadowCloneUsed2; }, [shadowCloneUsed2]);
  useEffect(() => { shadowClonesActive1Ref.current = shadowClonesActive1; }, [shadowClonesActive1]);
  useEffect(() => { shadowClonesActive2Ref.current = shadowClonesActive2; }, [shadowClonesActive2]);
  
  // Cleanup polling intervals and leave match/cancel matchmaking ONLY on unmount
  useEffect(() => {
    // Reset mount time on each effect run (handles StrictMode remount)
    componentMountTimeRef.current = Date.now();
    
    return () => {
      const mountDuration = Date.now() - componentMountTimeRef.current;
      
      // CRITICAL: Skip cleanup if component was mounted for less than MIN_MOUNT_DURATION_MS
      // This prevents React StrictMode's double-render from destroying game sessions
      if (mountDuration < MIN_MOUNT_DURATION_MS) {
        stopAllPolling();
        return;
      }
      
      const token = localStorage.getItem('token');
      if (isOnlineModeRef.current) {
        if (matchFoundRef.current) {
          // In a match - leave it (this will notify opponent and delete room)
          fetch(`${BACKEND_URL}/api/battle/game/leave`, {
            method: 'POST',
            headers: { 'Authorization': `Bearer ${token}` }
          }).catch(err => console.error('Leave match error:', err));
        } else {
          // Still searching - cancel matchmaking (this only removes from queue, safe to call anytime)
          fetch(`${BACKEND_URL}/api/battle/matchmaking/cancel`, {
            method: 'POST',
            headers: { 'Authorization': `Bearer ${token}` }
          }).catch(err => console.error('Cancel matchmaking error:', err));
        }
      }
      stopAllPolling();
    };
  }, []); // Empty dependency array - only runs on unmount

  const handleCanvasClick = (e) => {
    // Canvas clicks disabled - only touchpads control Beyblades
    // This prevents accidental Beyblade movement when tapping the stadium
  };

  const drawTop = (ctx) => {
    if (!top1Active || !top1ImageRef.current || !top1ImageRef.current.complete) return;

    const topSize = 60;

    ctx.save();
    ctx.translate(top1Pos.x, top1Pos.y);
    ctx.rotate((top1Rotation * Math.PI) / 180);

    // Draw spinning top
    ctx.drawImage(
      top1ImageRef.current,
      -topSize / 2,
      -topSize / 2,
      topSize,
      topSize
    );

    ctx.restore();

    // Draw speed indicator
    if (top1Speed > 0) {
      ctx.fillStyle = `rgba(68, 136, 255, ${top1Speed / 100})`;
      ctx.beginPath();
      ctx.arc(top1Pos.x, top1Pos.y, topSize / 2 + 5, 0, Math.PI * 2);
      ctx.fill();
    }
  };

  // REMOVED: handleDpadPress and handleDpadRelease - D-pad no longer exists

  // Refs to track pointer IDs for complete isolation
  const touchpad1PointerRef = useRef({ id: null, lastX: 0, lastY: 0 });
  const touchpad2PointerRef = useRef({ id: null, lastX: 0, lastY: 0 });
  const touchpad1Ref = useRef(null);
  const touchpad2Ref = useRef(null);
  
  // Player 1 Touchpad Handlers (using Pointer Events)
  // Speed boost button handlers
  const handleSpeedBoost1 = () => {
    if (!top1Active || speedBoost1 || speedBoostCooldown1) return;
    
    setSpeedBoost1(true);
    setSpeedMultiplier1(2);
    
    // Immediately sync to opponent
    if (isOnlineMode && matchFound) {
      setTimeout(() => sendImmediateState({ speedBoost: true }), 0);
    }
    
    if (boostTimer1Ref.current) clearTimeout(boostTimer1Ref.current);
    
    boostTimer1Ref.current = setTimeout(() => {
      setSpeedBoost1(false);
      setSpeedMultiplier1(1);
      
      // Start cooldown
      setSpeedBoostCooldown1(true);
      if (speedBoostCooldownTimer1Ref.current) clearTimeout(speedBoostCooldownTimer1Ref.current);
      speedBoostCooldownTimer1Ref.current = setTimeout(() => {
        setSpeedBoostCooldown1(false);
      }, 6000);
    }, 2000);
  };

  const handleSpeedBoost2 = () => {
    if (!top2Active || speedBoost2 || speedBoostCooldown2) return;
    
    setSpeedBoost2(true);
    setSpeedMultiplier2(2);
    
    if (boostTimer2Ref.current) clearTimeout(boostTimer2Ref.current);
    
    boostTimer2Ref.current = setTimeout(() => {
      setSpeedBoost2(false);
      setSpeedMultiplier2(1);
      
      // Start cooldown
      setSpeedBoostCooldown2(true);
      if (speedBoostCooldownTimer2Ref.current) clearTimeout(speedBoostCooldownTimer2Ref.current);
      speedBoostCooldownTimer2Ref.current = setTimeout(() => {
        setSpeedBoostCooldown2(false);
      }, 6000);
    }, 2000);
  };

  // BX-Infinity Xtreme Dash Line handlers - move beyblade along the line path
  // triggerPoint: 'blueTop', 'blueBottom', 'redTop', 'redBottom'
  // Loops continuously for the duration of speed boost (2 seconds)
  const handleXtremeDashLine1 = (triggerPoint) => {
    if (!top1Active || xtremeDashLine1Ref.current || speedBoostCooldown1) return;
    if (selectedStadium !== 'bxInfinity') return;
    
    // Determine color from trigger point
    const color = triggerPoint.startsWith('blue') ? 'blue' : 'red';
    const isTop = triggerPoint.endsWith('Top');
    
    // Check spin direction matches trigger color
    let spinDir = selectedBeyblade1?.spinDirection?.toLowerCase();
    if (spinDir === 'dual (left/right)' || spinDir === 'dual') {
      spinDir = dualSpinDirection1;
    }
    const isRightSpin = spinDir === 'right';
    const isLeftSpin = spinDir === 'left';
    
    // Blue triggers = right spin only, Red triggers = left spin only
    if (color === 'blue' && !isRightSpin) return;
    if (color === 'red' && !isLeftSpin) return;
    
    
    // BREAK MANUAL CONTROL - Release touchpad control so the dash takes over
    setIsControlling1(false);
    
    // Reset trackpad timer
    if (controlTimer1Ref.current) {
      clearTimeout(controlTimer1Ref.current);
      controlTimer1Ref.current = null;
    }
    
    // Activate speed boost if not already active
    if (!speedBoost1) {
      handleSpeedBoost1();
    }
    
    // Set line dash state - the dedicated useEffect will handle movement
    setXtremeDashLine1Active(true);
    setXtremeDashLine1Color(color);
    setXtremeDashLine1GoingDown(isTop); // If hit top, go down first
  };
  
  const handleXtremeDashLine2 = (triggerPoint) => {
    if (!top2Active || xtremeDashLine2Ref.current || speedBoostCooldown2) return;
    if (selectedStadium !== 'bxInfinity') return;
    
    // Determine color from trigger point
    const color = triggerPoint.startsWith('blue') ? 'blue' : 'red';
    const isTop = triggerPoint.endsWith('Top');
    
    // Check spin direction matches trigger color
    let spinDir = selectedBeyblade2?.spinDirection?.toLowerCase();
    if (spinDir === 'dual (left/right)' || spinDir === 'dual') {
      spinDir = dualSpinDirection2;
    }
    const isRightSpin = spinDir === 'right';
    const isLeftSpin = spinDir === 'left';
    
    // Blue triggers = right spin only, Red triggers = left spin only
    if (color === 'blue' && !isRightSpin) return;
    if (color === 'red' && !isLeftSpin) return;
    
    
    // BREAK MANUAL CONTROL - Release touchpad control so the dash takes over
    setIsControlling2(false);
    
    // Reset trackpad timer
    if (controlTimer2Ref.current) {
      clearTimeout(controlTimer2Ref.current);
      controlTimer2Ref.current = null;
    }
    
    // Activate speed boost if not already active
    if (!speedBoost2) {
      handleSpeedBoost2();
    }
    
    // Set line dash state - the dedicated useEffect will handle movement
    setXtremeDashLine2Active(true);
    setXtremeDashLine2Color(color);
    setXtremeDashLine2GoingDown(isTop); // If hit top, go down first
  };

  // Ghost mode button handlers
  const handleGhostMode1 = () => {
    if (!top1Active || ghostMode1 || ghostCooldown1) return;
    
    // Power-ups can stack - don't cancel deflection when activating ghost
    
    setGhostMode1(true);
    
    // Immediately sync to opponent
    if (isOnlineMode && matchFound) {
      setTimeout(() => sendImmediateState({ ghostMode: true }), 0);
    }
    
    // Break clash lock - small knockback to separate
    if (clashLocked) {
      setClashLocked(false);
      if (clashLockTimerRef.current) {
        clearTimeout(clashLockTimerRef.current);
        clashLockTimerRef.current = null;
      }
      // Reactivate trackpads with timer reset
      setCooldown1(false);
      setCooldown2(false);
      
      // Calculate knockback angle from clash positions
      const dx = clashLockP1Pos.x - clashLockP2Pos.x;
      const dy = clashLockP1Pos.y - clashLockP2Pos.y;
      const releaseAngle = Math.atan2(dy, dx);
      
      // Small knockback to separate (about 1/4 of normal clash end)
      const smallForce = 41 * SCALE_FACTOR * 0.2;
      
      setKnockbackVelocity1({ 
        x: Math.cos(releaseAngle) * smallForce, 
        y: Math.sin(releaseAngle) * smallForce 
      });
      setKnockbackVelocity2({ 
        x: -Math.cos(releaseAngle) * smallForce, 
        y: -Math.sin(releaseAngle) * smallForce 
      });
    }
    
    if (ghostTimer1Ref.current) clearTimeout(ghostTimer1Ref.current);
    
    ghostTimer1Ref.current = setTimeout(() => {
      setGhostMode1(false);
      
      // Start cooldown
      setGhostCooldown1(true);
      if (ghostCooldownTimer1Ref.current) clearTimeout(ghostCooldownTimer1Ref.current);
      ghostCooldownTimer1Ref.current = setTimeout(() => {
        setGhostCooldown1(false);
      }, 6000);
    }, 2000);
  };

  const handleGhostMode2 = () => {
    if (!top2Active || ghostMode2 || ghostCooldown2) return;
    
    // Power-ups can stack - don't cancel deflection when activating ghost
    
    setGhostMode2(true);
    
    // Break clash lock - small knockback to separate
    if (clashLocked) {
      setClashLocked(false);
      if (clashLockTimerRef.current) {
        clearTimeout(clashLockTimerRef.current);
        clashLockTimerRef.current = null;
      }
      // Reactivate trackpads with timer reset
      setCooldown1(false);
      setCooldown2(false);
      
      // Calculate knockback angle from clash positions
      const dx = clashLockP1Pos.x - clashLockP2Pos.x;
      const dy = clashLockP1Pos.y - clashLockP2Pos.y;
      const releaseAngle = Math.atan2(dy, dx);
      
      // Small knockback to separate (about 1/4 of normal clash end)
      const smallForce = 41 * SCALE_FACTOR * 0.2;
      
      setKnockbackVelocity1({ 
        x: Math.cos(releaseAngle) * smallForce, 
        y: Math.sin(releaseAngle) * smallForce 
      });
      setKnockbackVelocity2({ 
        x: -Math.cos(releaseAngle) * smallForce, 
        y: -Math.sin(releaseAngle) * smallForce 
      });
    }
    
    if (ghostTimer2Ref.current) clearTimeout(ghostTimer2Ref.current);
    
    ghostTimer2Ref.current = setTimeout(() => {
      setGhostMode2(false);
      
      // Start cooldown
      setGhostCooldown2(true);
      if (ghostCooldownTimer2Ref.current) clearTimeout(ghostCooldownTimer2Ref.current);
      ghostCooldownTimer2Ref.current = setTimeout(() => {
        setGhostCooldown2(false);
      }, 6000);
    }, 2000);
  };

  // Deflection button handlers
  const handleDeflection1 = () => {
    if (!top1Active || deflection1 || deflectionCooldown1) return;
    
    // Shield overrides ghost mode - cancel ghost and put it on cooldown
    if (ghostMode1) {
      setGhostMode1(false);
      if (ghostTimer1Ref.current) {
        clearTimeout(ghostTimer1Ref.current);
        ghostTimer1Ref.current = null;
      }
      // Ghost goes on cooldown when canceled
      setGhostCooldown1(true);
      if (ghostCooldownTimer1Ref.current) clearTimeout(ghostCooldownTimer1Ref.current);
      ghostCooldownTimer1Ref.current = setTimeout(() => {
        setGhostCooldown1(false);
      }, 6000);
    }
    
    setDeflection1(true);
    
    // Immediately sync to opponent
    if (isOnlineMode && matchFound) {
      setTimeout(() => sendImmediateState({ deflection: true }), 0);
    }
    
    if (deflectionTimer1Ref.current) clearTimeout(deflectionTimer1Ref.current);
    
    deflectionTimer1Ref.current = setTimeout(() => {
      setDeflection1(false);
      
      // Start cooldown
      setDeflectionCooldown1(true);
      if (deflectionCooldownTimer1Ref.current) clearTimeout(deflectionCooldownTimer1Ref.current);
      deflectionCooldownTimer1Ref.current = setTimeout(() => {
        setDeflectionCooldown1(false);
      }, 6000);
    }, 2000);
  };

  const handleDeflection2 = () => {
    if (!top2Active || deflection2 || deflectionCooldown2) return;
    
    // Shield overrides ghost mode - cancel ghost and put it on cooldown
    if (ghostMode2) {
      setGhostMode2(false);
      if (ghostTimer2Ref.current) {
        clearTimeout(ghostTimer2Ref.current);
        ghostTimer2Ref.current = null;
      }
      // Ghost goes on cooldown when canceled
      setGhostCooldown2(true);
      if (ghostCooldownTimer2Ref.current) clearTimeout(ghostCooldownTimer2Ref.current);
      ghostCooldownTimer2Ref.current = setTimeout(() => {
        setGhostCooldown2(false);
      }, 6000);
    }
    
    setDeflection2(true);
    
    if (deflectionTimer2Ref.current) clearTimeout(deflectionTimer2Ref.current);
    
    deflectionTimer2Ref.current = setTimeout(() => {
      setDeflection2(false);
      
      // Start cooldown
      setDeflectionCooldown2(true);
      if (deflectionCooldownTimer2Ref.current) clearTimeout(deflectionCooldownTimer2Ref.current);
      deflectionCooldownTimer2Ref.current = setTimeout(() => {
        setDeflectionCooldown2(false);
      }, 6000);
    }, 2000);
  };

  // Shadow Clone ability for Shinobi Shadow (ID 36)
  const handleShadowClones1 = () => {
    if (!top1Active || shadowClonesActive1 || shadowCloneUsed1) return;
    if (selectedBeyblade1?.id !== 36) return; // Only for Shinobi Shadow
    
    
    const originalPos = { x: top1Pos.x, y: top1Pos.y };
    const minDistance = 70 * SCALE_FACTOR; // Minimum distance between clones (increased to prevent overlap)
    const minTeleportDistance = 80 * SCALE_FACTOR; // Minimum distance original must teleport
    const centerRadius = 30 * SCALE_FACTOR; // Radius considered "center"
    
    // Check if original's old position is in the center
    const origDistFromCenter = Math.sqrt(Math.pow(originalPos.x - STADIUM_CENTER_X, 2) + Math.pow(originalPos.y - STADIUM_CENTER_Y, 2));
    const originalWasInCenter = origDistFromCenter < centerRadius;
    
    // First, generate a teleport position for the original (guaranteed to be far from current position AND not in center)
    let teleportPos = null;
    let teleportAttempts = 0;
    while (!teleportPos && teleportAttempts < 50) {
      const angle = Math.random() * 2 * Math.PI;
      const radius = 50 + Math.random() * (BEYBLADE_RADIUS - 60); // Start from 50 to avoid center
      const x = STADIUM_CENTER_X + radius * Math.cos(angle);
      const y = STADIUM_CENTER_Y + radius * Math.sin(angle);
      
      const distFromOriginal = Math.sqrt(Math.pow(x - originalPos.x, 2) + Math.pow(y - originalPos.y, 2));
      const distFromCenter = Math.sqrt(Math.pow(x - STADIUM_CENTER_X, 2) + Math.pow(y - STADIUM_CENTER_Y, 2));
      
      // Teleport must be far from original AND not in center
      if (distFromOriginal >= minTeleportDistance && distFromCenter >= centerRadius) {
        teleportPos = { x, y, angle };
      }
      teleportAttempts++;
    }
    
    // If we couldn't find a good teleport spot, use opposite side of stadium (guaranteed not center)
    if (!teleportPos) {
      const oppositeAngle = Math.atan2(originalPos.y - STADIUM_CENTER_Y, originalPos.x - STADIUM_CENTER_X) + Math.PI;
      teleportPos = {
        x: STADIUM_CENTER_X + BEYBLADE_RADIUS * 0.7 * Math.cos(oppositeAngle),
        y: STADIUM_CENTER_Y + BEYBLADE_RADIUS * 0.7 * Math.sin(oppositeAngle),
        angle: oppositeAngle
      };
    }
    
    // Generate 5 clone positions (one will be at the original's old position)
    const clones = [];
    // If original was in center, that clone counts as the center clone
    let centerCloneExists = originalWasInCenter;
    
    // First clone takes the original's old position
    clones.push({
      id: 0,
      x: originalPos.x,
      y: originalPos.y,
      angle: Math.atan2(originalPos.y - STADIUM_CENTER_Y, originalPos.x - STADIUM_CENTER_X),
    });
    
    // Generate 4 more clones at random positions - spread them out evenly
    const angleOffsets = [0, Math.PI/2, Math.PI, 3*Math.PI/2]; // 4 quadrants
    for (let i = 1; i < 5; i++) {
      let attempts = 0;
      let validPosition = false;
      let x, y;
      
      // Try to spread clones in different directions
      const baseAngle = angleOffsets[i-1] + (Math.random() - 0.5) * (Math.PI/3);
      
      while (!validPosition && attempts < 100) {
        const angle = baseAngle + (attempts * 0.1); // Gradually rotate if position taken
        const radius = 40 + Math.random() * (BEYBLADE_RADIUS - 50);
        x = STADIUM_CENTER_X + radius * Math.cos(angle);
        y = STADIUM_CENTER_Y + radius * Math.sin(angle);
        
        // Check if this position is in the center
        const distFromCenter = Math.sqrt(Math.pow(x - STADIUM_CENTER_X, 2) + Math.pow(y - STADIUM_CENTER_Y, 2));
        if (distFromCenter < centerRadius && centerCloneExists) {
          // Already have a clone in center, skip this position
          attempts++;
          continue;
        }
        
        // Check distance from teleport position (where original will be)
        const distFromTeleport = Math.sqrt(Math.pow(x - teleportPos.x, 2) + Math.pow(y - teleportPos.y, 2));
        if (distFromTeleport < minDistance) {
          attempts++;
          continue;
        }
        
        // Check distance from other clones
        validPosition = true;
        for (const clone of clones) {
          const dist = Math.sqrt(Math.pow(x - clone.x, 2) + Math.pow(y - clone.y, 2));
          if (dist < minDistance) {
            validPosition = false;
            break;
          }
        }
        attempts++;
      }
      
      // Only add clone if we found a valid position
      if (validPosition) {
        // Mark center as taken if this clone is in center
        const distFromCenter = Math.sqrt(Math.pow(x - STADIUM_CENTER_X, 2) + Math.pow(y - STADIUM_CENTER_Y, 2));
        if (distFromCenter < centerRadius) {
          centerCloneExists = true;
        }
        
        clones.push({
          id: i,
          x: x,
          y: y,
          angle: Math.atan2(y - STADIUM_CENTER_Y, x - STADIUM_CENTER_X),
        });
      }
    }
    
    // Teleport the original to the new position
    setTop1Pos({ x: teleportPos.x, y: teleportPos.y });
    
    setShadowClones1(clones);
    setShadowClonesActive1(true);
    setShadowCloneUsed1(true);
    
    // Clear clones after 10 seconds
    if (shadowCloneTimer1Ref.current) clearTimeout(shadowCloneTimer1Ref.current);
    shadowCloneTimer1Ref.current = setTimeout(() => {
      setShadowClones1([]);
      setShadowClonesActive1(false);
    }, 10000);
  };

  const handleShadowClones2 = () => {
    if (!top2Active || shadowClonesActive2 || shadowCloneUsed2) return;
    if (selectedBeyblade2?.id !== 36) return; // Only for Shinobi Shadow
    
    
    const originalPos = { x: top2Pos.x, y: top2Pos.y };
    const minDistance = 70 * SCALE_FACTOR; // Minimum distance between clones (increased to prevent overlap)
    const minTeleportDistance = 80 * SCALE_FACTOR; // Minimum distance original must teleport
    const centerRadius = 30 * SCALE_FACTOR; // Radius considered "center"
    
    // Check if original's old position is in the center
    const origDistFromCenter = Math.sqrt(Math.pow(originalPos.x - STADIUM_CENTER_X, 2) + Math.pow(originalPos.y - STADIUM_CENTER_Y, 2));
    const originalWasInCenter = origDistFromCenter < centerRadius;
    
    // First, generate a teleport position for the original (guaranteed to be far from current position AND not in center)
    let teleportPos = null;
    let teleportAttempts = 0;
    while (!teleportPos && teleportAttempts < 50) {
      const angle = Math.random() * 2 * Math.PI;
      const radius = 50 + Math.random() * (BEYBLADE_RADIUS - 60); // Start from 50 to avoid center
      const x = STADIUM_CENTER_X + radius * Math.cos(angle);
      const y = STADIUM_CENTER_Y + radius * Math.sin(angle);
      
      const distFromOriginal = Math.sqrt(Math.pow(x - originalPos.x, 2) + Math.pow(y - originalPos.y, 2));
      const distFromCenter = Math.sqrt(Math.pow(x - STADIUM_CENTER_X, 2) + Math.pow(y - STADIUM_CENTER_Y, 2));
      
      // Teleport must be far from original AND not in center
      if (distFromOriginal >= minTeleportDistance && distFromCenter >= centerRadius) {
        teleportPos = { x, y, angle };
      }
      teleportAttempts++;
    }
    
    // If we couldn't find a good teleport spot, use opposite side of stadium (guaranteed not center)
    if (!teleportPos) {
      const oppositeAngle = Math.atan2(originalPos.y - STADIUM_CENTER_Y, originalPos.x - STADIUM_CENTER_X) + Math.PI;
      teleportPos = {
        x: STADIUM_CENTER_X + BEYBLADE_RADIUS * 0.7 * Math.cos(oppositeAngle),
        y: STADIUM_CENTER_Y + BEYBLADE_RADIUS * 0.7 * Math.sin(oppositeAngle),
        angle: oppositeAngle
      };
    }
    
    // Generate 5 clone positions (one will be at the original's old position)
    const clones = [];
    // If original was in center, that clone counts as the center clone
    let centerCloneExists = originalWasInCenter;
    
    // First clone takes the original's old position
    clones.push({
      id: 0,
      x: originalPos.x,
      y: originalPos.y,
      angle: Math.atan2(originalPos.y - STADIUM_CENTER_Y, originalPos.x - STADIUM_CENTER_X),
    });
    
    // Generate 4 more clones at random positions - spread them out evenly
    const angleOffsets = [0, Math.PI/2, Math.PI, 3*Math.PI/2]; // 4 quadrants
    for (let i = 1; i < 5; i++) {
      let attempts = 0;
      let validPosition = false;
      let x, y;
      
      // Try to spread clones in different directions
      const baseAngle = angleOffsets[i-1] + (Math.random() - 0.5) * (Math.PI/3);
      
      while (!validPosition && attempts < 100) {
        const angle = baseAngle + (attempts * 0.1); // Gradually rotate if position taken
        const radius = 40 + Math.random() * (BEYBLADE_RADIUS - 50);
        x = STADIUM_CENTER_X + radius * Math.cos(angle);
        y = STADIUM_CENTER_Y + radius * Math.sin(angle);
        
        // Check if this position is in the center
        const distFromCenter = Math.sqrt(Math.pow(x - STADIUM_CENTER_X, 2) + Math.pow(y - STADIUM_CENTER_Y, 2));
        if (distFromCenter < centerRadius && centerCloneExists) {
          // Already have a clone in center, skip this position
          attempts++;
          continue;
        }
        
        // Check distance from teleport position (where original will be)
        const distFromTeleport = Math.sqrt(Math.pow(x - teleportPos.x, 2) + Math.pow(y - teleportPos.y, 2));
        if (distFromTeleport < minDistance) {
          attempts++;
          continue;
        }
        
        // Check distance from other clones
        validPosition = true;
        for (const clone of clones) {
          const dist = Math.sqrt(Math.pow(x - clone.x, 2) + Math.pow(y - clone.y, 2));
          if (dist < minDistance) {
            validPosition = false;
            break;
          }
        }
        attempts++;
      }
      
      // Only add clone if we found a valid position
      if (validPosition) {
        // Mark center as taken if this clone is in center
        const distFromCenter = Math.sqrt(Math.pow(x - STADIUM_CENTER_X, 2) + Math.pow(y - STADIUM_CENTER_Y, 2));
        if (distFromCenter < centerRadius) {
          centerCloneExists = true;
        }
        
        clones.push({
          id: i,
          x: x,
          y: y,
          angle: Math.atan2(y - STADIUM_CENTER_Y, x - STADIUM_CENTER_X),
        });
      }
    }
    
    // Teleport the original to the new position
    setTop2Pos({ x: teleportPos.x, y: teleportPos.y });
    
    setShadowClones2(clones);
    setShadowClonesActive2(true);
    setShadowCloneUsed2(true);
    
    // Clear clones after 10 seconds
    if (shadowCloneTimer2Ref.current) clearTimeout(shadowCloneTimer2Ref.current);
    shadowCloneTimer2Ref.current = setTimeout(() => {
      setShadowClones2([]);
      setShadowClonesActive2(false);
    }, 10000);
  };

  // Rewind Time ability handler for P1 (Clock Mirage - ID 49)
  const handleRewindTime1 = () => {
    if (rewindUsed1 || !top1Active) {
      return;
    }
    
    // Require at least some position history before allowing rewind
    if (positionHistory1.length < 5) {
      return;
    }
    
    setRewindUsed1(true);
    
    // Cancel any pending clash lock launch timer
    if (clashLockTimerRef.current) {
      clearTimeout(clashLockTimerRef.current);
      clashLockTimerRef.current = null;
    }
    
    // Break clash lock if active - rewind should undo the clash
    if (clashLocked) {
      setClashLocked(false);
    }
    
    // Stop any active knockback - rewind should cancel knockback motion
    setKnockbackVelocity1({ x: 0, y: 0 });
    setKnockbackVelocity2({ x: 0, y: 0 });
    
    // Get position, health, and ability states from 2 seconds ago (or earliest available)
    const rewindState1 = positionHistory1[0];
    const rewindState2 = positionHistory2.length > 0 ? positionHistory2[0] : { x: top2Pos.x, y: top2Pos.y, health: health2 };
    
    // Teleport both beyblades to their past positions
    setTop1Pos({ x: rewindState1.x, y: rewindState1.y });
    if (top2Active && !top2KnockedOut) {
      setTop2Pos({ x: rewindState2.x, y: rewindState2.y });
    }
    
    // Restore health for the beyblade that used Rewind Time (P1)
    if (rewindState1.health !== undefined) {
      setHealth1(rewindState1.health);
    }
    
    // Restore opponent's (P2) special ability cooldowns from 2 seconds ago
    if (rewindState2.deflectionCooldown !== undefined) {
      setDeflectionCooldown2(rewindState2.deflectionCooldown);
    }
    if (rewindState2.ghostCooldown !== undefined) {
      setGhostCooldown2(rewindState2.ghostCooldown);
    }
    if (rewindState2.speedBoostCooldown !== undefined) {
      setSpeedBoostCooldown2(rewindState2.speedBoostCooldown);
    }
    if (rewindState2.shadowCloneUsed !== undefined) {
      setShadowCloneUsed2(rewindState2.shadowCloneUsed);
    }
    
    // Restore opponent's (P2) active effects from 2 seconds ago
    // This removes any active abilities that were deployed in the last 2 seconds
    if (rewindState2.deflection !== undefined) {
      setDeflection2(rewindState2.deflection);
    }
    if (rewindState2.ghostMode !== undefined) {
      setGhostMode2(rewindState2.ghostMode);
    }
    if (rewindState2.speedBoost !== undefined) {
      setSpeedBoost2(rewindState2.speedBoost);
      // Also restore speed multiplier if speed boost was not active 2 seconds ago
      if (!rewindState2.speedBoost) {
        setSpeedMultiplier2(1);
      }
    }
    if (rewindState2.shadowClonesActive !== undefined) {
      setShadowClonesActive2(rewindState2.shadowClonesActive);
      // If shadow clones weren't active 2 seconds ago, remove them from the stadium
      if (!rewindState2.shadowClonesActive) {
        setShadowClones2([]);
      }
    }
    
    // Clear position history
    setPositionHistory1([]);
    setPositionHistory2([]);
    
    // Freeze both beyblades for 1 second
    setRewindFreezeActive(true);
    setIsControlling1(false);
    setIsControlling2(false);
    
    // Clear any existing freeze timer
    if (rewindFreezeTimerRef.current) {
      clearTimeout(rewindFreezeTimerRef.current);
    }
    
    // Unfreeze after 1 second
    rewindFreezeTimerRef.current = setTimeout(() => {
      setRewindFreezeActive(false);
    }, 1000);
  };

  // Rewind Time ability handler for P2 (Clock Mirage - ID 49)
  const handleRewindTime2 = () => {
    if (rewindUsed2 || !top2Active) {
      return;
    }
    
    // Require at least some position history before allowing rewind
    if (positionHistory2.length < 5) {
      return;
    }
    
    setRewindUsed2(true);
    
    // Cancel any pending clash lock launch timer
    if (clashLockTimerRef.current) {
      clearTimeout(clashLockTimerRef.current);
      clashLockTimerRef.current = null;
    }
    
    // Break clash lock if active - rewind should undo the clash
    if (clashLocked) {
      setClashLocked(false);
    }
    
    // Stop any active knockback - rewind should cancel knockback motion
    setKnockbackVelocity1({ x: 0, y: 0 });
    setKnockbackVelocity2({ x: 0, y: 0 });
    
    // Get position, health, and ability states from 2 seconds ago (or earliest available)
    const rewindState1 = positionHistory1.length > 0 ? positionHistory1[0] : { x: top1Pos.x, y: top1Pos.y, health: health1 };
    const rewindState2 = positionHistory2[0];
    
    // Teleport both beyblades to their past positions
    if (top1Active && !top1KnockedOut) {
      setTop1Pos({ x: rewindState1.x, y: rewindState1.y });
    }
    setTop2Pos({ x: rewindState2.x, y: rewindState2.y });
    
    // Restore health for the beyblade that used Rewind Time (P2)
    if (rewindState2.health !== undefined) {
      setHealth2(rewindState2.health);
    }
    
    // Restore opponent's (P1) special ability cooldowns from 2 seconds ago
    if (rewindState1.deflectionCooldown !== undefined) {
      setDeflectionCooldown1(rewindState1.deflectionCooldown);
    }
    if (rewindState1.ghostCooldown !== undefined) {
      setGhostCooldown1(rewindState1.ghostCooldown);
    }
    if (rewindState1.speedBoostCooldown !== undefined) {
      setSpeedBoostCooldown1(rewindState1.speedBoostCooldown);
    }
    if (rewindState1.shadowCloneUsed !== undefined) {
      setShadowCloneUsed1(rewindState1.shadowCloneUsed);
    }
    
    // Restore opponent's (P1) active effects from 2 seconds ago
    // This removes any active abilities that were deployed in the last 2 seconds
    if (rewindState1.deflection !== undefined) {
      setDeflection1(rewindState1.deflection);
    }
    if (rewindState1.ghostMode !== undefined) {
      setGhostMode1(rewindState1.ghostMode);
    }
    if (rewindState1.speedBoost !== undefined) {
      setSpeedBoost1(rewindState1.speedBoost);
      // Also restore speed multiplier if speed boost was not active 2 seconds ago
      if (!rewindState1.speedBoost) {
        setSpeedMultiplier1(1);
      }
    }
    if (rewindState1.shadowClonesActive !== undefined) {
      setShadowClonesActive1(rewindState1.shadowClonesActive);
      // If shadow clones weren't active 2 seconds ago, remove them from the stadium
      if (!rewindState1.shadowClonesActive) {
        setShadowClones1([]);
      }
    }
    
    // Clear position history
    setPositionHistory1([]);
    setPositionHistory2([]);
    
    // Freeze both beyblades for 1 second
    setRewindFreezeActive(true);
    setIsControlling1(false);
    setIsControlling2(false);
    
    // Clear any existing freeze timer
    if (rewindFreezeTimerRef.current) {
      clearTimeout(rewindFreezeTimerRef.current);
    }
    
    // Unfreeze after 1 second
    rewindFreezeTimerRef.current = setTimeout(() => {
      setRewindFreezeActive(false);
    }, 1000);
  };

  // Final Drive ability handler for P1 (Pegasus Blast - ID 21)
  const handleFinalDrive1 = () => {
    if (finalDriveUsed1 || !top1Active || finalDriveActive1) return;
    
    setFinalDriveUsed1(true);
    setFinalDriveActive1(true);
    
    // Halve current stamina
    setStamina1(prev => Math.max(1, Math.floor(prev / 2)));
    
    // Clear any existing timer
    if (finalDriveTimer1Ref.current) {
      clearTimeout(finalDriveTimer1Ref.current);
    }
    
    // Deactivate after 4 seconds
    finalDriveTimer1Ref.current = setTimeout(() => {
      setFinalDriveActive1(false);
      finalDriveTimer1Ref.current = null;
    }, 4000);
  };

  // Final Drive ability handler for P2 (Pegasus Blast - ID 21)
  const handleFinalDrive2 = () => {
    if (finalDriveUsed2 || !top2Active || finalDriveActive2) return;
    
    setFinalDriveUsed2(true);
    setFinalDriveActive2(true);
    
    // Halve current stamina
    setStamina2(prev => Math.max(1, Math.floor(prev / 2)));
    
    // Clear any existing timer
    if (finalDriveTimer2Ref.current) {
      clearTimeout(finalDriveTimer2Ref.current);
    }
    
    // Deactivate after 4 seconds
    finalDriveTimer2Ref.current = setTimeout(() => {
      setFinalDriveActive2(false);
      finalDriveTimer2Ref.current = null;
    }, 4000);
  };

  // Restore Health ability handler for P1 (Wizard Rod - ID 24, Wizard Arc - ID 38)
  const handleRestoreHealth1 = () => {
    if (restoreHealthUsed1 || !top1Active) return;
    if (!isWizard(selectedBeyblade1?.id)) return; // Only for Wizard beyblades
    
    setRestoreHealthUsed1(true);
    
    // Fully restore health to max
    const maxHealth = getBeybladeStats(selectedBeyblade1?.id)?.health || 10;
    setHealth1(maxHealth);
  };

  // Restore Health ability handler for P2 (Wizard Rod - ID 24, Wizard Arc - ID 38)
  const handleRestoreHealth2 = () => {
    if (restoreHealthUsed2 || !top2Active) return;
    if (!isWizard(selectedBeyblade2?.id)) return; // Only for Wizard beyblades
    
    setRestoreHealthUsed2(true);
    
    // Fully restore health to max
    const maxHealth = getBeybladeStats(selectedBeyblade2?.id)?.health || 10;
    setHealth2(maxHealth);
  };

  // Iron Wall ability handler for P1 (Knight Mail - ID 31, Knight Mail B - ID 16)
  const handleIronWall1 = () => {
    if (ironWallUsed1 || !top1Active || ironWallActive1) return;
    if (!isKnight(selectedBeyblade1?.id)) return; // Only for Knight Mail beyblades
    
    setIronWallUsed1(true);
    setIronWallActive1(true);
    
    // Clear any existing timer
    if (ironWallTimer1Ref.current) {
      clearTimeout(ironWallTimer1Ref.current);
    }
    
    // Deactivate after 4 seconds
    ironWallTimer1Ref.current = setTimeout(() => {
      setIronWallActive1(false);
      ironWallTimer1Ref.current = null;
    }, 4000);
  };

  // Iron Wall ability handler for P2 (Knight Mail - ID 31, Knight Mail B - ID 16)
  const handleIronWall2 = () => {
    if (ironWallUsed2 || !top2Active || ironWallActive2) return;
    if (!isKnight(selectedBeyblade2?.id)) return; // Only for Knight Mail beyblades
    
    setIronWallUsed2(true);
    setIronWallActive2(true);
    
    // Clear any existing timer
    if (ironWallTimer2Ref.current) {
      clearTimeout(ironWallTimer2Ref.current);
    }
    
    // Deactivate after 4 seconds
    ironWallTimer2Ref.current = setTimeout(() => {
      setIronWallActive2(false);
      ironWallTimer2Ref.current = null;
    }, 4000);
  };

  // Burning Strike ability handler for P1 (Hells Beyblades - IDs 9, 23, 40)
  const handleBurningStrike1 = () => {
    if (burningStrikeUsed1 || !top1Active || burningStrikeActive1) return;
    if (!isHells(selectedBeyblade1?.id)) return; // Only for Hells Beyblades
    
    setBurningStrikeUsed1(true);
    setBurningStrikeActive1(true);
    burningStrikeDamageRef1.current = false; // Reset damage flag
    
    // Clear any existing timer
    if (burningStrikeTimer1Ref.current) {
      clearTimeout(burningStrikeTimer1Ref.current);
    }
    
    // Deactivate after 2 seconds
    burningStrikeTimer1Ref.current = setTimeout(() => {
      setBurningStrikeActive1(false);
      burningStrikeTimer1Ref.current = null;
    }, 2000);
  };

  // Burning Strike ability handler for P2 (Hells Beyblades - IDs 9, 23, 40)
  const handleBurningStrike2 = () => {
    if (burningStrikeUsed2 || !top2Active || burningStrikeActive2) return;
    if (!isHells(selectedBeyblade2?.id)) return; // Only for Hells Beyblades
    
    setBurningStrikeUsed2(true);
    setBurningStrikeActive2(true);
    burningStrikeDamageRef2.current = false; // Reset damage flag
    
    // Clear any existing timer
    if (burningStrikeTimer2Ref.current) {
      clearTimeout(burningStrikeTimer2Ref.current);
    }
    
    // Deactivate after 2 seconds
    burningStrikeTimer2Ref.current = setTimeout(() => {
      setBurningStrikeActive2(false);
      burningStrikeTimer2Ref.current = null;
    }, 2000);
  };

  const handleTouchpad1PointerDown = (e) => {
    // P1 touchpad always controls MY beyblade in online mode (all players use P1)
    if (!top1Active || touchpad1PointerRef.current.id !== null || cooldown1) return;
    
    // Can't control if beyblade stopped spinning
    if (stoppedSpinning1) return;
    
    e.preventDefault();
    e.stopPropagation();
    
    // BX-Infinity: Cancel Xtreme Dash when manual control is re-engaged
    if (selectedStadium === 'bxInfinity' && xtremeDashLine1Active) {
      setXtremeDashLine1Active(false);
      setXtremeDashLine1Color(null);
    }
    
    // Capture this specific pointer
    touchpad1PointerRef.current.id = e.pointerId;
    touchpad1PointerRef.current.lastX = e.clientX;
    touchpad1PointerRef.current.lastY = e.clientY;
    
    e.currentTarget.setPointerCapture(e.pointerId);
    
    touchpadActive1Ref.current = true;
    setIsControlling1(true);
    
    if (controlTimer1Ref.current) clearTimeout(controlTimer1Ref.current);
    
    // 2 second control timer
    controlTimer1Ref.current = setTimeout(() => {
      setIsControlling1(false);
      touchpadActive1Ref.current = false;
      
      // Force release - player must lift finger and touch again
      if (touchpad1PointerRef.current.id !== null && touchpad1Ref.current) {
        try {
          touchpad1Ref.current.releasePointerCapture(touchpad1PointerRef.current.id);
        } catch (e) {
          // Pointer might already be released
        }
        touchpad1PointerRef.current.id = null;
      }
      
      // Start 1 second cooldown
      setCooldown1(true);
      if (cooldownTimer1Ref.current) clearTimeout(cooldownTimer1Ref.current);
      cooldownTimer1Ref.current = setTimeout(() => {
        setCooldown1(false);
      }, 1000);
    }, 2000);
  };
  
  const handleTouchpad1PointerMove = (e) => {
    if (e.pointerId !== touchpad1PointerRef.current.id || touchpad1PointerRef.current.id === null) return;
    if (!isControlling1) return; // Stop if control ended
    
    e.preventDefault();
    e.stopPropagation();
    
    // Get beyblade's speed stat for touchpad sensitivity
    // Speed 50 = 100% sensitivity, Speed 70 = 120% sensitivity
    const beybladeSpeedStat = getBeybladeStats(selectedBeyblade1?.id)?.speed || 50;
    const speedStatModifier = 1 + (beybladeSpeedStat - 50) / 100;
    const baseSensitivity = 4;
    const sensitivity = baseSensitivity * speedStatModifier;
    
    const deltaX = (e.clientX - touchpad1PointerRef.current.lastX) * sensitivity;
    const deltaY = (e.clientY - touchpad1PointerRef.current.lastY) * sensitivity;
    
    touchpad1PointerRef.current.lastX = e.clientX;
    touchpad1PointerRef.current.lastY = e.clientY;
    
    // Clear knockback when manually controlled - this saves the Beyblade!
    setKnockbackVelocity1({ x: 0, y: 0 });
    
    setTop1Velocity({ x: deltaX, y: deltaY });
    setTop1Pos(prev => {
      let newX = prev.x + deltaX;
      let newY = prev.y + deltaY;
      
      // BX-Stadium: Apply hard boundary limits for manual control
      if (selectedStadium === 'bx') {
        // Hard boundaries - can't go beyond these no matter what
        const minY = BX_CENTER_Y - BX_RAIL_HEIGHT + 30;
        const maxY = BX_CENTER_Y + BX_RAIL_HEIGHT + 50; // Allow going into bottom pockets
        const minX = BX_CENTER_X - BX_RAIL_WIDTH + 10;
        const maxX = BX_CENTER_X + BX_RAIL_WIDTH - 10;
        
        // Check if in a knockout pocket zone - use angle-based detection with zoneConfig
        const dx = newX - BX_CENTER_X;
        const dy = newY - BX_CENTER_Y;
        const normalizedDist = (dx * dx) / (BX_RAIL_WIDTH * BX_RAIL_WIDTH) + 
                               (dy * dy) / (BX_RAIL_HEIGHT * BX_RAIL_HEIGHT);
        const angle = Math.atan2(dy, dx);
        
        // Check pocket zones using zoneConfig (when near boundary and in bottom area)
        const inBottomArea = normalizedDist > 0.85 && dy > 0;
        const inLeftZone = angle > zoneConfig.leftZone.endAngle && angle <= zoneConfig.leftZone.startAngle;
        const inCenterZone = angle > zoneConfig.centerZone.endAngle && angle <= zoneConfig.centerZone.startAngle;
        const inRightZone = angle >= zoneConfig.rightZone.endAngle && angle <= zoneConfig.rightZone.startAngle;
        
        const nearPocket = inBottomArea && (inLeftZone || inCenterZone || inRightZone);
        
        if (!nearPocket) {
          // Not near a pocket - apply normal boundary limits
          // Clamp position to stadium boundaries
          newX = Math.max(minX, Math.min(maxX, newX));
          newY = Math.max(minY, Math.min(maxY, newY));
          
          // Also check distance from center for elliptical boundary
          const dx2 = newX - BX_CENTER_X;
          const dy2 = newY - BX_CENTER_Y;
          // Normalized distance for ellipse: (x/a)^2 + (y/b)^2 <= 1
          const normalizedDist2 = (dx2 * dx2) / (BX_RAIL_WIDTH * BX_RAIL_WIDTH) + 
                                 (dy2 * dy2) / (BX_RAIL_HEIGHT * BX_RAIL_HEIGHT);
          
          if (normalizedDist2 > 0.85) {
            // Too close to edge, scale back position
            const scale = Math.sqrt(0.85 / normalizedDist2);
            newX = BX_CENTER_X + dx2 * scale;
            newY = BX_CENTER_Y + dy2 * scale;
          }
        }
        // If near pocket, allow free movement toward it
      }
      
      // Prevent manual control from going out of bounds (but don't KO)
      if (isOutOfBounds({ x: newX, y: newY }).outOfBounds) {
        return prev; // Don't allow movement out of bounds
      }
      
      // Check wall collision
      const afterWallCollision = applyWallCollision1(newX, newY, prev.x, prev.y);
      
      // Check Beyblade collision
      const finalPos = handleBeybladeCollision(afterWallCollision, top2Pos, true, isControlling2, top1KnockedOut, top2KnockedOut);
      
      // Apply knockback if hitting
      applyKnockbackOnHit(finalPos, top2Pos, true, isControlling2);
      
      return finalPos;
    });
  };
  
  const handleTouchpad1PointerUp = (e) => {
    if (e.pointerId !== touchpad1PointerRef.current.id) return;
    
    e.preventDefault();
    e.stopPropagation();
    
    touchpad1PointerRef.current.id = null;
    setIsControlling1(false);
    touchpadActive1Ref.current = false;
    
    if (controlTimer1Ref.current) {
      clearTimeout(controlTimer1Ref.current);
      
      // Start cooldown when manually released early
      setCooldown1(true);
      if (cooldownTimer1Ref.current) clearTimeout(cooldownTimer1Ref.current);
      cooldownTimer1Ref.current = setTimeout(() => {
        setCooldown1(false);
      }, 1000);
    }
  };
  
  // Player 2 Touchpad Handlers (using Pointer Events)
  const handleTouchpad2PointerDown = (e) => {
    // In online mode, P2 is ALWAYS the opponent - disable touchpad completely
    if (isOnlineMode && matchFound) return;
    if (aiMode && e.pointerId !== 9999) return;
    if (!top2Active || touchpad2PointerRef.current.id !== null || cooldown2) return;
    
    // Can't control if beyblade stopped spinning
    if (stoppedSpinning2) return;
    
    e.preventDefault();
    e.stopPropagation();
    
    // BX-Infinity: Cancel Xtreme Dash when manual control is re-engaged
    if (selectedStadium === 'bxInfinity' && xtremeDashLine2Active) {
      setXtremeDashLine2Active(false);
      setXtremeDashLine2Color(null);
    }
    
    // Capture this specific pointer
    touchpad2PointerRef.current.id = e.pointerId;
    touchpad2PointerRef.current.lastX = e.clientX;
    touchpad2PointerRef.current.lastY = e.clientY;
    
    e.currentTarget.setPointerCapture(e.pointerId);
    
    touchpadActive2Ref.current = true;
    setIsControlling2(true);
    
    if (controlTimer2Ref.current) clearTimeout(controlTimer2Ref.current);
    
    // 2 second control timer
    controlTimer2Ref.current = setTimeout(() => {
      setIsControlling2(false);
      touchpadActive2Ref.current = false;
      
      // Force release - player must lift finger and touch again
      if (touchpad2PointerRef.current.id !== null && touchpad2Ref.current) {
        try {
          touchpad2Ref.current.releasePointerCapture(touchpad2PointerRef.current.id);
        } catch (e) {
          // Pointer might already be released
        }
        touchpad2PointerRef.current.id = null;
      }
      
      // Start 1 second cooldown
      setCooldown2(true);
      if (cooldownTimer2Ref.current) clearTimeout(cooldownTimer2Ref.current);
      cooldownTimer2Ref.current = setTimeout(() => {
        setCooldown2(false);
      }, 1000);
    }, 2000);
  };
  
  const handleTouchpad2PointerMove = (e) => {
    if (e.pointerId !== touchpad2PointerRef.current.id || touchpad2PointerRef.current.id === null) return;
    if (!isControlling2) return; // Stop if control ended
    
    e.preventDefault();
    e.stopPropagation();
    
    // Get beyblade's speed stat for touchpad sensitivity
    // Speed 50 = 100% sensitivity, Speed 70 = 120% sensitivity
    const beybladeSpeedStat = getBeybladeStats(selectedBeyblade2?.id)?.speed || 50;
    const speedStatModifier = 1 + (beybladeSpeedStat - 50) / 100;
    const baseSensitivity = 4;
    const sensitivity = baseSensitivity * speedStatModifier;
    
    const deltaX = (e.clientX - touchpad2PointerRef.current.lastX) * sensitivity;
    const deltaY = (e.clientY - touchpad2PointerRef.current.lastY) * sensitivity;
    
    touchpad2PointerRef.current.lastX = e.clientX;
    touchpad2PointerRef.current.lastY = e.clientY;
    
    // Clear knockback when manually controlled - this saves the Beyblade!
    setKnockbackVelocity2({ x: 0, y: 0 });
    
    setTop2Velocity({ x: deltaX, y: deltaY });
    setTop2Pos(prev => {
      let newX = prev.x + deltaX;
      let newY = prev.y + deltaY;
      
      // BX-Stadium: Apply hard boundary limits for manual control
      if (selectedStadium === 'bx') {
        // Hard boundaries - can't go beyond these no matter what
        const minY = BX_CENTER_Y - BX_RAIL_HEIGHT + 30;
        const maxY = BX_CENTER_Y + BX_RAIL_HEIGHT + 50; // Allow going into bottom pockets
        const minX = BX_CENTER_X - BX_RAIL_WIDTH + 10;
        const maxX = BX_CENTER_X + BX_RAIL_WIDTH - 10;
        
        // Check if in a knockout pocket zone - use angle-based detection with zoneConfig
        const dx = newX - BX_CENTER_X;
        const dy = newY - BX_CENTER_Y;
        const normalizedDist = (dx * dx) / (BX_RAIL_WIDTH * BX_RAIL_WIDTH) + 
                               (dy * dy) / (BX_RAIL_HEIGHT * BX_RAIL_HEIGHT);
        const angle = Math.atan2(dy, dx);
        
        // Check pocket zones using zoneConfig (when near boundary and in bottom area)
        const inBottomArea = normalizedDist > 0.85 && dy > 0;
        const inLeftZone = angle > zoneConfig.leftZone.endAngle && angle <= zoneConfig.leftZone.startAngle;
        const inCenterZone = angle > zoneConfig.centerZone.endAngle && angle <= zoneConfig.centerZone.startAngle;
        const inRightZone = angle >= zoneConfig.rightZone.endAngle && angle <= zoneConfig.rightZone.startAngle;
        
        const nearPocket = inBottomArea && (inLeftZone || inCenterZone || inRightZone);
        
        if (!nearPocket) {
          // Not near a pocket - apply normal boundary limits
          // Clamp position to stadium boundaries
          newX = Math.max(minX, Math.min(maxX, newX));
          newY = Math.max(minY, Math.min(maxY, newY));
          
          // Also check distance from center for elliptical boundary
          const dx2 = newX - BX_CENTER_X;
          const dy2 = newY - BX_CENTER_Y;
          // Normalized distance for ellipse: (x/a)^2 + (y/b)^2 <= 1
          const normalizedDist2 = (dx2 * dx2) / (BX_RAIL_WIDTH * BX_RAIL_WIDTH) + 
                                 (dy2 * dy2) / (BX_RAIL_HEIGHT * BX_RAIL_HEIGHT);
          
          // AI may push out to the rail to reach a rail-riding opponent
          const edgeLimit = aiMode ? 0.97 : 0.85;
          if (normalizedDist2 > edgeLimit) {
            // Too close to edge, scale back position
            const scale = Math.sqrt(edgeLimit / normalizedDist2);
            newX = BX_CENTER_X + dx2 * scale;
            newY = BX_CENTER_Y + dy2 * scale;
          }
        }
        // If near pocket, allow free movement toward it
      }
      
      // Prevent manual control from going out of bounds (but don't KO)
      if (isOutOfBounds({ x: newX, y: newY }).outOfBounds) {
        return prev; // Don't allow movement out of bounds
      }
      
      // Check wall collision
      const afterWallCollision = applyWallCollision2(newX, newY, prev.x, prev.y);
      
      // Check Beyblade collision  
      const finalPos = handleBeybladeCollision(afterWallCollision, top1Pos, true, isControlling1, top2KnockedOut, top1KnockedOut);
      
      // Apply knockback if hitting - P2 attacking P1
      applyKnockbackOnHit(top1Pos, finalPos, isControlling1, true);
      
      return finalPos;
    });
  };
  
  const handleTouchpad2PointerUp = (e) => {
    if (e.pointerId !== touchpad2PointerRef.current.id) return;
    
    e.preventDefault();
    e.stopPropagation();
    
    touchpad2PointerRef.current.id = null;
    setIsControlling2(false);
    touchpadActive2Ref.current = false;
    
    if (controlTimer2Ref.current) {
      clearTimeout(controlTimer2Ref.current);
      
      // Start cooldown when manually released early
      setCooldown2(true);


      if (cooldownTimer2Ref.current) clearTimeout(cooldownTimer2Ref.current);
      cooldownTimer2Ref.current = setTimeout(() => {
        setCooldown2(false);
      }, 1000);
    }
  };

  // ==================== VS AI BRAIN (Normal difficulty) ====================
  const AI_POINTER_ID = 9999;
  const aiStateRef = useRef({});
  aiStateRef.current = {
    top1Pos, top2Pos, health1, health2, isControlling1, isControlling2, cooldown2,
    speedBoost1, finalDriveActive1, burningStrikeActive1, ghostMode1, deflection1, ironWallActive1,
    speedBoost2, speedBoostCooldown2, ghostMode2, ghostCooldown2, deflection2, deflectionCooldown2,
    finalDriveUsed2, restoreHealthUsed2, ironWallUsed2, burningStrikeUsed2, shadowCloneUsed2, rewindUsed2,
    clashLocked, stoppedSpinning2, top1KnockedOut, top2KnockedOut, roundWinner, isDraw, top1Active,
    beyId: selectedBeyblade2?.id,
    down: handleTouchpad2PointerDown, move: handleTouchpad2PointerMove, up: handleTouchpad2PointerUp,
    boost: handleSpeedBoost2, ghost: handleGhostMode2, deflect: handleDeflection2,
    finalDrive: handleFinalDrive2, restore: handleRestoreHealth2, ironWall: handleIronWall2,
    burning: handleBurningStrike2, clones: handleShadowClones2, rewind: handleRewindTime2,
  };

  useEffect(() => {
    if (!aiMode || !top2Active) return;
    const fakeEvent = (x, y) => ({
      pointerId: AI_POINTER_ID, clientX: x, clientY: y,
      preventDefault: () => {}, stopPropagation: () => {},
      currentTarget: { setPointerCapture: () => {} },
    });
    const brain = { controlling: false, releaseAt: 0, nextDecisionAt: Date.now() + 300, nextAbilityAt: Date.now() + 1200, cx: 0, cy: 0, aimErr: 0, retreat: false, p1vx: 0, p1vy: 0, launchedAt: Date.now(), lastP1ActiveAt: 0 };
    const chance = (p) => Math.random() < p;

    const release = () => {
      if (!brain.controlling) return;
      brain.controlling = false;
      const s = aiStateRef.current;
      if (touchpad2PointerRef.current.id === AI_POINTER_ID) s.up(fakeEvent(brain.cx, brain.cy));
    };

    const useAbilities = (s, dist, now) => {
      if (now < brain.nextAbilityAt) return;
      brain.nextAbilityAt = now + 400;
      const id = s.beyId;
      const maxHp = getMaxHealth(id);
      const lowHp = s.health2 <= maxHp * 0.4;
      const p1Threat = s.speedBoost1 || s.finalDriveActive1 || s.burningStrikeActive1;
      const close = dist < 130 * SCALE_FACTOR;

      // Defensive reactions to P1 power attacks
      if (p1Threat && close) {
        if (isKnight(id) && !s.ironWallUsed2 && chance(0.7)) { s.ironWall(); return; }
        if (!s.deflection2 && !s.deflectionCooldown2 && chance(0.6)) { s.deflect(); return; }
        if (!s.ghostMode2 && !s.ghostCooldown2 && chance(0.5)) { s.ghost(); return; }
      }
      // Specials
      if (isWizard(id) && !s.restoreHealthUsed2 && lowHp) { s.restore(); return; }
      if ((id === 36 && !s.shadowCloneUsed2) && (lowHp || (close && chance(0.15)))) { s.clones(); return; }
      if ((id === 49 && !s.rewindUsed2) && lowHp && chance(0.5)) { s.rewind(); return; }
      if (isHells(id) && !s.burningStrikeUsed2 && dist < 100 * SCALE_FACTOR && chance(0.5)) { s.burning(); return; }
      if (hasFinalDrive(id) && !s.finalDriveUsed2 && close && !lowHp && chance(0.35)) { s.finalDrive(); return; }
      // Offensive speed boost when attacking at close range (or to break a clash lock)
      if (!s.speedBoost2 && !s.speedBoostCooldown2 && !s.deflection1 && !s.ironWallActive1 && !s.ghostMode1) {
        if (s.clashLocked && chance(0.6)) { s.boost(); return; }
        if (brain.controlling && !brain.retreat && close && chance(0.3)) { s.boost(); return; }
      }
    };

    const tick = () => {
      const s = aiStateRef.current;
      const now = Date.now();
      if (s.isControlling1) brain.lastP1ActiveAt = now;
      // Track P1 velocity (px/ms, smoothed) for predictive interception
      if (brain.lastP1) {
        const dt = Math.max(1, now - brain.lastP1.t);
        brain.p1vx = brain.p1vx * 0.7 + ((s.top1Pos.x - brain.lastP1.x) / dt) * 0.3;
        brain.p1vy = brain.p1vy * 0.7 + ((s.top1Pos.y - brain.lastP1.y) / dt) * 0.3;
      }
      brain.lastP1 = { x: s.top1Pos.x, y: s.top1Pos.y, t: now };
      if (s.roundWinner || s.isDraw || s.top1KnockedOut || s.top2KnockedOut || s.stoppedSpinning2) { release(); return; }
      const dx = s.top1Pos.x - s.top2Pos.x;
      const dy = s.top1Pos.y - s.top2Pos.y;
      const dist = Math.sqrt(dx * dx + dy * dy);
      if (s.top1Active) useAbilities(s, dist, now);

      if (brain.controlling) {
        // Control ended by the 2s game timer
        if (now - brain.startedAt > 150 && (!s.isControlling2 || touchpad2PointerRef.current.id !== AI_POINTER_ID)) {
          brain.controlling = false;
          return;
        }
        if (now >= brain.releaseAt) { release(); return; }
        const isBx = selectedStadium === 'bx';
        const ccx = isBx ? BX_CENTER_X : STADIUM_CENTER_X;
        const ccy = isBx ? BX_CENTER_Y : STADIUM_CENTER_Y;
        // Normalized distance from center (1 = movement edge); BX is an oval
        const edgeNorm = (x, y) => isBx
          ? Math.sqrt(((x - ccx) / BX_RAIL_WIDTH) ** 2 + ((y - ccy) / BX_RAIL_HEIGHT) ** 2) / Math.sqrt(0.97)
          : Math.sqrt((x - ccx) ** 2 + (y - ccy) ** 2) / BEYBLADE_RADIUS;
        const myEdge = edgeNorm(s.top2Pos.x, s.top2Pos.y);
        // Only back off the edge if P1 isn't out there too (don't chase past the wall)
        const nearEdge = myEdge > 1.02 && myEdge > edgeNorm(s.top1Pos.x, s.top1Pos.y);
        // Lead the target: aim where P1 will be, not where it is
        const lead = Math.min(700, dist / 0.35);
        const tx = s.top1Pos.x + (brain.p1vx || 0) * lead;
        const ty = s.top1Pos.y + (brain.p1vy || 0) * lead;
        let angle;
        if (nearEdge && (brain.retreat || dist > 90 * SCALE_FACTOR)) angle = Math.atan2(ccy - s.top2Pos.y, ccx - s.top2Pos.x);
        else if (brain.retreat) angle = Math.atan2(-dy, -dx);
        else angle = Math.atan2(ty - s.top2Pos.y, tx - s.top2Pos.x) + brain.aimErr;
        brain.aimErr *= 0.9;
        const step = dist > 150 * SCALE_FACTOR ? 2.2 : 1.6;
        brain.cx += Math.cos(angle) * step;
        brain.cy += Math.sin(angle) * step;
        s.move(fakeEvent(brain.cx, brain.cy));
        return;
      }

      if (now < brain.nextDecisionAt || s.cooldown2 || !s.top1Active) return;
      // Reaction delay so the AI isn't perfect
      brain.nextDecisionAt = now + 250 + Math.random() * 450;
      const lowHp = s.health2 <= getMaxHealth(s.beyId) * 0.4;
      const p1Attacking = s.isControlling1 || s.speedBoost1 || s.finalDriveActive1;
      const p1Idle = now - brain.lastP1ActiveAt > 1500;
      const shouldAct = p1Attacking ? chance(0.7) : p1Idle ? chance(0.8) : chance(0.45);
      if (!shouldAct) return;
      brain.retreat = lowHp && p1Attacking && chance(0.6);
      brain.aimErr = (Math.random() - 0.5) * 0.6;
      brain.cx = 0; brain.cy = 0;
      brain.controlling = true;
      brain.startedAt = now;
      brain.releaseAt = now + 900 + Math.random() * 900;
      s.down(fakeEvent(0, 0));
    };

    const interval = setInterval(tick, 30);
    return () => { clearInterval(interval); release(); };
  }, [aiMode, top2Active]);
  // ==========================================================================


  // Show loading screen while assets are being pre-loaded
  if (!assetsLoaded) {
    return (
      <div className="flex flex-col items-center justify-center gap-4" style={{ minHeight: '600px' }}>
        <div className="text-cyan-400 text-2xl font-bold animate-pulse">
          Loading Stadium...
        </div>
        <div className="flex gap-2">
          <div className="w-3 h-3 bg-cyan-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
          <div className="w-3 h-3 bg-cyan-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
          <div className="w-3 h-3 bg-cyan-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
        </div>
      </div>
    );
  }

  // Disable opponent controls in online mode
  // In online mode: RIGHT (P1) is always YOU, LEFT (P2) is always OPPONENT
  // EVERYONE controls the RIGHT side (P1)
  // EVERYONE's opponent is on the LEFT side (P2) - always disabled
  const isPlayer2Disabled = (isOnlineMode && matchFound) || aiMode;
  const p2StyleDisabled = isOnlineMode && matchFound;
  const isPlayer1Disabled = isOnlineMode && matchFound ? false : false; // Never disable RIGHT (you) in online mode
  
  return (
    <div 
      className="flex flex-col items-center gap-4 select-none relative overflow-hidden h-full"
      onContextMenu={(e) => e.preventDefault()}
      style={{
        WebkitTouchCallout: 'none',
        WebkitUserSelect: 'none',
        userSelect: 'none',
        touchAction: 'none',
        overscrollBehavior: 'none'
      }}
    >
      {/* Health Bars with Scoreboards - Always Visible */}
      {/* Consistent spacing for all stadiums */}
      <div className={`flex gap-3 items-center justify-center w-full max-w-md px-4 relative z-[100] mb-4`}>
        {/* Player 2 Health Bar (Left Side) - Entire section clickable - extended upward to title area */}
        <div 
          className={`flex-1 relative z-[100] px-2 pb-2 pt-24 -mx-2 -mb-2 -mt-24 ${!isOnlineMode && !top2Active && !top1Active ? 'cursor-pointer' : ''}`}
          onClick={(e) => {
            e.stopPropagation();
            if (!isOnlineMode && !top2Active && !top1Active) {
              setShowGuestPlayerModal(true);
            }
          }}
          onTouchEnd={(e) => {
            e.stopPropagation();
            if (!isOnlineMode && !top2Active && !top1Active) {
              setShowGuestPlayerModal(true);
            }
          }}
        >
          {/* Player 2 Name Banner - NO handlers, let clicks bubble to parent like health bars */}
          <div 
            className={`text-[8px] font-bold mb-1 flex items-center gap-1 h-6 px-1 rounded relative overflow-hidden ${!isOnlineMode && !guestPlayer && !top2Active && !top1Active ? 'animate-pulse' : ''}`}
            style={guestPlayer?.banner_image ? {
              backgroundImage: `url(${guestPlayer.banner_image})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center'
            } : { backgroundColor: 'rgba(30, 41, 59, 0.6)' }}
          >
            {guestPlayer?.banner_image && <div className="absolute inset-0 bg-black/40"></div>}
            
            {guestPlayer?.profile_picture ? (
              <img src={guestPlayer.profile_picture} alt="" className="w-5 h-5 rounded-full object-cover border flex-shrink-0 relative z-10" style={{ borderColor: guestPlayer.circle_color || '#475569' }} />
            ) : guestPlayer ? (
              <span className="w-5 h-5 rounded-full bg-slate-700 border flex items-center justify-center text-slate-300 text-[8px] font-semibold flex-shrink-0 relative z-10" style={{ borderColor: guestPlayer.circle_color || '#475569' }}>
                {guestPlayer.username.charAt(0).toUpperCase()}
              </span>
            ) : (
              <span className="w-5 h-5 rounded-full bg-slate-700/50 flex items-center justify-center text-orange-400 text-[8px] font-semibold flex-shrink-0 relative z-10">P2</span>
            )}
            
            {/* Username and BP/Lv stacked */}
            <div className="flex-1 min-w-0 flex flex-col justify-between py-[2px] relative z-10">
              <div className={`text-[10px] font-bold truncate flex items-center gap-0.5 -mt-[1px] ${guestPlayer?.banner_image ? 'text-white drop-shadow' : 'text-orange-400'}`}>
                {isOnlineMode && matchFound ? opponentUsername || 'Opponent' : guestPlayer ? guestPlayer.username : 'Add Player'}
                {guestPlayer?.has_made_first_purchase && (
                  <svg className="w-2 h-2 text-blue-500 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M6.267 3.455a3.066 3.066 0 001.745-.723 3.066 3.066 0 013.976 0 3.066 3.066 0 001.745.723 3.066 3.066 0 012.812 2.812c.051.643.304 1.254.723 1.745a3.066 3.066 0 010 3.976 3.066 3.066 0 00-.723 1.745 3.066 3.066 0 01-2.812 2.812 3.066 3.066 0 00-1.745.723 3.066 3.066 0 01-3.976 0 3.066 3.066 0 00-1.745-.723 3.066 3.066 0 01-2.812-2.812 3.066 3.066 0 00-.723-1.745 3.066 3.066 0 010-3.976 3.066 3.066 0 00.723-1.745 3.066 3.066 0 012.812-2.812zm7.44 5.252a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                )}
              </div>
              {guestPlayer && (
                <div className={`text-[9px] digital-font flex items-center justify-between -mt-0.5 ${guestPlayer?.banner_image ? 'text-white/80 drop-shadow' : 'text-orange-300/70'}`}>
                  <span>Lv{guestPlayer.level || 1}</span>
                  <span>{guestPlayer.battle_points || 0} BP</span>
                </div>
              )}
            </div>
          </div>
          
          {/* Stamina Bar */}
          <div className="h-2 bg-slate-700/80 rounded-full overflow-hidden border border-white/30 mb-1">
            <div className="h-full bg-gradient-to-r from-white to-gray-200 transition-all duration-100" style={{ width: `${(stamina2 / maxStamina2) * 100}%` }} />
          </div>
          <div className="h-4 bg-slate-700/80 rounded-full overflow-hidden border border-orange-500/50">
            <div className="h-full bg-gradient-to-r from-orange-500 to-red-500 transition-all duration-300" style={{ width: `${(health2 / getMaxHealth(selectedBeyblade2?.id)) * 100}%` }} />
          </div>
          <div className="text-xs text-orange-300 font-bold text-center mt-1">{health2}/{getMaxHealth(selectedBeyblade2?.id)}</div>
        </div>

        {/* Scoreboards - Between health bars (horizontal) */}
        <div className="flex flex-col items-center relative">
          {/* Scores row */}
          <div className="flex flex-row gap-1.5 -mt-[18px] mb-1">
            {/* Player 2 Score - Left */}
            <div 
              className={`bg-gradient-to-r from-orange-600/20 to-orange-500/10 border border-orange-500/50 rounded py-0.5 w-[46px] ${!(isOnlineMode && matchFound) ? 'cursor-pointer hover:border-orange-400 hover:bg-orange-500/20 transition-all' : ''}`}
              onClick={() => {
                if (!(isOnlineMode && matchFound)) {
                  setScoreAdjustPlayer(scoreAdjustPlayer === 2 ? null : 2);
                }
              }}
            >
              <div className="text-base font-bold text-orange-300 text-center digital-font">
                {String(isOnlineMode && matchFound ? (myPlayerNumber === 1 ? opponentScore : myScore) : player2Score).padStart(2, '0')}
              </div>
            </div>
          
          {/* Player 1 Score - Right */}
          <div 
            className={`bg-gradient-to-r from-cyan-600/20 to-cyan-500/10 border border-cyan-500/50 rounded py-0.5 w-[46px] ${!(isOnlineMode && matchFound) ? 'cursor-pointer hover:border-cyan-400 hover:bg-cyan-500/20 transition-all' : ''}`}
            onClick={() => {
              if (!(isOnlineMode && matchFound)) {
                setScoreAdjustPlayer(scoreAdjustPlayer === 1 ? null : 1);
              }
            }}
          >
            <div className="text-base font-bold text-cyan-300 text-center digital-font">
              {String(isOnlineMode && matchFound ? (myPlayerNumber === 2 ? opponentScore : myScore) : player1Score).padStart(2, '0')}
            </div>
          </div>
          </div>

          {/* Countdown Button - Centered below scores */}
          <button
            onClick={startCountdown}
            onTouchStart={(e) => { e.stopPropagation(); }}
            onTouchEnd={(e) => { e.stopPropagation(); e.preventDefault(); startCountdown(); }}
            disabled={countdownActive}
            className={`px-3 h-6 min-w-[75px] rounded text-xs font-bold digital-font transition-all flex items-center justify-center touch-manipulation bg-slate-700/50 border ${
              countdownText === 'Go Shoot!' 
                ? 'border-green-400/70' 
                : countdownText === 'START' 
                  ? 'border-slate-500/50' 
                  : countdownText === '1!'
                    ? 'border-yellow-400/70'
                    : 'border-red-400/70'
            } ${
              countdownActive 
                ? 'cursor-default pointer-events-none' 
                : 'hover:bg-slate-600/50 hover:border-slate-400/50 active:bg-slate-500/50'
            }`}
            style={{ WebkitTapHighlightColor: 'transparent' }}
          >
            <span className={
              countdownText === 'Go Shoot!' 
                ? 'text-green-400' 
                : countdownText === 'START' 
                  ? 'text-slate-300' 
                  : countdownText === '1!'
                    ? 'text-yellow-400'
                    : 'text-red-400'
            }>
              {countdownText}
            </span>
          </button>
          
          {/* Score Adjustment Popup */}
          {scoreAdjustPlayer && (
            <div 
              className={`absolute top-full mt-1 left-1/2 -translate-x-1/2 z-50 bg-gray-900/95 border ${scoreAdjustPlayer === 2 ? 'border-orange-500/50' : 'border-cyan-500/50'} rounded-lg p-2 shadow-lg`}
              onClick={(e) => e.stopPropagation()}
            >
              <div className={`text-xs font-bold mb-1 text-center ${scoreAdjustPlayer === 2 ? 'text-orange-300' : 'text-cyan-300'}`}>
                {scoreAdjustPlayer === 1 ? (user?.username || 'P1') : (guestPlayer?.username || 'P2')}
              </div>
              <div className="flex items-center gap-2">
                <button
                  className={`w-7 h-7 rounded ${scoreAdjustPlayer === 2 ? 'bg-orange-600 hover:bg-orange-500' : 'bg-cyan-600 hover:bg-cyan-500'} text-white font-bold text-lg flex items-center justify-center transition-colors`}
                  onClick={() => {
                    if (scoreAdjustPlayer === 1) {
                      setPlayer1Score(prev => Math.max(0, prev - 1));
                    } else {
                      setPlayer2Score(prev => Math.max(0, prev - 1));
                    }
                  }}
                >
                  −
                </button>
                <span className={`text-lg font-bold min-w-[28px] text-center digital-font ${scoreAdjustPlayer === 2 ? 'text-orange-300' : 'text-cyan-300'}`}>
                  {scoreAdjustPlayer === 1 ? player1Score : player2Score}
                </span>
                <button
                  className={`w-7 h-7 rounded ${scoreAdjustPlayer === 2 ? 'bg-orange-600 hover:bg-orange-500' : 'bg-cyan-600 hover:bg-cyan-500'} text-white font-bold text-lg flex items-center justify-center transition-colors`}
                  onClick={() => {
                    if (scoreAdjustPlayer === 1) {
                      setPlayer1Score(prev => prev + 1);
                    } else {
                      setPlayer2Score(prev => prev + 1);
                    }
                  }}
                >
                  +
                </button>
              </div>
              <button
                className="w-full mt-2 text-xs text-gray-400 hover:text-white transition-colors"
                onClick={() => setScoreAdjustPlayer(null)}
              >
                Close
              </button>
            </div>
          )}
        </div>

        {/* Player 1 Health Bar (Right Side) */}
        <div className="flex-1">
          {/* Player 1 Mini Profile Banner - Compact horizontal */}
          <div 
            className="rounded h-6 mb-1 px-1 flex items-center gap-1 relative overflow-hidden"
            style={user?.banner_image ? {
              backgroundImage: `url(${user.banner_image})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center'
            } : { backgroundColor: 'rgba(30, 41, 59, 0.6)' }}
          >
            {user?.banner_image && <div className="absolute inset-0 bg-black/40"></div>}
            
            {/* Profile Picture - tiny */}
            <div className="relative z-10 flex-shrink-0">
              {user?.profile_picture ? (
                <img src={user.profile_picture} alt="" className="w-5 h-5 rounded-full object-cover border" style={{ borderColor: user.circle_color || '#475569' }} />
              ) : (
                <div className="w-5 h-5 rounded-full bg-slate-700 border flex items-center justify-center text-slate-300 text-[8px] font-semibold" style={{ borderColor: user?.circle_color || '#475569' }}>
                  {user?.username?.charAt(0).toUpperCase() || 'P1'}
                </div>
              )}
            </div>
            
            {/* Username and BP/Lv stacked */}
            <div className="flex-1 min-w-0 flex flex-col justify-between py-[2px] relative z-10">
              <div className={`text-[10px] font-bold truncate flex items-center gap-0.5 -mt-[1px] ${user?.banner_image ? 'text-white drop-shadow' : 'text-cyan-400'}`}>
                {user?.username || 'PLAYER 1'}
                {user?.has_made_first_purchase && (
                  <svg className="w-2 h-2 text-blue-500 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M6.267 3.455a3.066 3.066 0 001.745-.723 3.066 3.066 0 013.976 0 3.066 3.066 0 001.745.723 3.066 3.066 0 012.812 2.812c.051.643.304 1.254.723 1.745a3.066 3.066 0 010 3.976 3.066 3.066 0 00-.723 1.745 3.066 3.066 0 01-2.812 2.812 3.066 3.066 0 00-1.745.723 3.066 3.066 0 01-3.976 0 3.066 3.066 0 00-1.745-.723 3.066 3.066 0 01-2.812-2.812 3.066 3.066 0 00-.723-1.745 3.066 3.066 0 010-3.976 3.066 3.066 0 00.723-1.745 3.066 3.066 0 012.812-2.812zm7.44 5.252a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                )}
              </div>
              <div className={`text-[9px] digital-font flex items-center justify-between -mt-0.5 ${user?.banner_image ? 'text-white/80 drop-shadow' : 'text-cyan-300/70'}`}>
                <span>Lv{user?.level || 1}</span>
                <span>{user?.battle_points || 0} BP</span>
              </div>
            </div>
          </div>
          
          {/* Stamina Bar */}
          <div className="h-2 bg-slate-700/80 rounded-full overflow-hidden border border-white/30 mb-1">
            <div className="h-full bg-gradient-to-r from-white to-gray-200 transition-all duration-100" style={{ width: `${(stamina1 / maxStamina1) * 100}%` }} />
          </div>
          <div className="h-4 bg-slate-700/80 rounded-full overflow-hidden border border-cyan-500/50">
            <div className="h-full bg-gradient-to-r from-cyan-500 to-blue-500 transition-all duration-300" style={{ width: `${(health1 / getMaxHealth(selectedBeyblade1?.id)) * 100}%` }} />
          </div>
          <div className="text-xs text-cyan-300 font-bold text-center mt-1">{health1}/{getMaxHealth(selectedBeyblade1?.id)}</div>
        </div>
      </div>
      
      {/* Map Canvas */}
      <div ref={containerRef} className="relative w-full max-w-4xl mx-auto" style={{ aspectRatio: '4/3' }}>
        <canvas
          ref={canvasRef}
          width={CANVAS_WIDTH}
          height={CANVAS_HEIGHT}
          onClick={handleCanvasClick}
          onContextMenu={(e) => e.preventDefault()}
          className="cursor-crosshair relative w-full h-full"
          style={{ 
            zIndex: 1,
            touchAction: 'none',
            WebkitTouchCallout: 'none',
            opacity: 0
          }}
        />
        
        {/* Stadium Image Overlay - z-index 5 ensures it's above grid but below beyblades */}
        {/* Stadium size now scales with canvas dimensions to match physics */}
        {/* BX-Stadium fills the entire area, BB-10 uses original sizing, BX-Infinity uses medium sizing */}
        <div 
          className="absolute pointer-events-none overflow-hidden"
          style={{
            top: selectedStadium === 'bx' ? '-10%' : selectedStadium === 'bxInfinity' ? '-15%' : '50%',
            left: selectedStadium === 'bx' ? 0 : selectedStadium === 'bxInfinity' ? '0%' : '50%',
            right: selectedStadium === 'bx' ? 0 : selectedStadium === 'bxInfinity' ? '0%' : 'auto',
            bottom: selectedStadium === 'bx' ? '-8%' : selectedStadium === 'bxInfinity' ? '-10%' : 'auto',
            width: selectedStadium === 'bx' ? '100%' : selectedStadium === 'bxInfinity' ? '100%' : `${320 * SCALE_FACTOR}px`,
            height: selectedStadium === 'bx' ? '118%' : selectedStadium === 'bxInfinity' ? '125%' : `${320 * SCALE_FACTOR}px`,
            zIndex: 5,
            isolation: 'isolate',
            transform: selectedStadium === 'bx' ? 'none' : selectedStadium === 'bxInfinity' ? 'none' : 'translate(-50%, -50%) translateZ(0)',
            willChange: 'transform',
            contain: 'layout style paint',
            display: selectedStadium === 'bxInfinity' ? 'flex' : 'block',
            alignItems: selectedStadium === 'bxInfinity' ? 'center' : 'stretch',
            justifyContent: selectedStadium === 'bxInfinity' ? 'center' : 'stretch',
          }}
        >
          <img
            src={STADIUM_IMAGE_URL}
            alt="Beyblade Stadium"
            style={{
              width: selectedStadium === 'bxInfinity' ? 'auto' : '100%',
              height: selectedStadium === 'bxInfinity' ? '100%' : '100%',
              maxWidth: selectedStadium === 'bxInfinity' ? '100%' : 'none',
              objectFit: selectedStadium === 'bx' ? 'cover' : 'contain',
              objectPosition: selectedStadium === 'bx' ? 'center top' : 'center',
              filter: 'drop-shadow(0 0 10px rgba(68, 136, 255, 0.3)) drop-shadow(0 0 20px rgba(102, 170, 255, 0.2))',
            }}
          />
        </div>

        {/* Knockout Zone Visualization for BX-Stadium */}
        {/* These zones show where beyblades can be knocked out */}
        {selectedStadium === 'bx' && !top1Active && !top2Active && (
          <svg 
            className="absolute pointer-events-none"
            style={{ top: 0, left: 0, width: '100%', height: '100%', zIndex: 15 }}
            viewBox={`0 0 ${CANVAS_WIDTH} ${CANVAS_HEIGHT}`}
            preserveAspectRatio="xMidYMid meet"
          >
            {/* Left Over Zone - curved arc following stadium contour (INWARD curve) */}
            <path
              d={`M ${BX_CENTER_X + Math.cos(zoneConfig.leftZone.startAngle) * (BX_RAIL_WIDTH + zoneConfig.leftZone.outerRadius)} ${BX_CENTER_Y + Math.sin(zoneConfig.leftZone.startAngle) * (BX_RAIL_HEIGHT + zoneConfig.leftZone.outerRadius)}
                  A ${BX_RAIL_WIDTH + zoneConfig.leftZone.outerRadius} ${BX_RAIL_HEIGHT + zoneConfig.leftZone.outerRadius} 0 0 0 ${BX_CENTER_X + Math.cos(zoneConfig.leftZone.endAngle) * (BX_RAIL_WIDTH + zoneConfig.leftZone.outerRadius)} ${BX_CENTER_Y + Math.sin(zoneConfig.leftZone.endAngle) * (BX_RAIL_HEIGHT + zoneConfig.leftZone.outerRadius)}
                  L ${BX_CENTER_X + Math.cos(zoneConfig.leftZone.endAngle) * BX_RAIL_WIDTH} ${BX_CENTER_Y + Math.sin(zoneConfig.leftZone.endAngle) * BX_RAIL_HEIGHT}
                  A ${BX_RAIL_WIDTH} ${BX_RAIL_HEIGHT} 0 0 1 ${BX_CENTER_X + Math.cos(zoneConfig.leftZone.startAngle) * BX_RAIL_WIDTH} ${BX_CENTER_Y + Math.sin(zoneConfig.leftZone.startAngle) * BX_RAIL_HEIGHT}
                  Z`}
              fill="rgba(255, 215, 0, 0.4)"
              stroke="gold"
              strokeWidth="3"
            />
            <text 
              x={BX_CENTER_X + Math.cos((zoneConfig.leftZone.startAngle + zoneConfig.leftZone.endAngle) / 2) * (BX_RAIL_WIDTH + zoneConfig.leftZone.outerRadius / 2 + 5)} 
              y={BX_CENTER_Y + Math.sin((zoneConfig.leftZone.startAngle + zoneConfig.leftZone.endAngle) / 2) * (BX_RAIL_HEIGHT + zoneConfig.leftZone.outerRadius / 2 + 5)}
              fill="white" 
              fontSize="10" 
              fontWeight="bold"
              textAnchor="middle"
            >
              OVER +2
            </text>
            
            {/* Center Xtreme Zone - curved arc at bottom center (INWARD curve) */}
            <path
              d={`M ${BX_CENTER_X + Math.cos(zoneConfig.centerZone.startAngle) * (BX_RAIL_WIDTH + zoneConfig.centerZone.outerRadius)} ${BX_CENTER_Y + Math.sin(zoneConfig.centerZone.startAngle) * (BX_RAIL_HEIGHT + zoneConfig.centerZone.outerRadius)}
                  A ${BX_RAIL_WIDTH + zoneConfig.centerZone.outerRadius} ${BX_RAIL_HEIGHT + zoneConfig.centerZone.outerRadius} 0 0 0 ${BX_CENTER_X + Math.cos(zoneConfig.centerZone.endAngle) * (BX_RAIL_WIDTH + zoneConfig.centerZone.outerRadius)} ${BX_CENTER_Y + Math.sin(zoneConfig.centerZone.endAngle) * (BX_RAIL_HEIGHT + zoneConfig.centerZone.outerRadius)}
                  L ${BX_CENTER_X + Math.cos(zoneConfig.centerZone.endAngle) * BX_RAIL_WIDTH} ${BX_CENTER_Y + Math.sin(zoneConfig.centerZone.endAngle) * BX_RAIL_HEIGHT}
                  A ${BX_RAIL_WIDTH} ${BX_RAIL_HEIGHT} 0 0 1 ${BX_CENTER_X + Math.cos(zoneConfig.centerZone.startAngle) * BX_RAIL_WIDTH} ${BX_CENTER_Y + Math.sin(zoneConfig.centerZone.startAngle) * BX_RAIL_HEIGHT}
                  Z`}
              fill="rgba(255, 165, 0, 0.4)"
              stroke="orange"
              strokeWidth="3"
            />
            <text 
              x={BX_CENTER_X + Math.cos((zoneConfig.centerZone.startAngle + zoneConfig.centerZone.endAngle) / 2) * (BX_RAIL_WIDTH + zoneConfig.centerZone.outerRadius / 2 + 5)} 
              y={BX_CENTER_Y + Math.sin((zoneConfig.centerZone.startAngle + zoneConfig.centerZone.endAngle) / 2) * (BX_RAIL_HEIGHT + zoneConfig.centerZone.outerRadius / 2 + 5)}
              fill="white" 
              fontSize="10" 
              fontWeight="bold"
              textAnchor="middle"
            >
              XTREME +3
            </text>
            
            {/* Right Over Zone - curved arc following stadium contour (INWARD curve) */}
            <path
              d={`M ${BX_CENTER_X + Math.cos(zoneConfig.rightZone.startAngle) * (BX_RAIL_WIDTH + zoneConfig.rightZone.outerRadius)} ${BX_CENTER_Y + Math.sin(zoneConfig.rightZone.startAngle) * (BX_RAIL_HEIGHT + zoneConfig.rightZone.outerRadius)}
                  A ${BX_RAIL_WIDTH + zoneConfig.rightZone.outerRadius} ${BX_RAIL_HEIGHT + zoneConfig.rightZone.outerRadius} 0 0 0 ${BX_CENTER_X + Math.cos(zoneConfig.rightZone.endAngle) * (BX_RAIL_WIDTH + zoneConfig.rightZone.outerRadius)} ${BX_CENTER_Y + Math.sin(zoneConfig.rightZone.endAngle) * (BX_RAIL_HEIGHT + zoneConfig.rightZone.outerRadius)}
                  L ${BX_CENTER_X + Math.cos(zoneConfig.rightZone.endAngle) * BX_RAIL_WIDTH} ${BX_CENTER_Y + Math.sin(zoneConfig.rightZone.endAngle) * BX_RAIL_HEIGHT}
                  A ${BX_RAIL_WIDTH} ${BX_RAIL_HEIGHT} 0 0 1 ${BX_CENTER_X + Math.cos(zoneConfig.rightZone.startAngle) * BX_RAIL_WIDTH} ${BX_CENTER_Y + Math.sin(zoneConfig.rightZone.startAngle) * BX_RAIL_HEIGHT}
                  Z`}
              fill="rgba(255, 215, 0, 0.4)"
              stroke="gold"
              strokeWidth="3"
            />
            <text 
              x={BX_CENTER_X + Math.cos((zoneConfig.rightZone.startAngle + zoneConfig.rightZone.endAngle) / 2) * (BX_RAIL_WIDTH + zoneConfig.rightZone.outerRadius / 2 + 5)} 
              y={BX_CENTER_Y + Math.sin((zoneConfig.rightZone.startAngle + zoneConfig.rightZone.endAngle) / 2) * (BX_RAIL_HEIGHT + zoneConfig.rightZone.outerRadius / 2 + 5)}
              fill="white" 
              fontSize="10" 
              fontWeight="bold"
              textAnchor="middle"
            >
              OVER +2
            </text>
          </svg>
        )}

        {/* BX-Infinity Zone Indicators - Only show before match starts */}
        {selectedStadium === 'bxInfinity' && !top1Active && !top2Active && (
          <svg 
            className="absolute inset-0 w-full h-full pointer-events-none"
            style={{ zIndex: 50 }}
            viewBox={`0 0 ${CANVAS_WIDTH} ${CANVAS_HEIGHT}`}
            preserveAspectRatio="xMidYMid meet"
          >
            {/* Xtreme Zone - Bottom (vertices 13-11) - Orange filled polygon */}
            <polygon
              points="279,595 521,595 521,560 279,560"
              fill="rgba(255, 165, 0, 0.4)"
              stroke="orange"
              strokeWidth="3"
            />
            <text 
              x={400} y={582}
              fill="white" 
              fontSize="10" 
              fontWeight="bold"
              textAnchor="middle"
            >
              XTREME +3
            </text>
            
            {/* Xtreme Zone - Top (vertices 23-1) - Orange filled polygon */}
            <polygon
              points="279,0 521,0 521,35 279,35"
              fill="rgba(255, 165, 0, 0.4)"
              stroke="orange"
              strokeWidth="3"
            />
            <text 
              x={400} y={22}
              fill="white" 
              fontSize="10" 
              fontWeight="bold"
              textAnchor="middle"
            >
              XTREME +3
            </text>
            
            {/* Knockout Zone - Bottom Right (vertices 11-9) - Gold/Yellow filled polygon - mirrored from left */}
            <polygon
              points="521,560 521,595 694,415 669,415"
              fill="rgba(255, 215, 0, 0.4)"
              stroke="gold"
              strokeWidth="3"
            />
            <text 
              x={595} y={505}
              fill="white" 
              fontSize="10" 
              fontWeight="bold"
              textAnchor="middle"
            >
              OVER +2
            </text>
            
            {/* Knockout Zone - Bottom Left (vertices 13-15) - Gold/Yellow filled polygon */}
            <polygon
              points="279,560 279,595 106,415 131,415"
              fill="rgba(255, 215, 0, 0.4)"
              stroke="gold"
              strokeWidth="3"
            />
            <text 
              x={205} y={505}
              fill="white" 
              fontSize="10" 
              fontWeight="bold"
              textAnchor="middle"
            >
              OVER +2
            </text>
            
            {/* Knockout Zone - Top Left (vertices 23-20) - Gold/Yellow filled polygon */}
            <polygon
              points="279,35 279,0 106,180 131,180"
              fill="rgba(255, 215, 0, 0.4)"
              stroke="gold"
              strokeWidth="3"
            />
            <text 
              x={205} y={95}
              fill="white" 
              fontSize="10" 
              fontWeight="bold"
              textAnchor="middle"
            >
              OVER +2
            </text>
            
            {/* Knockout Zone - Top Right (vertices 1-4) - Gold/Yellow filled polygon - mirrored from left */}
            <polygon
              points="521,35 521,0 694,180 669,180"
              fill="rgba(255, 215, 0, 0.4)"
              stroke="gold"
              strokeWidth="3"
            />
            <text 
              x={595} y={95}
              fill="white" 
              fontSize="10" 
              fontWeight="bold"
              textAnchor="middle"
            >
              OVER +2
            </text>
            
            {/* Wall Zone - Right Side - mirrored from left wall */}
            <polygon
              points="669,180 694,180 694,415 669,415"
              fill="rgba(128, 128, 128, 0.4)"
              stroke="grey"
              strokeWidth="3"
            />
            <text 
              x={680} y={283}
              fill="white" 
              fontSize="10" 
              fontWeight="bold"
              textAnchor="middle"
              transform="rotate(90, 680, 298)"
            >
              WALL
            </text>
            
            {/* Wall Zone - Left Side - perfectly vertical outer edge */}
            <polygon
              points="131,180 106,180 106,415 131,415"
              fill="rgba(128, 128, 128, 0.4)"
              stroke="grey"
              strokeWidth="3"
            />
            <text 
              x={120} y={298}
              fill="white" 
              fontSize="10" 
              fontWeight="bold"
              textAnchor="middle"
              transform="rotate(-90, 120, 298)"
            >
              WALL
            </text>
            
            {/* Infinity Xtreme Dash path - Blue (right spin) */}
            <path
              d={`M ${xtremeDashPoints.blueTop.x} ${xtremeDashPoints.blueTop.y} 
                  Q ${CANVAS_WIDTH/2 + 22} ${CANVAS_HEIGHT/2} ${xtremeDashPoints.blueBottom.x} ${xtremeDashPoints.blueBottom.y}
                  Q ${CANVAS_WIDTH/2 - 22} ${CANVAS_HEIGHT/2} ${xtremeDashPoints.blueTop.x} ${xtremeDashPoints.blueTop.y}`}
              fill="none"
              stroke="cyan"
              strokeWidth="3"
              strokeDasharray="10,5"
              opacity="0.6"
            />
            
            {/* Infinity Xtreme Dash path - Red (left spin) */}
            <path
              d={`M ${xtremeDashPoints.redTop.x} ${xtremeDashPoints.redTop.y} 
                  Q ${CANVAS_WIDTH/2 - 22} ${CANVAS_HEIGHT/2} ${xtremeDashPoints.redBottom.x} ${xtremeDashPoints.redBottom.y}
                  Q ${CANVAS_WIDTH/2 + 22} ${CANVAS_HEIGHT/2} ${xtremeDashPoints.redTop.x} ${xtremeDashPoints.redTop.y}`}
              fill="none"
              stroke="red"
              strokeWidth="3"
              strokeDasharray="10,5"
              opacity="0.6"
            />
            
            {/* Blue trigger dots (Right Spin) */}
            <circle
              cx={xtremeDashPoints.blueTop.x}
              cy={xtremeDashPoints.blueTop.y}
              r={7.5}
              fill="blue"
              stroke="cyan"
              strokeWidth="2"
              opacity="0.8"
            />
            <text x={xtremeDashPoints.blueTop.x} y={xtremeDashPoints.blueTop.y + 3} fill="white" fontSize="8" fontWeight="bold" textAnchor="middle">R</text>
            
            <circle
              cx={xtremeDashPoints.blueBottom.x}
              cy={xtremeDashPoints.blueBottom.y}
              r={7.5}
              fill="blue"
              stroke="cyan"
              strokeWidth="2"
              opacity="0.8"
            />
            <text x={xtremeDashPoints.blueBottom.x} y={xtremeDashPoints.blueBottom.y + 3} fill="white" fontSize="8" fontWeight="bold" textAnchor="middle">R</text>
            
            {/* Red trigger dots (Left Spin) */}
            <circle
              cx={xtremeDashPoints.redTop.x}
              cy={xtremeDashPoints.redTop.y}
              r={7.5}
              fill="red"
              stroke="orange"
              strokeWidth="2"
              opacity="0.8"
            />
            <text x={xtremeDashPoints.redTop.x} y={xtremeDashPoints.redTop.y + 3} fill="white" fontSize="8" fontWeight="bold" textAnchor="middle">L</text>
            
            <circle
              cx={xtremeDashPoints.redBottom.x}
              cy={xtremeDashPoints.redBottom.y}
              r={7.5}
              fill="red"
              stroke="orange"
              strokeWidth="2"
              opacity="0.8"
            />
            <text x={xtremeDashPoints.redBottom.x} y={xtremeDashPoints.redBottom.y + 3} fill="white" fontSize="8" fontWeight="bold" textAnchor="middle">L</text>
          </svg>
        )}

        {/* Vs AI & Choose Stadium Buttons - Only visible before any beyblade is launched */}
        {!top1Active && !top2Active && (
          <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 z-20 flex flex-col gap-2 items-center">
            {/* Vs AI Button */}
            <button
              onClick={() => setAiMode(!aiMode)}
              className={`${aiMode 
                ? 'bg-gradient-to-r from-green-600 to-emerald-500 border-green-400/50' 
                : 'bg-gradient-to-r from-red-600 to-orange-500 border-yellow-400/50'} 
                         hover:opacity-90 text-white text-xs font-bold px-4 py-2 rounded-full 
                         border shadow-lg transition-all duration-200
                         flex items-center gap-2`}
              style={{ fontSize: '11px' }}
              data-testid="vs-ai-btn"
            >
              <span className={`w-2 h-2 ${aiMode ? 'bg-green-300' : 'bg-yellow-400'} rounded-full ${aiMode ? '' : 'animate-pulse'}`}></span>
              {aiMode ? 'AI Mode: ON' : 'Vs AI'}
            </button>
            
            {/* Choose Stadium Button */}
            <button
              onClick={() => setShowStadiumPicker(true)}
              className="bg-slate-800/90 hover:bg-slate-700/90 text-white text-xs font-bold 
                         px-3 py-1.5 rounded-full border border-cyan-500/50 hover:border-cyan-400
                         shadow-lg shadow-cyan-500/20 transition-all duration-200"
              style={{ fontSize: '10px' }}
            >
              Choose Stadium
            </button>
          </div>
        )}

        {/* Stadium Picker Modal */}
        {showStadiumPicker && !showStadiumImagePicker && (
          <div className="absolute inset-0 z-50 flex items-start justify-center bg-black/70" style={{ paddingTop: '0px' }}>
            <div className="bg-slate-800 rounded-xl p-4 border border-cyan-500/50 shadow-xl max-w-md w-full mx-4" style={{ marginTop: '-30px' }}>
              <h3 className="text-white text-center font-bold mb-3 text-sm">Select Stadium</h3>
              {/* Top row - BX-Stadium and BB-10 Stadium */}
              <div className="grid grid-cols-2 gap-3">
                {['bx', 'bb10'].map((key) => {
                  const stadium = STADIUMS[key];
                  const hasAlternatives = stadium.alternativeImages && stadium.alternativeImages.length > 1;
                  return (
                    <button
                      key={key}
                      onClick={() => {
                        setSelectedStadium(key);
                        setShowStadiumPicker(false);
                        // Save stadium preference
                        saveStadiumPreferences(key, bxStadiumImageIndex, bb10StadiumImageIndex);
                      }}
                      onTouchStart={() => {
                        if (hasAlternatives) {
                          stadiumLongPressTimer.current = setTimeout(() => {
                            setShowStadiumImagePicker(key);
                          }, 500);
                        }
                      }}
                      onTouchEnd={() => {
                        if (stadiumLongPressTimer.current) {
                          clearTimeout(stadiumLongPressTimer.current);
                        }
                      }}
                      onMouseDown={() => {
                        if (hasAlternatives) {
                          stadiumLongPressTimer.current = setTimeout(() => {
                            setShowStadiumImagePicker(key);
                          }, 500);
                        }
                      }}
                      onMouseUp={() => {
                        if (stadiumLongPressTimer.current) {
                          clearTimeout(stadiumLongPressTimer.current);
                        }
                      }}
                      onMouseLeave={() => {
                        if (stadiumLongPressTimer.current) {
                          clearTimeout(stadiumLongPressTimer.current);
                        }
                      }}
                      className={`relative rounded-lg overflow-hidden border-2 transition-all duration-200 ${
                        selectedStadium === key 
                          ? 'border-cyan-400 ring-2 ring-cyan-400/50' 
                          : 'border-slate-600 hover:border-slate-500'
                      }`}
                    >
                      <div className="aspect-square bg-slate-900 flex items-center justify-center p-2 relative">
                        <img 
                          src={getStadiumImageUrl(key)} 
                          alt={stadium.name}
                          className="w-full h-full object-contain"
                        />
                        {hasAlternatives && (
                          <div className="absolute left-0 right-0 text-center" style={{ bottom: '-2px' }}>
                            <span className="text-yellow-400 text-[8px] font-semibold animate-pulse drop-shadow-lg" style={{ textShadow: '0 0 4px rgba(0,0,0,0.8)' }}>
                              Long Press For More Options
                            </span>
                          </div>
                        )}
                      </div>
                      <div className="bg-black/70 px-2 py-1">
                        <span className="text-white text-xs font-medium">{stadium.name}</span>
                      </div>
                      {selectedStadium === key && (
                        <div className="absolute top-1 right-1 bg-cyan-500 rounded-full p-0.5">
                          <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>
              {/* Bottom row - BX-Infinity Stadium (centered and bigger) */}
              <div className="flex justify-center mt-3">
                <button
                  onClick={() => {
                    setSelectedStadium('bxInfinity');
                    setShowStadiumPicker(false);
                    // Save stadium preference
                    saveStadiumPreferences('bxInfinity', bxStadiumImageIndex, bb10StadiumImageIndex);
                  }}
                  className={`relative rounded-lg overflow-hidden border-2 transition-all duration-200 w-full ${
                    selectedStadium === 'bxInfinity' 
                      ? 'border-cyan-400 ring-2 ring-cyan-400/50' 
                      : 'border-slate-600 hover:border-slate-500'
                  }`}
                >
                  <div className="bg-slate-900 flex items-center justify-center p-1">
                    <img 
                      src={STADIUMS.bxInfinity.url} 
                      alt={STADIUMS.bxInfinity.name}
                      className="object-contain w-full"
                    />
                  </div>
                  <div className="bg-black/70 px-2 py-1">
                    <span className="text-white text-sm font-medium">{STADIUMS.bxInfinity.name}</span>
                  </div>
                  {/* Checkmark for selected stadium */}
                  {selectedStadium === 'bxInfinity' && (
                    <div className="absolute top-1 right-1 bg-cyan-500 rounded-full p-0.5">
                      <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    </div>
                  )}
                </button>
              </div>
              <button
                onClick={() => setShowStadiumPicker(false)}
                className="w-full mt-3 bg-slate-700 hover:bg-slate-600 text-white py-2 rounded-lg text-sm font-medium transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        )}

        {/* Stadium Alternative Image Picker */}
        {showStadiumImagePicker && (
          <div className="absolute inset-0 z-50 flex items-start justify-center bg-black/80" style={{ paddingTop: '0px' }}>
            <div className="bg-slate-800 rounded-xl p-4 border border-cyan-500/50 shadow-xl max-w-md w-full mx-4" style={{ marginTop: '-30px' }}>
              <h3 className="text-white text-center font-bold text-sm">
                Select {STADIUMS[showStadiumImagePicker]?.name} Image
              </h3>
              <p className="text-center mb-1.5 -mt-0.5">
                <span className="text-yellow-400 text-[10px] font-semibold animate-pulse" style={{ textShadow: '0 0 4px rgba(0,0,0,0.8)' }}>
                  Scroll For More Options
                </span>
              </p>
              <div className="grid grid-cols-2 gap-3 max-h-[445px] overflow-y-auto pr-1">
                {STADIUMS[showStadiumImagePicker]?.alternativeImages?.map((imageUrl, index) => {
                  const isSelected = (showStadiumImagePicker === 'bx' && bxStadiumImageIndex === index) ||
                                     (showStadiumImagePicker === 'bb10' && bb10StadiumImageIndex === index);
                  return (
                    <button
                      key={index}
                      onClick={() => {
                        let newBxIndex = bxStadiumImageIndex;
                        let newBb10Index = bb10StadiumImageIndex;
                        if (showStadiumImagePicker === 'bx') {
                          setBxStadiumImageIndex(index);
                          newBxIndex = index;
                        } else if (showStadiumImagePicker === 'bb10') {
                          setBb10StadiumImageIndex(index);
                          newBb10Index = index;
                        }
                        // Save stadium preferences with new image index
                        saveStadiumPreferences(selectedStadium, newBxIndex, newBb10Index);
                        setShowStadiumImagePicker(null);
                      }}
                      className={`relative rounded-lg overflow-hidden border-2 ${
                        isSelected
                          ? 'border-cyan-400 ring-2 ring-cyan-400/50' 
                          : 'border-slate-600 active:border-slate-500'
                      }`}
                      style={{ WebkitTapHighlightColor: 'transparent' }}
                    >
                      <div className="aspect-square bg-slate-900 flex items-center justify-center p-2 overflow-hidden">
                        <img 
                          src={imageUrl} 
                          alt={`Option ${index + 1}`}
                          className="w-full h-full object-contain"
                          style={{ pointerEvents: 'none' }}
                        />
                      </div>
                      <div className="bg-black/70 px-2 py-1">
                        <span className="text-white text-xs font-medium">Option {index + 1}</span>
                      </div>
                      {isSelected && (
                        <div className="absolute top-1 right-1 bg-cyan-500 rounded-full p-0.5">
                          <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>
              <button
                onClick={() => setShowStadiumImagePicker(null)}
                className="w-full mt-3 bg-slate-700 hover:bg-slate-600 text-white py-2 rounded-lg text-sm font-medium transition-colors"
              >
                Back
              </button>
            </div>
          </div>
        )}

        {/* CHARACTER OVERLAY REMOVED - Walking character no longer exists */}

        {/* Walls are invisible but collision detection is active */}

        {/* Beyblade 1 Overlay (Blue - Right Side) */}
        {top1Active && !top1KnockedOut && (
          <div
            className="absolute pointer-events-none"
            style={{
              left: `${(top1Pos.x / CANVAS_WIDTH) * 100}%`,
              top: `${(top1Pos.y / CANVAS_HEIGHT) * 100}%`,
              transform: `translate(-50%, -50%) rotate(${top1Rotation}deg) translateZ(0)`,
              zIndex: 100,
              width: `${40 * SCALE_FACTOR}px`,
              height: `${40 * SCALE_FACTOR}px`,
              isolation: 'isolate',
              willChange: 'transform, left, top',
              contain: 'layout style paint',
              backfaceVisibility: 'hidden',
              perspective: 1000,
              borderRadius: '50%',
              boxShadow: ironWallActive1
                ? '0 0 5px 2px rgba(255, 255, 255, 1), 0 0 9px 3px rgba(255, 255, 255, 1), 0 0 13px 3px rgba(255, 255, 255, 1)'
                : burningStrikeActive1
                  ? '0 0 5px 2px rgba(255, 100, 0, 1), 0 0 9px 3px rgba(255, 69, 0, 1), 0 0 13px 3px rgba(255, 140, 0, 1)'
                  : finalDriveActive1
                    ? '0 0 5px 2px rgba(59, 130, 246, 1), 0 0 9px 3px rgba(59, 130, 246, 1), 0 0 13px 3px rgba(59, 130, 246, 1)'
                    : ghostMode1 
                      ? '0 0 5px 2px rgba(168, 85, 247, 1), 0 0 9px 3px rgba(168, 85, 247, 1), 0 0 13px 3px rgba(168, 85, 247, 1)'
                      : deflection1
                        ? '0 0 5px 2px rgba(239, 68, 68, 1), 0 0 9px 3px rgba(239, 68, 68, 1), 0 0 13px 3px rgba(239, 68, 68, 1)'
                        : speedBoost1
                          ? '0 0 5px 2px rgba(34, 197, 94, 1), 0 0 9px 3px rgba(34, 197, 94, 1), 0 0 13px 3px rgba(34, 197, 94, 1)'
                          : isControlling1
                            ? '0 0 5px 2px rgba(250, 204, 21, 1), 0 0 9px 3px rgba(250, 204, 21, 1), 0 0 13px 3px rgba(250, 204, 21, 1)'
                            : 'none',
              filter: top1Speed > 0 
                ? `drop-shadow(0 0 ${top1Speed / 5}px rgba(68, 136, 255, ${top1Speed / 100}))` 
                : 'none',
            }}
          >
            <img
              src={selectedBeyblade1?.url || ALL_BEYBLADES[0].url}
              alt="Beyblade 1"
              className="w-full h-full object-contain"
              style={{ backfaceVisibility: 'hidden' }}
            />
          </div>
        )}
        
        {/* Beyblade 2 Overlay (Red/Orange - Left Side / Opponent in Online) */}
        {top2Active && !top2KnockedOut && (
          <div
            className="absolute pointer-events-none"
            style={{
              left: `${(top2Pos.x / CANVAS_WIDTH) * 100}%`,
              top: `${(top2Pos.y / CANVAS_HEIGHT) * 100}%`,
              transform: `translate(-50%, -50%) rotate(${top2Rotation}deg) translateZ(0)`,
              zIndex: 100,
              width: `${40 * SCALE_FACTOR}px`,
              height: `${40 * SCALE_FACTOR}px`,
              isolation: 'isolate',
              willChange: 'transform, left, top',
              contain: 'layout style paint',
              backfaceVisibility: 'hidden',
              perspective: 1000,
              borderRadius: '50%',
              boxShadow: ironWallActive2
                ? '0 0 5px 2px rgba(255, 255, 255, 1), 0 0 9px 3px rgba(255, 255, 255, 1), 0 0 13px 3px rgba(255, 255, 255, 1)'
                : burningStrikeActive2
                  ? '0 0 5px 2px rgba(255, 100, 0, 1), 0 0 9px 3px rgba(255, 69, 0, 1), 0 0 13px 3px rgba(255, 140, 0, 1)'
                  : finalDriveActive2
                    ? '0 0 5px 2px rgba(59, 130, 246, 1), 0 0 9px 3px rgba(59, 130, 246, 1), 0 0 13px 3px rgba(59, 130, 246, 1)'
                    : ghostMode2 
                      ? '0 0 5px 2px rgba(168, 85, 247, 1), 0 0 9px 3px rgba(168, 85, 247, 1), 0 0 13px 3px rgba(168, 85, 247, 1)'
                      : deflection2
                        ? '0 0 5px 2px rgba(239, 68, 68, 1), 0 0 9px 3px rgba(239, 68, 68, 1), 0 0 13px 3px rgba(239, 68, 68, 1)'
                        : speedBoost2
                          ? '0 0 5px 2px rgba(34, 197, 94, 1), 0 0 9px 3px rgba(34, 197, 94, 1), 0 0 13px 3px rgba(34, 197, 94, 1)'
                          : isControlling2
                            ? '0 0 5px 2px rgba(250, 204, 21, 1), 0 0 9px 3px rgba(250, 204, 21, 1), 0 0 13px 3px rgba(250, 204, 21, 1)'
                            : 'none',
              filter: top2Speed > 0 
                ? `drop-shadow(0 0 ${top2Speed / 5}px rgba(255, 136, 68, ${top2Speed / 100}))` 
                : 'none',
            }}
          >
            <img
              src={selectedBeyblade2?.url || ALL_BEYBLADES[0].url}
              alt="Beyblade 2"
              className="w-full h-full object-contain"
              style={{ backfaceVisibility: 'hidden' }}
            />
          </div>
        )}

        {/* Shadow Clones for P1 (Shinobi Shadow) - move and spin like regular beyblades */}
        {shadowClonesActive1 && shadowClones1.map((clone) => (
          <div
            key={`p1-clone-${clone.id}`}
            className="absolute pointer-events-none"
            style={{
              left: `${(clone.x / CANVAS_WIDTH) * 100}%`,
              top: `${(clone.y / CANVAS_HEIGHT) * 100}%`,
              transform: `translate(-50%, -50%) rotate(${top1Rotation}deg) translateZ(0)`,
              zIndex: 95,
              width: `${40 * SCALE_FACTOR}px`,
              height: `${40 * SCALE_FACTOR}px`,
              borderRadius: '50%',
              // Match EXACTLY the same effects as the original beyblade
              boxShadow: finalDriveActive1
                ? '0 0 5px 2px rgba(59, 130, 246, 1), 0 0 9px 3px rgba(59, 130, 246, 1), 0 0 13px 3px rgba(59, 130, 246, 1)'
                : ghostMode1 
                  ? '0 0 5px 2px rgba(168, 85, 247, 1), 0 0 9px 3px rgba(168, 85, 247, 1), 0 0 13px 3px rgba(168, 85, 247, 1)'
                  : deflection1
                    ? '0 0 5px 2px rgba(239, 68, 68, 1), 0 0 9px 3px rgba(239, 68, 68, 1), 0 0 13px 3px rgba(239, 68, 68, 1)'
                    : speedBoost1
                      ? '0 0 5px 2px rgba(34, 197, 94, 1), 0 0 9px 3px rgba(34, 197, 94, 1), 0 0 13px 3px rgba(34, 197, 94, 1)'
                      : isControlling1
                        ? '0 0 5px 2px rgba(250, 204, 21, 1), 0 0 9px 3px rgba(250, 204, 21, 1), 0 0 13px 3px rgba(250, 204, 21, 1)'
                        : 'none',
              filter: top1Speed > 0 
                ? `drop-shadow(0 0 ${top1Speed / 5}px rgba(68, 136, 255, ${top1Speed / 100}))` 
                : 'none',
            }}
          >
            <img
              src={selectedBeyblade1?.url}
              alt={`Shadow Clone ${clone.id}`}
              className="w-full h-full object-contain"
            />
          </div>
        ))}

        {/* Shadow Clones for P2 (Shinobi Shadow) - move and spin like regular beyblades */}
        {shadowClonesActive2 && shadowClones2.map((clone) => (
          <div
            key={`p2-clone-${clone.id}`}
            className="absolute pointer-events-none"
            style={{
              left: `${(clone.x / CANVAS_WIDTH) * 100}%`,
              top: `${(clone.y / CANVAS_HEIGHT) * 100}%`,
              transform: `translate(-50%, -50%) rotate(${top2Rotation}deg) translateZ(0)`,
              zIndex: 95,
              width: `${40 * SCALE_FACTOR}px`,
              height: `${40 * SCALE_FACTOR}px`,
              borderRadius: '50%',
              // Match EXACTLY the same effects as the original beyblade
              boxShadow: finalDriveActive2
                ? '0 0 5px 2px rgba(59, 130, 246, 1), 0 0 9px 3px rgba(59, 130, 246, 1), 0 0 13px 3px rgba(59, 130, 246, 1)'
                : ghostMode2 
                  ? '0 0 5px 2px rgba(168, 85, 247, 1), 0 0 9px 3px rgba(168, 85, 247, 1), 0 0 13px 3px rgba(168, 85, 247, 1)'
                  : deflection2
                    ? '0 0 5px 2px rgba(239, 68, 68, 1), 0 0 9px 3px rgba(239, 68, 68, 1), 0 0 13px 3px rgba(239, 68, 68, 1)'
                    : speedBoost2
                      ? '0 0 5px 2px rgba(34, 197, 94, 1), 0 0 9px 3px rgba(34, 197, 94, 1), 0 0 13px 3px rgba(34, 197, 94, 1)'
                      : isControlling2
                        ? '0 0 5px 2px rgba(250, 204, 21, 1), 0 0 9px 3px rgba(250, 204, 21, 1), 0 0 13px 3px rgba(250, 204, 21, 1)'
                        : 'none',
              filter: top2Speed > 0 
                ? `drop-shadow(0 0 ${top2Speed / 5}px rgba(255, 136, 68, ${top2Speed / 100}))` 
                : 'none',
            }}
          >
            <img
              src={selectedBeyblade2?.url}
              alt={`Shadow Clone ${clone.id}`}
              className="w-full h-full object-contain"
            />
          </div>
        ))}

        {/* Burning Strike Fire Emojis for P1 */}
        {burningStrikeActive1 && top1Active && !top1KnockedOut && [0, 1, 2, 3, 4, 5].map((index) => {
          const angle = (fireRotation1 + index * 60) * (Math.PI / 180);
          const fireDistance = BEYBLADE_RADIUS * 0.9; // Fire emojis at outer edge
          const fireX = top1Pos.x + Math.cos(angle) * fireDistance;
          const fireY = top1Pos.y + Math.sin(angle) * fireDistance;
          return (
            <div
              key={`fire1-${index}`}
              className="absolute pointer-events-none"
              style={{
                left: `${(fireX / CANVAS_WIDTH) * 100}%`,
                top: `${(fireY / CANVAS_HEIGHT) * 100}%`,
                transform: 'translate(-50%, -50%)',
                fontSize: `${16 * SCALE_FACTOR}px`,
                zIndex: 101,
                textShadow: '0 0 10px rgba(255, 100, 0, 0.8), 0 0 20px rgba(255, 69, 0, 0.6)',
              }}
            >
              🔥
            </div>
          );
        })}

        {/* Burning Strike Fire Emojis for P2 */}
        {burningStrikeActive2 && top2Active && !top2KnockedOut && [0, 1, 2, 3, 4, 5].map((index) => {
          const angle = (fireRotation2 + index * 60) * (Math.PI / 180);
          const fireDistance = BEYBLADE_RADIUS * 0.9; // Fire emojis at outer edge
          const fireX = top2Pos.x + Math.cos(angle) * fireDistance;
          const fireY = top2Pos.y + Math.sin(angle) * fireDistance;
          return (
            <div
              key={`fire2-${index}`}
              className="absolute pointer-events-none"
              style={{
                left: `${(fireX / CANVAS_WIDTH) * 100}%`,
                top: `${(fireY / CANVAS_HEIGHT) * 100}%`,
                transform: 'translate(-50%, -50%)',
                fontSize: `${16 * SCALE_FACTOR}px`,
                zIndex: 101,
                textShadow: '0 0 10px rgba(255, 100, 0, 0.8), 0 0 20px rgba(255, 69, 0, 0.6)',
              }}
            >
              🔥
            </div>
          );
        })}

        {/* Orange Sparks during Clash Lock */}
        {clashSparks.map(spark => (
          <div
            key={spark.id}
            className="absolute pointer-events-none"
            style={{
              left: `${(spark.x / CANVAS_WIDTH) * 100}%`,
              top: `${(spark.y / CANVAS_HEIGHT) * 100}%`,
              width: `${spark.size}px`,
              height: `${spark.size}px`,
              borderRadius: '50%',
              background: `radial-gradient(circle, 
                rgba(255, 200, 50, ${spark.life}) 0%, 
                rgba(255, 140, 0, ${spark.life * 0.8}) 50%, 
                rgba(255, 80, 0, ${spark.life * 0.5}) 100%
              )`,
              boxShadow: `0 0 ${spark.size * 2}px rgba(255, 140, 0, ${spark.life})`,
              transform: 'translate(-50%, -50%)',
              zIndex: 20,
            }}
          />
        ))}

        {/* Clash Effects - Behind beyblades but above stadium */}
        {clashEffects.map(effect => {
          const progress = Math.min(effect.age / effect.maxAge, 1);
          const opacity = 1 - progress;
          const size = (60 + (progress * 40)) * SCALE_FACTOR;
          
          // Don't render if fully faded out
          if (opacity <= 0.01) return null;
          
          return (
            <div
              key={effect.id}
              className="absolute pointer-events-none"
              style={{
                left: `${(effect.x / CANVAS_WIDTH) * 100}%`,
                top: `${(effect.y / CANVAS_HEIGHT) * 100}%`,
                transform: 'translate(-50%, -50%)',
                zIndex: 15,
                width: `${size}px`,
                height: `${size}px`,
                opacity: opacity,
              }}
            >
              {/* Gold burst rays */}
              <div className="absolute inset-0" style={{
                background: `
                  radial-gradient(circle at center, 
                    rgba(255, 255, 255, ${opacity}) 0%, 
                    rgba(255, 215, 0, ${opacity * 0.8}) 20%,
                    rgba(255, 100, 0, ${opacity * 0.3}) 50%,
                    transparent 70%
                  )
                `,
                animation: 'spin 0.3s linear'
              }}>
                {/* Burst star shape */}
                <div className="absolute inset-0" style={{
                  clipPath: 'polygon(50% 0%, 61% 35%, 98% 35%, 68% 57%, 79% 91%, 50% 70%, 21% 91%, 32% 57%, 2% 35%, 39% 35%)',
                  background: `linear-gradient(45deg, 
                    rgba(255, 255, 255, ${opacity}) 0%, 
                    rgba(255, 215, 0, ${opacity}) 50%, 
                    rgba(255, 150, 0, ${opacity * 0.5}) 100%
                  )`,
                  filter: 'blur(2px)'
                }}></div>
              </div>
              
              {/* Center flash */}
              <div className="absolute inset-0 flex items-center justify-center">
                <div style={{
                  width: '30px',
                  height: '30px',
                  borderRadius: '50%',
                  background: `radial-gradient(circle, 
                    rgba(255, 255, 255, ${opacity}) 0%, 
                    rgba(255, 215, 0, ${opacity * 0.6}) 50%,
                    transparent 100%
                  )`,
                  boxShadow: `0 0 ${20 * opacity}px rgba(255, 215, 0, ${opacity})`
                }}></div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Player List */}
      {nearbyPlayers && nearbyPlayers.length > 0 && (
        <div className="w-full max-w-[800px] bg-slate-800/30 border border-slate-700/50 rounded-lg p-3">
          <p className="text-cyan-400 font-semibold text-sm mb-2">Nearby Players on Map:</p>
          <div className="space-y-1">
            {nearbyPlayers.map((player, index) => (
              <button
                key={index}
                onClick={() => onPlayerClick && onPlayerClick(player)}
                className="w-full text-left text-slate-300 hover:text-cyan-400 text-xs p-2 hover:bg-slate-700/30 rounded transition-colors"
              >
                👤 {player.username} - {player.distance.toFixed(1)} miles away
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Walls are now fixed and permanent for all users */}
      
      {/* Knockout Notifications */}
      {/* POWER-UP BUTTONS - Vertical layout on edges, tightly stacked */}
      {/* Player 2 Buttons - Left Edge (Stacked Vertically) - DISABLED in online mode */}
      {/* Deflection Button - Top */}
      <button
        onPointerDown={(e) => {
          e.preventDefault();
          e.stopPropagation();
          if (!isPlayer2Disabled) handleDeflection2();
        }}
        disabled={!top2Active || deflection2 || deflectionCooldown2 || isPlayer2Disabled}
        className={`fixed left-4 top-[calc(50%-136px)] w-14 h-14 rounded-full border-2 flex items-center justify-center transition-all z-30 select-none ${aiMode ? 'pointer-events-none' : ''} ${
          !top2Active || deflection2 || deflectionCooldown2 || p2StyleDisabled || roundWinner || isDraw
            ? 'bg-slate-700/50 border-red-500/30 cursor-not-allowed opacity-50'
            : 'bg-red-600 border-red-400 hover:bg-red-500 cursor-pointer shadow-lg hover:scale-110'
        }`}
        style={{ WebkitTapHighlightColor: 'transparent', touchAction: 'none' }}
      >
        <span className="text-3xl">🛡️</span>
      </button>

      {/* Ghost Mode Button - Middle */}
      <button
        onPointerDown={(e) => {
          e.preventDefault();
          e.stopPropagation();
          if (!isPlayer2Disabled) handleGhostMode2();
        }}
        disabled={!top2Active || ghostMode2 || ghostCooldown2 || isPlayer2Disabled}
        className={`fixed left-4 top-[calc(50%-78px)] w-14 h-14 rounded-full border-2 flex items-center justify-center transition-all z-30 select-none ${aiMode ? 'pointer-events-none' : ''} ${
          !top2Active || ghostMode2 || ghostCooldown2 || p2StyleDisabled || roundWinner || isDraw
            ? 'bg-slate-700/50 border-orange-500/30 cursor-not-allowed opacity-50'
            : 'bg-purple-600 border-purple-400 hover:bg-purple-500 cursor-pointer shadow-lg hover:scale-110'
        }`}
        style={{ WebkitTapHighlightColor: 'transparent', touchAction: 'none' }}
      >
        <span className="text-3xl">👻</span>
      </button>

      {/* Speed Boost Button - Bottom (Perfect Position) */}
      <button
        onPointerDown={(e) => {
          e.preventDefault();
          e.stopPropagation();
          if (!isPlayer2Disabled) handleSpeedBoost2();
        }}
        disabled={!top2Active || speedBoost2 || speedBoostCooldown2 || isPlayer2Disabled}
        className={`fixed left-4 top-[calc(50%-20px)] w-14 h-14 rounded-full border-2 flex items-center justify-center transition-all z-30 select-none ${aiMode ? 'pointer-events-none' : ''} ${
          !top2Active || speedBoost2 || speedBoostCooldown2 || p2StyleDisabled || roundWinner || isDraw
            ? 'bg-slate-700/50 border-green-500/30 cursor-not-allowed opacity-50'
            : 'bg-green-600 border-green-400 hover:bg-green-500 cursor-pointer shadow-lg hover:scale-110'
        }`}
        style={{ WebkitTapHighlightColor: 'transparent', touchAction: 'none' }}
      >
        <span className="text-3xl">⚡</span>
      </button>

      {/* Player 1 Buttons - Right Edge (Stacked Vertically) */}
      {/* Deflection Button - Top */}
      <button
        onPointerDown={(e) => {
          e.preventDefault();
          e.stopPropagation();
          handleDeflection1();
        }}
        disabled={!top1Active || deflection1 || deflectionCooldown1}
        className={`fixed right-4 top-[calc(50%-136px)] w-14 h-14 rounded-full border-2 flex items-center justify-center transition-all z-30 select-none ${
          !top1Active || deflection1 || deflectionCooldown1
            ? 'bg-slate-700/50 border-red-500/30 cursor-not-allowed opacity-50'
            : 'bg-red-600 border-red-400 hover:bg-red-500 cursor-pointer shadow-lg hover:scale-110'
        }`}
        style={{ WebkitTapHighlightColor: 'transparent', touchAction: 'none' }}
      >
        <span className="text-3xl">🛡️</span>
      </button>

      {/* Ghost Mode Button - Middle */}
      <button
        onPointerDown={(e) => {
          e.preventDefault();
          e.stopPropagation();
          handleGhostMode1();
        }}
        disabled={!top1Active || ghostMode1 || ghostCooldown1}
        className={`fixed right-4 top-[calc(50%-78px)] w-14 h-14 rounded-full border-2 flex items-center justify-center transition-all z-30 select-none ${
          !top1Active || ghostMode1 || ghostCooldown1
            ? 'bg-slate-700/50 border-cyan-500/30 cursor-not-allowed opacity-50'
            : 'bg-purple-600 border-purple-400 hover:bg-purple-500 cursor-pointer shadow-lg hover:scale-110'
        }`}
        style={{ WebkitTapHighlightColor: 'transparent', touchAction: 'none' }}
      >
        <span className="text-3xl">👻</span>
      </button>

      {/* Speed Boost Button - Bottom (Perfect Position) */}
      <button
        onPointerDown={(e) => {
          e.preventDefault();
          e.stopPropagation();
          handleSpeedBoost1();
        }}
        disabled={!top1Active || speedBoost1 || speedBoostCooldown1}
        className={`fixed right-4 top-[calc(50%-20px)] w-14 h-14 rounded-full border-2 flex items-center justify-center transition-all z-30 select-none ${
          !top1Active || speedBoost1 || speedBoostCooldown1
            ? 'bg-slate-700/50 border-green-500/30 cursor-not-allowed opacity-50'
            : 'bg-green-600 border-green-400 hover:bg-green-500 cursor-pointer shadow-lg hover:scale-110'
        }`}
        style={{ WebkitTapHighlightColor: 'transparent', touchAction: 'none' }}
      >
        <span className="text-3xl">⚡</span>
      </button>

      {/* 2-PLAYER CONTROLS LAYOUT */}
      <div className="flex gap-5 justify-center items-center mt-[-10px]">
        
        {/* LEFT SIDE - PLAYER 2 (Red Beyblade) */}
        <div className="flex flex-col gap-2 items-center">
          {/* Player 2 Touchpad Label */}
          <p className="text-orange-400 font-bold text-xs">
            P2 {cooldown2 && '⏱️'} {speedBoost2 && '⚡'} {ghostMode2 && '👻'} {deflection2 && '🛡️'}
          </p>
          
          {/* Player 2 Touchpad */}
          <div 
            ref={touchpad2Ref}
            className={`relative w-48 h-40 rounded-xl border-2 select-none overflow-hidden transition-all ${
              cooldown2 || p2StyleDisabled
                ? 'bg-red-900/50 border-red-500 cursor-not-allowed opacity-50' 
                : 'bg-slate-900 border-orange-500/50 cursor-move'
            }`}
            style={{ 
              touchAction: 'none',
              WebkitTouchCallout: 'none',
              WebkitUserSelect: 'none',
              userSelect: 'none'
            }}
            onPointerDown={handleTouchpad2PointerDown}
            onPointerMove={handleTouchpad2PointerMove}
            onPointerUp={handleTouchpad2PointerUp}
            onPointerCancel={handleTouchpad2PointerUp}
          >
            {/* Crosshair */}
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 pointer-events-none">
              <div className="w-10 h-0.5 bg-orange-500/50"></div>
              <div className="w-0.5 h-10 bg-orange-500/50 absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2"></div>
            </div>
            
            {/* Dual-Spin Beyblade (Gravity Perseus/Vari Ares) Spin Direction Toggle - Shows BEFORE launch */}
            {!top2Active && !isPlayer2Disabled && (selectedBeyblade2?.id === 55 || selectedBeyblade2?.id === 56) && (
              <button
                onPointerDown={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setDualSpinDirection2(prev => prev === 'right' ? 'left' : 'right');
                }}
                className={`absolute bottom-2 left-2 right-2 h-10 rounded-lg flex items-center justify-center transition-all text-white font-bold text-sm z-10 ${
                  dualSpinDirection2 === 'left'
                    ? 'bg-purple-600 hover:bg-purple-700 active:scale-95'
                    : 'bg-orange-600 hover:bg-orange-700 active:scale-95'
                }`}
                style={{ touchAction: 'manipulation' }}
              >
                <span className="flex items-center gap-2">
                  {dualSpinDirection2 === 'left' ? '↺ Left Spin' : 'Right Spin ↻'}
                </span>
              </button>
            )}
          </div>

          {/* Player 2 Launch Button - BELOW touchpad */}
          {/* Show "Go Shoot!" when beyblade not active, OR show special buttons for Dual-Spin/Shinobi Shadow when active */}
          {top2Active && (selectedBeyblade2?.id === 55 || selectedBeyblade2?.id === 56) ? (
            /* Dual-Spin Beyblade Spin Direction Toggle Button for P2 */
            <button
              onPointerDown={(e) => {
                e.preventDefault();
                e.stopPropagation();
                if (!isPlayer2Disabled && !top1KnockedOut && !top2KnockedOut && !roundWinner && !isDraw) {
                  // Toggle between 'left' and 'right' spin direction
                  setDualSpinDirection2(prev => prev === 'right' ? 'left' : 'right');
                }
              }}
              disabled={isPlayer2Disabled || top1KnockedOut || top2KnockedOut || roundWinner || isDraw}
              style={{ touchAction: 'manipulation' }}
              className={`w-full h-10 rounded-lg flex items-center justify-center relative transition-all text-white font-bold text-sm ${
                p2StyleDisabled || top1KnockedOut || top2KnockedOut || roundWinner || isDraw
                  ? 'bg-slate-600 cursor-not-allowed opacity-50'
                  : dualSpinDirection2 === 'left'
                    ? 'bg-purple-600 hover:bg-purple-700 active:scale-95'
                    : 'bg-orange-600 hover:bg-orange-700 active:scale-95'
              }`}
            >
              <span className="flex items-center gap-2">
                {dualSpinDirection2 === 'left' ? '↺ Left Spin' : 'Right Spin ↻'}
              </span>
            </button>
          ) : top2Active && selectedBeyblade2?.id === 36 ? (
            /* Shinobi Shadow - Shadow Clones Button for P2 */
            <button
              onPointerDown={(e) => {
                e.preventDefault();
                e.stopPropagation();
                if (!isPlayer2Disabled && !top1KnockedOut && !top2KnockedOut && !roundWinner && !isDraw) {
                  handleShadowClones2();
                }
              }}
              disabled={shadowCloneUsed2 || isPlayer2Disabled || top1KnockedOut || top2KnockedOut || roundWinner || isDraw}
              style={{ touchAction: 'manipulation' }}
              className={`w-full h-10 rounded-lg flex items-center justify-center relative transition-all text-white font-bold text-sm ${
                shadowCloneUsed2 || p2StyleDisabled || top1KnockedOut || top2KnockedOut || roundWinner || isDraw
                  ? 'bg-slate-600 cursor-not-allowed opacity-50'
                  : 'bg-violet-600 hover:bg-violet-700 active:scale-95'
              }`}
            >
              <span className="flex items-center gap-2">
                🥷 Shadow Clones
              </span>
            </button>
          ) : top2Active && selectedBeyblade2?.id === 49 ? (
            /* Clock Mirage - Rewind Time Button for P2 */
            <button
              onPointerDown={(e) => {
                e.preventDefault();
                e.stopPropagation();
                if (!isPlayer2Disabled && !top1KnockedOut && !top2KnockedOut && !roundWinner && !isDraw) {
                  handleRewindTime2();
                }
              }}
              disabled={rewindUsed2 || isPlayer2Disabled || top1KnockedOut || top2KnockedOut || roundWinner || isDraw}
              style={{ touchAction: 'manipulation' }}
              className={`w-full h-10 rounded-lg flex items-center justify-center relative transition-all text-white font-bold text-sm ${
                rewindUsed2 || p2StyleDisabled || top1KnockedOut || top2KnockedOut || roundWinner || isDraw
                  ? 'bg-slate-600 cursor-not-allowed opacity-50'
                  : 'bg-amber-600 hover:bg-amber-700 active:scale-95'
              }`}
            >
              <span className="flex items-center gap-2">
                ⏪ Rewind Time
              </span>
            </button>
          ) : top2Active && hasFinalDrive(selectedBeyblade2?.id) ? (
            /* Pegasus - Final Drive Button for P2 */
            <button
              onPointerDown={(e) => {
                e.preventDefault();
                e.stopPropagation();
                if (!isPlayer2Disabled && !top1KnockedOut && !top2KnockedOut && !roundWinner && !isDraw) {
                  handleFinalDrive2();
                }
              }}
              disabled={finalDriveUsed2 || isPlayer2Disabled || top1KnockedOut || top2KnockedOut || roundWinner || isDraw}
              style={{ touchAction: 'manipulation' }}
              className={`w-full h-10 rounded-lg flex items-center justify-center relative transition-all text-white font-bold text-sm ${
                finalDriveUsed2 || p2StyleDisabled || top1KnockedOut || top2KnockedOut || roundWinner || isDraw
                  ? 'bg-slate-600 cursor-not-allowed opacity-50'
                  : 'bg-blue-600 hover:bg-blue-700 active:scale-95'
              }`}
            >
              <span className="flex items-center gap-2">
                🚀 Final Drive
              </span>
            </button>
          ) : top2Active && isWizard(selectedBeyblade2?.id) ? (
            /* Wizard - Restore Health Button for P2 */
            <button
              onPointerDown={(e) => {
                e.preventDefault();
                e.stopPropagation();
                if (!isPlayer2Disabled && !top1KnockedOut && !top2KnockedOut && !roundWinner && !isDraw) {
                  handleRestoreHealth2();
                }
              }}
              disabled={restoreHealthUsed2 || isPlayer2Disabled || top1KnockedOut || top2KnockedOut || roundWinner || isDraw}
              style={{ touchAction: 'manipulation' }}
              className={`w-full h-10 rounded-lg flex items-center justify-center relative transition-all text-white font-bold text-sm ${
                restoreHealthUsed2 || p2StyleDisabled || top1KnockedOut || top2KnockedOut || roundWinner || isDraw
                  ? 'bg-slate-600 cursor-not-allowed opacity-50'
                  : 'bg-green-600 hover:bg-green-700 active:scale-95'
              }`}
            >
              <span className="flex items-center gap-2">
                💚 Restore Health
              </span>
            </button>
          ) : top2Active && isKnight(selectedBeyblade2?.id) ? (
            /* Knight Mail - Iron Wall Button for P2 */
            <button
              onPointerDown={(e) => {
                e.preventDefault();
                e.stopPropagation();
                if (!isPlayer2Disabled && !top1KnockedOut && !top2KnockedOut && !roundWinner && !isDraw) {
                  handleIronWall2();
                }
              }}
              disabled={ironWallUsed2 || isPlayer2Disabled || top1KnockedOut || top2KnockedOut || roundWinner || isDraw}
              style={{ touchAction: 'manipulation' }}
              className={`w-full h-10 rounded-lg flex items-center justify-center relative transition-all text-white font-bold text-sm ${
                ironWallUsed2 || p2StyleDisabled || top1KnockedOut || top2KnockedOut || roundWinner || isDraw
                  ? 'bg-slate-600 cursor-not-allowed opacity-50'
                  : 'bg-slate-400 hover:bg-slate-300 active:scale-95'
              }`}
            >
              <span className="flex items-center gap-2">
                🛡️ Iron Wall
              </span>
            </button>
          ) : top2Active && isHells(selectedBeyblade2?.id) ? (
            /* Hells Beyblades - Burning Strike Button for P2 */
            <button
              onPointerDown={(e) => {
                e.preventDefault();
                e.stopPropagation();
                if (!isPlayer2Disabled && !top1KnockedOut && !top2KnockedOut && !roundWinner && !isDraw) {
                  handleBurningStrike2();
                }
              }}
              disabled={burningStrikeUsed2 || isPlayer2Disabled || top1KnockedOut || top2KnockedOut || roundWinner || isDraw}
              style={{ touchAction: 'manipulation' }}
              className={`w-full h-10 rounded-lg flex items-center justify-center relative transition-all text-white font-bold text-sm ${
                burningStrikeUsed2 || p2StyleDisabled || top1KnockedOut || top2KnockedOut || roundWinner || isDraw
                  ? 'bg-slate-600 cursor-not-allowed opacity-50'
                  : 'bg-orange-500 hover:bg-orange-400 active:scale-95'
              }`}
            >
              <span className="flex items-center gap-2">
                🔥 Burning Strike
              </span>
            </button>
          ) : (
            /* Standard Go Shoot! Button for P2 */
            <button
              data-testid="p2-go-shoot-btn"
              onPointerDown={(e) => {
                e.preventDefault();
                e.stopPropagation();
                if (!top2Active && !isPlayer2Disabled && !top1KnockedOut && !top2KnockedOut && !roundWinner && !isDraw) {
                  setTop2Active(true);
                  // Launch on the outer ring (BEYBLADE_RADIUS from center, opposite side), slightly up
                  setTop2Pos({ x: STADIUM_CENTER_X - BEYBLADE_RADIUS, y: STADIUM_CENTER_Y });
                  setTop2Speed(100);
                  setTop2Rotation(0);
                  // Set health based on beyblade (Wizard Rod has 20 HP)
                  setHealth2(getMaxHealth(selectedBeyblade2?.id));
                }
              }}
              disabled={top2Active || isPlayer2Disabled || top1KnockedOut || top2KnockedOut || roundWinner || isDraw}
              style={{ touchAction: 'manipulation' }}
              className={`w-full h-10 rounded-lg flex items-center justify-center relative transition-all text-white font-bold text-sm ${
                top2Active || isPlayer2Disabled || top1KnockedOut || top2KnockedOut || roundWinner || isDraw
                  ? 'bg-slate-600 cursor-not-allowed opacity-50' 
                  : 'bg-orange-600 hover:bg-orange-700 active:scale-95'
              }`}
            >
              <img 
                src="https://customer-assets.emergentagent.com/job_beyblades-battle/artifacts/9mtw3fkf_IMG_1119.png" 
                alt="Launcher" 
                className="absolute left-2 w-7 h-7 object-contain scale-x-[-1]"
              />
              <span>Go Shoot!</span>
            </button>
          )}

          {/* Player 2 Beyblade Selection Box (LEFT SIDE) */}
          <div className="w-full bg-slate-800/50 border border-orange-500/30 rounded-lg p-2">
            <div className="flex items-center justify-between mb-2">
              <p className="text-orange-400 font-semibold text-[9px] truncate max-w-[80px]">
                {isOnlineMode && matchFound ? 'OPPONENT BEYBLADES' : (selectedBeyblade2?.name || 'MY BEYBLADES')}
              </p>
              {/* Show CUSTOMIZE button in local mode, or RELAUNCH when tops are active */}
              {!isOnlineMode && (
                <button
                  onClick={() => {
                    if (top1Active || top2Active) {
                      // Relaunch - reset round without changing scores
                      setTop1KnockedOut(false);
                      setTop2KnockedOut(false);
                      setTop1Active(false);
                      setTop2Active(false);
                      setKnockbackVelocity1({ x: 0, y: 0 });
                      setKnockbackVelocity2({ x: 0, y: 0 });
                      setHealth1(getMaxHealth(selectedBeyblade1?.id));
                      setHealth2(getMaxHealth(selectedBeyblade2?.id));
                      setStamina1(getMaxStamina(selectedBeyblade1?.id));
                      setStamina2(getMaxStamina(selectedBeyblade2?.id));
                      setMaxStamina1(getMaxStamina(selectedBeyblade1?.id));
                      setMaxStamina2(getMaxStamina(selectedBeyblade2?.id));
                      setStoppedSpinning1(false);
                      setStoppedSpinning2(false);
                      setRoundWinner(null);
                      setWinType(null);
                      setIsDraw(false);
                      setDrawType(null);
                      scoreAwardedRef.current = false;
                      
                      // Reset all power-ups
                      setDeflection1(false);
                      setDeflection2(false);
                      setGhostMode1(false);
                      setGhostMode2(false);
                      setSpeedBoost1(false);
                      setSpeedBoost2(false);
                      setSpeedMultiplier1(1);
                      setSpeedMultiplier2(1);
                      setDeflectionCooldown1(false);
                      setDeflectionCooldown2(false);
                      setGhostCooldown1(false);
                      setGhostCooldown2(false);
                      setSpeedBoostCooldown1(false);
                      setSpeedBoostCooldown2(false);
                      
                      // Clear timers
                      if (deflectionTimer1Ref.current) clearTimeout(deflectionTimer1Ref.current);
                      if (deflectionTimer2Ref.current) clearTimeout(deflectionTimer2Ref.current);
                      if (ghostTimer1Ref.current) clearTimeout(ghostTimer1Ref.current);
                      if (ghostTimer2Ref.current) clearTimeout(ghostTimer2Ref.current);
                      if (boostTimer1Ref.current) clearTimeout(boostTimer1Ref.current);
                      if (boostTimer2Ref.current) clearTimeout(boostTimer2Ref.current);
                      
                      // Reset special abilities
                      setShadowClones1([]);
                      setShadowClones2([]);
                      setShadowClonesActive1(false);
                      setShadowClonesActive2(false);
                      setShadowCloneUsed1(false);
                      setShadowCloneUsed2(false);
                      setPositionHistory1([]);
                      setPositionHistory2([]);
                      setRewindUsed1(false);
                      setRewindUsed2(false);
                      setRewindFreezeActive(false);
                      setFinalDriveActive1(false);
                      setFinalDriveActive2(false);
                      setFinalDriveUsed1(false);
                      setFinalDriveUsed2(false);
                      setRestoreHealthUsed1(false);
                      setRestoreHealthUsed2(false);
                      setIronWallActive1(false);
                      setIronWallActive2(false);
                      setIronWallUsed1(false);
                      setIronWallUsed2(false);
                      setBurningStrikeActive1(false);
                      setBurningStrikeActive2(false);
                      setBurningStrikeUsed1(false);
                      setBurningStrikeUsed2(false);
                    } else if (!top2KnockedOut) {
                      setShowRosterModal2(true);
                    }
                  }}
                  disabled={top2KnockedOut && !top1Active && !top2Active}
                  className={`text-[8px] px-2 py-0.5 rounded transition-all ${
                    (top1Active || top2Active)
                      ? 'text-yellow-400 hover:text-yellow-300 bg-yellow-900/30 hover:bg-yellow-900/50'
                      : (top2KnockedOut)
                        ? 'text-orange-400/50 bg-orange-900/20 cursor-not-allowed opacity-50'
                        : 'text-orange-400 hover:text-orange-300 bg-orange-900/30 hover:bg-orange-900/50'
                  }`}
                >
                  {(top1Active || top2Active) ? 'Relaunch' : 'Customize'}
                </button>
              )}
            </div>
            <div className="grid grid-cols-3 gap-1 min-h-[100px]">
              {/* In online mode, show opponent's roster. In local mode, show my Player 2 roster */}
              {(() => {
                const isOnline = isOnlineMode && matchFound;
                const rosterToShow = isOnline ? opponentRoster : player2Roster;
                
                
                if (rosterToShow.length === 0) {
                  return (
                    <div className="col-span-3 text-center text-slate-500 text-xs py-4">
                      {isOnline ? 'Opponent roster loading...' : 'No Beyblades selected'}
                    </div>
                  );
                }
                
                return rosterToShow.map((beyblade) => {
                  const rarity = getBeybladeRarity(beyblade.id);
                  const rarityColors = getRarityColors(rarity);
                  return (
                  <div
                    key={beyblade.id}
                    onClick={(e) => {
                      e.stopPropagation();
                      // Only allow selection in local mode and when not knocked out
                      if (!isOnline && !top2Active && !top2KnockedOut) {
                        setSelectedBeyblade2(beyblade);
                      }
                    }}
                    onMouseDown={() => { if (!top1Active && !top2Active) handleRosterLongPressStart(beyblade); }}
                    onMouseUp={handleRosterLongPressEnd}
                    onMouseLeave={handleRosterLongPressEnd}
                    onTouchStart={() => { if (!top1Active && !top2Active) handleRosterLongPressStart(beyblade); }}
                    onTouchEnd={handleRosterLongPressEnd}
                    onTouchCancel={handleRosterLongPressEnd}
                    className={`w-12 h-12 rounded border-2 flex items-center justify-center transition-all ${
                      isOnline ? 'cursor-default' : 'cursor-pointer'
                    } ${
                      selectedBeyblade2?.id === beyblade.id
                        ? `border-white ${rarityColors.cardBgSelected} ${!top2Active ? 'beyblade-selected-pulse' : ''}`
                        : `${rarityColors.border} ${rarityColors.cardBg} ${rarityColors.cardBgHover}`
                    }`}
                  >
                    <img src={beyblade.url} alt={beyblade.name} className="w-10 h-10 object-contain" />
                  </div>
                )});
              })()}
            </div>
          </div>
        </div>

        {/* D-PAD REMOVED - Beyblades controlled via touchpads only */}

        {/* RIGHT SIDE - PLAYER 1 (Blue Beyblade) */}
        <div className="flex flex-col gap-2 items-center">
          {/* Player 1 Touchpad Label */}
          <p className="text-cyan-400 font-bold text-xs">
            P1 {cooldown1 && '⏱️'} {speedBoost1 && '⚡'} {ghostMode1 && '👻'} {deflection1 && '🛡️'}
          </p>
          
          {/* Player 1 Touchpad */}
          <div 
            ref={touchpad1Ref}
            className={`relative w-48 h-40 rounded-xl border-2 select-none overflow-hidden transition-all ${
              cooldown1 || isPlayer1Disabled
                ? 'bg-red-900/50 border-red-500 cursor-not-allowed opacity-50' 
                : 'bg-slate-900 border-cyan-500/50 cursor-move'
            }`}
            style={{ 
              touchAction: 'none',
              WebkitTouchCallout: 'none',
              WebkitUserSelect: 'none',
              userSelect: 'none'
            }}
            onPointerDown={handleTouchpad1PointerDown}
            onPointerMove={handleTouchpad1PointerMove}
            onPointerUp={handleTouchpad1PointerUp}
            onPointerCancel={handleTouchpad1PointerUp}
          >
            {/* Crosshair */}
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 pointer-events-none">
              <div className="w-10 h-0.5 bg-cyan-500/50"></div>
              <div className="w-0.5 h-10 bg-cyan-500/50 absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2"></div>
            </div>
            
            {/* Dual-Spin Beyblade (Gravity Perseus/Vari Ares) Spin Direction Toggle - Shows BEFORE launch */}
            {!top1Active && (selectedBeyblade1?.id === 55 || selectedBeyblade1?.id === 56) && (
              <button
                onPointerDown={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setDualSpinDirection1(prev => prev === 'right' ? 'left' : 'right');
                }}
                className={`absolute bottom-2 left-2 right-2 h-10 rounded-lg flex items-center justify-center transition-all text-white font-bold text-sm z-10 ${
                  dualSpinDirection1 === 'left'
                    ? 'bg-purple-600 hover:bg-purple-700 active:scale-95'
                    : 'bg-cyan-600 hover:bg-cyan-700 active:scale-95'
                }`}
                style={{ touchAction: 'manipulation' }}
              >
                <span className="flex items-center gap-2">
                  {dualSpinDirection1 === 'left' ? '↺ Left Spin' : 'Right Spin ↻'}
                </span>
              </button>
            )}
          </div>

          {/* Player 1 Launch Button - BELOW touchpad */}
          {/* Show "Go Shoot!" when beyblade not active, OR show special buttons for Dual-Spin/Shinobi Shadow when active */}
          {top1Active && (selectedBeyblade1?.id === 55 || selectedBeyblade1?.id === 56) ? (
            /* Dual-Spin Beyblade Spin Direction Toggle Button */
            <button
              onPointerDown={(e) => {
                e.preventDefault();
                e.stopPropagation();
                if (!top1KnockedOut && !top2KnockedOut && !roundWinner && !isDraw) {
                  // Toggle between 'left' and 'right' spin direction
                  setDualSpinDirection1(prev => prev === 'right' ? 'left' : 'right');
                }
              }}
              disabled={top1KnockedOut || top2KnockedOut || roundWinner || isDraw}
              style={{ touchAction: 'manipulation' }}
              className={`w-full h-10 rounded-lg flex items-center justify-center relative transition-all text-white font-bold text-sm ${
                top1KnockedOut || top2KnockedOut || roundWinner || isDraw
                  ? 'bg-slate-600 cursor-not-allowed opacity-50'
                  : dualSpinDirection1 === 'left'
                    ? 'bg-purple-600 hover:bg-purple-700 active:scale-95'
                    : 'bg-cyan-600 hover:bg-cyan-700 active:scale-95'
              }`}
            >
              <span className="flex items-center gap-2">
                {dualSpinDirection1 === 'left' ? '↺ Left Spin' : 'Right Spin ↻'}
              </span>
            </button>
          ) : top1Active && selectedBeyblade1?.id === 36 ? (
            /* Shinobi Shadow - Shadow Clones Button */
            <button
              onPointerDown={(e) => {
                e.preventDefault();
                e.stopPropagation();
                if (!top1KnockedOut && !top2KnockedOut && !roundWinner && !isDraw) {
                  handleShadowClones1();
                }
              }}
              disabled={shadowCloneUsed1 || top1KnockedOut || top2KnockedOut || roundWinner || isDraw}
              style={{ touchAction: 'manipulation' }}
              className={`w-full h-10 rounded-lg flex items-center justify-center relative transition-all text-white font-bold text-sm ${
                shadowCloneUsed1 || top1KnockedOut || top2KnockedOut || roundWinner || isDraw
                  ? 'bg-slate-600 cursor-not-allowed opacity-50'
                  : 'bg-violet-600 hover:bg-violet-700 active:scale-95'
              }`}
            >
              <span className="flex items-center gap-2">
                🥷 Shadow Clones
              </span>
            </button>
          ) : top1Active && selectedBeyblade1?.id === 49 ? (
            /* Clock Mirage - Rewind Time Button */
            <button
              onPointerDown={(e) => {
                e.preventDefault();
                e.stopPropagation();
                if (!top1KnockedOut && !top2KnockedOut && !roundWinner && !isDraw) {
                  handleRewindTime1();
                }
              }}
              disabled={rewindUsed1 || top1KnockedOut || top2KnockedOut || roundWinner || isDraw}
              style={{ touchAction: 'manipulation' }}
              className={`w-full h-10 rounded-lg flex items-center justify-center relative transition-all text-white font-bold text-sm ${
                rewindUsed1 || top1KnockedOut || top2KnockedOut || roundWinner || isDraw
                  ? 'bg-slate-600 cursor-not-allowed opacity-50'
                  : 'bg-amber-600 hover:bg-amber-700 active:scale-95'
              }`}
            >
              <span className="flex items-center gap-2">
                ⏪ Rewind Time
              </span>
            </button>
          ) : top1Active && hasFinalDrive(selectedBeyblade1?.id) ? (
            /* Pegasus - Final Drive Button */
            <button
              onPointerDown={(e) => {
                e.preventDefault();
                e.stopPropagation();
                if (!top1KnockedOut && !top2KnockedOut && !roundWinner && !isDraw) {
                  handleFinalDrive1();
                }
              }}
              disabled={finalDriveUsed1 || top1KnockedOut || top2KnockedOut || roundWinner || isDraw}
              style={{ touchAction: 'manipulation' }}
              className={`w-full h-10 rounded-lg flex items-center justify-center relative transition-all text-white font-bold text-sm ${
                finalDriveUsed1 || top1KnockedOut || top2KnockedOut || roundWinner || isDraw
                  ? 'bg-slate-600 cursor-not-allowed opacity-50'
                  : 'bg-blue-600 hover:bg-blue-700 active:scale-95'
              }`}
            >
              <span className="flex items-center gap-2">
                🚀 Final Drive
              </span>
            </button>
          ) : top1Active && isWizard(selectedBeyblade1?.id) ? (
            /* Wizard - Restore Health Button */
            <button
              onPointerDown={(e) => {
                e.preventDefault();
                e.stopPropagation();
                if (!top1KnockedOut && !top2KnockedOut && !roundWinner && !isDraw) {
                  handleRestoreHealth1();
                }
              }}
              disabled={restoreHealthUsed1 || top1KnockedOut || top2KnockedOut || roundWinner || isDraw}
              style={{ touchAction: 'manipulation' }}
              className={`w-full h-10 rounded-lg flex items-center justify-center relative transition-all text-white font-bold text-sm ${
                restoreHealthUsed1 || top1KnockedOut || top2KnockedOut || roundWinner || isDraw
                  ? 'bg-slate-600 cursor-not-allowed opacity-50'
                  : 'bg-green-600 hover:bg-green-700 active:scale-95'
              }`}
            >
              <span className="flex items-center gap-2">
                💚 Restore Health
              </span>
            </button>
          ) : top1Active && isKnight(selectedBeyblade1?.id) ? (
            /* Knight Mail - Iron Wall Button */
            <button
              onPointerDown={(e) => {
                e.preventDefault();
                e.stopPropagation();
                if (!top1KnockedOut && !top2KnockedOut && !roundWinner && !isDraw) {
                  handleIronWall1();
                }
              }}
              disabled={ironWallUsed1 || top1KnockedOut || top2KnockedOut || roundWinner || isDraw}
              style={{ touchAction: 'manipulation' }}
              className={`w-full h-10 rounded-lg flex items-center justify-center relative transition-all text-white font-bold text-sm ${
                ironWallUsed1 || top1KnockedOut || top2KnockedOut || roundWinner || isDraw
                  ? 'bg-slate-600 cursor-not-allowed opacity-50'
                  : 'bg-slate-400 hover:bg-slate-300 active:scale-95'
              }`}
            >
              <span className="flex items-center gap-2">
                🛡️ Iron Wall
              </span>
            </button>
          ) : top1Active && isHells(selectedBeyblade1?.id) ? (
            /* Hells Beyblades - Burning Strike Button */
            <button
              onPointerDown={(e) => {
                e.preventDefault();
                e.stopPropagation();
                if (!top1KnockedOut && !top2KnockedOut && !roundWinner && !isDraw) {
                  handleBurningStrike1();
                }
              }}
              disabled={burningStrikeUsed1 || top1KnockedOut || top2KnockedOut || roundWinner || isDraw}
              style={{ touchAction: 'manipulation' }}
              className={`w-full h-10 rounded-lg flex items-center justify-center relative transition-all text-white font-bold text-sm ${
                burningStrikeUsed1 || top1KnockedOut || top2KnockedOut || roundWinner || isDraw
                  ? 'bg-slate-600 cursor-not-allowed opacity-50'
                  : 'bg-orange-500 hover:bg-orange-400 active:scale-95'
              }`}
            >
              <span className="flex items-center gap-2">
                🔥 Burning Strike
              </span>
            </button>
          ) : (
            /* Standard Go Shoot! Button */
            <button
              data-testid="p1-go-shoot-btn"
              onPointerDown={(e) => {
                e.preventDefault();
                e.stopPropagation();
                if (!top1Active && !top1KnockedOut && !top2KnockedOut && !roundWinner && !isDraw) {
                  // Launch on the outer ring (BEYBLADE_RADIUS from center), slightly up
                  const launchPos = { x: STADIUM_CENTER_X + BEYBLADE_RADIUS, y: STADIUM_CENTER_Y };
                  setTop1Active(true);
                  setTop1Pos(launchPos);
                  setTop1Speed(100);
                  setTop1Rotation(0);
                  // Set health based on beyblade (Wizard Rod has 20 HP)
                  setHealth1(getMaxHealth(selectedBeyblade1?.id));
                  
                  // IMMEDIATELY send launch state to opponent via WebSocket
                  if (isOnlineMode && matchFound) {
                    // Use setTimeout to ensure state is updated before sending
                    setTimeout(() => {
                      sendImmediateState({ active: true, pos: launchPos, speed: 100 });
                    }, 0);
                  }
                }
              }}
              disabled={top1Active || top1KnockedOut || top2KnockedOut || roundWinner || isDraw}
              style={{ touchAction: 'manipulation' }}
              className={`w-full h-10 rounded-lg flex items-center justify-center relative transition-all text-white font-bold text-sm ${
                top1Active || top1KnockedOut || top2KnockedOut || roundWinner || isDraw
                  ? 'bg-slate-600 cursor-not-allowed opacity-50' 
                  : 'bg-cyan-600 hover:bg-cyan-700 active:scale-95'
              }`}
            >
              <span>Go Shoot!</span>
              <img 
                src="https://customer-assets.emergentagent.com/job_beyblades-battle/artifacts/9mtw3fkf_IMG_1119.png" 
                alt="Launcher" 
                className="absolute right-2 w-7 h-7 object-contain"
            />
            </button>
          )}

          {/* Player 1 Beyblade Selection Box (RIGHT SIDE) */}
          <div className="w-full bg-slate-800/50 border border-cyan-500/30 rounded-lg p-2">
            <div className="flex items-center justify-between mb-2">
              <p className="text-cyan-400 font-semibold text-[9px] truncate max-w-[80px]">
                {selectedBeyblade1?.name || 'MY BEYBLADES'}
              </p>
              {/* Show CUSTOMIZE button, or RELAUNCH when tops are active */}
              {(
                <button
                  onClick={() => {
                    if (top1Active || top2Active) {
                      // Relaunch - reset round without changing scores
                      setTop1KnockedOut(false);
                      setTop2KnockedOut(false);
                      setTop1Active(false);
                      setTop2Active(false);
                      setKnockbackVelocity1({ x: 0, y: 0 });
                      setKnockbackVelocity2({ x: 0, y: 0 });
                      setHealth1(getMaxHealth(selectedBeyblade1?.id));
                      setHealth2(getMaxHealth(selectedBeyblade2?.id));
                      setStamina1(getMaxStamina(selectedBeyblade1?.id));
                      setStamina2(getMaxStamina(selectedBeyblade2?.id));
                      setMaxStamina1(getMaxStamina(selectedBeyblade1?.id));
                      setMaxStamina2(getMaxStamina(selectedBeyblade2?.id));
                      setStoppedSpinning1(false);
                      setStoppedSpinning2(false);
                      setRoundWinner(null);
                      setWinType(null);
                      setIsDraw(false);
                      setDrawType(null);
                      scoreAwardedRef.current = false;
                      
                      // Reset all power-ups
                      setDeflection1(false);
                      setDeflection2(false);
                      setGhostMode1(false);
                      setGhostMode2(false);
                      setSpeedBoost1(false);
                      setSpeedBoost2(false);
                      setSpeedMultiplier1(1);
                      setSpeedMultiplier2(1);
                      setDeflectionCooldown1(false);
                      setDeflectionCooldown2(false);
                      setGhostCooldown1(false);
                      setGhostCooldown2(false);
                      setSpeedBoostCooldown1(false);
                      setSpeedBoostCooldown2(false);
                      
                      // Clear timers
                      if (deflectionTimer1Ref.current) clearTimeout(deflectionTimer1Ref.current);
                      if (deflectionTimer2Ref.current) clearTimeout(deflectionTimer2Ref.current);
                      if (ghostTimer1Ref.current) clearTimeout(ghostTimer1Ref.current);
                      if (ghostTimer2Ref.current) clearTimeout(ghostTimer2Ref.current);
                      if (boostTimer1Ref.current) clearTimeout(boostTimer1Ref.current);
                      if (boostTimer2Ref.current) clearTimeout(boostTimer2Ref.current);
                      
                      // Reset special abilities
                      setShadowClones1([]);
                      setShadowClones2([]);
                      setShadowClonesActive1(false);
                      setShadowClonesActive2(false);
                      setShadowCloneUsed1(false);
                      setShadowCloneUsed2(false);
                      setPositionHistory1([]);
                      setPositionHistory2([]);
                      setRewindUsed1(false);
                      setRewindUsed2(false);
                      setRewindFreezeActive(false);
                      setFinalDriveActive1(false);
                      setFinalDriveActive2(false);
                      setFinalDriveUsed1(false);
                      setFinalDriveUsed2(false);
                      setRestoreHealthUsed1(false);
                      setRestoreHealthUsed2(false);
                      setIronWallActive1(false);
                      setIronWallActive2(false);
                      setIronWallUsed1(false);
                      setIronWallUsed2(false);
                      setBurningStrikeActive1(false);
                      setBurningStrikeActive2(false);
                      setBurningStrikeUsed1(false);
                      setBurningStrikeUsed2(false);
                    } else if (!top1KnockedOut) {
                      setShowRosterModal1(true);
                    }
                  }}
                  disabled={top1KnockedOut && !top1Active && !top2Active}
                  className={`text-[8px] px-2 py-0.5 rounded transition-all ${
                    (top1Active || top2Active)
                      ? 'text-yellow-400 hover:text-yellow-300 bg-yellow-900/30 hover:bg-yellow-900/50'
                      : (top1KnockedOut)
                        ? 'text-cyan-400/50 bg-cyan-900/20 cursor-not-allowed opacity-50'
                        : 'text-cyan-400 hover:text-cyan-300 bg-cyan-900/30 hover:bg-cyan-900/50'
                  }`}
                >
                  {(top1Active || top2Active) ? 'Relaunch' : 'Customize'}
                </button>
              )}
            </div>
            <div className="grid grid-cols-3 gap-1 min-h-[100px]">
              {/* Always show MY Player 1 roster here (both in local and online mode) */}
              {player1Roster.map((beyblade) => {
                const rarity = getBeybladeRarity(beyblade.id);
                const rarityColors = getRarityColors(rarity);
                return (
                <div
                  key={beyblade.id}
                  onClick={(e) => {
                    e.stopPropagation();
                    // Only allow selection when not active and not knocked out
                    if (!top1Active && !top1KnockedOut) {
                      setSelectedBeyblade1(beyblade);
                    }
                  }}
                  onMouseDown={() => { if (!top1Active && !top2Active) handleRosterLongPressStart(beyblade); }}
                  onMouseUp={handleRosterLongPressEnd}
                  onMouseLeave={handleRosterLongPressEnd}
                  onTouchStart={() => { if (!top1Active && !top2Active) handleRosterLongPressStart(beyblade); }}
                  onTouchEnd={handleRosterLongPressEnd}
                  onTouchCancel={handleRosterLongPressEnd}
                  className={`w-12 h-12 rounded border-2 flex items-center justify-center transition-all cursor-pointer ${
                    selectedBeyblade1?.id === beyblade.id
                      ? `border-white ${rarityColors.cardBgSelected} ${!top1Active ? 'beyblade-selected-pulse' : ''}`
                      : `${rarityColors.border} ${rarityColors.cardBg} ${rarityColors.cardBgHover}`
                  }`}
                >
                  <img src={beyblade.url} alt={beyblade.name} className="w-10 h-10 object-contain" />
                </div>
              )})}
            </div>
          </div>
        </div>
      </div>

      {/* ROSTER CUSTOMIZATION MODAL - Player 2 */}
      {showRosterModal2 && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 overflow-y-auto py-2">
          <div className="bg-slate-800 border-2 border-orange-500 rounded-lg p-4 max-w-lg w-full mx-4">
            <div className="flex items-center justify-between mb-2">
              <h2 className="text-orange-400 font-bold text-base">CUSTOMIZE P2 ROSTER</h2>
              <button
                onClick={() => setShowRosterModal2(false)}
                className="text-orange-400 hover:text-orange-300 text-2xl font-bold flex-shrink-0"
              >
                ×
              </button>
            </div>
            <div className="mb-2">
              <button
                onClick={() => {
                  setShowRosterModal2(false);
                  onOpenStore?.();
                }}
                className="flex items-center gap-1 text-purple-400 hover:text-purple-300 text-xs font-bold bg-purple-900/30 hover:bg-purple-900/50 px-2 py-1 rounded transition-all"
              >
                <img src="https://customer-assets.emergentagent.com/job_blader-progress/artifacts/zw2i6ecd_IMG_0908.png" alt="Cobalt Drake" className="w-4 h-4 object-contain" />
                <img src="https://customer-assets.emergentagent.com/job_57de14b1-dd79-40f8-9905-e428eae842bc/artifacts/ae815y75_IMG_0922.png" alt="Aero Pegasus" className="w-4 h-4 object-contain" />
                <img src="https://customer-assets.emergentagent.com/job_57de14b1-dd79-40f8-9905-e428eae842bc/artifacts/2rl351jz_IMG_0926.png" alt="Valkyrie Volt" className="w-4 h-4 object-contain" />
                <span>Beyblade Shop!</span>
              </button>
            </div>
            <p className="text-orange-300 text-xs mb-2">Select 6 Beyblades for your roster:</p>
            <div className="max-h-[75vh] overflow-y-auto mb-2 pr-2">
              <div className="grid grid-cols-3 gap-2">
                {(() => {
                  // Display roster Beyblades first in their correct order, then non-roster Beyblades
                  const rosterBeyblades = player2Roster;
                  const nonRosterBeyblades = availableBeyblades.filter(b => !player2Roster.some(r => r.id === b.id));
                  const sortedBeyblades = [...rosterBeyblades, ...nonRosterBeyblades];
                  
                  return sortedBeyblades.map((beyblade) => {
                const isInRoster = player2Roster.some(b => b.id === beyblade.id);
                const selectedIndex = player2Roster.findIndex(b => b.id === beyblade.id);
                const canAdd = player2Roster.length < 6 || isInRoster;
                const rarity = getBeybladeRarity(beyblade.id);
                const rarityColors = getRarityColors(rarity);
                return (
                  <div
                    key={beyblade.id}
                    draggable={isInRoster}
                    onDragStart={(e) => {
                      if (isInRoster) {
                        e.dataTransfer.effectAllowed = 'move';
                        e.dataTransfer.setData('beybladeId', beyblade.id.toString());
                        e.dataTransfer.setData('fromIndex', selectedIndex.toString());
                        e.currentTarget.style.opacity = '0.5';
                      }
                    }}
                    onDragEnd={(e) => {
                      e.currentTarget.style.opacity = '1';
                    }}
                    onDragOver={(e) => {
                      if (isInRoster) {
                        e.preventDefault();
                        e.dataTransfer.dropEffect = 'move';
                      }
                    }}
                    onDrop={(e) => {
                      e.preventDefault();
                      if (isInRoster) {
                        const draggedId = parseInt(e.dataTransfer.getData('beybladeId'));
                        const fromIndex = parseInt(e.dataTransfer.getData('fromIndex'));
                        const toIndex = selectedIndex;
                        
                        if (draggedId !== beyblade.id && fromIndex !== toIndex) {
                          // Swap positions
                          const newRoster = [...player2Roster];
                          [newRoster[fromIndex], newRoster[toIndex]] = [newRoster[toIndex], newRoster[fromIndex]];
                          setPlayer2Roster(newRoster);
                        }
                      }
                    }}
                    onClick={() => {
                      if (isInRoster) {
                        setPlayer2Roster(player2Roster.filter(b => b.id !== beyblade.id));
                      } else if (player2Roster.length < 6) {
                        setPlayer2Roster([...player2Roster, beyblade]);
                      }
                    }}
                    onMouseDown={() => handleRosterLongPressStart(beyblade)}
                    onMouseUp={handleRosterLongPressEnd}
                    onMouseLeave={handleRosterLongPressEnd}
                    onTouchStart={() => handleRosterLongPressStart(beyblade)}
                    onTouchEnd={handleRosterLongPressEnd}
                    onTouchCancel={handleRosterLongPressEnd}
                    className={`relative aspect-square rounded border-2 flex flex-col items-center justify-center transition-all ${
                      isInRoster ? 'cursor-move' : 'cursor-pointer'
                    } ${
                      isInRoster
                        ? `${rarityColors.borderSelected} ${rarityColors.cardBgSelected}`
                        : canAdd
                        ? `${rarityColors.border} ${rarityColors.cardBg} ${rarityColors.cardBgHover}`
                        : `${rarityColors.border} ${rarityColors.cardBg} opacity-50 cursor-not-allowed`
                    }`}
                  >
                    <img src={beyblade.url} alt={beyblade.name} className="w-16 h-16 object-contain pointer-events-none" />
                    <p className={`${rarityColors.accent} text-[8px] mt-1 text-center px-1 pointer-events-none`}>{beyblade.name}</p>
                    {isInRoster && (
                      <div className={`absolute top-1 right-1 ${rarityColors.checkBg} text-white rounded-full w-4 h-4 flex items-center justify-center text-xs font-bold pointer-events-none`}>
                        ✓
                      </div>
                    )}
                  </div>
                );
                });
                })()}
              </div>
            </div>
            <div className="text-center mb-2">
              <p className="text-orange-400 text-sm font-bold">
                {player2Roster.length} / 6 Selected
              </p>
            </div>
            <button
              onClick={async () => {
                if (player2Roster.length > 0) {
                  const saved = await saveRoster(2, player2Roster);
                  if (saved) {
                    setShowRosterModal2(false);
                  } else {
                    alert('Failed to save Player 2 roster. Please try again.');
                  }
                }
              }}
              disabled={player2Roster.length === 0}
              className={`w-full py-2 rounded-lg font-bold text-sm transition-all ${
                player2Roster.length > 0
                  ? 'bg-orange-600 hover:bg-orange-700 text-white cursor-pointer'
                  : 'bg-slate-700 text-slate-500 cursor-not-allowed'
              }`}
            >
              CONFIRM ROSTER
            </button>
          </div>
        </div>
      )}

      {/* ROSTER CUSTOMIZATION MODAL - Player 1 */}
      {showRosterModal1 && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 overflow-y-auto py-2">
          <div className="bg-slate-800 border-2 border-cyan-500 rounded-lg p-4 max-w-lg w-full mx-4">
            <div className="flex items-center justify-between mb-2">
              <h2 className="text-cyan-400 font-bold text-base">CUSTOMIZE P1 ROSTER</h2>
              <button
                onClick={() => setShowRosterModal1(false)}
                className="text-cyan-400 hover:text-cyan-300 text-2xl font-bold flex-shrink-0"
              >
                ×
              </button>
            </div>
            <div className="mb-2">
              <button
                onClick={() => {
                  setShowRosterModal1(false);
                  onOpenStore?.();
                }}
                className="flex items-center gap-1 text-purple-400 hover:text-purple-300 text-xs font-bold bg-purple-900/30 hover:bg-purple-900/50 px-2 py-1 rounded transition-all"
              >
                <img src="https://customer-assets.emergentagent.com/job_blader-progress/artifacts/zw2i6ecd_IMG_0908.png" alt="Cobalt Drake" className="w-4 h-4 object-contain" />
                <img src="https://customer-assets.emergentagent.com/job_57de14b1-dd79-40f8-9905-e428eae842bc/artifacts/ae815y75_IMG_0922.png" alt="Aero Pegasus" className="w-4 h-4 object-contain" />
                <img src="https://customer-assets.emergentagent.com/job_57de14b1-dd79-40f8-9905-e428eae842bc/artifacts/2rl351jz_IMG_0926.png" alt="Valkyrie Volt" className="w-4 h-4 object-contain" />
                <span>Beyblade Shop!</span>
              </button>
            </div>
            <p className="text-cyan-300 text-xs mb-2">Select 6 Beyblades for your roster:</p>
            <div className="max-h-[75vh] overflow-y-auto mb-2 pr-2">
              <div className="grid grid-cols-3 gap-2">
                {(() => {
                  // Display roster Beyblades first in their correct order, then non-roster Beyblades
                  const rosterBeyblades = player1Roster;
                  const nonRosterBeyblades = availableBeyblades.filter(b => !player1Roster.some(r => r.id === b.id));
                  const sortedBeyblades = [...rosterBeyblades, ...nonRosterBeyblades];
                  
                  return sortedBeyblades.map((beyblade) => {
                const isInRoster = player1Roster.some(b => b.id === beyblade.id);
                const selectedIndex = player1Roster.findIndex(b => b.id === beyblade.id);
                const canAdd = player1Roster.length < 6 || isInRoster;
                const rarity = getBeybladeRarity(beyblade.id);
                const rarityColors = getRarityColors(rarity);
                return (
                  <div
                    key={beyblade.id}
                    draggable={isInRoster}
                    onDragStart={(e) => {
                      if (isInRoster) {
                        e.dataTransfer.effectAllowed = 'move';
                        e.dataTransfer.setData('beybladeId', beyblade.id.toString());
                        e.dataTransfer.setData('fromIndex', selectedIndex.toString());
                        e.currentTarget.style.opacity = '0.5';
                      }
                    }}
                    onDragEnd={(e) => {
                      e.currentTarget.style.opacity = '1';
                    }}
                    onDragOver={(e) => {
                      if (isInRoster) {
                        e.preventDefault();
                        e.dataTransfer.dropEffect = 'move';
                      }
                    }}
                    onDrop={(e) => {
                      e.preventDefault();
                      if (isInRoster) {
                        const draggedId = parseInt(e.dataTransfer.getData('beybladeId'));
                        const fromIndex = parseInt(e.dataTransfer.getData('fromIndex'));
                        const toIndex = selectedIndex;
                        
                        if (draggedId !== beyblade.id && fromIndex !== toIndex) {
                          // Swap positions
                          const newRoster = [...player1Roster];
                          [newRoster[fromIndex], newRoster[toIndex]] = [newRoster[toIndex], newRoster[fromIndex]];
                          setPlayer1Roster(newRoster);
                        }
                      }
                    }}
                    onClick={() => {
                      if (isInRoster) {
                        setPlayer1Roster(player1Roster.filter(b => b.id !== beyblade.id));
                      } else if (player1Roster.length < 6) {
                        setPlayer1Roster([...player1Roster, beyblade]);
                      }
                    }}
                    onMouseDown={() => handleRosterLongPressStart(beyblade)}
                    onMouseUp={handleRosterLongPressEnd}
                    onMouseLeave={handleRosterLongPressEnd}
                    onTouchStart={() => handleRosterLongPressStart(beyblade)}
                    onTouchEnd={handleRosterLongPressEnd}
                    onTouchCancel={handleRosterLongPressEnd}
                    className={`relative aspect-square rounded border-2 flex flex-col items-center justify-center transition-all ${
                      isInRoster ? 'cursor-move' : 'cursor-pointer'
                    } ${
                      isInRoster
                        ? `${rarityColors.borderSelected} ${rarityColors.cardBgSelected}`
                        : canAdd
                        ? `${rarityColors.border} ${rarityColors.cardBg} ${rarityColors.cardBgHover}`
                        : `${rarityColors.border} ${rarityColors.cardBg} opacity-50 cursor-not-allowed`
                    }`}
                  >
                    <img src={beyblade.url} alt={beyblade.name} className="w-16 h-16 object-contain pointer-events-none" />
                    <p className={`${rarityColors.accent} text-[8px] mt-1 text-center px-1 pointer-events-none`}>{beyblade.name}</p>
                    {isInRoster && (
                      <div className={`absolute top-1 right-1 ${rarityColors.checkBg} text-white rounded-full w-4 h-4 flex items-center justify-center text-xs font-bold pointer-events-none`}>
                        ✓
                      </div>
                    )}
                  </div>
                );
                });
                })()}
              </div>
            </div>
            <div className="text-center mb-2">
              <p className="text-cyan-400 text-sm font-bold">
                {player1Roster.length} / 6 Selected
              </p>
            </div>
            <button
              onClick={async () => {
                if (player1Roster.length > 0) {
                  const saved = await saveRoster(1, player1Roster);
                  if (saved) {
                    setShowRosterModal1(false);
                  } else {
                    alert('Failed to save Player 1 roster. Please try again.');
                  }
                }
              }}
              disabled={player1Roster.length === 0}
              className={`w-full py-2 rounded-lg font-bold text-sm transition-all ${
                player1Roster.length > 0
                  ? 'bg-cyan-600 hover:bg-cyan-700 text-white cursor-pointer'
                  : 'bg-slate-700 text-slate-500 cursor-not-allowed'
              }`}
            >
              CONFIRM ROSTER
            </button>
          </div>
        </div>
      )}

      {/* GUEST PLAYER SELECTION MODAL - For verified users to load another verified user's roster as P2 */}
      {showGuestPlayerModal && (
        <div 
          className="fixed inset-0 bg-black/70 flex items-start justify-center z-50 pt-36"
          onClick={() => {
            setShowGuestPlayerModal(false);
            setGuestSearchQuery('');
            setVerifiedUsers([]);
          }}
        >
          <div className="bg-slate-800 border border-orange-500 rounded-lg p-3 w-64 mx-2" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-center mb-2 relative">
              <h2 className="text-orange-400 font-bold text-sm text-center">Player 2: Add User Account</h2>
              <button
                onClick={() => {
                  setShowGuestPlayerModal(false);
                  setGuestSearchQuery('');
                  setVerifiedUsers([]);
                }}
                className="text-orange-400 hover:text-orange-300 text-xl font-bold absolute right-0"
              >
                ×
              </button>
            </div>
            
            {/* Search Input */}
            <div className="relative mb-2">
              <input
                type="text"
                value={guestSearchQuery}
                onChange={(e) => {
                  const query = e.target.value;
                  setGuestSearchQuery(query);
                  if (query.length >= 2) {
                    searchVerifiedUsers(query);
                  } else {
                    setVerifiedUsers([]);
                  }
                }}
                placeholder="Search username..."
                className="w-full bg-slate-700 border border-orange-500/50 rounded px-2 py-1.5 text-white placeholder-slate-400 focus:outline-none focus:border-orange-500"
                style={{ fontSize: '16px' }}
                autoFocus
              />
              {searchingGuest && (
                <div className="absolute right-2 top-1/2 -translate-y-1/2">
                  <div className="animate-spin w-3 h-3 border-2 border-orange-500 border-t-transparent rounded-full"></div>
                </div>
              )}
            </div>
            
            {/* Current Guest Player - if any (shown below search) */}
            {guestPlayer && (
              <div className="mb-2">
                <div 
                  className="bg-slate-800/30 border border-slate-700/50 rounded-lg py-1.5 px-2 flex justify-between items-center relative ring-2 ring-orange-500"
                  style={guestPlayer.banner_image ? {
                    backgroundImage: `url(${guestPlayer.banner_image})`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    backgroundRepeat: 'no-repeat'
                  } : {}}
                >
                  {/* Overlay for better text visibility when banner is set */}
                  {guestPlayer.banner_image && (
                    <div className="absolute inset-0 bg-black/40 rounded-lg"></div>
                  )}
                  
                  {/* Level Badge - smaller for popup */}
                  <div 
                    className="absolute -top-0.5 left-1 text-[5px] px-1 py-0 text-white font-medium digital-font drop-shadow-lg flex items-center"
                    style={{ zIndex: 10 }}
                  >
                    Lv {guestPlayer.level || 1}
                    {guestPlayer.has_made_first_purchase && (
                      <svg className="w-2 h-2 ml-0.5 text-blue-500" fill="currentColor" viewBox="0 0 20 20" title="Verified Account">
                        <path fillRule="evenodd" d="M6.267 3.455a3.066 3.066 0 001.745-.723 3.066 3.066 0 013.976 0 3.066 3.066 0 001.745.723 3.066 3.066 0 012.812 2.812c.051.643.304 1.254.723 1.745a3.066 3.066 0 010 3.976 3.066 3.066 0 00-.723 1.745 3.066 3.066 0 01-2.812 2.812 3.066 3.066 0 00-1.745.723 3.066 3.066 0 01-3.976 0 3.066 3.066 0 00-1.745-.723 3.066 3.066 0 01-2.812-2.812 3.066 3.066 0 00-.723-1.745 3.066 3.066 0 010-3.976 3.066 3.066 0 00.723-1.745 3.066 3.066 0 012.812-2.812zm7.44 5.252a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                    )}
                  </div>
                  
                  <div className="flex items-center gap-2 relative z-10">
                    {/* Profile Picture */}
                    {guestPlayer.profile_picture ? (
                      <img
                        src={guestPlayer.profile_picture}
                        alt={guestPlayer.username}
                        className="w-7 h-7 rounded-full object-cover border-2"
                        style={{ borderColor: guestPlayer.circle_color || '#475569' }}
                      />
                    ) : (
                      <div 
                        className="w-7 h-7 rounded-full bg-slate-700 border-2 flex items-center justify-center text-slate-300 text-xs font-semibold"
                        style={{ borderColor: guestPlayer.circle_color || '#475569' }}
                      >
                        {guestPlayer.username.charAt(0).toUpperCase()}
                      </div>
                    )}
                    <span className={`font-medium text-xs ${guestPlayer.banner_image ? 'text-white drop-shadow-lg' : 'text-slate-200'}`}>
                      {guestPlayer.username}
                    </span>
                  </div>
                  
                  {/* Battle Points */}
                  <span className={`digital-font font-semibold text-xs relative z-10 ${guestPlayer.banner_image ? 'text-white drop-shadow-lg' : 'text-cyan-400'}`}>
                    {guestPlayer.battle_points || 0} BP
                  </span>
                </div>
                
                {/* Clear button below the banner */}
                <div className="flex justify-center mt-2">
                  <button
                    onClick={() => {
                      clearGuestPlayer();
                      setShowGuestPlayerModal(false);
                    }}
                    className="text-red-400 text-xs bg-red-900/50 hover:bg-red-900/70 px-4 py-1 rounded"
                  >
                    Clear
                  </button>
                </div>
              </div>
            )}
            
            {/* Search Results - Scrollable */}
            <div className="max-h-40 overflow-y-auto">
              {guestSearchQuery.length < 2 ? (
                null
              ) : searchingGuest ? (
                <p className="text-orange-300 text-[10px] text-center py-2">Searching...</p>
              ) : verifiedUsers.length === 0 ? (
                <p className="text-slate-400 text-[10px] text-center py-2">No results</p>
              ) : (
                <div className="space-y-2">
                  {verifiedUsers.map((verifiedUser) => (
                    <div
                      key={verifiedUser.id}
                      onClick={() => loadGuestPlayerRoster(verifiedUser)}
                      className={`bg-slate-800/30 border border-slate-700/50 rounded-lg py-1.5 px-2 flex justify-between items-center relative cursor-pointer transition-all ${
                        guestPlayer?.id === verifiedUser.id
                          ? 'ring-2 ring-orange-500'
                          : 'hover:ring-1 hover:ring-orange-500/50'
                      }`}
                      style={verifiedUser.banner_image ? {
                        backgroundImage: `url(${verifiedUser.banner_image})`,
                        backgroundSize: 'cover',
                        backgroundPosition: 'center',
                        backgroundRepeat: 'no-repeat'
                      } : {}}
                    >
                      {/* Overlay for better text visibility when banner is set */}
                      {verifiedUser.banner_image && (
                        <div className="absolute inset-0 bg-black/40 rounded-lg"></div>
                      )}
                      
                      {/* Level Badge - smaller for popup */}
                      <div 
                        className="absolute -top-0.5 left-1 text-[5px] px-1 py-0 text-white font-medium digital-font drop-shadow-lg flex items-center"
                        style={{ zIndex: 10 }}
                      >
                        Lv {verifiedUser.level || 1}
                        {verifiedUser.has_made_first_purchase && (
                          <svg className="w-2 h-2 ml-0.5 text-blue-500" fill="currentColor" viewBox="0 0 20 20" title="Verified Account">
                            <path fillRule="evenodd" d="M6.267 3.455a3.066 3.066 0 001.745-.723 3.066 3.066 0 013.976 0 3.066 3.066 0 001.745.723 3.066 3.066 0 012.812 2.812c.051.643.304 1.254.723 1.745a3.066 3.066 0 010 3.976 3.066 3.066 0 00-.723 1.745 3.066 3.066 0 01-2.812 2.812 3.066 3.066 0 00-1.745.723 3.066 3.066 0 01-3.976 0 3.066 3.066 0 00-1.745-.723 3.066 3.066 0 01-2.812-2.812 3.066 3.066 0 00-.723-1.745 3.066 3.066 0 010-3.976 3.066 3.066 0 00.723-1.745 3.066 3.066 0 012.812-2.812zm7.44 5.252a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                          </svg>
                        )}
                      </div>
                      
                      <div className="flex items-center gap-2 relative z-10">
                        {/* Profile Picture */}
                        {verifiedUser.profile_picture ? (
                          <img
                            src={verifiedUser.profile_picture}
                            alt={verifiedUser.username}
                            className="w-7 h-7 rounded-full object-cover border-2"
                            style={{ borderColor: verifiedUser.circle_color || '#475569' }}
                          />
                        ) : (
                          <div 
                            className="w-7 h-7 rounded-full bg-slate-700 border-2 flex items-center justify-center text-slate-300 text-xs font-semibold"
                            style={{ borderColor: verifiedUser.circle_color || '#475569' }}
                          >
                            {verifiedUser.username.charAt(0).toUpperCase()}
                          </div>
                        )}
                        <span className={`font-medium text-xs ${verifiedUser.banner_image ? 'text-white drop-shadow-lg' : 'text-slate-200'}`}>
                          {verifiedUser.username}
                        </span>
                      </div>
                      <span className={`digital-font font-semibold relative z-10 text-xs ${verifiedUser.banner_image ? 'text-white drop-shadow-lg' : 'text-cyan-400'}`}>
                        {verifiedUser.battle_points || 0} BP
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* WIN POPUPS - Top of screen */}
      {/* Player 2 wins (roundWinner === 2) - includes knockout, burst finish, and spin finish */}
      {roundWinner === 2 && !isDraw && (
        <div className="absolute top-[52px] left-1/2 -translate-x-1/2 w-full max-w-[250px] bg-orange-600/95 border-2 border-orange-500 rounded-lg p-2 animate-pulse z-30">
          <p className="text-white text-center font-bold text-sm">
            {isOnlineMode && matchFound ? 'OPPONENT WINS!' : aiMode ? 'AI WINS!' : guestPlayer ? `${guestPlayer.username} WINS!` : 'Player 2 WINS!'}
          </p>
          <p className="text-white text-center font-semibold text-xs mt-0.5">
            {winType === 'knockout' ? (
              isPocketKO ? (
                pocketKOType === 'xtremeZone' ? 'Xtreme Finish! +3 pts' : 'Over Finish! +2 pts'
              ) : 'Knock Out! +3 pts'
            ) : 
             winType === 'burstFinish' ? 'Burst Finish! +2 pts' : 
             'Spin Finish! +1 pt'}
          </p>
          {/* Show opponent wants rematch indicator in online mode */}
          {isOnlineMode && matchFound && opponentWantsRematch && !waitingForRematch && (
            <p className="text-yellow-300 text-center text-xs mt-1 animate-pulse">
              Opponent wants rematch!
            </p>
          )}
          <button
            onClick={async () => {
              if (isOnlineMode && matchFound) {
                // Online mode: Request rematch via WebSocket (faster) or API
                setWaitingForRematch(true);
                
                // Try WebSocket first for instant response
                const ws = battleWsRef.current;
                if (ws && ws.readyState === WebSocket.OPEN) {
                  ws.send(JSON.stringify({ type: 'rematch' }));
                } else {
                  // Fallback to HTTP
                  try {
                    const token = localStorage.getItem('token');
                    const response = await fetch(`${BACKEND_URL}/api/battle/game/rematch`, {
                      method: 'POST',
                      headers: { 'Authorization': `Bearer ${token}` }
                    });
                    const data = await response.json();
                    
                    if (data.error) {
                      alert(data.error);
                      setWaitingForRematch(false);
                    }
                  } catch (error) {
                    console.error('[Rematch] Error:', error);
                    setWaitingForRematch(false);
                  }
                }
              } else {
                // Local mode: Reset immediately
                setTop1KnockedOut(false);
                setTop2KnockedOut(false);
                setTop1Active(false);
                setTop2Active(false);
                setKnockbackVelocity1({ x: 0, y: 0 });
                setKnockbackVelocity2({ x: 0, y: 0 });
                setHealth1(getMaxHealth(selectedBeyblade1?.id));
                setHealth2(getMaxHealth(selectedBeyblade2?.id));
                setStamina1(getMaxStamina(selectedBeyblade1?.id));
                setStamina2(getMaxStamina(selectedBeyblade2?.id));
                setMaxStamina1(getMaxStamina(selectedBeyblade1?.id));
                setMaxStamina2(getMaxStamina(selectedBeyblade2?.id));
                setStoppedSpinning1(false);
                setStoppedSpinning2(false);
                setRoundWinner(null);
                setWinType(null);
                setIsDraw(false);
                setDrawType(null);
                scoreAwardedRef.current = false; // Reset score award flag for new round
                
                // Reset all power-ups and cooldowns
                setDeflection1(false);
                setDeflection2(false);
                setGhostMode1(false);
                setGhostMode2(false);
                setSpeedBoost1(false);
                setSpeedBoost2(false);
                setSpeedMultiplier1(1);
                setSpeedMultiplier2(1);
                
                // Reset all cooldowns instantly
                setDeflectionCooldown1(false);
                setDeflectionCooldown2(false);
                setGhostCooldown1(false);
                setGhostCooldown2(false);
                setSpeedBoostCooldown1(false);
                setSpeedBoostCooldown2(false);
                
                // Clear all timers
                if (deflectionTimer1Ref.current) clearTimeout(deflectionTimer1Ref.current);
                if (deflectionTimer2Ref.current) clearTimeout(deflectionTimer2Ref.current);
                if (ghostTimer1Ref.current) clearTimeout(ghostTimer1Ref.current);
                if (ghostTimer2Ref.current) clearTimeout(ghostTimer2Ref.current);
                if (boostTimer1Ref.current) clearTimeout(boostTimer1Ref.current);
                if (boostTimer2Ref.current) clearTimeout(boostTimer2Ref.current);
                if (deflectionCooldownTimer1Ref.current) clearTimeout(deflectionCooldownTimer1Ref.current);
                if (deflectionCooldownTimer2Ref.current) clearTimeout(deflectionCooldownTimer2Ref.current);
                if (ghostCooldownTimer1Ref.current) clearTimeout(ghostCooldownTimer1Ref.current);
                if (ghostCooldownTimer2Ref.current) clearTimeout(ghostCooldownTimer2Ref.current);
                if (speedBoostCooldownTimer1Ref.current) clearTimeout(speedBoostCooldownTimer1Ref.current);
                if (speedBoostCooldownTimer2Ref.current) clearTimeout(speedBoostCooldownTimer2Ref.current);
                
                // Reset shadow clones
                setShadowClones1([]);
                setShadowClones2([]);
                setShadowClonesActive1(false);
                setShadowClonesActive2(false);
                setShadowCloneUsed1(false);
                setShadowCloneUsed2(false);
                if (shadowCloneTimer1Ref.current) clearTimeout(shadowCloneTimer1Ref.current);
                if (shadowCloneTimer2Ref.current) clearTimeout(shadowCloneTimer2Ref.current);
                
                // Reset Rewind Time states
                setPositionHistory1([]);
                setPositionHistory2([]);
                setRewindUsed1(false);
                setRewindUsed2(false);
                setRewindFreezeActive(false);
                if (rewindFreezeTimerRef.current) clearTimeout(rewindFreezeTimerRef.current);
                
                // Reset Final Drive states
                setFinalDriveActive1(false);
                setFinalDriveActive2(false);
                setFinalDriveUsed1(false);
                setFinalDriveUsed2(false);
                if (finalDriveTimer1Ref.current) clearTimeout(finalDriveTimer1Ref.current);
                if (finalDriveTimer2Ref.current) clearTimeout(finalDriveTimer2Ref.current);
                
                // Reset Restore Health states
                setRestoreHealthUsed1(false);
                setRestoreHealthUsed2(false);
                
                // Reset Iron Wall states
                setIronWallActive1(false);
                setIronWallActive2(false);
                setIronWallUsed1(false);
                setIronWallUsed2(false);
                if (ironWallTimer1Ref.current) clearTimeout(ironWallTimer1Ref.current);
                if (ironWallTimer2Ref.current) clearTimeout(ironWallTimer2Ref.current);
                
                // Reset Burning Strike states
                setBurningStrikeActive1(false);
                setBurningStrikeActive2(false);
                setBurningStrikeUsed1(false);
                setBurningStrikeUsed2(false);
                if (burningStrikeTimer1Ref.current) clearTimeout(burningStrikeTimer1Ref.current);
                if (burningStrikeTimer2Ref.current) clearTimeout(burningStrikeTimer2Ref.current);
              }
            }}
            disabled={waitingForRematch}
            className={`mt-2 w-full px-2 py-1 rounded text-xs font-bold transition-all ${
              waitingForRematch 
                ? 'bg-yellow-400 text-yellow-900 cursor-wait'
                : 'bg-white text-orange-600 hover:bg-orange-50'
            }`}
          >
            {waitingForRematch ? 'Waiting for opponent...' : 'Rematch'}
          </button>
        </div>
      )}
      
      {/* Player 1 wins (roundWinner === 1) - includes knockout, burst finish, and spin finish */}
      {roundWinner === 1 && !isDraw && (
        <div className="absolute top-[52px] left-1/2 -translate-x-1/2 w-full max-w-[250px] bg-cyan-600/95 border-2 border-cyan-500 rounded-lg p-2 animate-pulse z-30">
          <p className="text-white text-center font-bold text-sm">
            {isOnlineMode && matchFound ? 'YOU WIN!' : 'PLAYER 1 WINS!'}
          </p>
          <p className="text-white text-center font-semibold text-xs mt-0.5">
            {winType === 'knockout' ? (
              isPocketKO ? (
                pocketKOType === 'xtremeZone' ? 'Xtreme Finish! +3 pts' : 'Over Finish! +2 pts'
              ) : 'Knock Out! +3 pts'
            ) : 
             winType === 'burstFinish' ? 'Burst Finish! +2 pts' : 
             'Spin Finish! +1 pt'}
          </p>
          {/* Show opponent wants rematch indicator in online mode */}
          {isOnlineMode && matchFound && opponentWantsRematch && !waitingForRematch && (
            <p className="text-yellow-300 text-center text-xs mt-1 animate-pulse">
              Opponent wants rematch!
            </p>
          )}
          <button
            onClick={async () => {
              if (isOnlineMode && matchFound) {
                // Online mode: Request rematch via WebSocket (faster) or API
                setWaitingForRematch(true);
                
                // Try WebSocket first for instant response
                const ws = battleWsRef.current;
                if (ws && ws.readyState === WebSocket.OPEN) {
                  ws.send(JSON.stringify({ type: 'rematch' }));
                } else {
                  // Fallback to HTTP
                  try {
                    const token = localStorage.getItem('token');
                    const response = await fetch(`${BACKEND_URL}/api/battle/game/rematch`, {
                      method: 'POST',
                      headers: { 'Authorization': `Bearer ${token}` }
                    });
                    const data = await response.json();
                    
                    if (data.error) {
                      alert(data.error);
                      setWaitingForRematch(false);
                    }
                  } catch (error) {
                    console.error('[Rematch] Error:', error);
                    setWaitingForRematch(false);
                  }
                }
              } else {
                // Local mode: Reset immediately
                setTop1KnockedOut(false);
                setTop2KnockedOut(false);
                setTop1Active(false);
                setTop2Active(false);
                setKnockbackVelocity1({ x: 0, y: 0 });
                setKnockbackVelocity2({ x: 0, y: 0 });
                setHealth1(getMaxHealth(selectedBeyblade1?.id));
                setHealth2(getMaxHealth(selectedBeyblade2?.id));
                setStamina1(getMaxStamina(selectedBeyblade1?.id));
                setStamina2(getMaxStamina(selectedBeyblade2?.id));
                setMaxStamina1(getMaxStamina(selectedBeyblade1?.id));
                setMaxStamina2(getMaxStamina(selectedBeyblade2?.id));
                setStoppedSpinning1(false);
                setStoppedSpinning2(false);
                setRoundWinner(null);
                setWinType(null);
                setIsDraw(false);
                setDrawType(null);
                scoreAwardedRef.current = false; // Reset score award flag for new round
                
                // Reset all power-ups and cooldowns
                setDeflection1(false);
                setDeflection2(false);
                setGhostMode1(false);
                setGhostMode2(false);
                setSpeedBoost1(false);
                setSpeedBoost2(false);
                setSpeedMultiplier1(1);
                setSpeedMultiplier2(1);
                
                // Reset all cooldowns instantly
                setDeflectionCooldown1(false);
                setDeflectionCooldown2(false);
                setGhostCooldown1(false);
                setGhostCooldown2(false);
                setSpeedBoostCooldown1(false);
                setSpeedBoostCooldown2(false);
                
                // Clear all timers
                if (deflectionTimer1Ref.current) clearTimeout(deflectionTimer1Ref.current);
                if (deflectionTimer2Ref.current) clearTimeout(deflectionTimer2Ref.current);
                if (ghostTimer1Ref.current) clearTimeout(ghostTimer1Ref.current);
                if (ghostTimer2Ref.current) clearTimeout(ghostTimer2Ref.current);
                if (boostTimer1Ref.current) clearTimeout(boostTimer1Ref.current);
                if (boostTimer2Ref.current) clearTimeout(boostTimer2Ref.current);
                if (deflectionCooldownTimer1Ref.current) clearTimeout(deflectionCooldownTimer1Ref.current);
                if (deflectionCooldownTimer2Ref.current) clearTimeout(deflectionCooldownTimer2Ref.current);
                if (ghostCooldownTimer1Ref.current) clearTimeout(ghostCooldownTimer1Ref.current);
                if (ghostCooldownTimer2Ref.current) clearTimeout(ghostCooldownTimer2Ref.current);
                if (speedBoostCooldownTimer1Ref.current) clearTimeout(speedBoostCooldownTimer1Ref.current);
                if (speedBoostCooldownTimer2Ref.current) clearTimeout(speedBoostCooldownTimer2Ref.current);
                
                // Reset shadow clones
                setShadowClones1([]);
                setShadowClones2([]);
                setShadowClonesActive1(false);
                setShadowClonesActive2(false);
                setShadowCloneUsed1(false);
                setShadowCloneUsed2(false);
                if (shadowCloneTimer1Ref.current) clearTimeout(shadowCloneTimer1Ref.current);
                if (shadowCloneTimer2Ref.current) clearTimeout(shadowCloneTimer2Ref.current);
                
                // Reset Rewind Time states
                setPositionHistory1([]);
                setPositionHistory2([]);
                setRewindUsed1(false);
                setRewindUsed2(false);
                setRewindFreezeActive(false);
                if (rewindFreezeTimerRef.current) clearTimeout(rewindFreezeTimerRef.current);
                
                // Reset Final Drive states
                setFinalDriveActive1(false);
                setFinalDriveActive2(false);
                setFinalDriveUsed1(false);
                setFinalDriveUsed2(false);
                if (finalDriveTimer1Ref.current) clearTimeout(finalDriveTimer1Ref.current);
                if (finalDriveTimer2Ref.current) clearTimeout(finalDriveTimer2Ref.current);
                
                // Reset Restore Health states
                setRestoreHealthUsed1(false);
                setRestoreHealthUsed2(false);
                
                // Reset Iron Wall states
                setIronWallActive1(false);
                setIronWallActive2(false);
                setIronWallUsed1(false);
                setIronWallUsed2(false);
                if (ironWallTimer1Ref.current) clearTimeout(ironWallTimer1Ref.current);
                if (ironWallTimer2Ref.current) clearTimeout(ironWallTimer2Ref.current);
                
                // Reset Burning Strike states
                setBurningStrikeActive1(false);
                setBurningStrikeActive2(false);
                setBurningStrikeUsed1(false);
                setBurningStrikeUsed2(false);
                if (burningStrikeTimer1Ref.current) clearTimeout(burningStrikeTimer1Ref.current);
                if (burningStrikeTimer2Ref.current) clearTimeout(burningStrikeTimer2Ref.current);
              }
            }}
            disabled={waitingForRematch}
            className={`mt-2 w-full px-2 py-1 rounded text-xs font-bold transition-all ${
              waitingForRematch 
                ? 'bg-yellow-400 text-yellow-900 cursor-wait'
                : 'bg-white text-cyan-600 hover:bg-cyan-50'
            }`}
          >
            {waitingForRematch ? 'Waiting for opponent...' : 'Rematch'}
          </button>
        </div>
      )}

      {/* DRAW POPUP - Both players defeated simultaneously - Always purple */}
      {isDraw && (
        <div className="absolute top-[52px] left-1/2 -translate-x-1/2 w-full max-w-[250px] bg-purple-600/95 border-2 border-purple-500 rounded-lg p-2 animate-pulse z-30">
          <p className="text-white text-center font-bold text-sm">
            DRAW!
          </p>
          <p className="text-white text-center font-semibold text-xs mt-0.5">
            {drawType === 'knockout' ? 'Both Knocked Out!' : 
             drawType === 'burstFinish' ? 'Both Burst!' : 
             'Both Ran Out of Stamina!'}
          </p>
          <p className="text-yellow-300 text-center text-xs mt-1">
            No points awarded
          </p>
          <button
            onClick={async () => {
              if (isOnlineMode && matchFound) {
                // Online mode: Request rematch via WebSocket (faster) or API
                setWaitingForRematch(true);
                
                // Try WebSocket first for instant response
                const ws = battleWsRef.current;
                if (ws && ws.readyState === WebSocket.OPEN) {
                  ws.send(JSON.stringify({ type: 'rematch' }));
                } else {
                  // Fallback to HTTP
                  try {
                    const token = localStorage.getItem('token');
                    const response = await fetch(`${BACKEND_URL}/api/battle/game/rematch`, {
                      method: 'POST',
                      headers: { 'Authorization': `Bearer ${token}` }
                    });
                    const data = await response.json();
                    if (data.error) {
                      alert(data.error);
                      setWaitingForRematch(false);
                    }
                  } catch (error) {
                    console.error('[Rematch] Error:', error);
                    setWaitingForRematch(false);
                  }
                }
              } else {
                // Local mode: Reset immediately
                setIsDraw(false);
                setDrawType(null);
                setTop1KnockedOut(false);
                setTop2KnockedOut(false);
                setTop1Active(false);
                setTop2Active(false);
                setKnockbackVelocity1({ x: 0, y: 0 });
                setKnockbackVelocity2({ x: 0, y: 0 });
                setHealth1(getMaxHealth(selectedBeyblade1?.id));
                setHealth2(getMaxHealth(selectedBeyblade2?.id));
                setStamina1(getMaxStamina(selectedBeyblade1?.id));
                setStamina2(getMaxStamina(selectedBeyblade2?.id));
                setMaxStamina1(getMaxStamina(selectedBeyblade1?.id));
                setMaxStamina2(getMaxStamina(selectedBeyblade2?.id));
                setStoppedSpinning1(false);
                setStoppedSpinning2(false);
                setRoundWinner(null);
                setWinType(null);
                scoreAwardedRef.current = false; // Reset score award flag for new round
                setDeflection1(false);
                setDeflection2(false);
                setGhostMode1(false);
                setGhostMode2(false);
                setSpeedBoost1(false);
                setSpeedBoost2(false);
                setSpeedMultiplier1(1);
                setSpeedMultiplier2(1);
                setDeflectionCooldown1(false);
                setDeflectionCooldown2(false);
                setGhostCooldown1(false);
                setGhostCooldown2(false);
                setSpeedBoostCooldown1(false);
                setSpeedBoostCooldown2(false);
              }
            }}
            disabled={waitingForRematch}
            className={`mt-2 w-full px-2 py-1 rounded text-xs font-bold transition-all ${
              waitingForRematch 
                ? 'bg-yellow-400 text-yellow-900 cursor-wait'
                : 'bg-white text-purple-600 hover:bg-purple-50'
            }`}
          >
            {waitingForRematch ? 'Waiting for opponent...' : 'Rematch'}
          </button>
        </div>
      )}

      {/* Matchmaking Modal */}
      {showMatchmaking && isOnlineMode && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="bg-slate-800 border-2 border-cyan-500 rounded-lg p-6 max-w-md w-full">
            <h2 className="text-2xl font-bold text-cyan-400 mb-4 text-center">Online Battle</h2>
            
            {isSearching || waitingForOpponent ? (
              <div className="text-center">
                <div className="text-white mb-4">
                  {isSearching ? '🔍 Searching for opponent...' : `⏳ Waiting for opponent...\nCode: ${matchCode}`}
                </div>
                <div className="flex gap-2 justify-center mb-4">
                  <div className="w-3 h-3 bg-cyan-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                  <div className="w-3 h-3 bg-cyan-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                  <div className="w-3 h-3 bg-cyan-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
                </div>
                <button
                  onClick={async () => {
                    // Cancel matchmaking via HTTP
                    try {
                      const token = localStorage.getItem('token');
                      await fetch(`${BACKEND_URL}/api/battle/matchmaking/cancel`, {
                        method: 'POST',
                        headers: { 'Authorization': `Bearer ${token}` }
                      });
                    } catch (error) {
                      console.error('[Matchmaking] Cancel failed:', error);
                    }
                    
                    // Stop polling and reset state
                    stopAllPolling();
                    setIsSearching(false);
                    setWaitingForOpponent(false);
                    setMatchCode('');
                  }}
                  className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded font-bold"
                >
                  Cancel
                </button>
              </div>
            ) : (
              <>
                <button
                  onClick={async () => {
                    setIsSearching(true);
                    try {
                      const token = localStorage.getItem('token');
                      const response = await fetch(`${BACKEND_URL}/api/battle/matchmaking/random`, {
                        method: 'POST',
                        headers: { 
                          'Authorization': `Bearer ${token}`,
                          'Content-Type': 'application/json'
                        }
                      });
                      
                      if (response.ok) {
                        const data = await response.json();
                        
                        // Check if matched immediately
                        if (data.matched) {
                          
                          // CRITICAL: Prevent self-matching
                          if (data.opponent_username === user?.username) {
                            console.error('[Random Matchmaking] ERROR: Matched with yourself! Cancelling...');
                            alert('Error: Cannot match with yourself. Please try again.');
                            setIsSearching(false);
                            // Cancel the match on backend
                            try {
                              await fetch(`${BACKEND_URL}/api/battle/matchmaking/cancel`, {
                                method: 'POST',
                                headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
                              });
                            } catch (e) {
                              console.error('Failed to cancel match:', e);
                            }
                            return;
                          }
                          
                          setMatchFound(true);
                          setOpponentUsername(data.opponent_username);
                          setOpponentRosterIds(data.opponent_roster || []);
                          setMyPlayerNumber(data.your_player_number);
                          setShowMatchmaking(false);
                          setIsSearching(false);
                          setMyScore(0);
                          setOpponentScore(0);
                          
                          // Start game state polling IMMEDIATELY
                          startGameStatePoll();
                          
                          // Show notification after a short delay (non-blocking to allow sync to start)
                          setTimeout(() => {
                            alert(`Match found! You're playing against ${data.opponent_username}!`);
                          }, 500);
                        } else {
                          // In queue - start polling
                          startMatchmakingPoll();
                        }
                      } else {
                        throw new Error('Failed to start matchmaking');
                      }
                    } catch (error) {
                      console.error('[Matchmaking] Failed:', error);
                      setIsSearching(false);
                      alert('Connection failed. Online multiplayer is currently unavailable. Please try Local mode.');
                    }
                  }}
                  className="w-full px-4 py-3 bg-green-600 hover:bg-green-700 text-white rounded-lg font-bold mb-3 transition-all"
                >
                  Random Match
                </button>
                
                <div className="relative my-4">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-slate-600"></div>
                  </div>
                  <div className="relative flex justify-center text-sm">
                    <span className="px-2 bg-slate-800 text-slate-400">OR</span>
                  </div>
                </div>
                
                <div className="space-y-3">
                  <input
                    type="text"
                    placeholder="Enter 8-digit code"
                    maxLength={8}
                    value={matchCode}
                    onChange={(e) => setMatchCode(e.target.value.toUpperCase())}
                    className="w-full px-4 py-2 bg-slate-900 border border-slate-600 rounded text-white placeholder-slate-500 text-center font-mono text-lg tracking-widest"
                  />
                  <button
                    onClick={async () => {
                      if (matchCode.length !== 8) {
                        alert('Code must be 8 characters!');
                        return;
                      }
                      setWaitingForOpponent(true);
                      try {
                        const token = localStorage.getItem('token');
                        const response = await fetch(`${BACKEND_URL}/api/battle/matchmaking/code`, {
                          method: 'POST',
                          headers: { 
                            'Authorization': `Bearer ${token}`,
                            'Content-Type': 'application/json'
                          },
                          body: JSON.stringify({ code: matchCode })
                        });
                        
                        if (response.ok) {
                          const data = await response.json();
                          
                          // Check if matched immediately (2nd player joining)
                          if (data.matched) {
                            
                            // Prevent self-matching (frontend check)
                            if (data.opponent_username === user?.username) {
                              console.error('[Code Matchmaking] Self-match detected! Cannot play against yourself.');
                              alert('Cannot match with yourself! Please use a different code or try again.');
                              setWaitingForOpponent(false);
                              return;
                            }
                            
                            setMatchFound(true);
                            setOpponentUsername(data.opponent_username);
                            setOpponentRosterIds(data.opponent_roster || []);
                            setMyPlayerNumber(data.your_player_number);
                            setShowMatchmaking(false);
                            setWaitingForOpponent(false);
                            setMyScore(0);
                            setOpponentScore(0);
                            
                            // Start game state polling IMMEDIATELY
                            startGameStatePoll();
                            
                            // Show notification after a short delay (non-blocking to allow sync to start)
                            setTimeout(() => {
                              alert(`Match found! You're playing against ${data.opponent_username}!`);
                            }, 500);
                          } else {
                            // Waiting for opponent - start polling
                            startMatchmakingPoll();
                          }
                        } else {
                          throw new Error('Failed to join with code');
                        }
                      } catch (error) {
                        console.error('[Matchmaking] Failed:', error);
                        setWaitingForOpponent(false);
                        alert('Connection failed. Online multiplayer is currently unavailable. Please try Local mode.');
                      }
                    }}
                    className="w-full px-4 py-3 bg-cyan-600 hover:bg-cyan-700 text-white rounded-lg font-bold transition-all"
                  >
                    Find Blader
                  </button>
                </div>
                
                <button
                  onClick={() => {
                    // Cancel any ongoing matchmaking on backend
                    const token = localStorage.getItem('token');
                    fetch(`${BACKEND_URL}/api/battle/matchmaking/cancel`, {
                      method: 'POST',
                      headers: { 'Authorization': `Bearer ${token}` }
                    }).catch(err => console.error('Cancel on back to local:', err));
                    
                    // Leave any active match
                    fetch(`${BACKEND_URL}/api/battle/game/leave`, {
                      method: 'POST',
                      headers: { 'Authorization': `Bearer ${token}` }
                    }).catch(err => console.error('Leave match on back to local:', err));
                    
                    stopAllPolling();
                    
                    // RESET BATTLE STATE - stop any spinning beyblades
                    setTop1Active(false);
                    setTop2Active(false);
                    setTop1Pos({ x: 400, y: 300 });
                    setTop2Pos({ x: 400, y: 300 });
                    setTop1Velocity({ x: 0, y: 0 });
                    setTop2Velocity({ x: 0, y: 0 });
                    setKnockbackVelocity1({ x: 0, y: 0 });
                    setKnockbackVelocity2({ x: 0, y: 0 });
                    setTop1KnockedOut(false);
                    setTop2KnockedOut(false);
                    setIsControlling1(false);
                    setIsControlling2(false);
                    
                    // Reset health bars, stamina, and ALL scoreboards
                    setHealth1(getMaxHealth(selectedBeyblade1?.id));
                    setHealth2(getMaxHealth(selectedBeyblade2?.id));
                    setStamina1(getMaxStamina(selectedBeyblade1?.id));
                    setStamina2(getMaxStamina(selectedBeyblade2?.id));
                    setMaxStamina1(getMaxStamina(selectedBeyblade1?.id));
                    setMaxStamina2(getMaxStamina(selectedBeyblade2?.id));
                    setStoppedSpinning1(false);
                    setStoppedSpinning2(false);
                    setMyScore(0);
                    setOpponentScore(0);
                    setPlayer1Score(0);
                    setPlayer2Score(0);
                    setRoundWinner(null);
                    
                    setShowMatchmaking(false);
                    setIsOnlineMode(false);
                    setIsSearching(false);
                    setWaitingForOpponent(false);
                    setMatchCode('');
                    setMatchFound(false);
                    setOpponentUsername(null);
                    setWaitingForRematch(false);
                    setOpponentWantsRematch(false);
                    setCurrentRound(1);
                  }}
                  className="w-full mt-4 px-4 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded font-bold"
                >
                  Back to Local
                </button>
              </>
            )}
          </div>
        </div>
      )}

      {/* Stats Popup Modal (Long Press on Roster) */}
      {statsPopup && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-[60]" onClick={() => setStatsPopup(null)}>
          <div 
            className={`bg-gradient-to-br ${statsPopup.colors.bg} border-2 ${statsPopup.colors.border} rounded-xl p-5 w-80 mx-4 relative`}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close button */}
            <button 
              onClick={() => setStatsPopup(null)}
              className="absolute top-2 right-2 text-slate-400 hover:text-white transition-colors text-xl font-bold"
            >
              ×
            </button>

            {/* Beyblade Image and Name */}
            <div className="flex flex-col items-center mb-4">
              <img 
                src={statsPopup.url} 
                alt={statsPopup.name}
                className="w-24 h-24 object-contain mb-2"
              />
              <h3 className={`text-xl font-bold ${statsPopup.colors.accent} digital-font text-center`}>{statsPopup.name}</h3>
            </div>

            {/* Stats Grid - Fixed height for consistency */}
            <div className="space-y-2">
              {/* Stamina Bar */}
              <div className="flex items-center gap-3 bg-slate-800/60 rounded-lg p-2 h-14">
                <span className="text-white text-lg">⏱️</span>
                <div className="flex-1">
                  <div className="text-xs text-slate-400 mb-1">Stamina</div>
                  <div className="flex items-center gap-2">
                    <div className="flex-1 h-2 bg-slate-700 rounded-full overflow-hidden">
                      <div className="h-full w-full bg-gradient-to-r from-white to-gray-300" />
                    </div>
                    <span className="text-white font-bold text-sm min-w-[45px] text-right">{Math.floor(statsPopup.stats.stamina / 60)}:{String(statsPopup.stats.stamina % 60).padStart(2, '0')}</span>
                  </div>
                </div>
              </div>

              {/* Health Bar */}
              <div className="flex items-center gap-3 bg-slate-800/60 rounded-lg p-2 h-14">
                <span className="text-red-400 text-lg">❤️</span>
                <div className="flex-1">
                  <div className="text-xs text-slate-400 mb-1">Health</div>
                  <div className="flex items-center gap-2">
                    <div className="flex-1 h-2 bg-slate-700 rounded-full overflow-hidden">
                      <div className="h-full w-full bg-gradient-to-r from-red-500 to-pink-500" />
                    </div>
                    <span className="text-white font-bold text-sm min-w-[45px] text-right">{statsPopup.stats.health}/{statsPopup.stats.health}</span>
                  </div>
                </div>
              </div>

              {/* Basic Damage */}
              <div className="flex items-center gap-3 bg-slate-800/60 rounded-lg p-2 h-14">
                <span className="text-orange-400 text-lg">⚔️</span>
                <div className="flex-1">
                  <div className="text-xs text-slate-400">Basic Damage</div>
                  <span className="text-white font-bold">{statsPopup.stats.basicDamage}</span>
                </div>
              </div>

              {/* Boost Damage */}
              <div className="flex items-center gap-3 bg-slate-800/60 rounded-lg p-2 h-14">
                <span className="text-yellow-400 text-lg">⚡</span>
                <div className="flex-1">
                  <div className="text-xs text-slate-400">Boost Damage</div>
                  <span className="text-white font-bold">{statsPopup.stats.boostDamage}</span>
                </div>
              </div>

              {/* Speed */}
              <div className="flex items-center gap-3 bg-slate-800/60 rounded-lg p-2 h-14">
                <span className="text-green-400 text-lg">💨</span>
                <div className="flex-1">
                  <div className="text-xs text-slate-400">Speed</div>
                  <span className="text-white font-bold">{statsPopup.stats.speed}%</span>
                </div>
              </div>

              {/* Spin Direction */}
              <div className="flex items-center gap-3 bg-slate-800/60 rounded-lg p-2 h-14">
                <span className="text-cyan-400 text-lg">↻</span>
                <div className="flex-1">
                  <div className="text-xs text-slate-400">Spin Direction</div>
                  <span className={`font-bold ${
                    statsPopup.stats.spinDirection === 'Left' ? 'text-purple-400' :
                    statsPopup.stats.spinDirection.includes('Dual') ? 'bg-gradient-to-r from-purple-400 to-orange-400 bg-clip-text text-transparent' :
                    'text-orange-400'
                  }`}>{statsPopup.stats.spinDirection}</span>
                </div>
              </div>

              {/* Hidden Ability */}
              <div className="flex items-start gap-3 bg-slate-800/60 rounded-lg p-2 min-h-[56px]">
                <span className="text-purple-400 text-lg mt-0.5">👁️</span>
                <div className="flex-1">
                  <div className="text-xs text-slate-400">Hidden Ability</div>
                  <span className={`text-sm leading-tight ${statsPopup.stats.hiddenAbility === 'None' ? 'text-slate-500' : 'text-purple-300'}`}>
                    {statsPopup.stats.hiddenAbility}
                  </span>
                </div>
              </div>

              {/* Special Move */}
              <div className="flex items-start gap-3 bg-slate-800/60 rounded-lg p-2 min-h-[56px]">
                <span className="text-yellow-400 text-lg mt-0.5">⭐</span>
                <div className="flex-1">
                  <div className="text-xs text-slate-400">Special Move</div>
                  <span className={`text-sm leading-tight ${statsPopup.stats.specialMove === 'None' ? 'text-slate-500' : 'text-yellow-300'}`}>
                    {statsPopup.stats.specialMove}
                  </span>
                </div>
              </div>
            </div>

            {/* Hint text */}
            <p className="text-xs text-slate-500 text-center mt-3">Tap outside or × to close</p>
          </div>
        </div>
      )}
    </div>
  );
});

export default InteractiveMap;
